import React, { useCallback, useEffect, useMemo, useRef, useState } from "https://esm.sh/react@18.2.0";
import { createRoot } from "https://esm.sh/react-dom@18.2.0/client";
import { createPortal } from "https://esm.sh/react-dom@18.2.0";
import htm from "https://esm.sh/htm@3.1.1";
import { io } from "https://cdn.socket.io/4.5.4/socket.io.esm.min.js";
import { PriceBox } from "./chart/box-primitive.js";
import { DrawingLayer } from "./chart/drawing-layer.js";
import { SessionBands } from "./chart/session-bands.js";
import { EventLane } from "./chart/event-lane.js";
import { DrawingController } from "./chart/drawing-controller.js";
import { composeShareImage, shareImage } from "./chart/share-image.js";
import { VolumeProfile } from "./chart/volume-profile.js";

const html = htm.bind(React.createElement);

// lightweight-charts is ~149KB of the customer bundle -- the single largest
// dependency in it -- and exactly one route draws a chart. It used to be a
// static import, so /dashboard, /settings, /journal, /help and the LOGIN page
// all downloaded, parsed and evaluated a charting engine they never call.
//
// Loaded on demand instead. esbuild's `splitting` turns this dynamic import
// into its own chunk, so the charting engine leaves the main bundle entirely
// and arrives only when a chart is actually about to be created. The module is
// cached after the first load, so switching timeframes or leaving and
// returning to /chart costs nothing.
let LWC = null;
let lwcPromise = null;

function loadCharting() {
  if (LWC) return Promise.resolve(LWC);
  if (!lwcPromise) {
    lwcPromise = import("https://esm.sh/lightweight-charts@4.1.1?bundle").then((mod) => {
      LWC = mod;
      return mod;
    });
  }
  return lwcPromise;
}

// Must match the server's TIMEFRAMES exactly. The mislabelled frames (3m/45m/
// 2h/3h/1W/1M) were removed on the server because each served a different bar
// size than its label; offering them here would hand the dropdown values the
// server now answers with 422.
const TIMEFRAME_GROUPS = [
  {
    label: "timeframes.minutes",
    items: [
      { value: "1m", label: "timeframes.1m" },
      { value: "5m", label: "timeframes.5m" },
      { value: "15m", label: "timeframes.15m" },
      { value: "30m", label: "timeframes.30m" }
    ]
  },
  {
    label: "timeframes.hours",
    items: [
      { value: "1h", label: "timeframes.1h" },
      { value: "4h", label: "timeframes.4h" }
    ]
  },
  {
    label: "timeframes.days",
    items: [
      { value: "1D", label: "timeframes.1D" }
    ]
  }
];

const VALID_TIMEFRAMES = new Set(
  TIMEFRAME_GROUPS.flatMap((group) => group.items.map((item) => item.value))
);

// The full series wardrobe (parity list: "chart core"), offered in the
// control centre -- the ONE home of chart style. Every one of these is a
// rendering of the SAME canonical OHLC -- Heikin-Ashi is a deterministic
// transform every platform defines identically, computed here from real
// bars and labelled as what it is.
const CHART_STYLES = [
  { value: "candlestick", label: "chart.style_candles", fallback: "Candles", icon: "fa-chart-bar" },
  { value: "hollow", label: "chart.style_hollow", fallback: "Hollow", icon: "fa-square" },
  { value: "bars", label: "chart.style_bars", fallback: "Bars", icon: "fa-bars-staggered" },
  { value: "heikin", label: "chart.style_heikin", fallback: "Heikin-Ashi", icon: "fa-chart-simple" },
  { value: "area", label: "chart.style_area", fallback: "Area", icon: "fa-chart-area" },
  { value: "line", label: "buttons.line", fallback: "Line", icon: "fa-chart-line" },
];

// Heikin-Ashi from real OHLC: haClose = (o+h+l+c)/4,
// haOpen = (prev haOpen + prev haClose)/2, seeded from the first bar.
function heikinAshi(bars) {
  const out = [];
  let prevOpen = null, prevClose = null;
  for (const bar of bars) {
    const haClose = (bar.open + bar.high + bar.low + bar.close) / 4;
    const haOpen = prevOpen == null
      ? (bar.open + bar.close) / 2
      : (prevOpen + prevClose) / 2;
    out.push({
      time: bar.time,
      open: haOpen,
      high: Math.max(bar.high, haOpen, haClose),
      low: Math.min(bar.low, haOpen, haClose),
      close: haClose,
    });
    prevOpen = haOpen; prevClose = haClose;
  }
  return out;
}

const HISTORY_DAYS = 30;
const MAX_HISTORY_DAYS = 365;
const INCREMENT_DAYS = 15;

function normalizeTimeframe(value) {
  if (!value) return "1h";
  return VALID_TIMEFRAMES.has(value) ? value : "1h";
}

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop().split(";").shift();
  }
  return null;
}

function getInitialLanguage() {
  return (
    localStorage.getItem("preferredLanguage") ||
    sessionStorage.getItem("preferredLanguage") ||
    getCookie("preferredLanguage") ||
    "en"
  );
}

function clearAuthData() {
  sessionStorage.removeItem("access_token");
  sessionStorage.removeItem("refresh_token");
  sessionStorage.removeItem("user");
  sessionStorage.removeItem("preferredTimeframe");

  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("user");

  ["access_token", "refresh_token", "session"].forEach((cookieName) => {
    document.cookie = `${cookieName}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    document.cookie = `${cookieName}=; path=/; domain=${window.location.hostname}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  });
}

function redirectToLogin() {
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
}

function getAuthToken() {
  let token = sessionStorage.getItem("access_token");
  if (!token) {
    token = getCookie("access_token");
    if (token) {
      sessionStorage.setItem("access_token", token);
    }
  }
  return token;
}

function getUserInfo() {
  const userStr = sessionStorage.getItem("user");
  if (!userStr) return null;
  try {
    return JSON.parse(userStr);
  } catch (error) {
    return null;
  }
}

function storeAuth(data) {
  if (!data) return;
  if (data.access_token) {
    sessionStorage.setItem("access_token", data.access_token);
    // NO document.cookie write. The server sets access_token itself as an
    // HttpOnly cookie on login and refresh; a JS-written copy of the JWT is
    // exactly the artifact HttpOnly exists to prevent (any XSS could read
    // it), and browsers ignore a non-HTTP set over an HttpOnly cookie
    // anyway. Requests authenticate via the header when this tab has a
    // token, and via the HttpOnly cookie otherwise.
  }
  if (data.refresh_token) {
    sessionStorage.setItem("refresh_token", data.refresh_token);
  }
  if (data.user) {
    sessionStorage.setItem("user", JSON.stringify(data.user));
  }
}

async function refreshToken() {
  const refreshTokenValue = sessionStorage.getItem("refresh_token");
  if (!refreshTokenValue) {
    return null;
  }
  const response = await fetch("/api/auth/refresh", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refreshTokenValue })
  });
  if (!response.ok) {
    return null;
  }
  const data = await response.json();
  storeAuth(data);
  return data;
}

// Single-flight: when the access token dies, every poller 401s at once;
// racing refreshes let a loser's cookie-retry wipe the winner's fresh
// token. One refresh serves them all.
let refreshInFlight = null;

async function authenticatedFetch(url, options = {}) {
  const token = getAuthToken();
  const headers = {
    "Content-Type": "application/json",
    ...options.headers
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  let response = await fetch(url, { ...options, headers, credentials: "include" });

  if (response.status === 401) {
    let refreshed = null;
    try {
      refreshInFlight = refreshInFlight || refreshToken()
        .finally(() => { refreshInFlight = null; });
      refreshed = await refreshInFlight;
    }
    catch (e) { /* a refresh that THROWS must not strand the dance */ }
    if (!refreshed || !refreshed.access_token) {
      // No refresh available (a cookie-only session, or a transient server
      // blip mid-poll). The HttpOnly cookie may still be perfectly valid --
      // so retry ONCE with the header dropped, letting the cookie speak for
      // itself: a stored token must never outvote a good cookie, and one
      // stray 401 must never throw a working session onto the login page.
      await new Promise((resolve) => setTimeout(resolve, 900));
      const cookieHeaders = { "Content-Type": "application/json", ...options.headers };
      delete cookieHeaders.Authorization;
      try { sessionStorage.removeItem("access_token"); } catch (e) { /* private mode */ }
      const cookieRetry = await fetch(url, { ...options, headers: cookieHeaders,
                                             credentials: "include" });
      if (cookieRetry.status !== 401) {
        return cookieRetry;
      }
      clearAuthData();
      redirectToLogin();
      throw new Error("Authentication failed");
    }

    const retryHeaders = {
      "Content-Type": "application/json",
      ...options.headers,
      Authorization: `Bearer ${refreshed.access_token}`
    };
    response = await fetch(url, { ...options, headers: retryHeaders, credentials: "include" });
  }

  if (response.status === 401) {
    clearAuthData();
    redirectToLogin();
    throw new Error("Authentication failed");
  }

  return response;
}

// ---- Web Push -------------------------------------------------------------
// Browsers hand the VAPID public key over as raw bytes, so the base64url the
// server stores has to be converted before subscribe() will accept it.
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}


// Whether THIS browser will actually receive a push.
//
// The server knowing about a subscription is not the same fact: a row can
// outlive the browser profile that created it, and then the app cheerfully
// says "alerts are on" while every push goes to a dead endpoint and the user
// sits waiting for a notification that can never arrive. That is exactly the
// failure this reconciliation exists to end -- the truth lives in the browser,
// so ask the browser, and let the server row be the weaker claim.
async function localPushSubscription() {
  try {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return null;
    if (!window.isSecureContext) return null;
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return null;
    return await registration.pushManager.getSubscription();
  } catch (error) {
    return null;
  }
}

// The server's row and this browser, reconciled into one honest state.
async function reconcilePushState(config) {
  const local = await localPushSubscription();
  if (local) {
    // This device is genuinely subscribed. If the server does not know that
    // -- a restored profile, a wiped database -- re-register silently rather
    // than making the user press a button to fix our bookkeeping.
    if (!config.subscribed) {
      try {
        await authenticatedFetch("/api/push/subscribe", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ subscription: local.toJSON() }),
        });
      } catch (error) { /* the state below is still correct for this device */ }
    }
    return { subscribed: true, stale: false };
  }
  // No subscription here. If the server thinks there is one, it belongs to
  // another browser or a profile that is gone; say so, because "on" would be
  // a lie on this screen.
  return { subscribed: false, stale: !!config.subscribed };
}

async function enablePushNotifications() {
  // Six things have to be true for an alert to arrive, and when one of them is
  // not, the user pressed a button and nothing happened. Every step below
  // reports WHICH step refused, and keeps the browser's own message -- that
  // sentence is usually the entire answer, and throwing it away is what made
  // this feature look broken rather than blocked.
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    return { ok: false, reason: "unsupported" };
  }
  // Web Push needs a secure context. https, or localhost -- a LAN address like
  // http://192.168.1.20:5000 is NOT one, which is the single most likely
  // reason this silently does nothing while everything looks configured.
  if (!window.isSecureContext) {
    return { ok: false, reason: "insecure", detail: window.location.origin };
  }

  let config;
  try {
    config = await authenticatedFetch("/api/push/config").then((r) => r.json());
  } catch (error) {
    return { ok: false, reason: "no_server", detail: String(error && error.message) };
  }
  if (!config.enabled || !config.public_key) return { ok: false, reason: "not_configured" };

  let permission;
  try {
    permission = await Notification.requestPermission();
  } catch (error) {
    return { ok: false, reason: "permission_failed", detail: String(error && error.message) };
  }
  if (permission !== "granted") return { ok: false, reason: permission };

  let subscription;
  try {
    const registration = await navigator.serviceWorker.ready;
    subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(config.public_key),
      });
    }
  } catch (error) {
    // The browser itself refused. Private/incognito windows refuse outright,
    // and so does a profile with push disabled at the OS level.
    return { ok: false, reason: "browser_refused", detail: String(error && error.message) };
  }

  try {
    const response = await authenticatedFetch("/api/push/subscribe", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ subscription: subscription.toJSON() }),
    });
    const payload = await response.json();
    return { ok: !!payload.success, reason: payload.error || "server_refused" };
  } catch (error) {
    return { ok: false, reason: "no_server", detail: String(error && error.message) };
  }
}

async function disablePushNotifications() {
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return { ok: true };
  await authenticatedFetch("/api/push/unsubscribe", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ endpoint: subscription.endpoint }),
  });
  await subscription.unsubscribe();
  return { ok: true };
}

// Theme, resolved before the first paint. Reading it inside a component
// would paint dark first and then correct itself, which is the flash every
// theme toggle is judged by.
function resolveTheme(choice) {
  if (choice === "light" || choice === "dark") return choice;
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

function applyTheme(choice) {
  const resolved = resolveTheme(choice);
  document.documentElement.setAttribute("data-theme", resolved);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", resolved === "light" ? "#FAF8F4" : "#06080D");
  window.dispatchEvent(new CustomEvent("theme:change", { detail: resolved }));
  return resolved;
}

function readThemeChoice() {
  try { return localStorage.getItem("theme") || "system"; } catch (e) { return "system"; }
}

applyTheme(readThemeChoice());

function formatOpensIn(iso, lang) {
  // A countdown answers "how long"; a trader planning the weekend asks
  // "when". Inside a session gap the countdown is the better answer —
  // beyond eight hours, the moment itself is.
  const then = Date.parse(iso);
  if (!isFinite(then)) return "—";
  const mins = Math.max(0, Math.round((then - Date.now()) / 60000));
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 8) return `${hours}h ${mins % 60}m`;
  const at = new Date(then);
  const locale = lang === "ar" ? "ar" : undefined;
  const day = at.toLocaleDateString(locale, { weekday: "short" });
  const time = at.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  return `${day} ${time}`;
}

function formatPrice(value) {
  // Returning "0.00" for a missing value fabricates a price -- the exact fault
  // the dashboard's own comments forbid elsewhere. An em dash is absence.
  if (typeof value !== "number" || Number.isNaN(value)) return "—";
  return value.toFixed(2);
}

// Times and dates follow the CHOSEN language, with Western digits (the
// app's numeral convention everywhere). Locale-less calls fell back to the
// browser, so an Arabic reader got "3:02:23 AM" inside an Arabic sentence.
function appLocale() {
  // NUMBERS. 'ar-u-nu-latn' groups as 4,349.30 -- Western digits, dot decimal,
  // which is what a gold trader reads.
  const lang = document.documentElement.lang || "en";
  return lang === "ar" ? "ar-u-nu-latn" : lang;
}

function appDateLocale() {
  // DATES, and deliberately a DIFFERENT locale from the numbers one.
  //
  // Neither Arabic locale is right for both jobs: 'ar-u-nu-latn' formats
  // 4,349.30 correctly but names the month أغسطس (Egyptian/Gulf), while
  // 'ar-LB-u-nu-latn' says آب as a Lebanese reader expects and formats the
  // same price as 4.349,30 -- German grouping, which is unreadable here. The
  // landing had one and the app had the other, so the product showed German
  // prices on the web and Egyptian months in the app. Each locale now does
  // only the job it is right for.
  const lang = document.documentElement.lang || "en";
  return lang === "ar" ? "ar-LB-u-nu-latn" : lang;
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  return `${date.toLocaleDateString(appDateLocale())} ${date.toLocaleTimeString(appDateLocale())}`;
}

// Bidi isolate (LRI…PDI). A number dropped into RTL text is directionally
// weak: "18 شراء · 0 بيع" re-orders on screen until the 18 sits beside بيع
// and the Arabic dashboard reports the opposite of the data. Every number
// interpolated into a translatable string goes through this.
function iso(value) {
  return `⁦${value}⁩`;
}

// R with never-a-signed-zero: two decimals normally; a non-zero R that
// would round to 0.00 keeps enough digits to stay visibly non-zero.
function formatR(r) {
  const value = Number(r);
  if (!Number.isFinite(value)) return "";
  const sign = value > 0 ? "+" : "";
  if (value !== 0 && Math.abs(value) < 0.005) return `${sign}${value.toFixed(4)}R`;
  return `${sign}${value.toFixed(2)}R`;
}

// The engine names regimes in snake_case ('trending_up'). Those enums leaked
// into the timeframe menu and the Why sheet verbatim — customer UI speaking
// the codebase's dialect. One translation point for every place a regime shows.
function regimeLabel(t, regime) {
  if (!regime) return "";
  const key = String(regime).toLowerCase();
  const fallback = key.replace(/_/g, " ");
  return t(`regime.${key}`, fallback);
}

// A "point" is {ns, key, args} -- a sentence identified rather than written.
// Batch 18: Python returns data and a key, and the sentence lives in the
// locale so it can be read in either language.
//
// ns 'raw' is the honest half-way state: explain.py COMPOSES lines written by
// other modules, and the ones not yet converted travel as finished English
// and are rendered verbatim. A guard counts them so the debt only shrinks.
function renderPoint(t, point) {
  if (!point) return "";
  if (point.ns === "raw") return point.args?.text || "";
  const args = { ...(point.args || {}) };
  // Engine enums run through the SAME dictionaries every other surface uses,
  // rather than gaining a second spelling inside a sentence.
  if (args.state) args.state = t(`mstate.${args.state}`, args.state);
  // The two halves of a transition are states too. They were left raw, so
  // "انتقل السوق expansion ← compression" put two English enums inside an
  // otherwise Arabic sentence.
  if (args.was) args.was = t(`mstate.${args.was}`, args.was);
  if (args.now) args.now = t(`mstate.${args.now}`, args.now);
  if (args.regime) args.regime = regimeLabel(t, args.regime);
  if (args.movement) args.movement = t(`mktcond.cln_${args.movement}`, args.movement);
  if (args.volatility) args.volatility = t(`mktcond.vol_${args.volatility}`, args.volatility);
  if (Array.isArray(args.topics)) args.topics = args.topics.join("، ");
  if (args.label === "generic_level") args.label = t("narr.generic_level", "level");
  return formatTemplate(t(`${point.ns}.${point.key}`, ""), args);
}


// Alert notes store the chart's level codes ("S/R x2 + D-R1") -- honest
// provenance, wrong dialect for a settings row. Mirror of the server's
// describe_note(); the codes stay in the data.
function levelNote(t, note) {
  if (!note) return "";
  return note.split("+").map((raw) => {
    const token = raw.trim();
    let m = token.match(/^S\/R x(\d+)$/);
    if (m) {
      return Number(m[1]) > 1
        ? formatTemplate(t("levels.touched_n", "level touched {n}×"), { n: iso(m[1]) })
        : t("levels.swing", "swing level");
    }
    m = token.match(/^([DW])-(P|R1|R2|S1|S2)$/);
    if (m) {
      const scope = m[1] === "D" ? t("levels.daily", "daily") : t("levels.weekly", "weekly");
      const code = {
        P: t("levels.pivot", "pivot"),
        R1: t("levels.r1", "resistance 1"), R2: t("levels.r2", "resistance 2"),
        S1: t("levels.s1", "support 1"), S2: t("levels.s2", "support 2"),
      }[m[2]];
      // word order is the locale's call: "daily pivot" vs "البيفوت اليومي"
      return formatTemplate(t("levels.scoped", "{scope} {code}"), { scope, code });
    }
    if (/^\$[\d,]+$/.test(token)) {
      return `${t("levels.round", "round number")} ${iso(token)}`;
    }
    return token;
  }).join(" + ");
}

// The server composes push notifications, so it must know the language the
// user chose — localStorage alone never reaches the daemon.
function persistLanguage(lang) {
  lastSyncedLanguage = lang;
  authenticatedFetch("/api/preferences", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ language: lang }),
  }).catch(() => { lastSyncedLanguage = null; });
}

// THE SCREEN AND THE NOTIFICATIONS MUST SPEAK THE SAME LANGUAGE.
//
// persistLanguage only ran when somebody pressed a language control. Every
// other way the app arrives at a language — the cookie from a previous
// session, localStorage, the browser's own header on first visit — left the
// SERVER's copy untouched. So the owner read an Arabic app for months while
// user_preferences.language said "en", and every push he received (the
// morning briefing among them) was composed in English by a server doing
// exactly what it was told.
//
// One reconciliation at boot, and only when the two actually differ: a blind
// PUT on every mount would write a preference row on every page load.
let lastSyncedLanguage = null;

async function reconcileLanguage(effective) {
  if (!effective || lastSyncedLanguage === effective) return;
  try {
    const prefs = await (await authenticatedFetch("/api/preferences")).json();
    const stored = prefs?.preferences?.language;
    lastSyncedLanguage = stored || null;
    if (stored && stored !== effective) persistLanguage(effective);
  } catch (e) { /* a guest, or the server is down: nothing to reconcile */ }
}

// ------------------------------------------------------------ alert sound
// The client picks the in-app alert tone. A web app cannot choose the OS
// notification sound — the device does, when the app is closed. What we own
// is the chime played when an alert lands while the product is open. Tones
// are synthesized, so there is nothing to download, cache, or license.
const ALERT_SOUNDS = ["chime", "bell", "pulse", "off"];

function alertSoundChoice() {
  let stored = null;
  try { stored = localStorage.getItem("sv_alert_sound"); } catch (e) {}
  return ALERT_SOUNDS.includes(stored) ? stored : "chime";
}

function setAlertSoundChoice(key) {
  try { localStorage.setItem("sv_alert_sound", key); } catch (e) {}
}

let _alertAudioCtx = null;
function playAlertSound(key = alertSoundChoice()) {
  if (key === "off") return;
  try {
    _alertAudioCtx = _alertAudioCtx
      || new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _alertAudioCtx;
    // Browsers keep the context suspended until the user has touched the
    // page once; resume is a no-op after that and harmless before it.
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    const now = ctx.currentTime;
    const notes = key === "bell"
      ? [[523.25, 0, 0.9, "sine", 0.14]]
      : key === "pulse"
        ? [[660, 0, 0.09, "square", 0.05], [660, 0.14, 0.09, "square", 0.05]]
        : [[659.25, 0, 0.22, "sine", 0.12], [987.77, 0.12, 0.35, "sine", 0.12]];
    for (const [freq, at, dur, wave, peak] of notes) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = wave;
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + at);
      gain.gain.exponentialRampToValueAtTime(peak, now + at + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + at + dur);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + at);
      osc.stop(now + at + dur + 0.05);
    }
  } catch (e) { /* a missing chime must never break an alert */ }
}

function formatRelativeTime(value) {
  if (!value) return "";
  const date = new Date(value);
  const diffMs = date.getTime() - Date.now();
  const diffMinutes = Math.round(diffMs / 60000);
  const diffHours = Math.round(diffMinutes / 60);
  const diffDays = Math.round(diffHours / 24);
  const locale = document.documentElement.lang || "en";

  if (Math.abs(diffMinutes) < 1) {
    return new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(0, "minute");
  }
  if (Math.abs(diffMinutes) < 60) {
    return new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(diffMinutes, "minute");
  }
  if (Math.abs(diffHours) < 24) {
    return new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(diffHours, "hour");
  }
  return new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(diffDays, "day");
}

// The price freshness, ticking every second so it FEELS live (the number moves
// on screen) while staying honest: it shows the real age of the price, so when
// the feed stops the label counts up ("2m ago") -- the signal actually worth
// having. Within ~30s it shows a pulsing "live" dot; older, a muted "stale" one.
function LivePriceAge({ lastUpdate, t }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => (n + 1) % 3600), 1000);
    return () => clearInterval(id);
  }, []);
  if (!lastUpdate) {
    return html`<small className="price-age">${t("price.awaiting_data", "awaiting data")}</small>`;
  }
  const ageSec = Math.max(0, Math.round((Date.now() - new Date(lastUpdate).getTime()) / 1000));
  const live = ageSec <= 30;
  const locale = document.documentElement.lang || "en";
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  const ago = ageSec < 60 ? rtf.format(-ageSec, "second")
    : ageSec < 3600 ? rtf.format(-Math.floor(ageSec / 60), "minute")
      : rtf.format(-Math.floor(ageSec / 3600), "hour");
  return html`
    <small className=${"price-age " + (live ? "price-age-live" : "price-age-stale")}
           title=${lastUpdate}>
      <span className="price-age-dot" aria-hidden="true"></span>
      <span dir="auto">${live ? t("price.live", "Live") : ""} <span dir="ltr">${ago}</span></span>
    </small>`;
}


function cacheNewsItem(item, language) {
  if (!item || !item.uuid) return;
  try {
    const payload = language ? { ...item, _lang: language } : item;
    sessionStorage.setItem(`news_item_${item.uuid}`, JSON.stringify(payload));
  } catch (error) {
    // ignore storage errors
  }
}

function getCachedNewsItem(uuid, language) {
  if (!uuid) return null;
  try {
    const raw = sessionStorage.getItem(`news_item_${uuid}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (language && parsed && parsed._lang && parsed._lang !== language) {
      return null;
    }
    return parsed;
  } catch (error) {
    return null;
  }
}

function formatTemplate(text, variables) {
  if (!variables) return text;
  return Object.entries(variables).reduce((result, [key, value]) => {
    return result.replaceAll(`{${key}}`, value);
  }, text);
}

// Why a setup was withheld, as a sentence. src/trading/gates.py writes these
// reasons for a log line -- 'event:CPI', 'adx 18.3 below 25.0 outside a choppy
// regime', 'scalping does not trade the asia session' -- and they are also the
// only thing the customer is ever told about the product's silence: they travel
// in `withheld_by` into the verdict spine, the Why sheet, the day-close receipt
// and the chat. An Arabic reader was shown the wire format, colon and all.
//
// This mirrors gates.describe() exactly; tests/test_gate_reasons.py drives every
// real gate output through both and fails the build if they drift.
const GATE_STYLE_NAMES = {
  scalping: { en: "Scalping", ar: "سكالبينغ" },
  intraday: { en: "Intraday", ar: "يومي" },
  swing: { en: "Swing", ar: "سوينغ" },
};
const GATE_SESSION_NAMES = {
  asia: { en: "Tokyo / Asia", ar: "آسيا / طوكيو" },
  london: { en: "London", ar: "لندن" },
  london_ny_overlap: { en: "London / New York overlap", ar: "تداخل لندن / نيويورك" },
  new_york: { en: "New York", ar: "نيويورك" },
};
// The verdict's own blockers arrive as bare keys and land in the same slots.
const GATE_BLOCKER_KEYS = {
  yield_headwind: {
    en: "Yields are moving against this direction, which raises the cost of holding gold.",
    ar: "العوائد تتحرك عكس هذا الاتجاه، ما يرفع تكلفة حيازة الذهب.",
  },
  high_impact_pending: {
    en: "A high-impact release is minutes away.",
    ar: "هناك بيانات عالية التأثير على بُعد دقائق.",
  },
  news_opposes: {
    en: "News flow leans against this direction.",
    ar: "ميل الأخبار معاكس لهذا الاتجاه.",
  },
};

function describeGateReason(reason, language) {
  const text = String(reason || "").trim();
  if (!text) return null;
  const ar = language === "ar";
  const pick = (entry, fallback) => (entry ? (ar ? entry.ar : entry.en) : fallback);

  if (GATE_BLOCKER_KEYS[text]) return pick(GATE_BLOCKER_KEYS[text]);

  const session = text.match(/^(\w+) does not trade the (\w+) session$/);
  if (session) {
    // 'closed' is not a session the style declines to trade -- it is the market
    // being shut, which is most of every weekend and the most-seen reason in
    // the product. "the closed session is not one of them" about a Sunday is
    // nonsense.
    if (session[2] === "closed") return ar ? "السوق مغلق الآن." : "The market is closed.";
    const style = pick(GATE_STYLE_NAMES[session[1]], session[1]);
    const name = pick(GATE_SESSION_NAMES[session[2]], session[2].replace(/_/g, " "));
    return ar
      ? `خارج ساعات التداول لأسلوب ${style} — لا نتداول جلسة ${name}.`
      : `Outside the hours ${style} trades — the ${name} session is not one of them.`;
  }

  if (text.startsWith("event:")) {
    const event = text.slice("event:".length).trim();
    const named = event && event !== "high-impact release";
    return ar
      ? `نقف جانباً حتى ${named ? `صدور ${event}` : "صدور بيانات عالية التأثير"} — لا يمكن الاعتماد على وقف الخسارة الفني أثناء الصدور.`
      : `Standing aside through ${named ? event : "a high-impact release"} — technical stops cannot be relied on through the print.`;
  }

  const adx = text.match(/^adx ([\d.]+) below ([\d.]+) outside a choppy regime$/);
  if (adx) {
    const floor = adx[2].replace(/\.0+$/, "");
    return ar
      ? `قوة الاتجاه دون الحدّ لهذا الأسلوب (ADX ${adx[1]}، والحدّ ${floor}).`
      : `Trend strength is below this style's floor (ADX ${adx[1]}, floor ${floor}).`;
  }

  return null;
}

const I18nContext = React.createContext(null);

function I18nProvider({ children }) {
  const [language, setLanguage] = useState(getInitialLanguage());
  const [translations, setTranslations] = useState({});
  const [ready, setReady] = useState(false);
  const latestLanguageRef = useRef(language);

  const loadTranslations = useCallback(async (lang) => {
    setReady(false);
    try {
      // Date.now() made this uncacheable BY DESIGN, so 47.7KB of copy (16KB
      // gzipped) came down on every single page load forever. The bundle
      // version changes exactly when the translations can have changed, which
      // is what a cache-buster is supposed to mean.
      const stamp = document.querySelector('meta[name="sv-asset-version"]')
        ?.getAttribute("content") || "1";
      const response = await fetch(`/api/translations/${lang}?v=${stamp}`);
      if (!response.ok) {
        throw new Error("Failed to load translations");
      }
      const data = await response.json();
      if (latestLanguageRef.current !== lang) return;
      setTranslations(data || {});
      setReady(true);
    } catch (error) {
      if (latestLanguageRef.current !== lang) return;
      if (lang !== "en") {
        setLanguage("en");
        return;
      }
      setTranslations({});
      setReady(true);
    }
  }, []);

  useEffect(() => {
    // The login page is English-only: no language control ships on it, so the
    // app must not render it RTL just because Arabic was chosen in a previous
    // session. Everywhere else honours the saved language.
    const onLogin = window.location.pathname === "/login";
    const effective = onLogin ? "en" : language;
    sessionStorage.setItem("preferredLanguage", language);
    localStorage.setItem("preferredLanguage", language);
    document.cookie = `preferredLanguage=${encodeURIComponent(language)}; path=/; max-age=31536000`;
    document.documentElement.lang = effective;
    document.documentElement.dir = effective === "ar" ? "rtl" : "ltr";
    document.body.classList.toggle("rtl", effective === "ar");
    latestLanguageRef.current = effective;
    loadTranslations(effective);
    // The server's copy decides what language a PUSH arrives in, and it is
    // read by a background loop the browser never talks to.
    if (!onLogin) reconcileLanguage(effective);
  }, [language, loadTranslations]);

  useEffect(() => {
    function handleStorage(event) {
      if (event.key === "preferredLanguage" && event.newValue && event.newValue !== language) {
        setLanguage(event.newValue);
      }
    }
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [language]);

  const t = useCallback(
    (keyPath, fallback = "") => {
      const keys = keyPath.split(".");
      let value = translations;
      for (const key of keys) {
        if (value && typeof value === "object" && key in value) {
          value = value[key];
        } else {
          return fallback || keyPath;
        }
      }
      return value || fallback || keyPath;
    },
    [translations]
  );

  // Translate decision_engine reasons (technical analysis reasons)
  const translateTechnicalReason = useCallback(
    (reason) => {
      if (!reason) return t("advisor.no_reason", "No specific reason provided");
      
      const reasonMap = {
        'en': {
          'Price reached overbought level - potential correction opportunity': 'Price reached overbought level - potential correction opportunity',
          'Price reached oversold level - potential bounce opportunity': 'Price reached oversold level - potential bounce opportunity',
          'Price breaking above volatility bands - strong buying pressure': 'Price breaking above volatility bands - strong buying pressure',
          'Price breaking below volatility bands - strong selling pressure': 'Price breaking below volatility bands - strong selling pressure',
          'No high-probability trading setup detected - market lacks clear signals': 'No high-probability trading setup detected - market lacks clear signals'
        },
        'ar': {
          'Price reached overbought level - potential correction opportunity': 'وصل السعر لمستوى تشبع شرائي - فرصة محتملة للتصحيح',
          'Price reached oversold level - potential bounce opportunity': 'وصل السعر لمستوى تشبع بيعي - فرصة محتملة للارتداد',
          'Price breaking above volatility bands - strong buying pressure': 'السعر يخترق أعلى نطاقات التقلب - ضغط شراء قوي',
          'Price breaking below volatility bands - strong selling pressure': 'السعر يخترق أسفل نطاقات التقلب - ضغط بيع قوي',
          'No high-probability trading setup detected - market lacks clear signals': 'لم يتم اكتشاف إعداد تداول عالي الاحتمال - السوق يفتقر لإشارات واضحة',
          // The no-trade card's own sentences (feature #35/#55) -- the hero
          // card of the product rendered them in English on the Arabic UI.
          'Market is sideways with low volatility - wait for breakout or clear trend': 'السوق عرضي بتقلب منخفض - انتظر اختراقاً أو اتجاهاً واضحاً',
          'The market is ranging, with no clean direction to trade.': 'السوق يتحرك ضمن نطاق، بلا اتجاه نظيف للتداول.',
          'Market direction is unclear - no strong trend detected': 'اتجاه السوق غير واضح - لا يوجد اتجاه قوي',
          'No clear support/resistance levels found for safe entry/exit': 'لا توجد مستويات دعم/مقاومة واضحة لدخول وخروج آمنين',
          'a decisive break and close beyond a nearby level': 'اختراق حاسم وإغلاق خارج مستوى قريب',
          // no_trade.py's own "what would help" lines. The map carried two of
          // the four; these are the rest, and they render directly under the
          // gauge on the product's hero card.
          'a clearer signal to develop — nothing actionable yet': 'أن تتّضح إشارة — لا شيء قابل للتنفيذ بعد',
          'a clearer trend or a defined range to develop': 'أن يتكوّن اتجاه أوضح أو نطاق محدّد',
          'the pending high-impact release to pass': 'أن يمرّ الخبر عالي التأثير المنتظَر',
          'the yield headwind to ease': 'أن تخفّ رياح العوائد المعاكسة',
          'the active filter to clear': 'أن يُرفَع الفلتر الفعّال',
          'one more confirmation to complete the setup': 'تأكيد إضافي واحد لاكتمال الإعداد',
          'the trend to strengthen (ADX above 25)': 'أن يتقوى الاتجاه (ADX فوق 25)',
          'Price pulled back to strong support level in uptrend': 'ارتد السعر إلى مستوى دعم قوي ضمن اتجاه صاعد',
          'Price pulled back to strong resistance level in downtrend': 'ارتد السعر إلى مستوى مقاومة قوي ضمن اتجاه هابط',
          'Momentum indicator shows healthy price correction': 'مؤشر الزخم يُظهر تصحيحاً سعرياً صحياً',
          'Trend strength indicator confirms upward momentum': 'مؤشر قوة الاتجاه يؤكد الزخم الصاعد',
          'Trend strength indicator confirms downward momentum': 'مؤشر قوة الاتجاه يؤكد الزخم الهابط',
          // The eight the map never had. DecisionEngine emits twenty reason
          // sentences and this map carried twelve of them, so eight of the
          // lines under "على ماذا يستند" -- the evidence sheet, the one screen
          // this product is built to be judged on -- rendered in English to an
          // Arabic reader. tests/test_grounding.py now compares the two sets.
          'Deep pullback within a downtrend': 'ارتداد عميق ضمن اتجاه هابط',
          'Deep pullback within an uptrend': 'ارتداد عميق ضمن اتجاه صاعد',
          'Directional strength supports the break': 'قوة الاتجاه تدعم الاختراق',
          'Momentum confirms the downside break': 'الزخم يؤكّد الاختراق الهابط',
          'Momentum confirms the upside break': 'الزخم يؤكّد الاختراق الصاعد',
          'Overbought reading sits on a validated resistance level': 'قراءة تشبّع شرائي عند مستوى مقاومة مُثبَت',
          'Oversold reading sits on a validated support level': 'قراءة تشبّع بيعي عند مستوى دعم مُثبَت',
          'Setup points against the prevailing move - standing aside': 'الإعداد يعاكس الحركة السائدة — الوقوف جانباً'
        }
      };

      if (language === 'ar') {
        // Sentences carrying live numbers cannot be exact-matched.
        const adxWeak = String(reason).match(/^Trend strength is weak \(ADX ([\d.]+), below (\d+)\)\.$/);
        if (adxWeak) return `قوة الاتجاه ضعيفة (ADX ${adxWeak[1]}، دون ${adxWeak[2]}).`;
      }
      const mapped = reasonMap[language]?.[reason];
      if (mapped) return mapped;
      // Gate shorthand and blocker keys, in BOTH languages -- "event:CPI" is
      // no more readable to an English customer than to an Arabic one.
      return describeGateReason(reason, language) || reason;
    },
    [language, t]
  );

  const translateSignal = useCallback(
    (signal) => {
      if (!signal) return signal;
      const signalLower = signal.toLowerCase().trim();
      const signalMap = {
        "strong buy": "signal_values.strong_buy",
        buy: "signal_values.buy",
        "strong sell": "signal_values.strong_sell",
        sell: "signal_values.sell",
        neutral: "signal_values.neutral",
        // ADX emits these two and nothing could translate them, so the
        // indicator table printed "STRONG TREND" in English on the Arabic
        // screen — beside nine badges that were translated, which reads as a
        // rendering fault rather than as a word. They are matched BEFORE the
        // substring pass below, because "strong trend" contains neither buy
        // nor sell and must not fall through to one.
        "strong trend": "signal_values.strong_trend",
        "weak trend": "signal_values.weak_trend"
      };
      if (signalMap[signalLower]) {
        return t(signalMap[signalLower], signal);
      }
      for (const [key, translationKey] of Object.entries(signalMap)) {
        if (signalLower.includes(key)) {
          return t(translationKey, signal);
        }
      }
      return signal;
    },
    [t]
  );

  const translateSentiment = useCallback(
    (sentiment) => {
      if (!sentiment) return sentiment;
      const sentimentLower = sentiment.toLowerCase();
      if (sentimentLower === "bullish") return t("statistics.bullish", sentiment);
      if (sentimentLower === "bearish") return t("statistics.bearish", sentiment);
      if (sentimentLower === "neutral") return t("statistics.neutral", sentiment);
      if (sentimentLower === "low") return t("statistics.low", sentiment);
      if (sentimentLower === "medium") return t("statistics.medium", sentiment);
      if (sentimentLower === "high") return t("statistics.high", sentiment);
      if (sentimentLower === "strong") return t("statistics.strong", sentiment);
      if (sentimentLower === "weak") return t("statistics.weak", sentiment);
      return sentiment;
    },
    [t]
  );

  const value = useMemo(
    () => ({ language, setLanguage, t, translateSignal, translateSentiment, translateTechnicalReason, ready }),
    [language, t, translateSignal, translateSentiment, translateTechnicalReason, ready]
  );

  return html`<${I18nContext.Provider} value=${value}>${children}<//>`;
}

function useI18n() {
  return React.useContext(I18nContext);
}

const AuthContext = React.createContext(null);

function AuthProvider({ children }) {
  const [user, setUser] = useState(getUserInfo());
  const [status, setStatus] = useState("checking");
  // Which services this account may use. The operator can switch any of them
  // off; the app must then say so where the feature was, rather than showing
  // a control that fails when pressed.
  const [features, setFeatures] = useState(null);
  // What the operator wants users told, for the services they switched off.
  const [featureNotes, setFeatureNotes] = useState({});

  const verifyToken = useCallback(async () => {
    // Three separate defects lived in this one function, and all of them came
    // from treating "I could not check" as "you are not signed in".
    //
    //  * A 500 from this endpoint set status=guest, which redirects to /login
    //    -- where the server sees a perfectly valid cookie and 302s straight
    //    back. Measured: 83 verify calls and 84 navigations in twelve seconds,
    //    never terminating. A server hiccup became an infinite reload loop.
    //  * A dropped connection or a truncated body hit the catch, which wiped
    //    the stored credentials. On a PWA that means driving through a tunnel
    //    signs you out -- offline, where you cannot sign back in.
    //  * A 200 carrying an unexpected body fell through every branch and left
    //    status on "checking" forever. Every page gate returns null on that,
    //    and the redirect only fires on "guest", so the app froze blank with
    //    no error, no spinner and no way out but a reload that repeats it.
    //
    // Only a 401 or 403 means "not signed in". Everything else is "could not
    // reach the server", which keeps the session and says so.
    try {
      const response = await authenticatedFetch("/api/auth/verify", { method: "GET" });
      if (response.status === 401 || response.status === 403) {
        setStatus("guest");
        return null;
      }
      if (!response.ok) {
        setStatus("unreachable");
        return null;
      }
      const data = await response.json();
      if (data && data.user) {
        sessionStorage.setItem("user", JSON.stringify(data.user));
        setUser(data.user);
        setFeatures(data.features || null);
        setFeatureNotes(data.feature_notes || {});
        setStatus("authenticated");
        return data.user;
      }
      // 200 with a body we do not understand: not a session, not a reason to
      // destroy one, and never a reason to hang on "checking".
      setStatus("unreachable");
      return null;
    } catch (error) {
      // A thrown fetch is the network, not the answer. Credentials survive.
      setStatus("unreachable");
    }
    return null;
  }, []);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  // Unreachable is a RETRY state, not a dead end: the server was not
  // answering, the session is still good, and the app tries again on a
  // backoff instead of throwing the customer at a login page that will
  // bounce them straight back.
  useEffect(() => {
    if (status !== "unreachable") return undefined;
    const timer = setTimeout(() => { verifyToken(); }, 4000);
    return () => clearTimeout(timer);
  }, [status, verifyToken]);

  useEffect(() => {
    if (status === "guest") {
      redirectToLogin();
    }
  }, [status]);

  const login = useCallback(async (username, password) => {
    const response = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password })
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || "Login failed");
    }

    storeAuth(data);
    setUser(data.user);
    setStatus("authenticated");
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      // Send the refresh token so the server can revoke it too, not just the
      // short-lived access token.
      const refresh = sessionStorage.getItem("refresh_token");
      await authenticatedFetch("/api/auth/logout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refresh || "" }),
      });
    } catch (error) {
      // ignore
    } finally {
      clearAuthData();
      setUser(null);
      setStatus("guest");
    }
  }, []);

  const value = useMemo(
    // Unknown (null) reads as enabled: a features payload that has not arrived
    // yet must not blank out the app.
    () => ({ user, status, login, logout, verifyToken, setUser, features, featureNotes,
             can: (key) => (features ? features[key] !== false : true) }),
    [user, status, login, logout, verifyToken, features, featureNotes]
  );

  return html`<${AuthContext.Provider} value=${value}>${children}<//>`;
}

function useAuth() {
  return React.useContext(AuthContext);
}

// One dismissal contract for every layered surface: Escape closes it, and
// so does a press anywhere outside it. The ref must wrap the toggle button
// too, or the closing press and the toggle fight and the layer reopens.
function useDismissable(open, onClose, alsoInside = null) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    const onPress = (event) => {
      // `alsoInside` is a second element that counts as INSIDE.
      //
      // The overflow menu is portalled to <body> so the top bar's
      // backdrop-filter cannot trap its fixed positioning — and that put its
      // buttons outside `ref`, so this pointerdown handler closed the menu
      // BEFORE the click could reach them. Every anchor still worked (the
      // browser navigates on its own) and every BUTTON silently did nothing:
      // the language toggle, "What's new", Share. The owner's report was
      // exactly that — pressing العربية changed nothing at all.
      const inside = (ref.current && ref.current.contains(event.target))
        || (alsoInside && alsoInside.current
            && alsoInside.current.contains(event.target));
      if (!inside) onClose();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPress);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPress);
    };
  }, [open, onClose, alsoInside]);
  return ref;
}

function Modal({ open, title, onClose, children, footer, maxWidth }) {
  const { t } = useI18n();
  const dialogRef = useRef(null);
  const titleId = useMemo(() => "modal-title-" + Math.random().toString(36).slice(2), []);
  // Hooks stay above the early return: a hook that renders only when the
  // modal is open crashes React the first time `open` flips while mounted.
  useEffect(() => {
    if (!open || !onClose) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Dialog behaviour, not just dialog looks: focus moves in on open, Tab
  // cycles inside instead of escaping to the inert page behind, the body
  // stops scrolling underneath, and focus returns to the trigger on close --
  // a screen reader otherwise keeps reading the page as if nothing opened.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const node = dialogRef.current;
    const focusables = () => node
      ? node.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])')
      : [];
    const first = focusables()[0];
    if (first) first.focus();
    const onTab = (e) => {
      if (e.key !== "Tab") return;
      const list = focusables();
      if (!list.length) return;
      const a = list[0], z = list[list.length - 1];
      if (e.shiftKey && document.activeElement === a) { e.preventDefault(); z.focus(); }
      else if (!e.shiftKey && document.activeElement === z) { e.preventDefault(); a.focus(); }
    };
    document.addEventListener("keydown", onTab);
    return () => {
      document.removeEventListener("keydown", onTab);
      document.body.style.overflow = prevOverflow;
      if (previous && previous.focus) previous.focus();
    };
  }, [open]);

  if (!open) return null;

  return html`
    <div className="modal-backdrop" onClick=${onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby=${titleId}
        ref=${dialogRef}
        style=${maxWidth ? { maxWidth } : undefined}
        onClick=${(event) => event.stopPropagation()}
      >
        <div className="modal-header">
          <h3 id=${titleId}>${title}</h3>
          <button className="btn btn-ghost" onClick=${onClose} aria-label=${t("buttons.close", "Close")}>
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div className="modal-body">${children}</div>
        ${footer
          ? html`<div className="modal-footer">${footer}</div>`
          : html`<div className="modal-footer">
              <button className="btn btn-secondary" onClick=${onClose}>${t("buttons.close", "Close")}</button>
            </div>`}
      </div>
    </div>
  `;
}

// OUR forward record -- every signal this system has published since the
// ledger began, resolved against real bars. One component, used by the
// dashboard AND the chart page, so the surface a push alert deep-links into
// carries the same disclosure as the dashboard: no caller can omit it. The
// full backtest stays one tap away in Settings. Numbers pass through iso() so
// the Arabic page cannot re-order them against their labels.
// The single number that says what ignoring the verdict has cost THIS user.
// It belongs on the screen they open every day, not two taps away in the
// journal: the whole point is that it is seen before the next override, not
// after. It carries its sample, and stays silent when the sample is too small
// to mean anything -- a delta computed from four trades is a coin flip
// wearing a decimal point.
// Records what the trader decided, keyed on the verdict they were looking at.
// The key is the important part: without it a decision cannot be tied back to
// the state that prompted it, and the record becomes a pile of taps.
function useVerdictAnswer(verdict, timeframe, price) {
  const [answered, setAnswered] = useState(null);
  const key = verdict
    ? `${timeframe}:${verdict.action}:${verdict.reason || ""}`
    : null;

  // A new verdict is a new question. Clearing here is what stops yesterday's
  // "passed" from greying out today's setup.
  useEffect(() => { setAnswered(null); }, [key]);

  const answer = useCallback(async (response) => {
    if (!verdict || !key) return;
    setAnswered(response);
    try {
      await authenticatedFetch("/api/verdict-response", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          response, timeframe, price,
          action: verdict.action,
          state_digest: key,
        }),
      });
    } catch (error) { /* the tap stands locally; the next one retries */ }
  }, [verdict, key, timeframe, price]);

  return { answered, answer };
}

function DisciplineLine({ analytics, preferences, t }) {
  if (!analytics) return null;
  const d = analytics.discipline || {};
  const followed = d.followed?.trades || 0;
  const override = d.override?.trades || 0;
  const enough = Math.min(followed, override) >= 3;
  // The streak stands on its own even before the delta has a sample: a run
  // of trades taken WITH the verdict is the one number worth being proud
  // of in this product (a run of TRADES would reward overtrading).
  const streak = analytics.streak;
  if ((d.delta_r == null || !enough) && streak && streak.current >= 3) {
    return html`
      <a className="discipline-line" href="/journal">
        <span className="discipline-delta">${iso(streak.current)}</span>
        <span className="discipline-copy">
          ${formatTemplate(t("home.streak", "trades in a row taken with the verdict — your longest run is {best}."),
            { best: iso(String(streak.best)) })}
        </span>
      </a>`;
  }
  // THE DELTA ITSELF BELONGS TO THE MIRROR, ONCE.
  //
  // This line and the Mirror card sat in the same fold, both printing the same
  // delta and both printing the same "followed · overridden" counts. The Mirror
  // shows the two columns the number came out of, which is the stronger of
  // the two, so it keeps the delta and the money sentence with it. What stays
  // here is the streak — the one thing the Mirror does not draw, and the only
  // number in this product worth being proud of (a streak of TRADES would
  // reward overtrading; a streak of obedience rewards the discipline the
  // delta proves is worth money).
  return null;
}

function MeasuredStrip({ performance, t }) {
  const led = performance?.ledger;
  if (!led) return null;
  const total = led.published != null
    ? led.published
    : (led.resolved || 0) + (led.open || 0);
  if (!total) return null;
  const minMeaning = performance.live_min_trades_for_meaning || 30;
  const enough = led.resolved >= minMeaning;
  const positive = led.expectancy_r > 0;
  // ISO date, matching every other date on the page -- toLocaleDateString()
  // gave this one line a US 8/11/2026 beside the tiles' 2026-08-11.
  const sinceStr = led.since ? new Date(led.since * 1000).toISOString().slice(0, 10) : null;
  return html`
    <a className="measured-strip" href="/trading">
      <span className="measured-strip-name">${t("performance.ours_short", "Our record")}</span>
      <span>
        ${total === 1
          ? t("performance.signal_one", "1 signal")
          : html`${iso(total)} ${t("performance.signals", "calls")}`}
        ${sinceStr && html` · ${t("performance.since", "since")} ${iso(sinceStr)}`}
        ${led.open > 0 && html` · ${iso(led.open)} ${t("performance.open_now", "open — waiting on stop or target")}`}
        ${led.expired > 0 && html` · ${iso(led.expired)} ${t("performance.expired_short", "never filled")}`}
      </span>
      ${led.resolved > 0 && html`
        ${/* Comfort audit #18: "win rate 100%" in bold over two trades is the
             single most misleading pixel this product can draw -- and it is
             our own number. Below the meaning threshold it renders in the
             same muted weight the expectancy already used, so the caveat
             line underneath is not arguing with a bold headline above it. */""}
        <span>${t("performance.achieved_short", "win rate")}${" "}
          <b className=${enough ? "" : "inconclusive"}>${iso(`${led.win_rate}%`)}</b></span>
        <span>${t("performance.expectancy_short", "per trade")}${" "}
          <b className=${enough ? (positive ? "pos" : "neg") : "inconclusive"}>
            ${iso(`${positive ? "+" : ""}${led.expectancy_r}R`)}
          </b>
        </span>`}
      ${!enough && html`
        <span className="measured-strip-ci">
          ${formatTemplate(
            t("performance.too_few_tpl", "{resolved} resolved of the {needed} needed before a number means anything"),
            { resolved: iso(led.resolved), needed: iso(minMeaning) })}
        </span>`}
    </a>`;
}

// Which analysis is drawn on the chart. Every layer was previously drawn at
// once and unconditionally, which on a phone put six price lines and their
// axis labels on top of each other. The legend is now the control: each entry
// toggles its own layer, and the choice is remembered -- the three toggles are
// written out where they render, and a duplicate table of them that nothing
// read is in attic/2026-08-15-dead-frontend.md.

// One green and one red for the whole product. Three unrelated pairs were in
// use at once -- the CSS tokens, the candle colours and the price-line colours
// -- so "up" was a different green depending on which element drew it. These
// repeat the CSS tokens because lightweight-charts paints to a canvas and
// cannot read a CSS custom property.
const CHART_UP = "#5E9E7A";
const CHART_DOWN = "#C96F63";

const LAYERS_STORAGE_KEY = "chartLayers";
const STUDIES_STORAGE_KEY = "chartStudies";

// One tap to the timeframes a gold trader actually lives on. The dropdown
// stays for the rest.
const QUICK_TIMEFRAMES = ["5m", "15m", "1h", "4h", "1D"];

// Overlay studies. The series are fetched from the server, computed by the
// same causal code the engine reasons with -- recomputing them in the browser
// would put a second implementation of every indicator on the screen.
const STUDIES = [
  { key: "ema20", label: "EMA 20", color: "#CBB68A", width: 1 },
  { key: "ema50", label: "EMA 50", color: "#B9A88F", width: 1 },
  { key: "bb", label: "Bollinger", color: "rgba(233,196,106,0.55)", width: 1 },
  // Volume is data, not a computed study; it rides the studies row because
  // that is where a trader looks for it.
  { key: "vol", label: "Vol", color: "rgba(216,203,179,0.6)", width: 1 },
  // Session VWAP, server-anchored per day -- the institutional mean.
  { key: "vwap", label: "VWAP", color: "#E2C275", width: 1 },
  // Sub-pane studies: they draw UNDER the price in their own synced pane,
  // not over it -- an oscillator on the price scale is unreadable noise.
  { key: "rsi", label: "RSI", color: "#D8CBB3", width: 1 },
  { key: "adx", label: "ADX", color: "#C9A227", width: 1 },
  { key: "atr", label: "ATR", color: "#A78A5C", width: 1 },
  // Volume Profile: paid at TradingView, a primitive here. Not a line series;
  // drawStudies skips it and the primitive owns the paint.
  { key: "vp", label: "VP", color: "rgba(201,162,39,0.5)", width: 1 },
];

// Guides give an oscillator pane its meaning: RSI without 30/70 is a squiggle.
const PANE_DEFS = {
  rsi: { color: "#D8CBB3", guides: [30, 70] },
  adx: { color: "#C9A227", guides: [20, 25] },
  atr: { color: "#A78A5C", guides: [] },
};

const TIMEFRAME_SECONDS = {
  "1m": 60, "5m": 300, "15m": 900, "30m": 1800,
  "1h": 3600, "4h": 14400, "1D": 86400,
};

// The reader's own chart appearance -- background and candle colours --
// persisted and broadcast so every mounted chart repaints live. `null`
// means "follow the theme", which stays the default.
// A LAYOUT is the chart set up your way, nameable and returnable: every
// remembered switch, the style, the palette, the frame. Stored locally --
// it is a preference bundle, not data.
const LAYOUT_KEYS = ["chartLayers", "chartStudies", "sv_chart_style",
  "sv_chart_appearance", "sv_chart_sessions", "sv_chart_drawings",
  "sv_chart_mtf", "sv_chart_spine"];
function readLayouts() {
  try { return JSON.parse(localStorage.getItem("sv_layouts") || "{}"); }
  catch (e) { return {}; }
}

const CHART_APPEARANCE_KEY = "sv_chart_appearance";
function chartAppearance() {
  try { return JSON.parse(localStorage.getItem(CHART_APPEARANCE_KEY) || "{}"); }
  catch (e) { return {}; }
}
function saveChartAppearance(patch) {
  const next = { ...chartAppearance(), ...patch };
  try { localStorage.setItem(CHART_APPEARANCE_KEY, JSON.stringify(next)); }
  catch (e) { /* private mode */ }
  window.dispatchEvent(new CustomEvent("sv:chart-appearance"));
}

const CANDLE_PALETTES = [
  { key: "classic", up: null, down: null },                 // theme default
  { key: "gold",    up: "#C9A227", down: "#6E7480" },
  { key: "ocean",   up: "#2F80ED", down: "#EB8C34" },       // colourblind-safe
  { key: "mono",    up: "#D8D2C2", down: "#5A5548" },
];
const BG_CHOICES = [null, "#0B0E13", "#0f182b", "#14110B", "#FAF8F4"];

function fmtSpan(secs) {
  // THE UNIT LETTER FOLLOWS THE PAGE. This printed "1h 32m" on every Arabic
  // screen that shows a countdown — the session strip, the candle clock, the
  // trade's shelf life — three Latin letters inside a right-to-left sentence,
  // in a product whose Arabic surface is otherwise checked word by word.
  //
  // Read off document.documentElement.lang rather than taking a parameter:
  // I18nProvider sets it on every language change, and threading `t` through
  // every call site of a pure formatter would be the larger change for the
  // same output.
  const ar = (document.documentElement.lang || "").startsWith("ar");
  const u = ar ? { d: "ي", h: "س", m: "د" } : { d: "d", h: "h", m: "m" };
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (d) return `${d}${u.d} ${h}${u.h}`;
  if (h) return `${h}${u.h} ${m}${u.m}`;
  return `${m}${u.m}`;
}

function readStoredStudies() {
  const defaults = { ema20: false, ema50: false, bb: false };
  try {
    return { ...defaults, ...JSON.parse(localStorage.getItem(STUDIES_STORAGE_KEY) || "{}") };
  } catch (err) { return defaults; }
}

function readStoredLayers() {
  // First open: a BARE chart (the owner's default -- candles and nothing
  // else). Every switch the user flips is stored and wins forever after,
  // so closing the app changes nothing they set up.
  //
  // One exception to bare: the open position. A user 4R underwater opened
  // this chart and their committed capital simply was not drawn until they
  // found the layers gear. Levels and proposals are decoration a trader may
  // decline; their own money at risk is not.
  const defaults = { levels: false, proposal: false, position: true };
  try {
    const stored = JSON.parse(localStorage.getItem(LAYERS_STORAGE_KEY) || "{}");
    return { ...defaults, ...stored };
  } catch (err) {
    return defaults;
  }
}

// P6, merged into the timeframe strip itself: every tf chip carries the
// engine's read for that frame (/api/mtf -- the daemon's own last
// decisions, stale frames dimmed). One row answers "which frame am I on"
// AND "what does the engine see everywhere else" -- the separate strip was
// a second row saying half of the same thing, on the page where every
// vertical pixel belongs to the candles.
function useMtfFrames() {
  const [frames, setFrames] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => authenticatedFetch("/api/mtf").then((r) => r.json())
      .then((p) => { if (alive && p.success) setFrames(p.frames || []); })
      .catch(() => {});
    load();
    const timer = setInterval(load, 60000);
    return () => { alive = false; clearInterval(timer); };
  }, []);
  return frames;
}

// What every mark on the chart MEANS, in one tappable place. Static truth:
// each row describes an element the chart actually draws, with the same
// visual it draws it with. The owner's ask, verbatim: "the lines aren't
// clear -- there must be a legend, and tapping it explains everything."
const LEGEND_ITEMS = [
  { sw: "lg-candles", label: "legend.candles", lf: "Candles",
    desc: "legend.candles_d", df: "Green closed higher, red closed lower. The last one is still forming — the countdown says when it locks." },
  { sw: "lg-sr", label: "legend.sr", lf: "Support & resistance (dotted)",
    desc: "legend.sr_d", df: "Levels the engine computed from real touches. S ×3 means the market touched it three times — tap the level chips below to be alerted at one." },
  { sw: "lg-plan", label: "legend.plan", lf: "Proposed setup (dashed)",
    desc: "legend.plan_d", df: "Entry, stop and target of the engine's current plan, with risk (red) and reward (green) shaded. Dashed = proposed, not taken." },
  { sw: "lg-pos", label: "legend.pos", lf: "Your open trade (solid)",
    desc: "legend.pos_d", df: "Solid lines are committed capital: white entry with live R, red stop, green target." },
  { sw: "lg-alert", label: "legend.alert", lf: "Your price alert (gold dashed)",
    desc: "legend.alert_d", df: "A level you asked to be told about. Drag the line to move the alert." },
  { sw: "lg-draw", label: "legend.draw", lf: "Your drawings (gold)",
    desc: "legend.draw_d", df: "Lines, zones, fibs and positions you drew. Long-press one for its measured facts, then ask the engine to critique it." },
  { sw: "lg-session", label: "legend.session", lf: "Session shading",
    desc: "legend.session_d", df: "The trading day's chapters: Asia, London, the London+NY overlap (the widest stretch, measured), New York." },
  { sw: "lg-event", label: "legend.event", lf: "Event pennant (bottom lane)",
    desc: "legend.event_d", df: "A high-impact economic release landed on that bar. Hover the bar and the readout names it." },
  { sw: "lg-journal", label: "legend.journal", lf: "Your trade history",
    desc: "legend.journal_d", df: "Arrows are your entries, circles your exits with the R result — straight from your journal." },
  { sw: "lg-pulse", label: "legend.pulse", lf: "Live price dot",
    desc: "legend.pulse_d", df: "Breathes once per tick at the live price. Silence means the feed is quiet — the freshness clock above tells you why." },
  { sw: "lg-readout", label: "legend.readout", lf: "The reading row (O H L C)",
    desc: "legend.readout_d", df: "The bar under your finger: Open, High, Low, Close, its range (R), when it was — and the countdown to the forming candle's close. The bell arms an alert at that price." },
  { sw: "lg-chip", label: "legend.chips", lf: "Key level chips (below the chart)",
    desc: "legend.chips_d", df: "The engine's strongest levels with the arithmetic that made them (S/R ×touches + the round number). Tap a chip and the app will alert you when price reaches that level." },
];

function ChartLegend({ t, onClose }) {
  return html`
    <div className="chart-settings chart-legend" role="dialog"
         aria-label=${t("legend.title", "What's on this chart")}>
      <b className="chart-settings-title">${t("legend.title", "What's on this chart")}</b>
      ${LEGEND_ITEMS.map((item) => html`
        <div key=${item.sw} className="legend-row">
          <span className=${"lg-sw " + item.sw} aria-hidden="true"></span>
          <span className="legend-text">
            <b>${t(item.label, item.lf)}</b>
            <small>${t(item.desc, item.df)}</small>
          </span>
        </div>`)}
      <button className="btn btn-secondary legend-close" onClick=${onClose}>
        ${t("buttons.close", "Close")}
      </button>
    </div>`;
}

function ChartPanel({ timeframe, chartType, onChangeTimeframe, onChangeChartType, livePrice, verdict, onWhy, trades, marketOpen = true, onDragStop = null, replaySignal = null }) {
  const { t } = useI18n();
  // Withholding watchLevel is what removes the alert affordances: the level
  // chips, the level sheet's button and the crosshair bell all render only
  // when they are handed a handler, so one flag closes all three.
  const canPriceAlerts = useAuth().can("price_alerts");
  const [layers, setLayers] = useState(readStoredLayers);
  const [studies, setStudies] = useState(readStoredStudies);
  const [logScale, setLogScale] = useState(false);
  // Price alerts on the key levels. Owned here so both the dashboard and the
  // chart page get the feature without threading state through either.
  const [alertedLevels, setAlertedLevels] = useState(() => new Set());
  const [alertBusy, setAlertBusy] = useState(null);
  const [alertNote, setAlertNote] = useState(null);
  const [whyOpen, setWhyOpen] = useState(false);

  const [activeAlerts, setActiveAlerts] = useState([]);
  const loadAlerts = useCallback(async () => {
    try {
      const response = await authenticatedFetch("/api/price-alerts");
      const payload = await response.json();
      if (payload.success) {
        const live = (payload.alerts || []).filter((a) => a.active);
        setAlertedLevels(new Set(live.map((a) => Math.round(a.price * 100) / 100)));
        // The full rows travel to the chart: the lines it draws are these,
        // and the drag needs their ids.
        setActiveAlerts(live.map((a) => ({ id: a.id, price: a.price, kind: a.kind })));
      }
    } catch (error) { /* the bells simply render unlit */ }
  }, []);

  useEffect(() => { loadAlerts(); }, [loadAlerts]);

  // The confirmation is a toast, not a permanent sentence: it says its
  // piece and leaves. A note that never left read as "the page hung".
  useEffect(() => {
    if (!alertNote) return undefined;
    const timer = setTimeout(() => setAlertNote(null), 4000);
    return () => clearTimeout(timer);
  }, [alertNote]);

  // Drag-to-edit: PATCH the new level, then reload -- on failure the reload
  // snaps the line back to where the alert truly is, which is the honest
  // outcome of a save that did not happen.
  const moveAlert = useCallback(async (id, price) => {
    try {
      await authenticatedFetch(`/api/price-alerts/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ price }),
      });
    } catch (error) { /* reload below states the truth either way */ }
    loadAlerts();
  }, [loadAlerts]);

  // The zone is passed in rather than looked up: the overlays live in the
  // child that renders the level chips, and reaching for them from here was a
  // reference to a ref that does not exist in this scope -- which threw, was
  // swallowed by the catch, and silently stopped every level alert being
  // created at all.
  const watchLevel = useCallback(async (levelPrice, zone) => {
    setAlertBusy(levelPrice);
    setAlertNote(null);
    // A TOGGLE, not a one-way switch: tapping a level you already watch
    // cancels the watch. "It selects but you can't undo" -- the owner.
    const rounded = Math.round(levelPrice * 100) / 100;
    const existing = activeAlerts.find(
      (a) => Math.round(a.price * 100) / 100 === rounded);
    if (existing) {
      try {
        await authenticatedFetch(`/api/price-alerts/${existing.id}`, { method: "DELETE" });
        await loadAlerts();
        setAlertNote({ ok: true, off: true, price: levelPrice });
      } catch (error) {
        setAlertNote({ ok: false, reason: "failed" });
      }
      setAlertBusy(null);
      return;
    }
    try {
      // The arithmetic travels with the alert. "4,420 reached" is a number;
      // "4,420 reached · D-R1 + $4,400" is the reason the level existed, and
      // it arrives at the moment the trader has to decide something. No other
      // product's notification can say this, because no other product kept
      // the derivation.
      const note = zone
        ? (zone.sources || []).map((x) => x.source).filter(Boolean).join(" + ")
        : null;
      const response = await authenticatedFetch("/api/price-alerts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ price: levelPrice, kind: "touch", note })
      });
      const payload = await response.json();
      if (payload.success) {
        setAlertedLevels((prev) => new Set(prev).add(rounded));
        setAlertNote({ ok: true, price: levelPrice });
        // Refresh the full list so the chart draws the new dashed line now.
        loadAlerts();
      } else {
        setAlertNote({ ok: false,
                       reason: payload.error === "too_many_alerts" ? "quota" : "failed" });
      }
    } catch (error) {
      setAlertNote({ ok: false, reason: "failed" });
    } finally {
      setAlertBusy(null);
    }
  }, [activeAlerts, loadAlerts]);

  const toggleStudy = useCallback((key) => {
    setStudies((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(STUDIES_STORAGE_KEY, JSON.stringify(next)); } catch (e) {}
      return next;
    });
  }, []);

  const toggleLayer = useCallback((key) => {
    setLayers((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(LAYERS_STORAGE_KEY, JSON.stringify(next)); } catch (err) {}
      return next;
    });
  }, []);

  // The chart is the base; everything around it opens and closes at the
  // user's will, and the will is remembered.
  const [showSessions, setShowSessions] = useState(() => {
    // Bare by default, remembered when flipped -- same law as the layers.
    try { return localStorage.getItem("sv_chart_sessions") === "1"; } catch (e) { return false; }
  });
  // The user's own drawings: shown by default (they made them), and
  // switchable off without deleting anything.
  const [showDrawings, setShowDrawings] = useState(() => {
    try { return localStorage.getItem("sv_chart_drawings") !== "0"; } catch (e) { return true; }
  });
  const flipDrawings = useCallback(() => setShowDrawings((v) => {
    try { localStorage.setItem("sv_chart_drawings", v ? "0" : "1"); } catch (e) { /* private */ }
    return !v;
  }), []);
  const flipSessions = useCallback(() => setShowSessions((v) => {
    try { localStorage.setItem("sv_chart_sessions", v ? "0" : "1"); } catch (e) { /* private */ }
    return !v;
  }), []);
  const mtfFrames = useMtfFrames();

  // Studies as a dropdown beside the timeframe one -- the owner's call,
  // and the last full-width strip above the candles goes with it.
  const [studiesOpen, setStudiesOpen] = useState(false);
  const studiesRef = useDismissable(studiesOpen, useCallback(() => setStudiesOpen(false), []));
  const [showSpine, setShowSpine] = useState(() => {
    try { return localStorage.getItem("sv_chart_spine") !== "0"; } catch (e) { return true; }
  });
  const flipSpine = useCallback(() => setShowSpine((v) => {
    try { localStorage.setItem("sv_chart_spine", v ? "0" : "1"); } catch (e) { /* private */ }
    return !v;
  }), []);
  const [showMtf, setShowMtf] = useState(() => {
    try { return localStorage.getItem("sv_chart_mtf") !== "0"; } catch (e) { return true; }
  });
  const flipMtf = useCallback(() => setShowMtf((v) => {
    try { localStorage.setItem("sv_chart_mtf", v ? "0" : "1"); } catch (e) { /* private */ }
    return !v;
  }), []);

  // ONE control centre (the owner's spec): everything the chart can show or
  // hide, and every colour the reader may change, behind one gear.
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useDismissable(settingsOpen, useCallback(() => setSettingsOpen(false), []));
  const [, setAppearanceTick] = useState(0);   // value unused: forces a repaint
  const appearance = chartAppearance();

  // Drawing tools live in a docked strip UNDER the chart; the pen in the
  // anchors row opens it. State lives here so the fixed chrome and the
  // canvas agree.
  const [drawOpen, setDrawOpen] = useState(false);
  // Mirrors the view's replay state for the toggle's lit state -- the owner
  // closed the dock and could not find his way back to play.
  const [replayOn, setReplayOn] = useState(false);
  useEffect(() => {
    const onState = (e) => setReplayOn(!!e.detail?.active);
    window.addEventListener("chart:replay-state", onState);
    return () => window.removeEventListener("chart:replay-state", onState);
  }, []);

  // The legend: what every mark means, one tap away.
  const [legendOpen, setLegendOpen] = useState(false);
  const legendRef = useDismissable(legendOpen, useCallback(() => setLegendOpen(false), []));

  // Timeframes as ONE dropdown -- the header's width belongs to the rest.
  const [tfOpen, setTfOpen] = useState(false);
  const tfRef = useDismissable(tfOpen, useCallback(() => setTfOpen(false), []));

  // Layouts: snapshot every remembered chart choice under a name; applying
  // writes the bundle back and reloads -- a full, honest apply with zero
  // half-set states.
  const [layouts, setLayouts] = useState(readLayouts);
  const [layoutName, setLayoutName] = useState("");
  const saveLayout = useCallback(() => {
    const name = layoutName.trim() || new Date().toLocaleString();
    const snap = { __tf: timeframe };
    LAYOUT_KEYS.forEach((k) => {
      try { const v = localStorage.getItem(k); if (v != null) snap[k] = v; } catch (e) {}
    });
    const next = { ...readLayouts(), [name]: snap };
    try { localStorage.setItem("sv_layouts", JSON.stringify(next)); } catch (e) {}
    setLayouts(next);
    setLayoutName("");
  }, [layoutName, timeframe]);
  const applyLayout = useCallback((name) => {
    const snap = readLayouts()[name];
    if (!snap) return;
    LAYOUT_KEYS.forEach((k) => {
      try {
        if (snap[k] != null) localStorage.setItem(k, snap[k]);
        else localStorage.removeItem(k);
      } catch (e) {}
    });
    try { if (snap.__tf) sessionStorage.setItem("preferredTimeframe", snap.__tf); } catch (e) {}
    window.location.reload();
  }, []);
  const deleteLayout = useCallback((name) => {
    const next = { ...readLayouts() };
    delete next[name];
    try { localStorage.setItem("sv_layouts", JSON.stringify(next)); } catch (e) {}
    setLayouts(next);
  }, []);

  // Theater mode: the chart takes the screen. Real fullscreen where the
  // platform allows it; the CSS theater class carries iOS, where
  // requestFullscreen does not exist for arbitrary elements.
  const [theater, setTheater] = useState(false);
  const flipTheater = useCallback(() => {
    setTheater((v) => {
      const next = !v;
      try {
        if (next) document.documentElement.requestFullscreen?.();
        else if (document.fullscreenElement) document.exitFullscreen?.();
      } catch (e) { /* CSS theater still applies */ }
      try { document.body.classList.toggle("chart-theater", next); } catch (e) { /* SSR-safe */ }
      return next;
    });
  }, []);
  useEffect(() => () => { try { document.body.classList.remove("chart-theater"); } catch (e) {} }, []);

  return html`
    <div className="surface chart-card">
      <div className="chart-header">
        ${window.location.pathname !== "/chart" && html`
          <h3><i className="fa-solid fa-chart-area"></i> ${t("chart.title", "Price Chart")}</h3>`}
        ${/* ONE dropdown, not five chips: the owner's call -- the header's
             width belongs to the other controls. It lives OUTSIDE the
             scrolling rail, or its menu is clipped to the rail's box (the
             gear's lesson, relearned live on the owner's screen). The menu
             still carries the engine's read per frame. */""}
        <div className="chart-settings-wrap tf-wrap" ref=${tfRef}>
            <button className="tf-current" aria-expanded=${tfOpen}
                    aria-label=${t("chart.timeframe", "Timeframe")}
                    onClick=${() => setTfOpen((v) => !v)}>
              <b dir="ltr">${timeframe}</b>
              ${(() => {
                const frame = showMtf ? (mtfFrames || []).find((f) => f.timeframe === timeframe) : null;
                if (!frame || frame.action !== "buy" && frame.action !== "sell") return null;
                const glyph = frame.action === "buy" ? "▲" : "▼";
                const cls = frame.action === "buy" ? "pos" : "neg";
                return html`<b className=${"tf-read " + cls}>${glyph}</b>`;
              })()}
              <i className="fa-solid fa-chevron-down" aria-hidden="true"></i>
            </button>
            ${tfOpen && html`
              <div className="chart-settings tf-menu" role="menu"
                   aria-label=${t("chart.timeframe", "Timeframe")}>
                ${Object.keys(TIMEFRAME_SECONDS).map((tf) => {
                  const frame = showMtf ? (mtfFrames || []).find((f) => f.timeframe === tf) : null;
                  const glyph = frame
                    ? (frame.action === "buy" ? "▲" : frame.action === "sell" ? "▼" : "·")
                    : null;
                  const glyphCls = frame
                    ? (frame.action === "buy" ? "pos" : frame.action === "sell" ? "neg" : "")
                    : "";
                  return html`
                  <button key=${tf} role="menuitemradio"
                          className=${"chart-settings-row tf-row" + (timeframe === tf ? " is-active" : "")}
                          aria-checked=${timeframe === tf}
                          onClick=${() => { onChangeTimeframe(tf); setTfOpen(false); }}>
                    <b dir="ltr">${tf}</b>
                    ${glyph && html`
                      <span className=${"tf-read " + glyphCls + (frame.stale ? " is-stale" : "")}>
                        ${glyph}${frame.regime ? html` <small>${regimeLabel(t, frame.regime)}</small>` : ""}
                      </span>`}
                  </button>`;
                })}
              </div>`}
        </div>
        ${/* Studies as a dropdown too (the owner's call): toggles stay open
             while picking, the active count rides the chip, and the last
             full-width strip above the candles is gone. */""}
        <div className="chart-settings-wrap tf-wrap" ref=${studiesRef}>
          <button className="tf-current" aria-expanded=${studiesOpen}
                  aria-label=${t("chart.studies", "Indicators")}
                  onClick=${() => setStudiesOpen((v) => !v)}>
            <i className="fa-solid fa-wave-square" aria-hidden="true"></i>
            ${(() => {
              const n = STUDIES.filter((s) => studies[s.key]).length;
              return n > 0 ? html`<b className="studies-count">${iso(String(n))}</b>` : null;
            })()}
            <i className="fa-solid fa-chevron-down" aria-hidden="true"></i>
          </button>
          ${studiesOpen && html`
            <div className="chart-settings studies-menu" role="menu"
                 aria-label=${t("chart.studies", "Indicators")}>
              <b className="chart-settings-title">${t("chart.studies", "Indicators")}</b>
              ${STUDIES.map((study) => html`
                <button key=${study.key} className="chart-settings-row" role="switch"
                        aria-checked=${!!studies[study.key]}
                        onClick=${() => toggleStudy(study.key)}>
                  <span>
                    <span className="tool-swatch" style=${{ background: study.color }}></span>
                    ${study.label}
                  </span>
                  <i className=${studies[study.key] ? "fa-solid fa-toggle-on" : "fa-solid fa-toggle-off"}
                     aria-hidden="true"></i>
                </button>`)}
            </div>`}
        </div>
        ${/* No quick type switch: the control centre's style grid is the
             ONE place chart style lives -- the two-button copy was
             redundant with it and, squeezed at phone widths, it overlapped
             the anchors. One control, one home. */
          window.location.pathname !== "/chart" && html`
        <div className="chart-controls">
          <a className="btn btn-secondary chart-expand" href="/chart"
             title=${t("chart.open_chart", "Full chart")}>
            <i className="fa-solid fa-up-right-and-down-left-from-center"></i>
            <span>${t("chart.open_chart", "Full chart")}</span>
          </a>
        </div>`}
        ${/* The verdict rides the SAME row as the controls (audit #2: two
             stacked rows spent 105px saying what one 44px row can). It takes
             the middle ground -- TF left, verdict center, icons right -- and
             on a phone it wraps to its own slim line below them. */""}
        ${showSpine && html`
        <${VerdictSpine} verdict=${verdict} timeframe=${timeframe}
                         onWhy=${() => setWhyOpen(true)} />`}
        ${/* Theater and the control centre live OUTSIDE the scrolling strip:
             a popover born inside an overflow rail is clipped to it, and the
             one gear that controls everything must never scroll away. */""}
        <div className="chart-anchors">
          ${/* The pen and "back to now" are FIXED chrome, not floats over
               candles: TradingView and MT5 both keep every control off the
               canvas on phones, and the owner's screens showed why. */""}
          <button className=${"chart-aux-toggle" + (drawOpen ? " is-on" : "")}
                  aria-pressed=${drawOpen}
                  onClick=${() => setDrawOpen((v) => !v)}
                  title=${t("draw.tools", "Drawing tools")}
                  aria-label=${t("draw.tools", "Drawing tools")}>
            <i className="fa-solid fa-pen-ruler"></i>
          </button>
          <button className="chart-aux-toggle"
                  onClick=${() => window.dispatchEvent(new CustomEvent("chart:reset"))}
                  title=${t("chart.reset_view", "Back to now")}
                  aria-label=${t("chart.reset_view", "Back to now")}>
            <i className="fa-solid fa-arrow-right-to-bracket"></i>
          </button>
          <button className=${"chart-aux-toggle" + (replayOn ? " is-on" : "")}
                  aria-pressed=${replayOn}
                  onClick=${() => window.dispatchEvent(new CustomEvent("chart:replay-toggle"))}
                  title=${t("replay.title", "Bar replay")}
                  aria-label=${t("replay.title", "Bar replay")}>
            <i className="fa-solid fa-clock-rotate-left"></i>
          </button>
          ${/* Zoom joins the fixed chrome, desktop only -- a phone pinches. */""}
          <button className="chart-aux-toggle chart-zoom-btn"
                  onClick=${() => window.dispatchEvent(new CustomEvent("chart:zoom", { detail: 1.4 }))}
                  title=${t("chart.zoom_in", "Zoom in")}
                  aria-label=${t("chart.zoom_in", "Zoom in")}>
            <i className="fa-solid fa-magnifying-glass-plus"></i>
          </button>
          <button className="chart-aux-toggle chart-zoom-btn"
                  onClick=${() => window.dispatchEvent(new CustomEvent("chart:zoom", { detail: 0.7 }))}
                  title=${t("chart.zoom_out", "Zoom out")}
                  aria-label=${t("chart.zoom_out", "Zoom out")}>
            <i className="fa-solid fa-magnifying-glass-minus"></i>
          </button>
          <div className="chart-settings-wrap" ref=${legendRef}>
            <button className=${"chart-aux-toggle" + (legendOpen ? " is-on" : "")}
                    aria-expanded=${legendOpen}
                    onClick=${() => setLegendOpen((v) => !v)}
                    title=${t("legend.title", "What's on this chart")}
                    aria-label=${t("legend.title", "What's on this chart")}>
              <i className="fa-solid fa-circle-info"></i>
            </button>
            ${legendOpen && html`
              <${ChartLegend} t=${t} onClose=${() => setLegendOpen(false)} />`}
          </div>
          <button className=${"chart-aux-toggle" + (theater ? " is-on" : "")}
                  aria-pressed=${theater} onClick=${flipTheater}
                  title=${t("chart.theater", "Full screen")}
                  aria-label=${t("chart.theater", "Full screen")}>
            <i className=${theater ? "fa-solid fa-compress" : "fa-solid fa-expand"}></i>
          </button>
          <div className="chart-settings-wrap" ref=${settingsRef}>
            <button className=${"chart-aux-toggle" + (settingsOpen ? " is-on" : "")}
                    aria-expanded=${settingsOpen}
                    onClick=${() => setSettingsOpen((v) => !v)}
                    title=${t("chart.settings", "Chart settings")}
                    aria-label=${t("chart.settings", "Chart settings")}>
              <i className="fa-solid fa-gear"></i>
            </button>
            ${settingsOpen && html`
              <div className="chart-settings" role="dialog"
                   aria-label=${t("chart.settings", "Chart settings")}>
                <b className="chart-settings-title">${t("chart.show_hide", "Show on the chart")}</b>
                ${[[t("chart.mtf", "Timeframe alignment"), showMtf, flipMtf],
                   [t("chart.toggle_spine", "Engine caption"), showSpine, flipSpine],
                   [t("chart.sessions_bands", "Session shading"), showSessions, flipSessions],
                   [t("chart.layer_levels", "Key levels"), layers.levels !== false, () => toggleLayer("levels")],
                   [t("chart.layer_proposal", "The plan"), layers.proposal !== false, () => toggleLayer("proposal")],
                   [t("chart.layer_position", "My trades"), layers.position !== false, () => toggleLayer("position")],
                   [t("chart.my_drawings", "My drawings"), showDrawings, flipDrawings],
                   [t("chart.log_scale", "Log scale"), logScale, () => setLogScale((v) => !v)],
                  ].map(([label, on, flip]) => html`
                  <button key=${label} className="chart-settings-row" role="switch"
                          aria-checked=${on} onClick=${flip}>
                    <span>${label}</span>
                    <i className=${on ? "fa-solid fa-toggle-on" : "fa-solid fa-toggle-off"}
                       aria-hidden="true"></i>
                  </button>`)}
                <b className="chart-settings-title">${t("chart.style", "Chart style")}</b>
                <div className="chart-style-row" role="group" aria-label=${t("chart.style", "Chart style")}>
                  ${CHART_STYLES.map((style) => html`
                    <button key=${style.value}
                            className=${"chart-style-chip" + (chartType === style.value ? " is-on" : "")}
                            aria-pressed=${chartType === style.value}
                            title=${t(style.label, style.fallback)}
                            aria-label=${t(style.label, style.fallback)}
                            onClick=${() => onChangeChartType(style.value)}>
                      <i className=${`fa-solid ${style.icon}`}></i>
                      <small>${t(style.label, style.fallback)}</small>
                    </button>`)}
                </div>
                ${[[t("chart.watermark", "Watermark"), !!appearance.watermark,
                    () => { saveChartAppearance({ watermark: !appearance.watermark }); setAppearanceTick((n) => n + 1); }],
                   [t("chart.grid", "Grid lines"), appearance.grid !== false,
                    () => { saveChartAppearance({ grid: appearance.grid === false }); setAppearanceTick((n) => n + 1); }],
                   [t("chart.pulse", "Last-price pulse"), appearance.pulse !== false,
                    () => { saveChartAppearance({ pulse: appearance.pulse === false }); setAppearanceTick((n) => n + 1); }],
                  ].map(([label, on, flip]) => html`
                  <button key=${label} className="chart-settings-row" role="switch"
                          aria-checked=${on} onClick=${flip}>
                    <span>${label}</span>
                    <i className=${on ? "fa-solid fa-toggle-on" : "fa-solid fa-toggle-off"}
                       aria-hidden="true"></i>
                  </button>`)}
                <b className="chart-settings-title">${t("chart.goto_date", "Go to date")}</b>
                <input type="date" className="chart-goto" dir="ltr"
                       aria-label=${t("chart.goto_date", "Go to date")}
                       onChange=${(e) => {
                         const value = e.target.value;
                         if (!value) return;
                         const secs = Math.floor(Date.parse(`${value}T12:00:00Z`) / 1000);
                         if (Number.isFinite(secs)) {
                           window.dispatchEvent(new CustomEvent("chart:goto", { detail: secs }));
                           setSettingsOpen(false);
                         }
                       }} />
                <b className="chart-settings-title">${t("chart.layouts", "My layouts")}</b>
                <div className="layout-save-row">
                  <input type="text" className="chart-goto layout-name"
                         aria-label=${t("chart.layout_name", "Name this setup")}
                         placeholder=${t("chart.layout_name", "Name this setup")}
                         value=${layoutName}
                         onChange=${(e) => setLayoutName(e.target.value)} />
                  <button className="btn btn-secondary layout-save-btn" onClick=${saveLayout}>
                    ${t("chart.layout_save", "Save")}
                  </button>
                </div>
                ${Object.keys(layouts).map((name) => html`
                  <div key=${name} className="layout-row">
                    <button className="layout-apply" onClick=${() => applyLayout(name)}
                            title=${t("chart.layout_apply", "Apply this layout")}>
                      <i className="fa-solid fa-window-restore" aria-hidden="true"></i>
                      <span>${name}</span>
                    </button>
                    <button className="layout-delete" onClick=${() => deleteLayout(name)}
                            aria-label=${t("draw.delete", "Delete")}>
                      <i className="fa-solid fa-xmark"></i>
                    </button>
                  </div>`)}
                <b className="chart-settings-title">${t("chart.colors", "Colours")}</b>
                <div className="chart-settings-swatches" aria-label=${t("chart.bg", "Background")}>
                  <small>${t("chart.bg", "Background")}</small>
                  ${BG_CHOICES.map((bg) => html`
                    <button key=${bg || "auto"}
                            className=${"chart-swatch" + ((appearance.bg || null) === bg ? " is-on" : "")}
                            style=${{ background: bg || "linear-gradient(135deg,#0B0E13 50%,#FAF8F4 50%)" }}
                            aria-label=${bg || t("chart.bg_auto", "Follow the theme")}
                            onClick=${() => { saveChartAppearance({ bg }); setAppearanceTick((n) => n + 1); }}></button>`)}
                </div>
                <div className="chart-settings-swatches" aria-label=${t("chart.candles_colors", "Candles")}>
                  <small>${t("chart.candles_colors", "Candles")}</small>
                  ${CANDLE_PALETTES.map((pal) => html`
                    <button key=${pal.key}
                            className=${"chart-swatch is-pair" + ((appearance.up || null) === pal.up ? " is-on" : "")}
                            aria-label=${pal.key}
                            onClick=${() => { saveChartAppearance({ up: pal.up, down: pal.down }); setAppearanceTick((n) => n + 1); }}>
                      <span style=${{ background: pal.up || CHART_UP }}></span>
                      <span style=${{ background: pal.down || CHART_DOWN }}></span>
                    </button>`)}
                </div>
              </div>`}
          </div>
        </div>
      </div>
      <${WhySheet} open=${whyOpen} verdict=${verdict}
                   onClose=${() => setWhyOpen(false)} />
      <${ChartView} timeframe=${timeframe} chartType=${chartType}
                    livePrice=${livePrice} layers=${layers}
                    studies=${studies} logScale=${logScale}
                    onWatchLevel=${canPriceAlerts ? watchLevel : null}
                    alertedLevels=${alertedLevels}
                    alertBusy=${alertBusy} trades=${trades}
                    replaySignal=${replaySignal}
                    showSessions=${showSessions} marketOpen=${marketOpen}
                    alerts=${activeAlerts} onMoveAlert=${moveAlert}
                    drawOpen=${drawOpen} showDrawings=${showDrawings}
                    onDragStop=${onDragStop} />
      ${alertNote && html`
        <p className=${"level-alert-note" + (alertNote.ok ? "" : " is-error")} role="status">
          ${alertNote.ok
            ? (alertNote.off
              ? formatTemplate(t("levels.unwatched", "Stopped watching {price}."),
                               { price: iso(formatPrice(alertNote.price)) })
              : formatTemplate(t("levels.watching", "Watching {price} — you will be alerted when price reaches it."),
                               { price: iso(formatPrice(alertNote.price)) }))
            : (alertNote.reason === "quota"
              ? t("levels.quota", "You already have 50 active alerts. Cancel one first.")
              : t("levels.failed", "Could not set that alert."))}
        </p>`}
    </div>
  `;
}

// The chart is painted to a canvas by lightweight-charts, so it cannot inherit
// a CSS variable the way the rest of the UI does. This reads the resolved
// tokens and hands them over, and is re-run on every theme change, so the chart
// surface always matches the card it sits in instead of being a fixed blue
// rectangle on a themed page.
// lightweight-charts hands the crosshair a UTCTimestamp in SECONDS (or a
// business-day object on daily series); render whichever arrives.
// A fixed silhouette for the loading skeleton. Deliberately deterministic:
// a skeleton that reshuffles every render animates noise, and the whole point
// is a calm placeholder that occupies the shape of the answer.
const SKELETON_BARS = [
  { h: 34, top: 30 }, { h: 46, top: 20 }, { h: 28, top: 34 }, { h: 52, top: 14 },
  { h: 38, top: 24 }, { h: 60, top: 10 }, { h: 30, top: 28 }, { h: 44, top: 18 },
  { h: 56, top: 12 }, { h: 36, top: 26 }, { h: 48, top: 16 }, { h: 26, top: 36 },
  { h: 42, top: 22 }, { h: 54, top: 12 }, { h: 32, top: 30 }, { h: 46, top: 18 },
  { h: 38, top: 26 }, { h: 50, top: 14 }, { h: 30, top: 32 }, { h: 44, top: 20 },
];

function formatBarTime(time) {
  if (time == null) return "";
  if (typeof time === "object") {
    return `${time.year}-${String(time.month).padStart(2, "0")}-${String(time.day).padStart(2, "0")}`;
  }
  const d = new Date(time * 1000);
  return d.toLocaleString(appDateLocale(), { month: "short", day: "numeric",
                                       hour: "2-digit", minute: "2-digit" });
}

// Candle colors: the user's chosen palette wins; otherwise the THEME's
// pair. The light theme reuses dark-tuned greens at 3.2:1 on ivory -- the
// audit called the candles washed; CSS now carries a pair per theme.
function candleColors(ap) {
  const cs = getComputedStyle(document.documentElement);
  const v = (name) => cs.getPropertyValue(name).trim();
  return {
    up: (ap && ap.up) || v("--candle-up") || CHART_UP,
    down: (ap && ap.down) || v("--candle-down") || CHART_DOWN,
  };
}

function chartTheme() {
  const cs = getComputedStyle(document.documentElement);
  const v = (name, fallback) => (cs.getPropertyValue(name).trim() || fallback);
  return {
    surface: v("--s1", "#121110"),
    grid: v("--line", "rgba(232,224,208,0.06)"),
    text: v("--ink-3", "#6E675D"),
    crosshair: v("--line-strong", "rgba(232,224,208,0.18)"),
    label: v("--s3", "#242120"),
  };
}

// "Why?" — the chart's interrogation sheet.
//
// Three layers, in this order, always: the claim in the engine's own voice,
// the evidence field-by-field, then the audit trail. There is deliberately NO
// free-text box: an open prompt over a price chart is an invitation for a
// language model to invent a number, and every answer here is instead read
// out of engine state that already exists.
function WhySheet({ open, verdict, onClose }) {
  const { t, translateTechnicalReason } = useI18n();
  if (!open || !verdict) return null;

  const quality = verdict.quality;
  const noTrade = verdict.no_trade;
  const readiness = verdict.readiness;
  const scores = verdict.scores || {};
  const blockers = verdict.blockers || [];
  const dir = String(verdict.ai_action || "hold").toLowerCase();

  const claim = (() => {
    if (verdict.withheld_by) {
      return formatTemplate(
        t("why.claim_withheld", "A setup was found and withheld: {why}"),
        { why: translateTechnicalReason(verdict.withheld_by) });
    }
    if (blockers.length) {
      return verdict.reason_key
        ? t(`verdict.reasons.${verdict.reason_key}`, verdict.reason || "")
        : (verdict.reason || t("verdict.status_wait", "Stand aside"));
    }
    if (dir === "buy" || dir === "sell") {
      return verdict.reason || t("verdict.status_take", "Setup stands");
    }
    return (noTrade && noTrade.headline && translateTechnicalReason(noTrade.headline))
      || t("verdict.status_none", "No setup");
  })();

  const rows = [];
  if (verdict.regime) {
    rows.push([t("why.regime", "Market regime"), regimeLabel(t, verdict.regime)]);
  }
  if (scores.buy != null || scores.sell != null) {
    // "Why not the other side?" answered from the engine's own tally rather
    // than a narrative. "BUY 0 · SELL 0 · gate 3" was that tally in the
    // engine's shorthand; a member should not need the source to read it.
    rows.push([t("why.scores", "Score by side"),
               `${t("signals.buy", "BUY")} ${iso(scores.buy ?? 0)}/${iso(3)} · ${t("signals.sell", "SELL")} ${iso(scores.sell ?? 0)}/${iso(3)} — ${t("why.gate_note", "a side publishes at 3")}`]);
  }
  (quality?.components || []).forEach((c) => {
    rows.push([t(`quality.${c.key}`, c.key.replace(/_/g, " ")),
               `${iso(c.points)}/${iso(c.max)} · ${c.detail}`]);
  });
  if (readiness && readiness.forming) {
    rows.push([t("why.readiness", "Forming"), readiness.detail || readiness.headline]);
  }
  // The full sentence, not the short chip label: this is the evidence sheet,
  // the one screen the product asks to be judged on, and it printed the bare
  // key "yield_headwind" in the row that says what stopped the trade.
  blockers.forEach((b) => rows.push([t("why.blocker", "Blocked by"),
                                     t(`verdict.reasons.${b}`, translateTechnicalReason(b))]));

  const help = (noTrade && noTrade.what_would_help) || [];

  return html`
    <div className="why-scrim" onClick=${onClose}>
      <section className="why-sheet" role="dialog" aria-modal="true"
               onClick=${(e) => e.stopPropagation()}>
        <header className="why-head">
          <h2>${t("why.title", "Why")}</h2>
          <button className="btn btn-ghost" onClick=${onClose}
                  aria-label=${t("buttons.close", "Close")}>
            <i className="fa-solid fa-xmark"></i>
          </button>
        </header>

        ${/* L1 — the claim, in the engine's voice. */""}
        <p className="why-claim">${claim}</p>
        ${verdict.as_of && html`
          <p className="why-stamp" dir="ltr">
            ${t("why.read_at", "read at")} ${new Date(verdict.as_of).toLocaleTimeString(appDateLocale())}
          </p>`}

        ${/* L2 — the evidence, field by field. */""}
        ${rows.length > 0 && html`
          <div className="why-rows">
            ${rows.map(([label, value], i) => html`
              <div className="why-row" key=${i}>
                <span>${label}</span><b dir="auto">${value}</b>
              </div>`)}
          </div>`}

        <${NoTradeMeasuredLine} />
        ${help.length > 0 && html`
          <div className="why-help">
            <h3>${t("verdict.no_trade_watch", "What would change it")}</h3>
            <ul>${help.map((h) => html`<li key=${h}>${translateTechnicalReason(h)}</li>`)}</ul>
          </div>`}

        ${/* L3 — the audit trail. Almost nobody opens it; its existence is
             the point. */""}
        <details className="why-audit">
          <summary>${t("why.audit", "The record behind this")}</summary>
          <p>${t("why.audit_note", "Every call this product publishes is written to a hash-chained log and republished publicly, losses included.")}</p>
          <a href="/verify">${t("performance.ours_short", "Our record")}</a>
        </details>
      </section>
    </div>`;
}

// The verdict spine: the chart's caption, not a card. It is the one element
// that is NEVER absent -- the engine is silent most of the time, and silence
// has to be a stated state rather than an empty screen. Four states, all from
// the same verdict the rail renders, so the two can never disagree.
function VerdictSpine({ verdict, timeframe, onWhy }) {
  const { t, translateTechnicalReason } = useI18n();
  // A verdict stamped with a different frame is the one the user just left.
  // Painting its words under the new frame's label made two parts of one
  // screen disagree for up to ~3s after a switch; "reading" is the truth.
  if (!verdict || (verdict.timeframe && timeframe && verdict.timeframe !== timeframe)) {
    return html`
      <div className="spine spine-unknown">
        <span className="spine-state">${t("spine.reading", "Reading the market…")}</span>
      </div>`;
  }
  const dir = String(verdict.ai_action || "hold").toLowerCase();
  const directional = dir === "buy" || dir === "sell";
  const blockers = verdict.blockers || [];
  const readiness = verdict.readiness;
  const noTrade = verdict.no_trade;
  const quality = verdict.quality;

  let state = "no_setup";
  if (directional && !blockers.length) state = "setup";
  else if (directional && blockers.length) state = "stood_aside";
  else if (readiness && readiness.forming) state = "forming";

  const sideWord = dir === "buy" ? t("signals.buy", "BUY") : t("signals.sell", "SELL");
  const detail = (() => {
    if (state === "setup") {
      return quality ? `${t("quality.label", "Setup quality")} ${iso(quality.score)} · ${quality.grade}` : "";
    }
    if (state === "stood_aside") {
      return verdict.reason_key
        ? t(`verdict.reasons.${verdict.reason_key}`, verdict.reason || "")
        : (verdict.reason || "");
    }
    if (state === "forming") return readiness.detail || readiness.headline || "";
    // no_setup: say WHY, including when a gate withheld it -- that used to be
    // indistinguishable from "the engine had no opinion".
    if (verdict.withheld_by) {
      return formatTemplate(t("spine.withheld", "Withheld: {why}"),
                            { why: translateTechnicalReason(verdict.withheld_by) });
    }
    return (noTrade && noTrade.reasons && noTrade.reasons[0]
            && translateTechnicalReason(noTrade.reasons[0]))
      || t("verdict.status_none", "No setup");
  })();

  const label = {
    setup: `${sideWord} ${iso(timeframe)}`,
    stood_aside: t("verdict.status_wait", "Stand aside"),
    forming: formatTemplate(t("spine.forming", "{side} forming"), { side: sideWord }),
    no_setup: t("verdict.status_none", "No setup"),
  }[state];

  return html`
    <button type="button" className=${`spine spine-${state} spine-dir-${dir}`}
            onClick=${onWhy} aria-label=${t("spine.why", "Why?")}>
      <span className="spine-state">${label}</span>
      ${detail && html`<span className="spine-detail">${detail}</span>`}
      ${verdict.as_of && html`
        <span className="spine-stamp" dir="ltr">
          ${regimeLabel(t, verdict.regime)} · ${new Date(verdict.as_of).toLocaleTimeString(appDateLocale())}
        </span>`}
      <i className="fa-solid fa-circle-question spine-why" aria-hidden="true"></i>
    </button>`;
}

// Only shapes that are exactly representable as typed geometry. No free-form
// brush, no text, no arrows -- deliberately. A scribble has no representation
// except pixels, and the assistant can only read a drawing it can describe in
// numbers. That is a product decision, not a technical limit.
const DRAW_TOOLS = [
  { key: "hline", icon: "fa-minus", label: "draw.hline", fallback: "Horizontal level" },
  { key: "vline", icon: "fa-grip-lines-vertical", label: "draw.vline", fallback: "Vertical marker" },
  { key: "trendline", icon: "fa-slash", label: "draw.trendline", fallback: "Trend line" },
  { key: "ray", icon: "fa-arrow-trend-up", label: "draw.ray", fallback: "Ray" },
  { key: "arrow", icon: "fa-arrow-right-long", label: "draw.arrow", fallback: "Arrow" },
  { key: "rect", icon: "fa-square", label: "draw.rect", fallback: "Zone" },
  { key: "channel", icon: "fa-bars", label: "draw.channel", fallback: "Channel" },
  { key: "fib", icon: "fa-bars-staggered", label: "draw.fib", fallback: "Fibonacci" },
  { key: "trade", icon: "fa-arrows-up-down", label: "draw.trade", fallback: "Long / Short" },
  { key: "measure", icon: "fa-ruler", label: "draw.measure", fallback: "Measure" },
];

// How this product's own level map scores — rendered under the level map.
//
// Every charting app draws pivots and none of them grade their own, because
// the answer is usually "about as often as chance" and saying so costs a
// sale. Ours is exactly that: 59.9% reaction, against 58.7% for the identical
// levels mirrored to the WRONG side of price, and 61.2% for a random line in
// the same band. The map is for orientation. A product that cannot say that
// has no business calling any of its lines strong.
function LevelScorecard() {
  const { t } = useI18n();
  const [card, setCard] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const p = await (await authenticatedFetch("/api/levels/scorecard")).json();
        if (alive && p.success && p.available) setCard(p);
      } catch (e) { /* the line simply does not appear */ }
    })();
    return () => { alive = false; };
  }, []);

  if (!card) return null;

  return html`
    <div className="level-score">
      <button className="level-score-toggle" aria-expanded=${open}
              onClick=${() => setOpen((v) => !v)}>
        <i className="fa-solid fa-scale-balanced" aria-hidden="true"></i>
        <span>${t("levels.graded", "How often have these levels actually held?")}</span>
        <i className=${"fa-solid " + (open ? "fa-chevron-up" : "fa-chevron-down")}
           aria-hidden="true"></i>
      </button>
      ${open && html`
        <div className="level-score-body">
          <p className="level-score-reading">${card.reading}</p>
          <ul className="level-score-arms" dir="ltr">
            ${[["ours", t("levels.arm_ours", "Our levels"), card.overall],
               ["mirror", t("levels.arm_mirror", "The same levels, wrong side"), card.mirror],
               ["band", t("levels.arm_band", "A random line, same range"), card.band]]
              .filter((row) => row[2] && row[2].reaction_rate != null)
              .map((row) => html`
                <li key=${row[0]}>
                  <span>${row[1]}</span>
                  <b>${iso(String(row[2].reaction_rate))}%</b>
                  <small>${formatTemplate(
                    t("levels.arm_sample", "{reacted} of {reached} reached"),
                    { reacted: iso(String(row[2].reacted)),
                      reached: iso(String(row[2].reached)) })}</small>
                </li>`)}
          </ul>
          <p className="level-score-use">${card.use}</p>
          <small className="muted">${card.measured_on}</small>
        </div>`}
    </div>`;
}

function ChartView({ timeframe, chartType, livePrice, layers, studies, logScale,
                     onWatchLevel, alertedLevels = new Set(), alertBusy = null,
                     trades = [], showSessions = true, marketOpen = true,
                     alerts = [], onMoveAlert = null, drawOpen = false,
                     showDrawings = true, onDragStop = null,
                     replaySignal = null }) {
  const activeLayers = layers || { levels: true, proposal: true, position: true };
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const dataRef = useRef([]);
  const rangeRef = useRef({ from: null, to: null });
  const loadingRef = useRef(false);
  const initialLoadRef = useRef(false);
  const userInteractedRef = useRef(false);
  // Monotonic fetch counter -- the stale-response guard for loadChart.
  const loadSeqRef = useRef(0);
  // The ?at= deep link is honoured once per mount, not once per timeframe.
  const atConsumedRef = useRef(false);
  const [status, setStatus] = useState({ state: "idle", message: "" });
  // The series' IDENTITY as state. Every effect that touches the series
  // (overlays, drawing layer, bands) depends on this number, so a rebuild
  // re-runs them against the NEW series. The old architecture applied
  // overlays to a series that was about to be destroyed, and the chart went
  // naked until the next 30-second poll noticed.
  const [seriesEpoch, setSeriesEpoch] = useState(0);
  const [hoveredBar, setHoveredBar] = useState(null);
  const studySeriesRef = useRef({});
  // The user's own lines. One layer for all of them -- see drawing-layer.js
  // for why not one primitive per shape.
  const drawingLayerRef = useRef(null);
  const drawingControllerRef = useRef(null);
  const [drawings, setDrawings] = useState([]);
  const [armedTool, setArmedTool] = useState(null);
  const [selectedDrawing, setSelectedDrawing] = useState(null);
  // Closing the dock disarms: a hidden armed tool would turn the next
  // innocent tap into a drawing.
  useEffect(() => { if (!drawOpen) setArmedTool(null); }, [drawOpen]);
  const [measuring, setMeasuring] = useState(null);
  const [measureResult, setMeasureResult] = useState(null);
  const sessionBandsRef = useRef(null);
  const volumeProfileRef = useRef(null);
  useEffect(() => {
    sessionBandsRef.current?.setEnabled(showSessions !== false);
  }, [showSessions]);
  const eventLaneRef = useRef(null);
  // bar time -> event titles, for the hover chip: the lane says WHERE, this
  // says WHAT, and no text ever sits on the candles.
  const eventsByBarRef = useRef(new Map());

  // The forming bar's own clock. A chart that says "12:41 until this candle
  // closes" feels alive in a way a static last-price never does -- and it is
  // pure arithmetic on the frame, nothing invented. Hidden when the market
  // is closed: a countdown on a candle nobody is forming would be a lie.
  const [closeIn, setCloseIn] = useState(null);
  useEffect(() => {
    if (!marketOpen) { setCloseIn(null); return undefined; }
    const tfSec = TIMEFRAME_SECONDS[timeframe];
    if (!tfSec) { setCloseIn(null); return undefined; }
    const tick = () => {
      const now = Math.floor(Date.now() / 1000);
      // The clock counts on the LAST REAL BAR'S grid, not midnight UTC's:
      // 4h and daily bars anchor to the New York day, and epoch-modulo
      // arithmetic was hours wrong there. Anchoring the modulo at the last
      // bar's own open inherits whatever anchoring the feed truly uses, and
      // keeps counting across the feed's normal lag. No bars, no clock.
      const last = dataRef.current[dataRef.current.length - 1];
      if (last && Number.isFinite(last.time) && now >= last.time) {
        setCloseIn(tfSec - ((now - last.time) % tfSec));
      } else {
        setCloseIn(null);
      }
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [timeframe, marketOpen]);
  const fmtCloseIn = (secs) => {
    const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = secs % 60;
    const mm = String(m).padStart(2, "0"), ss = String(s).padStart(2, "0");
    return h ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
  };
  // The spacing the chart opened with, so "back to now" has something true to
  // return to after zooming has rewritten the option it would otherwise use.
  const defaultSpacingRef = useRef(6);
  const [measurement, setMeasurement] = useState(null);
  const drawingsRef = useRef([]);
  const overlaysRef = useRef(null);
  const studyDataRef = useRef(null);
  // Always the CURRENT loadChart. The infinite-scroll subscription is
  // registered once, on first render, and would otherwise hold that render's
  // loadChart forever -- whose closed-over timeframe then fetches 1h bars and
  // splices them into the 5m series by timestamp.
  const loadChartRef = useRef(null);
  // Always the CURRENT rebuildSeries, so initChart can stay dependency-free.
  const rebuildSeriesRef = useRef(null);
  // Same pattern for drawStudies: loadChart (declared first) redraws the
  // volume histogram after fresh candles land.
  const drawStudiesRef = useRef(null);
  // The risk/reward bands. Attached once to the series and mutated after that:
  // re-attaching on every overlay poll would leak primitives.
  const riskBoxRef = useRef(null);
  const rewardBoxRef = useRef(null);
  // The open position's entry price-line, held so the live tick can update
  // its R label without rebuilding every overlay.
  const posLineRef = useRef(null);
  // Alert lines by id, so a drag can slide ONE line live without a rebuild;
  // alertsRef keeps the current list readable from controller callbacks.
  const alertLinesRef = useRef(new Map());
  const alertsRef = useRef([]);
  useEffect(() => { alertsRef.current = alerts || []; }, [alerts]);
  // Ref'd so the controller (built per seriesEpoch) never holds a stale copy.
  const onMoveAlertRef = useRef(onMoveAlert);
  useEffect(() => { onMoveAlertRef.current = onMoveAlert; }, [onMoveAlert]);
  const onDragStopRef = useRef(onDragStop);
  useEffect(() => { onDragStopRef.current = onDragStop; }, [onDragStop]);
  // The open position's STOP line: draggable like an alert, governed by the
  // server (a widening drag is refused and the line snaps back).
  const posStopRef = useRef(null);
  // Hide-my-drawings: the layer paints an empty list while off; the data
  // stays in drawingsRef, nothing is deleted.
  const showDrawingsRef = useRef(showDrawings);
  useEffect(() => {
    showDrawingsRef.current = showDrawings;
    drawingLayerRef.current?.set(showDrawings ? drawingsRef.current : []);
  }, [showDrawings]);
  const { t } = useI18n();

  // True once ANY bars have painted this mount. A timeframe switch clears
  // dataRef (the accumulators) but the canvas still shows the old candles --
  // and blanking them under a spinner for ~2.4s was the audit's #1 fatigue
  // source. This ref is how the overlay knows "switch" from "first load".
  const hasPaintedRef = useRef(false);

  // Bar replay: {index, playing, speed} while the trainer is on, else null.
  // Every repaint flows through applySeriesData, so replay lives THERE: the
  // full series is remembered, only the first `index` bars paint, and live
  // updates keep accumulating underneath without breaking the illusion.
  const [replay, setReplay] = useState(null);
  const replayRef = useRef(null);
  replayRef.current = replay;
  const fullDataRef = useRef([]);

  const applySeriesData = useCallback(
    (data) => {
      if (!seriesRef.current) return;
      fullDataRef.current = data || [];
      const rp = replayRef.current;
      const shown = rp ? (data || []).slice(0, Math.max(2, rp.index)) : data;
      if (chartType === "line" || chartType === "area") {
        seriesRef.current.setData(shown.map((item) => ({ time: item.time, value: item.close })));
      } else if (chartType === "heikin") {
        seriesRef.current.setData(heikinAshi(shown));
      } else {
        seriesRef.current.setData(shown);
      }
      if (data && data.length) hasPaintedRef.current = true;
      volumeProfileRef.current?.setBars(shown);
    },
    [chartType]
  );

  // Repaint when the replay cursor moves; restore the full series on exit.
  useEffect(() => {
    applySeriesData(fullDataRef.current);
  }, [replay?.index, replay == null, applySeriesData]);

  // Replay-a-call: the published record as theater. The call's own levels
  // paint as solid lines, the cursor rewinds to the bar before publication,
  // and the bars that decided it play out -- proof no screenshot can fake.
  const sigStartedRef = useRef(false);
  useEffect(() => { sigStartedRef.current = false; }, [replaySignal?.signal_id]);
  useEffect(() => {
    const series = seriesRef.current;
    if (!series || !replaySignal) return undefined;
    const mk = (price, color, title) => {
      try {
        return series.createPriceLine({
          price: Number(price), color, lineWidth: 2,
          lineStyle: LWC.LineStyle.Solid, axisLabelVisible: true, title });
      } catch (e) { return null; }
    };
    const lines = [
      replaySignal.entry != null && mk(replaySignal.entry, "#E9E4D8",
        `${String(replaySignal.side || "").toUpperCase()} ${t("advisor.entry", "Entry")}`),
      replaySignal.stop != null && mk(replaySignal.stop, CHART_DOWN, t("positions.stop", "Stop")),
      replaySignal.target != null && mk(replaySignal.target, CHART_UP, t("positions.target", "Target")),
    ].filter(Boolean);
    // The call's whole geometry must be VISIBLE: a price line does not
    // extend autoscale, so a winning target could sit below the axis while
    // its own replay played. Expand the scale to hold all three levels.
    const levels = [replaySignal.entry, replaySignal.stop, replaySignal.target]
      .filter((v) => v != null).map(Number);
    try {
      series.applyOptions({
        autoscaleInfoProvider: (original) => {
          const base = original();
          if (!base || !base.priceRange) return base;
          return {
            ...base,
            priceRange: {
              minValue: Math.min(base.priceRange.minValue, ...levels),
              maxValue: Math.max(base.priceRange.maxValue, ...levels),
            },
          };
        },
      });
    } catch (e) { /* older lib */ }
    return () => {
      lines.forEach((line) => {
        try { series.removePriceLine(line); } catch (e) { /* series gone */ }
      });
      try { series.applyOptions({ autoscaleInfoProvider: null }); } catch (e) {}
    };
  }, [replaySignal, seriesEpoch, t]);
  useEffect(() => {
    if (!replaySignal || sigStartedRef.current) return;
    if (status.state === "loading" || !fullDataRef.current.length) return;
    const bars = fullDataRef.current;
    const at = Number(replaySignal.published_at) || 0;
    let idx = bars.findIndex((b) => b.time >= at);
    if (idx < 0) idx = Math.floor(bars.length * 0.6);
    sigStartedRef.current = true;
    setReplay({ index: Math.max(5, idx - 1), playing: true, speed: 5 });
  }, [replaySignal, status.state]);

  // Tap-any-line (the owner's direct ask): a tap near any ENGINE line opens
  // its biography sheet -- what it is, its arithmetic, its touches -- with
  // "ask the AI about it" one button away. User drawings keep their own
  // long-press path; this covers the lines the ENGINE drew.
  const [levelSheet, setLevelSheet] = useState(null);
  useEffect(() => {
    const chart = chartRef.current;
    const series = seriesRef.current;
    if (!chart || !series) return undefined;
    const onClick = (param) => {
      if (!param || !param.point) return;
      let price = null;
      try { price = series.coordinateToPrice(param.point.y); } catch (e) { return; }
      if (price == null) return;
      const px = (level) => {
        try {
          const y = series.priceToCoordinate(level);
          return y == null ? Infinity : Math.abs(y - param.point.y);
        } catch (e) { return Infinity; }
      };
      const candidates = [];
      const ov = overlaysRef.current || {};
      (ov.key_levels || []).forEach((z) => candidates.push({
        price: z.price, dist: px(z.price),
        kind: z.side === "above" ? "resistance" : "support",
        sources: z.sources || [], confluence: z.confluence,
      }));
      (ov.support || []).forEach((g) => candidates.push({
        price: g.price, dist: px(g.price), kind: "support", touches: g.touches }));
      (ov.resistance || []).forEach((g) => candidates.push({
        price: g.price, dist: px(g.price), kind: "resistance", touches: g.touches }));
      (alerts || []).forEach((al) => candidates.push({
        price: al.price, dist: px(al.price), kind: "alert", note: al.note }));
      (trades || []).forEach((tr) => {
        if (tr.entry_price != null) candidates.push({ price: tr.entry_price, dist: px(tr.entry_price), kind: "entry", side: tr.side });
        if (tr.stop_loss != null) candidates.push({ price: tr.stop_loss, dist: px(tr.stop_loss), kind: "stop" });
        if (tr.take_profit != null) candidates.push({ price: tr.take_profit, dist: px(tr.take_profit), kind: "target" });
      });
      const hit = candidates.filter((c) => c.dist <= 9)
        .sort((a, b) => a.dist - b.dist)[0];
      if (hit) setLevelSheet(hit);
    };
    chart.subscribeClick(onClick);
    return () => { try { chart.unsubscribeClick(onClick); } catch (e) {} };
  }, [alerts, trades, seriesEpoch]);

  // The trainer switch lives in the panel's chrome, one component up; the
  // same CustomEvent bus the reset button already uses carries the toggle.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("chart:replay-state",
                                         { detail: { active: !!replay } }));
  }, [replay == null]);

  useEffect(() => {
    const toggle = () => setReplay((prev) => {
      if (prev) return null;
      const bars = fullDataRef.current.length || dataRef.current.length;
      if (bars < 30) return null;
      return { index: Math.max(20, Math.floor(bars * 0.35)),
               playing: false, speed: 2 };
    });
    window.addEventListener("chart:replay-toggle", toggle);
    return () => window.removeEventListener("chart:replay-toggle", toggle);
  }, []);

  // The play clock. Speed is bars per second; reaching the last bar pauses
  // rather than exits, so the reader sees where "now" caught up with them.
  useEffect(() => {
    if (!replay || !replay.playing) return undefined;
    const timer = setInterval(() => {
      setReplay((prev) => {
        if (!prev) return prev;
        const last = fullDataRef.current.length;
        const next = prev.index + 1;
        return next >= last ? { ...prev, index: last, playing: false }
                            : { ...prev, index: next };
      });
    }, 1000 / (replay.speed || 2));
    return () => clearInterval(timer);
  }, [replay?.playing, replay?.speed]);

  // ---- Overlays -------------------------------------------------------
  // The engine's decisions rest on distance to the nearest support and
  // resistance, and none of it was drawn, so a trader could not check the one
  // thing a recommendation was built on without redrawing the level by hand in
  // another terminal.
  //
  // One grammar, applied without exception:
  //   solid   = committed capital        dashed = proposed, not taken
  //   dotted  = observed structure
  //   red = stop   green = target   neutral = entry   amber/blue = structure
  const [overlays, setOverlays] = useState(null);

  // Saving is debounced past the end of a drag: a drag fires a request per
  // frame otherwise, and the user is still holding the line. ONE timer PER
  // drawing -- a shared timer meant finishing shape A and quickly placing
  // shape B silently cancelled A's save, and A vanished on the next reload.
  const saveTimersRef = useRef(new Map());
  const [saveNote, setSaveNote] = useState(null);
  const persist = useCallback((drawing) => {
    const timers = saveTimersRef.current;
    clearTimeout(timers.get(drawing.id));
    timers.set(drawing.id, setTimeout(async () => {
      timers.delete(drawing.id);
      try {
        // Strip the logical index before storing. It is a position in the
        // CURRENTLY loaded series, and this app prepends history when the user
        // scrolls back -- a persisted logical index therefore describes a
        // different bar every time more history arrives, and an anchor in the
        // right margin (which has no time at all) would jump hundreds of bars.
        // It is re-derived at paint time, where it is always current.
        const anchors = (drawing.anchors || []).map(({ l, snappedTo, ...rest }) => rest);
        const response = await authenticatedFetch("/api/drawings", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // The drawing's OWN timeframe, not the one on screen: nudging a
          // daily level while viewing 5m must not re-tag it as a 5m line.
          body: JSON.stringify({ ...drawing, anchors,
                                 timeframe: drawing.timeframe || timeframe }),
        });
        const payload = await response.json();
        if (!payload.success) setSaveNote("failed");
      } catch (error) {
        // Say so. A silently unsaved level is a trader's level lost.
        setSaveNote("failed");
      }
    }, 400));
  }, [timeframe]);
  useEffect(() => {
    if (!saveNote) return undefined;
    const timer = setTimeout(() => setSaveNote(null), 5000);
    return () => clearTimeout(timer);
  }, [saveNote]);

  // ---- Undo / redo ----------------------------------------------------
  // A history of INVERSES: create<->delete, move<->move-back. Undoing a
  // delete recreates the drawing under a NEW id, because the server's
  // soft-delete rule (deleted stays deleted) is the one we wrote on
  // purpose; the content comes back, the identity is new.
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const lastAnchorsRef = useRef(new Map());
  const pendingCreatesRef = useRef(new Set());
  const [historyTick, setHistoryTick] = useState(0);
  const pushHistory = useCallback((entry) => {
    undoStackRef.current.push(entry);
    if (undoStackRef.current.length > 50) undoStackRef.current.shift();
    redoStackRef.current = [];
    setHistoryTick((n) => n + 1);
  }, []);

  const removeDrawing = useCallback(async (id, opts = {}) => {
    const snapshot = drawingsRef.current.find((d) => d.id === id);
    if (snapshot && !opts.skipHistory) {
      pushHistory({ type: "delete",
                    drawing: JSON.parse(JSON.stringify(snapshot)) });
    }
    // Cancel any save still in flight for this drawing, or the debounced
    // POST lands after the DELETE and the server sees an edit of a dead row.
    clearTimeout(saveTimersRef.current.get(id));
    saveTimersRef.current.delete(id);
    drawingsRef.current = drawingsRef.current.filter((d) => d.id !== id);
    setDrawings([...drawingsRef.current]);
    drawingLayerRef.current?.set(showDrawingsRef.current ? drawingsRef.current : []);
    drawingLayerRef.current?.select(null);
    setSelectedDrawing(null);
    try {
      await authenticatedFetch(`/api/drawings/${id}`, { method: "DELETE" });
    } catch (error) { /* the local removal stands */ }
  }, []);
  const priceLinesRef = useRef([]);

  // Undo/redo application. Each entry knows its own inverse.
  const reviveDrawing = useCallback((snapshot) => {
    const clone = { ...JSON.parse(JSON.stringify(snapshot)),
                    id: `d-${Date.now()}-${Math.floor(Math.random() * 1e6)}` };
    drawingsRef.current = [...drawingsRef.current, clone];
    drawingLayerRef.current?.set(showDrawingsRef.current ? drawingsRef.current : []);
    setDrawings([...drawingsRef.current]);
    lastAnchorsRef.current.set(clone.id, JSON.parse(JSON.stringify(clone.anchors || [])));
    persist(clone);
    return clone;
  }, [persist]);
  const restoreAnchors = useCallback((id, anchors) => {
    const target = drawingsRef.current.find((d) => d.id === id);
    if (!target) return;
    target.anchors = JSON.parse(JSON.stringify(anchors));
    drawingLayerRef.current?.set(showDrawingsRef.current ? drawingsRef.current : []);
    setDrawings([...drawingsRef.current]);
    lastAnchorsRef.current.set(id, JSON.parse(JSON.stringify(anchors)));
    persist(target);
  }, [persist]);
  const undo = useCallback(() => {
    const entry = undoStackRef.current.pop();
    if (!entry) return;
    if (entry.type === "create") {
      removeDrawing(entry.drawing.id, { skipHistory: true });
      redoStackRef.current.push(entry);
    } else if (entry.type === "delete") {
      const revived = reviveDrawing(entry.drawing);
      redoStackRef.current.push({ type: "delete", drawing: revived });
    } else if (entry.type === "move") {
      restoreAnchors(entry.id, entry.before);
      redoStackRef.current.push(entry);
    }
    setHistoryTick((n) => n + 1);
  }, [removeDrawing, reviveDrawing, restoreAnchors]);
  const redo = useCallback(() => {
    const entry = redoStackRef.current.pop();
    if (!entry) return;
    if (entry.type === "create") {
      const revived = reviveDrawing(entry.drawing);
      undoStackRef.current.push({ type: "create", drawing: revived });
    } else if (entry.type === "delete") {
      removeDrawing(entry.drawing.id, { skipHistory: true });
      undoStackRef.current.push(entry);
    } else if (entry.type === "move") {
      restoreAnchors(entry.id, entry.after);
      undoStackRef.current.push(entry);
    }
    setHistoryTick((n) => n + 1);
  }, [removeDrawing, reviveDrawing, restoreAnchors]);

  useEffect(() => {
    let alive = true;
    let retry = null;
    const load = async () => {
      try {
        const response = await authenticatedFetch(`/api/overlays/${timeframe}`);
        const payload = await response.json();
        if (alive && payload.success) setOverlays(payload);
        else if (alive) retry = setTimeout(load, 5000);
      } catch (err) {
        // Overlays are additive; the chart must still draw without them --
        // but one failed fetch at mount (seen after the language-switch
        // remount) left the KEY LEVELS strip empty for a full 30s poll.
        // A quick retry closes that hole.
        if (alive) retry = setTimeout(load, 5000);
      }
    };
    load();
    const timer = setInterval(load, 30000);
    return () => { alive = false; clearInterval(timer); clearTimeout(retry); };
  }, [timeframe]);

  useEffect(() => {
    const series = seriesRef.current;
    if (!series) return;

    priceLinesRef.current.forEach((line) => {
      try { series.removePriceLine(line); } catch (err) { /* series was rebuilt */ }
    });
    priceLinesRef.current = [];
    if (!overlays) return;

    // Two axis labels closer together than this overlap into an unreadable
    // smear on the right-hand scale -- which is exactly what "Target 4380.06"
    // and "R x1 4371.69" were doing. The later, less important label loses its
    // axis tag; the line itself is still drawn.
    const claimedLabels = [];
    // The LIVE PRICE claims its axis row before anything else asks (design
    // law #1: the last-price label always wins collisions). The owner's
    // screen caught four tags piled into forty pixels around it.
    if (overlays.price != null) claimedLabels.push(overlays.price);
    const labelFits = (price) => {
      const span = Math.abs((overlays.price || price) * 0.0025) || 1;
      if (claimedLabels.some((taken) => Math.abs(taken - price) < span)) return false;
      claimedLabels.push(price);
      return true;
    };

    const add = (opts) => {
      const options = { ...opts };
      if (options.axisLabelVisible && !labelFits(options.price)) {
        options.axisLabelVisible = false;
      }
      try {
        const line = series.createPriceLine(options);
        priceLinesRef.current.push(line);
        return line;
      } catch (err) { return null; }
    };

    // Bars are NOT epoch-aligned on every frame -- 4h bars anchor to the New
    // York day, daily bars to the exchange day -- and a marker whose time is
    // not an ACTUAL bar time is silently dropped by the library. So markers
    // snap to the latest real bar at or before the event, never to
    // arithmetic flooring that assumes midnight-UTC bar boundaries.
    const barTimes = dataRef.current.map((candle) => candle.time);
    const snapToBar = (secs) => {
      if (!Number.isFinite(secs) || !barTimes.length) return null;
      let lo = 0, hi = barTimes.length - 1, found = null;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (barTimes[mid] <= secs) { found = barTimes[mid]; lo = mid + 1; }
        else hi = mid - 1;
      }
      return found;
    };

    // Secondary levels are omitted entirely on a narrow canvas rather than
    // drawn faintly: six lines on a 390px pane is not a chart.
    const wide = (containerRef.current?.clientWidth || 0) >= 480;

    // An open position outranks everything for label space: it is committed
    // capital, so it claims its axis labels before the proposal or the levels.
    posLineRef.current = null;
    posStopRef.current = null;
    if (activeLayers.position) {
      (overlays.positions || []).forEach((pos, posIndex) => {
        const r = pos.unrealised_r;
        const entryLine = add({ price: pos.entry_price, color: "#ffffff", lineWidth: 2,
              lineStyle: LWC.LineStyle.Solid, axisLabelVisible: true,
              title: `${(pos.side || "").toUpperCase()}${r != null ? ` ${r > 0 ? "+" : ""}${Number(r).toFixed(2)}R` : ""}` });
        // The first position's entry label is kept live off the same quote
        // the rail uses -- one R formula on the whole screen, or the two
        // numbers visibly disagree between polls.
        if (posIndex === 0 && entryLine) posLineRef.current = { line: entryLine, pos };
        if (pos.stop_loss != null) {
          const stopLine = add({ price: pos.stop_loss, color: CHART_DOWN, lineWidth: 2,
                lineStyle: LWC.LineStyle.Solid, axisLabelVisible: true, title: t("advisor.stop_loss", "STOP") });
          // MT5's gesture, our rule: the stop line drags, the server
          // accepts only a TIGHTENING move, a refused drag snaps back.
          if (posIndex === 0 && stopLine) {
            posStopRef.current = { line: stopLine, tradeId: pos.id, price: pos.stop_loss };
          }
        }
        if (pos.take_profit != null)
          add({ price: pos.take_profit, color: CHART_UP, lineWidth: 2,
                lineStyle: LWC.LineStyle.Solid, axisLabelVisible: true, title: t("advisor.take_profit", "TARGET") });
      });
    }

    // Comfort audit #8, and a disagreement it exposed. The canvas used to draw
    // the raw pivot S/R -- up to seven dotted lines, ranked by STRENGTH -- while
    // the chip row below drew key_levels, the CONFLUENCE zones the product
    // actually ranks and lets you tap. They are not the same list: after a hard
    // drop the pivots sat forty dollars above price while the chips named the
    // level under the current candle, so the chart drew lines nobody could act
    // on and named levels it never drew.
    //
    // key_levels is built FROM the pivots plus the daily and weekly levels, so
    // it is the superset and the one the rest of the product stands behind. The
    // canvas now draws from it: the two that BRACKET price, plus the
    // next-nearest on a wide canvas. Chip and line are finally the same thing,
    // and proximity decides what is drawn because price reaches the level that
    // is close, not the level that is strong. Strength still decides weight.
    if (activeLayers.levels) {
      const px = Number(overlays.price) || Number(dataRef.current.at(-1)?.close) || 0;
      // key_levels is built FROM the pivots, so it is normally the superset.
      // If zone-building failed upstream it arrives empty -- and a chart with
      // no levels at all would be a worse answer than the raw pivots, so they
      // stand in rather than leaving the canvas bare.
      const raw = (overlays.key_levels || []).length
        ? overlays.key_levels
        : [...(overlays.support || []), ...(overlays.resistance || [])];
      const zones = raw.map((z) => ({
        ...z, distance: Math.abs(Number(z.price) - px),
      })).sort((a, b) => a.distance - b.distance);
      const below = zones.filter((z) => Number(z.price) < px);
      const above = zones.filter((z) => Number(z.price) > px);
      const drawn = [below[0], above[0]].filter(Boolean);
      if (wide) {
        const next = [below[1], above[1]].filter(Boolean)
          .sort((a, b) => a.distance - b.distance)[0];
        if (next) drawn.push(next);
      }
      drawn.forEach((zone) => {
        const support = Number(zone.price) < px;
        // Confluence is the strength here: two or more agreeing sources draw
        // the heavy line, a lone source draws the faint one.
        const strong = (zone.confluence || 1) >= 2;
        add({
          price: zone.price,
          color: support
            ? (strong ? "#e0a458" : "#C9A22773")
            : (strong ? "#B9A88F" : "#B9A88F55"),
          lineWidth: strong ? 2 : 1,
          lineStyle: LWC.LineStyle.Dotted,
          axisLabelVisible: true,
          title: `${support ? "S" : "R"}${zone.confluence > 1 ? ` ×${zone.confluence}` : ""}`,
        });
      });
    }

    // A proposal is only drawn when a stop came with it. Drawing an entry and
    // a target without one implies a bounded trade that has no bound.
    const p = activeLayers.proposal ? overlays.proposal : null;

    // Every marker in ONE call: setMarkers replaces the whole set, so the
    // proposal's deciding bar and the user's own trade history must be
    // built together or they erase each other.
    try {
      if (series.setMarkers) {
        const markers = [];
        if (p && p.at) {
          markers.push({
            time: snapToBar(p.at) ?? p.at,
            position: p.side === "buy" ? "belowBar" : "aboveBar",
            color: p.side === "buy" ? CHART_UP : CHART_DOWN,
            shape: p.side === "buy" ? "arrowUp" : "arrowDown",
            text: p.side === "buy" ? "BUY" : "SELL",
          });
        }
        // The journal, on the price that produced it (spec P20): entries as
        // side-coloured arrows, exits as circles carrying the R result.
        // Times floor to the bar boundary or the library drops the marker.
        if (activeLayers.position && trades && trades.length) {
          const bar = (iso) => snapToBar(Math.floor(Date.parse(iso) / 1000));
          trades.forEach((trade) => {
            const openedAt = trade.opened_at && bar(trade.opened_at);
            if (openedAt) {
              markers.push({
                time: openedAt,
                position: trade.side === "buy" ? "belowBar" : "aboveBar",
                color: trade.side === "buy" ? CHART_UP : CHART_DOWN,
                shape: trade.side === "buy" ? "arrowUp" : "arrowDown",
                // No text: the arrow IS the statement. "▲ Entry" repeated
                // down a column of clustered trades read as a rendering bug.
              });
            }
            const closedAt = trade.closed_at && bar(trade.closed_at);
            if (closedAt && trade.r_multiple != null) {
              // A flat close carries no lesson; its label was pure noise
              // ("0.00R" three deep). The dot marks the exit, the label
              // speaks only when the R meant something.
              const flat = Math.abs(trade.r_multiple) < 0.005;
              markers.push({
                time: closedAt,
                position: "aboveBar",
                color: trade.r_multiple >= 0 ? CHART_UP : CHART_DOWN,
                shape: "circle",
                text: flat ? "" : `${trade.r_multiple > 0 ? "+" : ""}${Number(trade.r_multiple).toFixed(2)}R`,
              });
            }
          });
        }
        markers.sort((m1, m2) => m1.time - m2.time);
        series.setMarkers(markers);
      }
    } catch (err) { /* markers are additive */ }

    // Event flags (spec P18) go to the LANE at the pane's bottom edge --
    // design law #6: never text over candles. The owner's phone showed the
    // marker version as a clipped yellow smear across the top of the pane.
    try {
      const byBar = new Map();
      (overlays.calendar_events || []).forEach((event) => {
        const eventBar = snapToBar(event.at);
        if (eventBar == null) return;
        if (!byBar.has(eventBar)) byBar.set(eventBar, []);
        byBar.get(eventBar).push(String(event.title || ""));
      });
      eventsByBarRef.current = byBar;
      eventLaneRef.current?.setEvents([...byBar.keys()].map((time) => ({ time })));
      eventLaneRef.current?.setEnabled(activeLayers.levels !== false);
    } catch (err) { /* the lane is additive */ }
    // The user's own price alerts, ON the chart they were armed from --
    // dashed gold, draggable (the controller slides them, PATCH persists).
    // No axis labels: the axis budget belongs to the price and the levels;
    // the line and its bell say enough.
    alertLinesRef.current = new Map();
    (alerts || []).forEach((alert) => {
      if (alert == null || alert.price == null) return;
      // QUIET lines: several watched levels turned into a lattice of full
      // strength dashes, and the clock glyph rendered as broken letters in
      // the canvas font. Faint, thin, textless -- the chips carry the
      // meaning; the line just marks the spot (and still drags).
      const line = add({ price: alert.price, color: "rgba(201, 154, 58, 0.45)",
                         lineWidth: 1, lineStyle: LWC.LineStyle.SparseDotted,
                         axisLabelVisible: false, title: "" });
      if (line) alertLinesRef.current.set(alert.id, line);
    });

    // "What needs to happen", as a line you can see and watch. Amber says
    // "not met yet" -- a colour the plan grammar reserves for a condition,
    // never for a direction. Only when there is no live setup: with a plan on
    // screen the entry/stop/target already are the geometry.
    if (activeLayers.levels) {
      (overlays.triggers || []).slice(0, 2).forEach((trg) => {
        add({
          price: trg.price,
          color: trg.kind === "invalidation" ? "#B4635A" : "#C99A3A",
          lineWidth: 1,
          lineStyle: LWC.LineStyle.Dashed,
          // Line only, NO axis label: five labels were stacking into an
          // unreadable pile at one price region. The trigger's arithmetic
          // lives in the KEY LEVELS chips right below the canvas -- the
          // axis budget belongs to the price and one S, one R.
          axisLabelVisible: false,
          title: "",
        });
      });
    }

    // Risk and reward as BLOCKS, not just lines: the distance between entry
    // and stop is money, and a filled band says that in a way three parallel
    // lines never do. Drawn under the candles so price stays readable.
    try {
      if (p && p.stop != null && p.entry != null && p.target != null) {
        if (!riskBoxRef.current) {
          riskBoxRef.current = new PriceBox(p.entry, p.stop, "rgba(180, 99, 90, 0.13)");
          rewardBoxRef.current = new PriceBox(p.entry, p.target, "rgba(46, 158, 107, 0.11)");
          series.attachPrimitive(riskBoxRef.current);
          series.attachPrimitive(rewardBoxRef.current);
        } else {
          riskBoxRef.current.set(p.entry, p.stop);
          rewardBoxRef.current.set(p.entry, p.target);
        }
        riskBoxRef.current.setVisible(true);
        rewardBoxRef.current.setVisible(true);
      } else {
        riskBoxRef.current?.setVisible(false);
        rewardBoxRef.current?.setVisible(false);
      }
    } catch (err) { /* the lines below still carry the plan */ }

    if (p && p.stop != null && p.entry != null) {
      // The entry sits at the current price, so an axis label there just
      // duplicates the live price tag and collides with it. The stop and the
      // target are the two the trader needs to read off the axis.
      const nearLive = overlays.price != null
        && Math.abs(p.entry - overlays.price) < Math.abs(p.entry - p.stop) * 0.1;
      [[p.entry, "#A39C90", t("chart.entry_proposed", "Entry"), !nearLive],
       [p.stop, CHART_DOWN, t("chart.stop_proposed", "Stop"), true],
       [p.target, CHART_UP, t("chart.target_proposed", "Target"), true]]
        .forEach(([price, color, title, showAxis]) => {
          if (price == null) return;
          add({ price, color, lineWidth: 1, lineStyle: LWC.LineStyle.Dashed,
                axisLabelVisible: showAxis, title });
        });
    }

    // Open positions are drawn first, above, because committed capital outranks
    // a suggestion for the scarce space on the price axis.
    //
    // seriesEpoch replaces chartType here: a chart-type flip rebuilds the
    // series and bumps the epoch, so this re-runs against the new series
    // instead of decorating one that is about to be removed. status.state
    // re-runs it when the candles land, because snapToBar needs real bars.
  }, [overlays, seriesEpoch, status.state, t, activeLayers, trades, timeframe, alerts]);

  // The live quote keeps the entry label's R current between overlay polls,
  // with the SAME formula the rail card uses -- one number, two places, no
  // disagreement. applyOptions on the one line, not a full overlay rebuild.
  useEffect(() => {
    const held = posLineRef.current;
    const price = Number(livePrice);
    if (!held || !Number.isFinite(price)) return;
    const { line, pos } = held;
    if (pos.entry_price == null || pos.stop_loss == null) return;
    const risk = Math.abs(pos.entry_price - pos.stop_loss) || 1;
    const r = pos.side === "buy"
      ? (price - pos.entry_price) / risk
      : (pos.entry_price - price) / risk;
    try {
      line.applyOptions({
        title: `${(pos.side || "").toUpperCase()} ${r >= 0 ? "+" : ""}${r.toFixed(2)}R`,
      });
    } catch (err) { /* the line went with a rebuilt series */ }
  }, [livePrice]);

  // A price_update arrives roughly every 60s and the header moves with it.
  // Without this the chart stayed on whatever it last loaded, so the two
  // disagreed -- a chart that looks live while showing an older price is worse
  // than one that is visibly stale, because nothing signals the difference.
  useEffect(() => {
    const price = Number(livePrice);
    if (!seriesRef.current || !Number.isFinite(price) || !dataRef.current.length) return;
    // Mid-replay the live edge stays hidden: a forming bar appended at the
    // right would break both the rewound illusion and the cursor's math.
    if (replayRef.current) return;

    const bars = dataRef.current;
    const last = bars[bars.length - 1];
    if (!last) return;

    // Extend the bar that is still forming rather than appending a new one:
    // only the backend knows when a candle actually closes.
    const updated = {
      ...last,
      close: price,
      high: Math.max(last.high, price),
      low: Math.min(last.low, price),
    };
    bars[bars.length - 1] = updated;

    try {
      if (chartType === "line" || chartType === "area") {
        seriesRef.current.update({ time: updated.time, value: updated.close });
      } else if (chartType === "heikin") {
        // The last HA bar depends on the one before it; recomputing the
        // ladder is O(n) over a few thousand bars -- nothing, once a tick.
        const ha = heikinAshi(bars);
        seriesRef.current.update(ha[ha.length - 1]);
      } else {
        seriesRef.current.update(updated);
      }
    } catch (err) {
      // A stale series after a timeframe switch is not worth surfacing.
    }
  }, [livePrice, chartType]);

  const rebuildSeries = useCallback(() => {
    if (!chartRef.current) return;
    if (seriesRef.current) {
      chartRef.current.removeSeries(seriesRef.current);
      // Primitives were attached to THAT series; forget them so the next
      // overlay pass attaches fresh ones instead of mutating orphans.
      riskBoxRef.current = null;
      rewardBoxRef.current = null;
    }

    const ap = chartAppearance();
    const { up, down } = candleColors(ap);
    if (chartType === "line") {
      seriesRef.current = chartRef.current.addLineSeries({ color: "#CBB68A", lineWidth: 2 });
    } else if (chartType === "area") {
      seriesRef.current = chartRef.current.addAreaSeries({
        lineColor: "#CBB68A", lineWidth: 2,
        topColor: "rgba(203,182,138,0.24)", bottomColor: "rgba(203,182,138,0.02)",
      });
    } else if (chartType === "bars") {
      seriesRef.current = chartRef.current.addBarSeries({
        upColor: up, downColor: down, thinBars: false,
      });
    } else if (chartType === "hollow") {
      // Hollow candles the classic way: up bodies are outlines, down bodies
      // stay filled -- the up/down read survives at a glance.
      seriesRef.current = chartRef.current.addCandlestickSeries({
        upColor: "rgba(0,0,0,0)", downColor: down,
        borderVisible: true, borderUpColor: up, borderDownColor: down,
        wickUpColor: up, wickDownColor: down,
      });
    } else {
      // candlestick and heikin share the body style; the DATA differs.
      seriesRef.current = chartRef.current.addCandlestickSeries({
        upColor: up,
        downColor: down,
        borderVisible: false,
        wickUpColor: up,
        wickDownColor: down
      });
    }

    if (dataRef.current.length) {
      applySeriesData(dataRef.current);
    }
    setSeriesEpoch((n) => n + 1);
  }, [applySeriesData, chartType]);
  rebuildSeriesRef.current = rebuildSeries;

  const initChart = useCallback(() => {
    if (!containerRef.current || chartRef.current) return;

    // The LIVE theme tokens, not a hardcoded dark palette: the chart used to
    // paint dark text and dark crosshair labels on a light page until a
    // theme:change event happened to fire, so a light-mode load opened on an
    // unreadable chart.
    const theme = chartTheme();
    chartRef.current = LWC.createChart(containerRef.current, {
      width: containerRef.current.clientWidth,
      height: containerRef.current.clientHeight || 500,
      layout: {
        // Matches the page rather than sitting on its own darker slab, so the
        // chart reads as part of the interface instead of an embedded widget.
        background: { color: "transparent" },
        textColor: theme.text,
        fontFamily: "Inter Tight, system-ui, sans-serif",
        fontSize: 11
      },
      grid: {
        // Grid lines were nearly as bright as the candles. A grid is a
        // reference, not content: it should be felt, not read. Verticals go
        // entirely -- the time axis already answers "when".
        vertLines: { visible: false },
        horzLines: { color: theme.grid }
      },
      crosshair: {
        // Normal, NOT Magnet. Magnet snaps the crosshair to the nearest OHLC,
        // which means the price label reports a number the user did not point
        // at -- they aim at 4,405 between two bars and the label says 4,400.
        // On a product whose whole position is that its numbers can be
        // checked, a label that quietly reports something else is the worst
        // kind of small lie, and it is the number people read a level off
        // before arming an alert.
        //
        // Nothing is lost: the OHLC legend above the chart already gives the
        // hovered bar's exact open, high, low and close. The crosshair says
        // where you are; the legend says what the bar is. Magnet conflated
        // the two.
        mode: LWC.CrosshairMode.Normal,
        vertLine: { color: theme.crosshair, width: 1, style: LWC.LineStyle.Dashed,
                    labelBackgroundColor: theme.label },
        horzLine: { color: theme.crosshair, width: 1, style: LWC.LineStyle.Dashed,
                    labelBackgroundColor: theme.label }
      },
      rightPriceScale: {
        borderVisible: false,
        // Headroom above and below so the proposal and level labels are not
        // pinned against the frame.
        scaleMargins: { top: 0.12, bottom: 0.12 }
      },
      timeScale: {
        borderVisible: false,
        timeVisible: true,
        secondsVisible: false,
        // Law #12: the live candle never touches the scale.
        rightOffset: 8,
        // Digits on a price axis stay Latin in both languages: Arabic-Indic
        // numerals here would not match the numbers everywhere else on screen.
        locale: "en-US"
      },
      localization: { locale: "en-US" },
      // Price-axis drag is what makes a chart feel like a real terminal, and
      // it was explicitly disabled.
      handleScale: { axisPressedMouseMove: { time: true, price: true } },
      // NOT conditioned on the page: the research flagged charts embedded in a
      // scrolling dashboard swallowing vertical swipes, but this app's
      // dashboard has no chart at all -- the chart lives only on /chart, where
      // it IS the page and every gesture should reach it. A guard for a
      // situation that does not exist here would be dead code with a
      // confident comment on it, which is worse than none.
      // On a phone there is no hover: a tap pins the crosshair and the next
      // tap releases it, so the bar readout is reachable at all.
      trackingMode: { exitMode: 1 }
    });

    rebuildSeriesRef.current?.();

    const resizeObserver = new ResizeObserver(() => {
      if (!chartRef.current || !containerRef.current) return;
      chartRef.current.applyOptions({
        width: containerRef.current.clientWidth,
        height: containerRef.current.clientHeight
      });
    });
    resizeObserver.observe(containerRef.current);

    const markInteracted = () => {
      userInteractedRef.current = true;
    };
    containerRef.current.addEventListener("wheel", markInteracted, { passive: true });
    containerRef.current.addEventListener("pointerdown", markInteracted);
    containerRef.current.addEventListener("touchstart", markInteracted, { passive: true });

    // Reading a value off a candle is the base interaction of every chart in
    // this category, and there was none: no crosshair subscription, no
    // tooltip, no legend. The chart displayed prices and could not tell you
    // one. This publishes the hovered bar to a header readout, and falls back
    // to the most recent bar when the pointer leaves, so the row is never
    // empty and never stale.
    chartRef.current.subscribeCrosshairMove((param) => {
      const series = seriesRef.current;
      if (!series) return;
      const bar = param?.seriesData?.get(series);
      if (bar && (bar.close != null || bar.value != null)) {
        // Overlay studies answer at the same bar: an EMA drawn without a
        // readable value is a colored guess. Only the named lines report;
        // bands and volume stay visual.
        const studyValues = {};
        ["ema20", "ema50", "vwap"].forEach((id) => {
          const line = studySeriesRef.current[id];
          const point = line && param?.seriesData?.get(line);
          if (point && point.value != null) studyValues[id] = point.value;
        });
        setHoveredBar({
          time: param.time,
          open: bar.open ?? bar.value,
          high: bar.high ?? bar.value,
          low: bar.low ?? bar.value,
          close: bar.close ?? bar.value,
          studies: studyValues,
        });
      } else {
        setHoveredBar(null);
      }
    });

    chartRef.current.timeScale().subscribeVisibleLogicalRangeChange((logicalRange) => {
      if (!logicalRange || loadingRef.current || !seriesRef.current) return;
      if (!initialLoadRef.current || !userInteractedRef.current) return;
      const bars = seriesRef.current.barsInLogicalRange(logicalRange);
      if (bars && bars.barsBefore < 50) {
        const range = rangeRef.current;
        if (!range.from || !range.to) return;
        const totalDays = Math.ceil((range.to - range.from) / (1000 * 60 * 60 * 24));
        if (totalDays < MAX_HISTORY_DAYS) {
          loadChartRef.current?.(true);
        }
      }
    });

    return () => {
      resizeObserver.disconnect();
      if (containerRef.current) {
        containerRef.current.removeEventListener("wheel", markInteracted);
        containerRef.current.removeEventListener("pointerdown", markInteracted);
        containerRef.current.removeEventListener("touchstart", markInteracted);
      }
      // Destroy the instance LAST (the listener removal above still needs the
      // container). Without this the canvas leaks per mount the moment the
      // chart renders conditionally or a client router lands.
      try { chartRef.current?.remove(); } catch { /* already disposed */ }
      chartRef.current = null;
      seriesRef.current = null;
      studySeriesRef.current = {};
      // The primitives belonged to the destroyed series.
      riskBoxRef.current = null;
      rewardBoxRef.current = null;
    };
    // Deliberately NO deps: the chart instance is created once and lives for
    // the mount. It used to depend on rebuildSeries -> chartType, so flipping
    // candles/line destroyed the chart and rebuilt it -- losing the zoom, the
    // pan position and (once they exist) every drawing and primitive on it.
  }, []);

  // Chart-vision: while this chart is mounted, the assistant can be handed a
  // picture of it. The owner asked the chat "fik tshouf l chart?" and it had
  // to say no -- on every platform he compares against, the AI can look.
  // JPEG at ~1000px keeps the payload well under the request cap.
  useEffect(() => {
    window.__mithqalChartShot = () => {
      const chart = chartRef.current;
      if (!chart || typeof chart.takeScreenshot !== "function") return null;
      const shot = chart.takeScreenshot();
      if (!shot || !shot.width) return null;
      const scale = Math.min(1, 1000 / shot.width);
      const out = document.createElement("canvas");
      out.width = Math.round(shot.width * scale);
      out.height = Math.round(shot.height * scale);
      const ctx = out.getContext("2d");
      if (!ctx) return null;
      ctx.drawImage(shot, 0, 0, out.width, out.height);
      return out.toDataURL("image/jpeg", 0.85);
    };
    return () => { delete window.__mithqalChartShot; };
  }, []);

  const loadChart = useCallback(
    async (appendMode = false) => {
      if (!timeframe) return;
      // Monotonic guard: tap 5m then 1h fast enough and the 5m response can
      // resolve LAST, painting the wrong frame's candles under a header that
      // says 1h. Whatever finishes out of turn is discarded.
      const seq = ++loadSeqRef.current;
      loadingRef.current = true;

      const loadingMessage = formatTemplate(
        t("chart.loading_history", "Loading {days} days of chart data..."),
        { days: HISTORY_DAYS }
      );
      const errorMessage = t("chart.error_loading", "Unable to load chart data");
      const noDataMessage = t("chart.no_data", "No data available for this timeframe");
      let fetchFrom;
      let fetchTo;

      if (appendMode && rangeRef.current.from) {
        fetchTo = new Date(rangeRef.current.from);
        fetchFrom = new Date(fetchTo.getTime() - INCREMENT_DAYS * 24 * 60 * 60 * 1000);
      } else {
        fetchTo = new Date();
        fetchFrom = new Date(fetchTo.getTime() - HISTORY_DAYS * 24 * 60 * 60 * 1000);
        setStatus({ state: "loading", message: loadingMessage });
      }

      const fromParam = encodeURIComponent(fetchFrom.toISOString());
      const toParam = encodeURIComponent(fetchTo.toISOString());
      // Bare OHLC arrays in epoch seconds. The old /api/data served a full
      // Plotly figure as a JSON string -- multi-MB on minute frames -- of
      // which this code kept five columns and threw the rest away.
      const endpoint = `/api/candles/${timeframe}?from=${fromParam}&to=${toParam}&t=${Date.now()}`;

      try {
        const response = await authenticatedFetch(endpoint);
        if (seq !== loadSeqRef.current) return;
        if (!response.ok) {
          if (!appendMode) {
            setStatus({ state: "error", message: errorMessage });
          }
          return;
        }
        const data = await response.json();
        if (seq !== loadSeqRef.current) return;
        const stamps = Array.isArray(data?.t) ? data.t : [];
        if (!data?.success || stamps.length === 0) {
          if (!appendMode) {
            setStatus({ state: "error", message: noDataMessage });
          }
          return;
        }

        const newData = stamps
          .map((sec, index) => {
            const open = Number(data.o?.[index]);
            const high = Number(data.h?.[index]);
            const low = Number(data.l?.[index]);
            const close = Number(data.c?.[index]);
            if (![sec, open, high, low, close].every((value) => Number.isFinite(value))) {
              return null;
            }
            const volume = Number(data.v?.[index]);
            return { time: Math.floor(sec), open, high, low, close,
                     volume: Number.isFinite(volume) ? volume : 0 };
          })
          .filter(Boolean);

        if (newData.length === 0) {
          if (!appendMode) {
            setStatus({ state: "error", message: noDataMessage });
          }
          return;
        }

        // Mid-replay a scroll-back prepend would silently re-aim the
        // cursor's index at different bars: freeze history growth.
        if (appendMode && replayRef.current) return;
        let mergedData = [];
        if (appendMode && dataRef.current.length) {
          const combined = [...newData, ...dataRef.current];
          const uniqueMap = new Map();
          combined.forEach((candle) => {
            if (!uniqueMap.has(candle.time)) {
              uniqueMap.set(candle.time, candle);
            }
          });
          mergedData = Array.from(uniqueMap.values()).sort((a, b) => a.time - b.time);
        } else {
          mergedData = newData;
        }

        dataRef.current = mergedData;
        if (mergedData.length) {
          rangeRef.current = {
            from: new Date(mergedData[0].time * 1000),
            to: new Date(mergedData[mergedData.length - 1].time * 1000)
          };
        }

        applySeriesData(mergedData);
        // The volume histogram is built from these bars: rebuild it now,
        // not on the next toggle.
        drawStudiesRef.current?.();

        if (!appendMode && chartRef.current) {
          // A journal row's "view on chart" arrives as /chart?tf=..&at=<epoch>:
          // land ON that trade with breathing room either side, once, instead
          // of at the live edge the reader would then scroll away from.
          const wantedAt = (() => {
            try {
              const raw = new URLSearchParams(window.location.search).get("at");
              const secs = raw ? parseInt(raw, 10) : null;
              return Number.isFinite(secs) ? secs : null;
            } catch (e) { return null; }
          })();
          // Consumed exactly ONCE for the whole mount: a timeframe switch
          // resets initialLoadRef (a new series needs its scroll-to-now),
          // but re-consuming ?at= on every switch yanked the reader back to
          // the historical trade each time they changed frames.
          const firstBar = mergedData[0]?.time;
          if (wantedAt && !atConsumedRef.current
              && (firstBar == null || wantedAt >= firstBar)) {
            atConsumedRef.current = true;
            const tfSec = TIMEFRAME_SECONDS[timeframe] || 3600;
            try {
              chartRef.current.timeScale().setVisibleRange({
                from: wantedAt - 45 * tfSec, to: wantedAt + 45 * tfSec,
              });
            } catch (e) { chartRef.current.timeScale().scrollToRealTime(); }
          } else {
            // An ?at= older than the loaded window lands at the live edge
            // honestly instead of framing thirty days of nothing.
            chartRef.current.timeScale().scrollToRealTime();
          }
          initialLoadRef.current = true;
        }

        if (!appendMode) {
          setStatus({ state: "ready", message: "" });
        }
      } catch (error) {
        if (!appendMode) {
          setStatus({ state: "error", message: errorMessage });
        }
      } finally {
        loadingRef.current = false;
      }
    },
    [applySeriesData, timeframe, t]
  );
  loadChartRef.current = loadChart;

  // The charting engine now arrives as its own chunk, so the chart is created
  // once it has landed rather than on the first commit. Nothing else needs to
  // change: every effect that touches the chart already returns early while
  // chartRef is null, and initChart calls rebuildSeries when it runs, which
  // re-applies whatever candles loadChart has buffered in dataRef meanwhile --
  // the same path a chart-type switch already uses.
  const [chartingReady, setChartingReady] = useState(() => LWC !== null);
  useEffect(() => {
    if (chartingReady) return undefined;
    let alive = true;
    loadCharting()
      .then(() => { if (alive) setChartingReady(true); })
      .catch((err) => {
        console.error("charting engine failed to load", err);
        if (alive) setStatus({ state: "error", message: t("chart.load_failed", "Chart failed to load") });
      });
    return () => { alive = false; };
  }, [chartingReady, t]);

  // Return the cleanup so React actually runs it -- it was being discarded,
  // which left the chart instance alive forever.
  useEffect(() => {
    if (!chartingReady) return undefined;
    return initChart();
  }, [initChart, chartingReady]);

  useEffect(() => {
    const scale = chartRef.current?.timeScale();
    if (scale) defaultSpacingRef.current = scale.options().barSpacing || 6;
  }, [status.state]);

  useEffect(() => {
    rebuildSeries();
  }, [rebuildSeries]);

  // Attach the drawing layer to whichever series currently exists, and build
  // the pointer controller once the chart element is there. The layer is
  // re-attached on every series rebuild because a candles/line switch removes
  // the series the primitive was on.
  useEffect(() => {
    const chart = chartRef.current;
    const series = seriesRef.current;
    if (!chart || !series) return undefined;
    if (!drawingLayerRef.current) drawingLayerRef.current = new DrawingLayer();
    const layer = drawingLayerRef.current;
    series.attachPrimitive(layer);
    layer.set(showDrawingsRef.current ? drawingsRef.current : []);

    // The trading day's chapters, behind everything. Same UTC windows as
    // the server's sessions module -- painted here, decided there. Attached
    // HERE because it is detached in THIS effect's cleanup: an attach
    // without its detach stacked a fresh copy of the bands on every re-run,
    // and the shading tripled in one session, darkening toward opaque.
    if (!sessionBandsRef.current) sessionBandsRef.current = new SessionBands();
    const bands = sessionBandsRef.current;
    series.attachPrimitive(bands);
    bands.setEnabled(showSessions !== false);

    // The event lane: flags live at the pane's bottom edge, never on the
    // candles. Same attach/detach discipline as the bands.
    if (!eventLaneRef.current) eventLaneRef.current = new EventLane();
    const lane = eventLaneRef.current;
    series.attachPrimitive(lane);

    // Volume Profile: same attach/detach discipline. It reads the series
    // for price->pixel and the bars fed to it on every repaint.
    if (!volumeProfileRef.current) volumeProfileRef.current = new VolumeProfile();
    const profile = volumeProfileRef.current;
    series.attachPrimitive(profile);
    profile.setSeries(series);

    const controller = new DrawingController({
      chart,
      series,
      layer,
      timeframe,
      element: chart.chartElement(),
      // The magnet locks onto the engine's own zones, so a snapped line is
      // PROVABLY on a computed level rather than near one -- which is what
      // lets the assistant state it as a fact later.
      snapTargets: () => (overlaysRef.current?.key_levels || []).map((z) => ({
        price: z.price,
        label: (z.sources || []).map((x) => x.source).join(" + "),
      })),
      // MT5's order-line drag: alerts AND the open position's stop share
      // the same grab-slide-release lifecycle; ids tell them apart.
      alertTargets: () => {
        const targets = [...alertsRef.current];
        if (posStopRef.current) {
          targets.push({ id: `posstop:${posStopRef.current.tradeId}`,
                         price: posStopRef.current.price });
        }
        return targets;
      },
      onAlertDrag: (id, phase, price) => {
        if (id.startsWith("posstop:")) {
          const held = posStopRef.current;
          if (!held) return;
          if (phase === "move" || phase === "cancel") {
            try { held.line.applyOptions({ price }); } catch (e) { /* rebuilt */ }
          } else if (phase === "done") {
            onDragStopRef.current?.(held.tradeId, Math.round(price * 100) / 100);
          }
          return;
        }
        const line = alertLinesRef.current.get(id);
        if (phase === "move" && line) {
          try { line.applyOptions({ price }); } catch (e) { /* line rebuilt */ }
        } else if (phase === "cancel" && line) {
          try { line.applyOptions({ price }); } catch (e) { /* back where it was */ }
        } else if (phase === "done") {
          onMoveAlertRef.current?.(id, Math.round(price * 100) / 100);
        }
      },
      onSelect: (drawing) => setSelectedDrawing(drawing || null),
      onChange: (drawing, phase) => {
        // The measure tool is a QUESTION, not a drawing: two taps, an
        // answer chip (Δ$ · Δ% · bars · duration), and nothing persisted --
        // a measurement worth keeping is what a trendline is for.
        if (drawing.kind === "measure") {
          if (phase === "done") {
            const [a, b] = drawing.anchors || [];
            if (a && b && a.p != null && b.p != null) {
              const dollars = b.p - a.p;
              const pct = a.p ? (dollars / a.p) * 100 : null;
              const secs = (a.t != null && b.t != null) ? Math.abs(b.t - a.t) : null;
              // ACTUAL bars between the anchors, counted from the loaded
              // series -- wall-clock division reported ~48 phantom bars
              // across a weekend no bar was ever formed in.
              let barCount = null;
              if (a.t != null && b.t != null) {
                const lo = Math.min(a.t, b.t), hi = Math.max(a.t, b.t);
                const n = dataRef.current.reduce(
                  (acc, candle) => acc + (candle.time >= lo && candle.time <= hi ? 1 : 0), 0);
                barCount = n > 0 ? n - 1 : 0;
              }
              setMeasureResult({ dollars, pct, bars: barCount, secs });
            }
            drawingsRef.current = drawingsRef.current.filter((d) => d.id !== drawing.id);
            layer.set(showDrawingsRef.current ? drawingsRef.current : []);
            setDrawings([...drawingsRef.current]);
            setArmedTool(null);
            // The measure never reached the server, so the selection the
            // controller reported on the finishing tap points at a drawing
            // that does not exist -- clearing it removes a live trash button
            // whose only possible outcome was a 404.
            layer.select(null);
            setSelectedDrawing(null);
            return;
          }
          if (phase === "cancelled") {
            drawingsRef.current = drawingsRef.current.filter((d) => d.id !== drawing.id);
          } else if (!drawingsRef.current.some((d) => d.id === drawing.id)) {
            drawingsRef.current = [...drawingsRef.current, drawing];
          }
          layer.set(showDrawingsRef.current ? drawingsRef.current : []);
          setDrawings([...drawingsRef.current]);
          return;
        }
        if (phase === "cancelled") {
          drawingsRef.current = drawingsRef.current.filter((d) => d.id !== drawing.id);
        } else if (!drawingsRef.current.some((d) => d.id === drawing.id)) {
          drawingsRef.current = [...drawingsRef.current, drawing];
          if (phase === "created") pendingCreatesRef.current.add(drawing.id);
        }
        layer.set(showDrawingsRef.current ? drawingsRef.current : []);
        setDrawings([...drawingsRef.current]);
        if (phase === "done") {
          // History: a first 'done' after 'created' is a CREATE; any later
          // 'done' is a MOVE, recorded with both sides of the journey.
          const after = JSON.parse(JSON.stringify(drawing.anchors || []));
          if (pendingCreatesRef.current.has(drawing.id)
              || !lastAnchorsRef.current.has(drawing.id)) {
            pendingCreatesRef.current.delete(drawing.id);
            pushHistory({ type: "create",
                          drawing: JSON.parse(JSON.stringify(drawing)) });
          } else {
            const before = lastAnchorsRef.current.get(drawing.id);
            if (JSON.stringify(before) !== JSON.stringify(after)) {
              pushHistory({ type: "move", id: drawing.id, before, after });
            }
          }
          lastAnchorsRef.current.set(drawing.id, after);
          setArmedTool(null);
          persist(drawing);
        }
      },
      onLongPress: (drawing) => {
        layer.select(drawing.id);
        setSelectedDrawing(drawing);
        // The payoff of drawing on THIS chart rather than any other: the app
        // can say what the line actually sits on. Fetched on demand, because
        // it needs the bar history and the current level map.
        setMeasuring(drawing.id);
      },
    });
    drawingControllerRef.current = controller;
    // A rebuild while a tool was armed left React saying "armed" and a fresh
    // controller that had never heard of it -- the toolbar glowed gold and
    // taps panned the chart. The new controller inherits the arming.
    if (armedToolRef.current) controller.arm(armedToolRef.current);

    return () => {
      controller.destroy();
      drawingControllerRef.current = null;
      try { series.detachPrimitive(layer); } catch (error) { /* series already gone */ }
      try { series.detachPrimitive(bands); } catch (error) { /* series already gone */ }
      try { series.detachPrimitive(lane); } catch (error) { /* series already gone */ }
      try { series.detachPrimitive(profile); } catch (error) { /* series already gone */ }
    };
    // seriesEpoch, not chartType/status: the epoch bumps exactly when the
    // series is rebuilt, which is the one moment primitives must re-attach.
  }, [seriesEpoch, persist, timeframe]);

  const armedToolRef = useRef(null);
  useEffect(() => {
    armedToolRef.current = armedTool;
    drawingControllerRef.current?.arm(armedTool);
  }, [armedTool]);

  // Keyboard: Escape abandons an armed tool, a half-placed shape and the
  // measure chip; Delete removes the selected drawing. Mouse users had no
  // path out of an armed tool except knowing to re-tap the tool chip.
  useEffect(() => {
    const onKey = (event) => {
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (event.key === "Escape") {
        setArmedTool(null);
        setMeasureResult(null);
        drawingLayerRef.current?.select(null);
        setSelectedDrawing(null);
      } else if ((event.key === "Delete" || event.key === "Backspace")
                 && selectedDrawing) {
        event.preventDefault();
        removeDrawing(selectedDrawing.id);
      } else if ((event.ctrlKey || event.metaKey)
                 && event.key.toLowerCase() === "z") {
        event.preventDefault();
        if (event.shiftKey) redo(); else undo();
      } else if ((event.ctrlKey || event.metaKey)
                 && event.key.toLowerCase() === "y") {
        event.preventDefault();
        redo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedDrawing, removeDrawing, undo, redo]);

  useEffect(() => { overlaysRef.current = overlays; }, [overlays]);

  // barSpacing is the direct control -- it is what a pinch actually changes,
  // and it keeps the right edge fixed for free.
  //
  // The first attempt computed a new logical range instead, and measured
  // BACKWARDS: the visible range extends past the last bar into the right
  // margin, so shrinking it landed inside that empty space and the library
  // clamped the result into a wider view. Zoom-in made candles narrower.
  // Verified by measuring the median candle width in pixels.
  const zoomBy = useCallback((factor) => {
    const chart = chartRef.current;
    if (!chart) return;
    const scale = chart.timeScale();
    const current = scale.options().barSpacing || 6;
    scale.applyOptions({ barSpacing: Math.max(1.5, Math.min(current * factor, 80)) });
  }, []);

  const resetView = useCallback(() => {
    const scale = chartRef.current?.timeScale();
    if (!scale) return;
    // resetTimeScale() resets to the CURRENT options, and applyOptions has
    // been changing barSpacing -- so after a few zoom-outs "back to now"
    // returned to the last bar still zoomed out, which is not a reset at all.
    // Restore the spacing the chart was built with, explicitly.
    scale.applyOptions({ barSpacing: defaultSpacingRef.current });
    scale.resetTimeScale();
    scale.scrollToRealTime();
  }, []);

  // What the engine can honestly say about a line the user drew: which
  // computed level it sits on, how many bars actually touched it, how far away
  // in ATR. Facts only -- the product has no measured basis for calling a
  // drawing good, and inventing one is exactly what it refuses to do.
  useEffect(() => {
    if (!measuring) { setMeasurement(null); setCritique(null); return undefined; }
    let cancelled = false;
    setCritique(null);
    authenticatedFetch(`/api/drawings/${measuring}/measure?timeframe=${timeframe}`)
      .then((r) => r.json())
      .then((p) => { if (!cancelled && p.success) setMeasurement(p.measurement); })
      .catch(() => { if (!cancelled) setMeasurement({ failed: true }); });
    return () => { cancelled = true; };
  }, [measuring, timeframe]);

  // One tap: the engine reads the user's drawing (via its measured facts --
  // the assistant only ever reads a drawing it can describe in numbers) and
  // answers with the same cited, checkable claims the chat uses.
  const [critique, setCritique] = useState(null);
  const critiqueDrawing = useCallback(async () => {
    if (!measuring) return;
    setCritique({ loading: true });
    try {
      const response = await authenticatedFetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: "What do you think of my drawing?",
                               timeframe, drawing_id: measuring }),
      });
      const payload = await response.json();
      if (!payload.success) throw new Error(payload.error || "failed");
      setCritique(payload);
    } catch (error) {
      setCritique({ failed: true });
    }
  }, [measuring, timeframe]);

  const [sharing, setSharing] = useState(false);
  const [shareNote, setShareNote] = useState(null);
  const shareAnalysis = useCallback(async () => {
    if (!chartRef.current) return;
    setSharing(true);
    setShareNote(null);
    try {
      const blob = await composeShareImage({
        chart: chartRef.current,
        symbol: "XAUUSD",
        timeframe,
        price: dataRef.current.length
          ? dataRef.current[dataRef.current.length - 1].close
          : null,
        drawings: drawingsRef.current,
        zones: overlaysRef.current?.key_levels || [],
        labels: {
          takenAt: new Date().toLocaleString(appDateLocale()),
          madeWith: "Mithqal",
        },
      });
      const result = await shareImage(
        blob, `xauusd-${timeframe}.png`,
        t("draw.share_text", "My analysis on XAUUSD"));
      setShareNote(result.ok
        ? (result.how === "downloaded" ? "saved" : null)
        : "failed");
    } catch (error) {
      setShareNote("failed");
    }
    setSharing(false);
  }, [timeframe, t]);

  // The user's lines for this frame. A daily level stays on screen when they
  // drop to 5 minutes; a 5-minute line does not climb to the daily.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await authenticatedFetch(`/api/drawings?timeframe=${timeframe}`);
        const payload = await response.json();
        if (cancelled || !payload.success) return;
        drawingsRef.current = payload.drawings || [];
        setDrawings(payload.drawings || []);
        drawingLayerRef.current?.set(showDrawingsRef.current ? drawingsRef.current : []);
        // Seed the move-history baseline for everything already saved.
        (payload.drawings || []).forEach((d) => {
          lastAnchorsRef.current.set(d.id, JSON.parse(JSON.stringify(d.anchors || [])));
        });
      } catch (error) { /* an empty chart is honest; it retries on the next frame change */ }
    })();
    return () => { cancelled = true; };
  }, [timeframe]);

  const drawStudies = useCallback(() => {
    const chart = chartRef.current;
    const data = studyDataRef.current;
    if (!chart) return;

    // Remove first: a chart-type switch tears down and rebuilds the series,
    // which silently invalidates every handle we hold.
    Object.values(studySeriesRef.current).forEach((series) => {
      try { chart.removeSeries(series); } catch (err) {}
    });
    studySeriesRef.current = {};
    if (!data || !studies) return;

    const add = (id, points, color, width) => {
      if (!points || !points.length) return;
      const line = chart.addLineSeries({
        color, lineWidth: width, priceLineVisible: false,
        lastValueVisible: false, crosshairMarkerVisible: false,
      });
      line.setData(points);
      studySeriesRef.current[id] = line;
    };

    if (studies.ema20) add("ema20", data.ema20, "#CBB68A", 1);
    if (studies.ema50) add("ema50", data.ema50, "#B9A88F", 1);
    if (studies.vwap) add("vwap", data.vwap, "#E2C275", 1);
    if (studies.bb) {
      add("bbu", data.bb_upper, "rgba(201,162,39,0.45)", 1);
      add("bbm", data.bb_middle, "rgba(201,162,39,0.24)", 1);
      add("bbl", data.bb_lower, "rgba(201,162,39,0.45)", 1);
    }

    // Volume, the TradingView way: a muted histogram on its own overlay
    // scale in the bottom fifth. It is DATA (from the candles feed), not a
    // computed study -- direction-tinted at low alpha so it reads without
    // shouting (law #2: desaturated direction colours only).
    if (studies.vol && dataRef.current.length && dataRef.current.some((b) => b.volume)) {
      try {
        const hist = chart.addHistogramSeries({
          priceScaleId: "vol",
          priceFormat: { type: "volume" },
          priceLineVisible: false,
          lastValueVisible: false,
        });
        hist.priceScale().applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
        hist.setData(dataRef.current.map((bar) => ({
          time: bar.time,
          value: bar.volume || 0,
          color: bar.close >= bar.open
            ? "rgba(94, 158, 122, 0.35)" : "rgba(180, 99, 90, 0.35)",
        })));
        studySeriesRef.current.vol = hist;
      } catch (err) { /* volume is additive */ }
    }
  }, [studies]);
  drawStudiesRef.current = drawStudies;

  useEffect(() => {
    // A new timeframe is a new series: drop the accumulators BEFORE loading,
    // or the append path merges the old resolution's bars into the new one by
    // timestamp. The interaction flag resets too, so a fresh series does not
    // auto-append from a scroll position that belonged to the previous one.
    dataRef.current = [];
    rangeRef.current = { from: null, to: null };
    initialLoadRef.current = false;
    userInteractedRef.current = false;
    // Everything derived from the OLD frame goes with it, synchronously:
    // the previous frame's S/R prices painted on the new frame's candles --
    // even for a second -- is the chart stating something false.
    setOverlays(null);
    setMeasureResult(null);
    studyDataRef.current = null;
    drawStudiesRef.current?.();
    Object.values(panesRef.current).forEach((pane) => {
      try { pane.series.setData([]); } catch (e) { /* pane torn down */ }
    });
    loadChartRef.current?.(false);
    // TIMEFRAME ONLY. This is the frame-change reset -- it wipes the bars, the
    // range, the overlays and every pane, then refetches from scratch. It was
    // also keyed on loadChart and drawStudies, and drawStudies changes
    // identity whenever `studies` does, so switching ON AN INDICATOR ran the
    // whole reset: the reader lost the history they had scrolled back to, the
    // key levels went to three empty ghost boxes for the 30 seconds until the
    // next poll, and /api/candles was fetched twice. You cannot add an EMA to
    // a chart you have scrolled to -- which is most of what studying a past
    // setup is. Both functions are read through refs so they stay current
    // without making the frame reset depend on them.
  }, [timeframe]);

  useEffect(() => {
    const repaint = () => {
      const c = chartRef.current; if (!c) return;
      const th = chartTheme();
      const ap = chartAppearance();
      try {
        c.applyOptions({
          layout: { background: { color: ap.bg || th.surface }, textColor: th.text },
          // The grid is opt-out, the watermark opt-in -- both remembered.
          grid: { horzLines: { color: th.grid, visible: ap.grid !== false } },
          watermark: { visible: !!ap.watermark, text: "XAUUSD",
                       fontSize: 48, horzAlign: "center", vertAlign: "center",
                       color: "rgba(128,128,128,0.12)" },
          crosshair: { vertLine: { labelBackgroundColor: th.label },
                       horzLine: { labelBackgroundColor: th.label } },
        });
      } catch (e) {}
      // Live recolour of the candles themselves -- the reader picked a
      // palette and should not need a reload to see it. Each series style
      // wears the palette on the options it actually renders with.
      try {
        const { up, down } = candleColors(ap);
        const series = seriesRef.current;
        if (series && (chartType === "candlestick" || chartType === "heikin")) {
          series.applyOptions({ upColor: up, downColor: down,
                                wickUpColor: up, wickDownColor: down });
        } else if (series && chartType === "bars") {
          series.applyOptions({ upColor: up, downColor: down });
        } else if (series && chartType === "hollow") {
          series.applyOptions({ downColor: down, borderUpColor: up,
                                borderDownColor: down, wickUpColor: up,
                                wickDownColor: down });
        }
      } catch (e) {}
      // The study panes are charts too: a theme flip that repainted only the
      // main canvas left them wearing the old theme's text and grid.
      Object.values(panesRef.current).forEach((pane) => {
        try {
          pane.chart.applyOptions({
            layout: { background: { color: "transparent" }, textColor: th.text },
            grid: { horzLines: { color: th.grid } },
          });
        } catch (e) { /* pane torn down mid-flip */ }
      });
    };
    repaint();
    window.addEventListener("theme:change", repaint);
    window.addEventListener("sv:chart-appearance", repaint);
    return () => {
      window.removeEventListener("theme:change", repaint);
      window.removeEventListener("sv:chart-appearance", repaint);
    };
  }, [chartType]);

  useEffect(() => { drawStudies(); }, [drawStudies, chartType]);
  useEffect(() => {
    volumeProfileRef.current?.setEnabled(!!(studies && studies.vp));
  }, [studies?.vp]);

  // Study series, from the server. Refetched per timeframe AND on a timer:
  // fetched once, the EMA visibly froze N bars behind the live candle after
  // an evening on screen while the levels beside it refreshed every 30s --
  // the chart disagreeing with itself.
  useEffect(() => {
    let alive = true;
    let retry = null;
    const load = async () => {
      try {
        const response = await authenticatedFetch(`/api/indicator-series/${timeframe}`);
        const payload = await response.json();
        if (!alive) return;
        if (payload.success) {
          studyDataRef.current = payload.series;
        } else {
          // A cold cache 404s on a first-visited timeframe. Keeping the
          // PREVIOUS frame's series painted RSI computed on 1h bars under a
          // 30m chart for up to a minute -- wrong data wearing the right
          // label. Blank panes are honest; and the server warms within
          // seconds, so retry sooner than the slow poll.
          studyDataRef.current = {};
          retry = setTimeout(load, 5000);
        }
        drawStudies();
        setPaneTick((n) => n + 1);
      } catch (error) { /* studies are additive; the chart still draws */ }
    };
    load();
    const timer = setInterval(load, 60000);
    return () => { alive = false; clearInterval(timer); clearTimeout(retry); };
  }, [timeframe, drawStudies]);

  // Sub-panes (spec P5): RSI/ADX under the price, each its own small chart.
  // v4 has no native panes, so a pane is a second chart whose visible TIME
  // range mirrors the main's in both directions -- time, not logical index,
  // because the study series starts after indicator warmup and logical
  // indices would misalign the frames.
  const paneHostRef = useRef({});
  const panesRef = useRef({});
  const [paneTick, setPaneTick] = useState(0);
  useEffect(() => {
    const main = chartRef.current;
    if (!main) return undefined;
    const data = studyDataRef.current || {};
    const wanted = Object.keys(PANE_DEFS).filter((k) => studies && studies[k]);

    Object.entries(panesRef.current).forEach(([key, pane]) => {
      if (!wanted.includes(key)) {
        try { pane.unsub(); pane.chart.remove(); } catch (e) { /* torn down */ }
        delete panesRef.current[key];
      }
    });

    wanted.forEach((key) => {
      const host = paneHostRef.current[key];
      const points = data[key];
      if (!host || !points || !points.length) return;
      if (panesRef.current[key]) {
        try { panesRef.current[key].series.setData(points); } catch (e) { /* rebuilt next tick */ }
        return;
      }
      const th = chartTheme();
      let scaleWidth = 72;
      try { scaleWidth = main.priceScale("right").width() || 72; } catch (e) { /* default */ }
      const chart = LWC.createChart(host, {
        // The HOST decides the height (CSS shrinks panes on short screens
        // so the level row below them never leaves the page).
        width: host.clientWidth, height: host.clientHeight || 110,
        layout: { background: { color: "transparent" }, textColor: th.text,
                  fontSize: 10, fontFamily: "Inter Tight, system-ui, sans-serif" },
        grid: { vertLines: { visible: false }, horzLines: { color: th.grid } },
        // Match the main chart's axis gutter, or the two time scales shear.
        rightPriceScale: { minimumWidth: scaleWidth },
        timeScale: { visible: false },
        handleScroll: true, handleScale: true,
      });
      const series = chart.addLineSeries({
        color: PANE_DEFS[key].color, lineWidth: 1,
        priceLineVisible: false, lastValueVisible: true,
        crosshairMarkerVisible: false,
      });
      series.setData(points);
      PANE_DEFS[key].guides.forEach((g) => series.createPriceLine({
        price: g, color: th.grid, lineWidth: 1,
        lineStyle: LWC.LineStyle.Dashed, axisLabelVisible: false,
      }));
      let syncing = false;
      const mainTs = main.timeScale();
      const paneTs = chart.timeScale();
      const fromMain = (range) => {
        if (syncing || !range) return;
        syncing = true;
        try { paneTs.setVisibleRange(range); } catch (e) { /* pane shorter */ }
        syncing = false;
      };
      const fromPane = (range) => {
        if (syncing || !range) return;
        syncing = true;
        try { mainTs.setVisibleRange(range); } catch (e) { /* main shorter */ }
        syncing = false;
      };
      mainTs.subscribeVisibleTimeRangeChange(fromMain);
      paneTs.subscribeVisibleTimeRangeChange(fromPane);
      try {
        const range = mainTs.getVisibleRange();
        if (range) paneTs.setVisibleRange(range);
      } catch (e) { /* syncs on first move */ }
      // The pane was created at one width and never told about another:
      // rotate the phone and the two time axes sheared apart.
      const paneResize = new ResizeObserver(() => {
        try {
          chart.applyOptions({ width: host.clientWidth,
                               height: host.clientHeight || 110 });
        } catch (e) {}
      });
      paneResize.observe(host);
      panesRef.current[key] = {
        chart, series,
        unsub: () => {
          try {
            paneResize.disconnect();
            mainTs.unsubscribeVisibleTimeRangeChange(fromMain);
            paneTs.unsubscribeVisibleTimeRangeChange(fromPane);
          } catch (e) { /* already detached */ }
        },
      };
    });

    return undefined;
  }, [studies, timeframe, paneTick]);

  // Full pane teardown when the chart itself unmounts.
  useEffect(() => () => {
    Object.values(panesRef.current).forEach((pane) => {
      try { pane.unsub(); pane.chart.remove(); } catch (e) { /* gone */ }
    });
    panesRef.current = {};
  }, []);


  // Log scale, and a fit-to-content control the toolbar dispatches to.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    try {
      chart.priceScale("right").applyOptions({ mode: logScale ? 1 : 0 });
    } catch (err) {}
  }, [logScale]);

  useEffect(() => {
    const fit = () => { try { chartRef.current?.timeScale().fitContent(); } catch (e) {} };
    window.addEventListener("chart:fit", fit);
    return () => window.removeEventListener("chart:fit", fit);
  }, []);

  // "Back to now" and zoom moved into the header anchors -- fixed chrome
  // dispatches, the canvas obeys. Nothing floats over candles, anywhere.
  useEffect(() => {
    const onReset = () => resetView();
    const onZoom = (event) => zoomBy(Number(event.detail) || 1);
    window.addEventListener("chart:reset", onReset);
    window.addEventListener("chart:zoom", onZoom);
    return () => {
      window.removeEventListener("chart:reset", onReset);
      window.removeEventListener("chart:zoom", onZoom);
    };
  }, [resetView, zoomBy]);

  // Go-to-date (parity: every terminal has it). History is APPENDED until
  // the date is actually loaded -- bounded by the same MAX_HISTORY_DAYS the
  // scroll path respects -- then the view frames it. If the archive simply
  // does not reach that far, the view lands at the oldest truth we have.
  useEffect(() => {
    const onGoto = async (event) => {
      const target = Number(event.detail);
      if (!Number.isFinite(target)) return;
      let guard = 0;
      while (dataRef.current.length && dataRef.current[0].time > target && guard < 14) {
        const oldest = dataRef.current[0].time;
        const range = rangeRef.current;
        const totalDays = range.from && range.to
          ? Math.ceil((range.to - range.from) / (1000 * 60 * 60 * 24)) : 0;
        if (totalDays >= MAX_HISTORY_DAYS) break;
        await loadChartRef.current?.(true);
        if (!dataRef.current.length || dataRef.current[0].time >= oldest) break;
        guard += 1;
      }
      const tfSec = TIMEFRAME_SECONDS[timeframe] || 3600;
      const oldest = dataRef.current[0]?.time;
      const anchor = oldest != null ? Math.max(target, oldest) : target;
      try {
        chartRef.current?.timeScale().setVisibleRange({
          from: anchor - 10 * tfSec, to: anchor + 80 * tfSec,
        });
      } catch (e) { /* no data at all; the chart stays where it was */ }
    };
    window.addEventListener("chart:goto", onGoto);
    return () => window.removeEventListener("chart:goto", onGoto);
  }, [timeframe]);

  // The last-price pulse: a small breathing dot riding the live price at
  // the scale edge -- the one legitimately animated pixel on the canvas
  // (research: platforms that do this well always ship the off switch).
  const pulseRef = useRef(null);
  useEffect(() => {
    const el = pulseRef.current;
    if (!el) return;
    const ap = chartAppearance();
    const price = Number(livePrice);
    const series = seriesRef.current;
    if (ap.pulse === false || !marketOpen || !series || !Number.isFinite(price)) {
      el.style.opacity = "0";
      return;
    }
    let y = null;
    try { y = series.priceToCoordinate(price); } catch (e) { /* no scale yet */ }
    if (y == null) { el.style.opacity = "0"; return; }
    let axis = 72;
    try { axis = chartRef.current.priceScale("right").width() || 72; } catch (e) {}
    el.style.opacity = "1";
    el.style.top = `${y - 4}px`;
    el.style.right = `${axis + 2}px`;
    try {
      el.animate(
        [{ boxShadow: "0 0 0 0 rgba(201,162,39,0.45)" },
         { boxShadow: "0 0 0 10px rgba(201,162,39,0)" }],
        { duration: 700, easing: "ease-out" });
    } catch (e) { /* old engine; the dot still marks the price */ }
  }, [livePrice, marketOpen]);

  // The bar being read: the one under the pointer, else the most recent.
  const readout = hoveredBar || dataRef.current[dataRef.current.length - 1] || null;
  const readoutUp = readout ? readout.close >= readout.open : true;

  return html`
    <div className="chart-canvas" ref=${containerRef}>
      <div className="price-pulse" ref=${pulseRef} aria-hidden="true"></div>
      ${readout && html`
        <div className=${"ohlc-readout " + (readoutUp ? "is-up" : "is-down")} dir="ltr">
          ${/* The row NAMES itself, and every figure explains itself on
               hold -- the owner's test: a beginner must never have to ask
               what a row is for. */""}
          <span className="row-label ohlc-name">${t("chart.this_bar", "This bar")}</span>
          <span title=${t("chart.open_full", "Open — where this bar started")}><small>O</small>${formatPrice(readout.open)}</span>
          <span title=${t("chart.high_full", "High — the bar's top")}><small>H</small>${formatPrice(readout.high)}</span>
          <span title=${t("chart.low_full", "Low — the bar's bottom")}><small>L</small>${formatPrice(readout.low)}</span>
          <span title=${t("chart.close_full", "Close — where it ended (or is now)")}><small>C</small>${formatPrice(readout.close)}</span>
          ${readout.open ? html`
            <span className="ohlc-change">
              ${readout.close >= readout.open ? "+" : ""}${(readout.close - readout.open).toFixed(2)}
              (${(((readout.close - readout.open) / readout.open) * 100).toFixed(2)}%)
            </span>` : ""}
          ${/* The bar's own range and WHEN it was -- both already captured on
               every crosshair move and both thrown away. A reader inspecting a
               bar is asking "how big was this move, and when": answering only
               O/H/L/C makes them do arithmetic and check the axis. */""}
          ${readout.high != null && readout.low != null && html`
            <span className="ohlc-range"
                  title=${t("chart.range_full", "Range — how far this bar travelled, top to bottom")}>
              <small>R</small>${(readout.high - readout.low).toFixed(2)}
            </span>`}
          ${readout.time != null && html`
            <span className="ohlc-when">${formatBarTime(readout.time)}</span>`}
          ${/* Named overlay lines report their value at this bar. */""}
          ${readout.studies && Object.entries(readout.studies).map(([id, value]) => html`
            <span key=${id} className="ohlc-study">
              <small>${{ ema20: "EMA20", ema50: "EMA50", vwap: "VWAP" }[id] || id}</small>${formatPrice(value)}
            </span>`)}
          ${/* The hovered bar carried a release: name it HERE, in the DOM
               readout, where text belongs -- the lane below only marks the
               spot. */""}
          ${hoveredBar && eventsByBarRef.current.has(hoveredBar.time) && html`
            <span className="ohlc-event" title=${eventsByBarRef.current.get(hoveredBar.time).join("\n")}>
              <i className="fa-solid fa-flag" aria-hidden="true"></i>
              ${eventsByBarRef.current.get(hoveredBar.time).slice(0, 2).join(" · ")}
            </span>`}
          ${closeIn != null && !hoveredBar && html`
            <span className="ohlc-countdown" dir="ltr"
                  title=${t("chart.close_in", "This candle closes in")}>
              <i className="fa-regular fa-clock" aria-hidden="true"></i>
              ${fmtCloseIn(closeIn)}
            </span>`}
          ${/* Alert-from-chart: the price under the crosshair becomes a watch
                in one tap -- the TradingView interaction traders miss most.
                Reuses the same quota'd, per-user price-alert the level chips
                create. */""}
          ${onWatchLevel && readout.close != null && html`
            <button className="ohlc-watch"
                    disabled=${alertBusy != null}
                    title=${t("levels.watch_here", "Alert me at this price")}
                    aria-label=${t("levels.watch_here", "Alert me at this price")}
                    onClick=${() => onWatchLevel(Math.round(readout.close * 100) / 100, null)}>
              <i className=${alertedLevels.has(Math.round(readout.close * 100) / 100)
                             ? "fa-solid fa-bell" : "fa-regular fa-bell"}></i>
            </button>`}
        </div>`}
      ${/* The measure answer: two taps asked "how far?", this is the reply --
            dollars, percent, bars and duration -- dismissed with a tap and
            never persisted. */""}
      ${measureResult && html`
        <div className="measure-chip" dir="ltr" role="status">
          <b className=${measureResult.dollars >= 0 ? "pos" : "neg"}>
            ${measureResult.dollars >= 0 ? "+" : ""}${measureResult.dollars.toFixed(2)}
          </b>
          ${measureResult.pct != null && html`
            <span>${measureResult.pct >= 0 ? "+" : ""}${measureResult.pct.toFixed(2)}%</span>`}
          ${measureResult.bars != null && html`<span>${measureResult.bars} ${t("chart.bars", "bars")}</span>`}
          ${measureResult.secs != null && html`<span>${fmtSpan(measureResult.secs)}</span>`}
          <button onClick=${() => setMeasureResult(null)}
                  aria-label=${t("buttons.close", "Close")}>
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>`}
      ${/* The drawing tools. DOM, never canvas: anything a user taps has to be
           focusable, labelled and readable aloud -- the same house rule
           box-primitive.js states. */""}
      ${/* Collapsed to a single button by default: a permanent rail of four
           icons sits exactly where the oldest candles are on a 390px screen,
           and a chart page cannot spend width on tools nobody is using. */""}
      ${/* Zoom and reset as buttons. Pinch is undiscoverable, impossible
           one-handed on a phone, and a trader scrolling back through history
           has no way to get home again without it. */""}
      ${measuring && html`
        <div className="drawing-facts" role="dialog"
             aria-label=${t("draw.facts", "About this line")}>
          <button className="drawing-facts-close" onClick=${() => setMeasuring(null)}
                  aria-label=${t("chat.close", "Close")}>
            <i className="fa-solid fa-xmark"></i>
          </button>
          ${!measurement && html`<p className="muted">${t("draw.measuring", "Reading the chart…")}</p>`}
          ${measurement?.failed && html`
            <p className="muted">${t("draw.measure_failed", "Could not read this line.")}</p>`}
          ${measurement && !measurement.failed && html`
            <${DrawingFacts} m=${measurement} t=${t} />`}
          ${measurement && !measurement.failed && !critique && html`
            <button className="btn btn-secondary drawing-critique-btn"
                    onClick=${critiqueDrawing}>
              <i className="fa-solid fa-wand-magic-sparkles" aria-hidden="true"></i>
              ${t("draw.ai_critique", "Ask the engine about this line")}
            </button>`}
          ${critique?.loading && html`
            <p className="muted">${t("chat.reading", "Reading the state…")}</p>`}
          ${critique?.failed && html`
            <p className="muted">${t("chat.failed", "Could not reach the engine. Nothing was answered.")}</p>`}
          ${critique?.claims && html`
            <${ChatAnswer} turn=${critique} t=${t} />`}
        </div>`}

      ${shareNote && html`
        <p className="draw-hint" role="status">
          ${shareNote === "saved"
            ? t("draw.share_saved", "Saved to your device.")
            : t("draw.share_failed", "Could not make the image.")}
        </p>`}

      ${saveNote && html`
        <p className="draw-hint" role="status">
          ${t("draw.save_failed", "Could not save that drawing — it stays on screen and the next edit retries.")}
        </p>`}

      ${armedTool && html`
        <p className="draw-hint" role="status">
          ${armedTool === "hline"
            ? t("draw.hint_hline", "Tap the price where you want the line.")
            : t("draw.hint_line", "Drag from one point to the other.")}
        </p>`}

      ${/* The full veil is for a canvas with NOTHING on it (first load) and
            for errors, which must cover whatever stale bars sit beneath. A
            timeframe SWITCH keeps the old candles at full strength -- the
            audit measured 2.4s of blank per switch, dozens of times an hour
            -- and announces itself with a quiet corner pill instead. */""}
      ${/* Comfort audit #20: the first load showed a spinner and a sentence
            on an empty rectangle -- the shape of "something is wrong" rather
            than "something is coming". A skeleton of candles occupies the
            same space the real chart will, so the eye lands where it will
            land anyway and the wait stops reading as a fault. Errors keep
            the icon and the sentence: an error IS something being wrong. */""}
      ${status.state === "loading" && !hasPaintedRef.current && html`
        <div className="chart-skeleton" role="status"
             aria-label=${status.message || t("chart.loading", "Loading the chart")}>
          <div className="chart-skeleton-bars" aria-hidden="true">
            ${SKELETON_BARS.map((bar, i) => html`
              <span key=${i} className="skeleton-bar"
                    style=${{ blockSize: `${bar.h}%`, marginBlockStart: `${bar.top}%` }}></span>`)}
          </div>
          <span className="chart-skeleton-note">${status.message}</span>
        </div>`}
      ${status.state === "error" && html`
        <div className="chart-overlay">
          <div>
            <i className="fa-solid fa-triangle-exclamation"></i>
            <p>${status.message}</p>
            <button className="btn btn-secondary chart-retry"
                    onClick=${() => loadChart(false)}>
              ${t("chart.retry", "Try again")}
            </button>
          </div>
        </div>
      `}
      ${status.state === "loading" && hasPaintedRef.current && html`
        <div className="chart-switch-pill" role="status">
          <i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>
          <span dir="ltr">${timeframe}</span>
        </div>`}
      ${/* Bar replay: the practice tool. Docked over the canvas bottom,
            transport-first -- step, play, speed, and where the cursor
            stands. Exit restores the full series in one paint. */""}
      ${levelSheet && html`
        <div className="sourced-scrim" onClick=${() => setLevelSheet(null)}>
          <div className="sourced-sheet" role="dialog" aria-modal="true"
               onClick=${(e) => e.stopPropagation()}>
            <div className="sourced-head">
              <span>${{
                support: t("chart.support", "Support"),
                resistance: t("chart.resistance", "Resistance"),
                alert: t("alerts.price_alerts", "Price alerts"),
                entry: t("advisor.entry", "Entry"),
                stop: t("positions.stop", "Stop"),
                target: t("positions.target", "Target"),
              }[levelSheet.kind] || levelSheet.kind}</span>
              <b dir="ltr">${formatPrice(levelSheet.price)}</b>
            </div>
            ${levelSheet.touches != null && html`
              <div className="sourced-row"><dt>${t("levels.touched", "Touched")}</dt>
                <dd dir="ltr">×${iso(levelSheet.touches)}</dd></div>`}
            ${levelSheet.confluence != null && html`
              <div className="sourced-row"><dt>${t("levels.confluence", "Sources agreeing")}</dt>
                <dd dir="ltr">${iso(levelSheet.confluence)}</dd></div>`}
            ${(levelSheet.sources || []).slice(0, 4).map((src, i) => html`
              <div key=${i} className="sourced-row">
                <dt>${levelNote(t, src.source)}</dt>
                <dd dir="ltr">${src.formula || ""}</dd>
              </div>`)}
            ${levelSheet.note && html`
              <div className="sourced-row"><dt>${t("alerts.your_alerts", "Your price alerts")}</dt>
                <dd>${levelNote(t, levelSheet.note)}</dd></div>`}
            <div className="level-sheet-actions">
              <button className="btn btn-primary"
                      onClick=${() => {
                        const kindWord = levelSheet.kind === "resistance"
                          ? t("chart.resistance", "resistance")
                          : levelSheet.kind === "support"
                            ? t("chart.support", "support") : levelSheet.kind;
                        const q = formatTemplate(
                          t("levels.ask_q", "What does the {price} level ({kind}) mean right now?"),
                          { price: formatPrice(levelSheet.price), kind: kindWord });
                        window.dispatchEvent(new CustomEvent("chat:open", {
                          detail: { question: q } }));
                        setLevelSheet(null);
                      }}>
                <i className="fa-solid fa-comment-dots"></i>
                ${t("levels.ask_ai", "Ask the AI about it")}
              </button>
              ${onWatchLevel && (levelSheet.kind === "support" || levelSheet.kind === "resistance") && html`
                <button className="btn btn-secondary"
                        onClick=${() => { onWatchLevel(levelSheet.price, null); setLevelSheet(null); }}>
                  <i className="fa-regular fa-bell"></i>
                  ${t("levels.watch_here", "Alert me at this price")}
                </button>`}
            </div>
          </div>
        </div>`}
      ${replay && html`
        <div className="replay-dock" dir="ltr">
          <button aria-label=${t("replay.back", "Back one bar")}
                  onClick=${() => setReplay((p) => p && { ...p, playing: false, index: Math.max(2, p.index - 1) })}>
            <i className="fa-solid fa-backward-step"></i>
          </button>
          <button className="replay-play" aria-label=${replay.playing ? t("replay.pause", "Pause") : t("replay.play", "Play")}
                  onClick=${() => setReplay((p) => p && { ...p, playing: !p.playing })}>
            <i className=${replay.playing ? "fa-solid fa-pause" : "fa-solid fa-play"}></i>
          </button>
          <button aria-label=${t("replay.forward", "Forward one bar")}
                  onClick=${() => setReplay((p) => p && { ...p, playing: false, index: Math.min(fullDataRef.current.length, p.index + 1) })}>
            <i className="fa-solid fa-forward-step"></i>
          </button>
          ${[2, 5, 10].map((bps) => html`
            <button key=${bps} className=${"replay-speed" + (replay.speed === bps ? " is-on" : "")}
                    onClick=${() => setReplay((p) => p && { ...p, speed: bps })}>
              ${iso(bps)}×
            </button>`)}
          <span className="replay-pos">${iso(replay.index)} / ${iso(fullDataRef.current.length)}</span>
          <button className="replay-exit" aria-label=${t("buttons.close", "Close")}
                  onClick=${() => setReplay(null)}>
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>`}
    </div>
    ${/* The drawing tools: a DOCKED strip under the canvas, never a float
         over the candles -- TradingView and MT5 both keep tools in fixed
         chrome on phones, and the owner's screens showed what floating
         costs. Named, so the row explains itself. */
      drawOpen && html`
      <div className="draw-dock" role="toolbar" aria-label=${t("draw.tools", "Drawing tools")}>
        <span className="row-label">
          <i className="fa-solid fa-pen-ruler" aria-hidden="true"></i>
          ${t("draw.tools", "Drawing tools")}
        </span>
        ${DRAW_TOOLS.map((tool) => html`
          <button key=${tool.key}
                  className=${"draw-tool" + (armedTool === tool.key ? " is-armed" : "")}
                  aria-pressed=${armedTool === tool.key}
                  title=${t(tool.label, tool.fallback)}
                  aria-label=${t(tool.label, tool.fallback)}
                  onClick=${() => setArmedTool(armedTool === tool.key ? null : tool.key)}>
            <i className=${`fa-solid ${tool.icon}`}></i>
          </button>`)}
        <button className="draw-tool" disabled=${undoStackRef.current.length === 0}
                data-tick=${historyTick}
                aria-label=${t("draw.undo", "Undo")} title=${t("draw.undo", "Undo")}
                onClick=${undo}>
          <i className="fa-solid fa-rotate-left"></i>
        </button>
        <button className="draw-tool" disabled=${redoStackRef.current.length === 0}
                aria-label=${t("draw.redo", "Redo")} title=${t("draw.redo", "Redo")}
                onClick=${redo}>
          <i className="fa-solid fa-rotate-right"></i>
        </button>
        ${selectedDrawing && html`
          <button className="draw-tool is-danger"
                  aria-label=${t("draw.delete", "Delete this drawing")}
                  title=${t("draw.delete", "Delete this drawing")}
                  onClick=${() => removeDrawing(selectedDrawing.id)}>
            <i className="fa-solid fa-trash"></i>
          </button>`}
        <button className="draw-tool" disabled=${sharing}
                aria-label=${t("draw.share", "Share this analysis")}
                title=${t("draw.share", "Share this analysis")}
                onClick=${shareAnalysis}>
          <i className=${sharing ? "fa-solid fa-spinner fa-spin" : "fa-solid fa-share-nodes"}></i>
        </button>
        ${drawings.length > 0 && html`
          <span className="draw-count">${iso(String(drawings.length))}</span>`}
      </div>`}
    ${/* Sub-pane hosts sit DIRECTLY under the canvas, before the level
         row -- rendered after it, they painted past the page bottom and
         over the tab bar on short screens (the owner's: "the chart is on
         top of the component"). Always mounted so their refs exist. */""}
    ${Object.keys(PANE_DEFS).map((key) => html`
      <div key=${key}
           className="study-pane"
           style=${{ display: studies && studies[key] ? "block" : "none" }}>
        ${/* Anonymous colored lines forced the reader to memorize which pane
              is which; the name rides the corner of its own pane. */""}
        <span className="study-pane-name">${key.toUpperCase()}</span>
        <div className="study-pane-canvas"
             ref=${(el) => { paneHostRef.current[key] = el; }}></div>
      </div>`)}
    ${/* During a timeframe switch the old frame's levels are honestly gone
          (painting them on new candles would state something false) -- but
          unmounting the ROW made the canvas grow 62px and snap back on every
          switch: two hard reflows a trader felt dozens of times an hour.
          The row stays; the chips go quiet until the new frame's levels
          land. */""}
    ${!overlays && hasPaintedRef.current && html`
      <div className="key-levels is-waiting" dir="ltr" aria-hidden="true">
        <span className="key-levels-title row-label">
          <i className="fa-solid fa-layer-group" aria-hidden="true"></i>
          ${t("levels.title", "Key levels")}
        </span>
        ${[0, 1, 2].map((i) => html`<span key=${i} className="key-level-ghost"></span>`)}
      </div>`}
    ${/* The key-level map: confluence zones where every chip states the
         arithmetic that made it (D-P, W-R1, $4,400, S/R x3) -- the opposite
         of a mystery-line chart.

         Ordered by DISTANCE from the price, not by strength. Ranked by
         strength, the row opened with a very strong level $48 away while the
         support sitting under the current candle came third -- the reader's
         first chip was the one least likely to matter today. Strength still
         shows, in the chip's own words; proximity decides the order. */
      (overlays?.key_levels || []).length > 0 && html`
      <div className="key-levels" dir="ltr">
        <span className="key-levels-title row-label">
          <i className="fa-solid fa-layer-group" aria-hidden="true"></i>
          ${t("levels.title", "Key levels")}
        </span>
        ${[...overlays.key_levels]
            .sort((a, b) => Math.abs(a.price - (overlays.price || a.price))
                          - Math.abs(b.price - (overlays.price || b.price)))
            .slice(0, 5).map((z) => html`
          ${/* Tap the level to watch it: the alert is armed on OUR tick feed
               and fires with our own price stamped on it. */""}
          <button key=${z.price} type="button"
                  className=${`key-level ${z.side}` + (alertedLevels.has(z.price) ? " is-watched" : "")}
                  disabled=${alertBusy === z.price}
                  onClick=${() => onWatchLevel && onWatchLevel(z.price, z)}
                  title=${z.sources.map((s) => `${s.source} = ${s.price} (${s.formula})`).join("\n")
                          + "\n" + t("levels.tap_to_watch", "Tap to be alerted when price reaches this level")}>
            ${/* The chip SAYS what it is, in the colour of ITS line on the
                 chart -- amber support, blue resistance -- so the chip and
                 the line read as one thing. The formula stays in the
                 tooltip; the words carry the meaning. */""}
            <small className="key-level-side">
              ${(z.side === "support" || z.side === "below")
                ? t("chart.support", "Support") : t("chart.resistance", "Resistance")}
              ${/* Confluence, said as what it IS. "×2" meant "two computed
                   sources agree here" and nobody could know that -- but the
                   replacement, "strong" and "very strong", reads as "likely
                   to hold", and that was measured on 359 days: our levels
                   react 59.9%, the SAME levels mirrored to the wrong side of
                   price react 58.7%. A count of agreeing methods is a fact.
                   A strength is a forecast this product cannot make. */
                z.confluence >= 2
                  ? ` · ${formatTemplate(t("levels.agree", "{n} methods agree"),
                                         { n: iso(String(z.confluence)) })}`
                  : ""}
            </small>
            <span className="key-level-main">
              <b>${formatPrice(z.price)}</b>
              ${/* HOW FAR AWAY IT IS. A level is only useful relative to the
                   price, and the chip printed an absolute figure a trader had
                   to subtract in their head, on a phone, five times over. The
                   server has always computed this (src/trading/triggers.py
                   rounds |zone − price| into the payload) and the client threw
                   it away. Signed, so the direction is read rather than
                   worked out from which side the chip claims to be. */""}
              ${overlays.price != null && html`
                <small className="key-level-away">
                  ${(z.price >= overlays.price ? "+" : "−")}
                  ${iso(Math.abs(z.price - overlays.price).toFixed(2))}
                </small>`}
              <i className=${alertedLevels.has(z.price) ? "fa-solid fa-bell" : "fa-regular fa-bell"}></i>
            </span>
          </button>`)}
      </div>
      <${LevelScorecard} />`}
  `;
}

function useMarketStatus() {
  // Asks the server. The browser used to carry its own copy of the session
  // rules with UTC hours hardcoded -- Friday close pinned to 22:00 UTC -- but
  // gold closes at 17:00 New York, which is 21:00 UTC in summer. So for half
  // the year this badge announced "MARKET OPEN" for a full hour after the
  // market had shut, and every number beside it inherited that credibility.
  // One rule, on the server, with a real timezone database.
  const [status, setStatus] = useState({ open: null, next_change: null });

  useEffect(() => {
    let alive = true;
    const check = async () => {
      try {
        const response = await authenticatedFetch("/api/market-status");
        const payload = await response.json();
        if (alive && payload.success) {
          setStatus({ open: payload.open, next_change: payload.next_change });
        }
      } catch (error) { /* keep the last known answer rather than guessing */ }
    };
    check();
    const timer = setInterval(check, 60000);
    return () => { alive = false; clearInterval(timer); };
  }, []);

  return status;
}

function useDashboardData(currentTimeframe) {
  // current_price starts null, not 0. Zero is a price, and it rendered as
  // "$0.00" in green -- a fabricated quote with a fabricated direction, shown
  // before anything had loaded and again after any failure. A value the app
  // does not have must be absent, never a number that looks like data.
  const [data, setData] = useState({
    current_price: null,
    price_change: null,
    price_change_pct: null,
    indicators: {},
    extended_indicators: null,
    summary: null,
    signals: {},
    verdict: null,
    insufficient_data: false,
    stats: {},
    last_update: null
  });
  const [loadState, setLoadState] = useState({ state: "loading", error: null, stale: false });
  // The trading style the REST payload was computed for. The socket fans out
  // one payload per ROOM, so it carries every style keyed under `by_style`;
  // this ref is how the handlers pick OUR style instead of letting the
  // default style's (often empty) card overwrite the one REST painted.
  const styleRef = useRef(null);

  const loadInitial = useCallback(async (timeframeOverride) => {
    const timeframe = timeframeOverride || currentTimeframe;
    try {
      setLoadState((prev) => ({ ...prev, state: prev.state === "ready" ? "ready" : "loading" }));
      const response = await authenticatedFetch(`/api/indicators/${timeframe}`);
      if (!response.ok) {
        throw new Error(`Failed to load indicators (${response.status})`);
      }
      const payload = await response.json();
      if (payload.style) styleRef.current = payload.style;
      setData((prev) => ({
        ...prev,
        // `|| 0` reintroduced exactly what the comment above forbids: a missing
        // price became $0.00 rendered in green, a fabricated quote with a
        // fabricated direction. Absent must stay absent.
        //
        // The fix landed on current_price and stopped there, one line short of
        // the two fields that carry the DIRECTION. An absent 24h change still
        // became 0, priceUp read `0 >= 0` as up, and the header printed
        // "+0.00 (+0.00%)" in green: a claim that gold is flat and rising,
        // invented by a coercion, at the top of the screen.
        current_price: payload.stats?.current ?? null,
        price_change: payload.stats?.change_24h ?? null,
        price_change_pct: payload.stats?.change_pct_24h ?? null,
        indicators: payload.indicators || {},
        extended_indicators: payload.extended_indicators || null,
        summary: payload.summary || null,
        signals: payload.signals || {},
        verdict: payload.verdict || null,
        // The server's sizing. This whitelist silently dropped it, so the
        // volatility-trimmed lot size never reached the screen and the browser
        // fell back to its own untrimmed arithmetic -- the exact disagreement
        // between what is shown and what gets recorded that moving sizing to
        // the server was supposed to end.
        sizing: payload.sizing || null,
        insufficient_data: payload.insufficient_data || false,
        stats: payload.stats || {},
        last_update: payload.last_update || null
      }));
      setLoadState({ state: "ready", error: null, stale: false });
      return { ok: true, timeframe };
    } catch (error) {
      // A failure keeps whatever was last known but marks it stale, so the
      // screen never presents old numbers as current. Previously the request
      // failed silently and the dashboard carried on looking authoritative.
      setLoadState((prev) => ({
        state: prev.state === "ready" ? "ready" : "error",
        error: error.message || "load failed",
        stale: true,
      }));
      return { ok: false, timeframe };
    }
  }, [currentTimeframe]);

  const applyUpdate = useCallback((update) => {
    setData((prev) => ({
      ...prev,
      ...update,
      indicators: update.indicators || prev.indicators,
      signals: update.signals || prev.signals,
      verdict: update.verdict || prev.verdict,
      insufficient_data: update.insufficient_data ?? prev.insufficient_data,
      extended_indicators: update.extended_indicators || prev.extended_indicators,
      summary: update.summary || prev.summary,
      stats: update.stats ? { ...prev.stats, ...update.stats } : prev.stats
    }));
  }, []);

  return { data, loadInitial, applyUpdate, loadState, setLoadState, styleRef };
}

function useNewsFeed(symbol, language, limit = 10, page = 1) {
  const [items, setItems] = useState([]);
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!symbol) return;
    let active = true;
    const lang = language === "ar" ? "ar" : "en";

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await authenticatedFetch(
          `/api/news?symbol=${encodeURIComponent(symbol)}&limit=${limit}&page=${page}&lang=${lang}`,
          { method: "GET" }
        );
        if (!response.ok) {
          throw new Error("Failed to load news");
        }
        const data = await response.json();
        if (active) {
          setItems(data.headlines || []);
          // pending_events included: the verdict's news row names the pending
          // release from it. Rebuilding the context without it silently killed
          // that feature everywhere -- the row fell back to the unnamed
          // "high-impact release pending" it was built to replace.
          setContext({ bias: data.bias, caution: data.caution, summary: data.summary,
                       high_impact_count: data.high_impact_count,
                       pending_events: data.pending_events || [],
                       next_red: data.next_red || null });
        }
      } catch (err) {
        if (active) {
          setError(err.message || "Failed to load news");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [symbol, language, limit, page]);

  return { items, loading, error , context };
}

// symbol, priceChangePct and priceUp were passed by both call sites and read
// by neither -- and one of them computed `(price_change_pct || 0).toFixed(2)`
// to feed a prop nobody consumed, which is how that coercion survived the
// sweep that removed its siblings.
function NewsList({ items, onOpen, t, context }) {
  return html`
    <div className="news-list">
      ${/* The panel header already says whose headlines these are; a second
           line restating it ("XAUUSD · headlines affecting this market")
           was the same sentence three ways. */""}
      ${/* The clock outranks the commentary: the next release that can take
            a stop out, as a countdown from the engine's own calendar, with
            the path to the full week one tap away. The page used to answer
            "when?" with headline wording, which outlives the print. */""}
      ${context && context.next_red && context.next_red.minutes != null && html`
        <a className="news-next-red" href="/events">
          <i className="fa-solid fa-clock" aria-hidden="true"></i>
          <span className="news-next-red-name">${context.next_red.title}</span>
          <b dir="ltr">${(() => {
            const m = context.next_red.minutes;
            if (m < 60) return formatTemplate(t("news.in_minutes", "in {m}m"), { m: iso(m) });
            if (m < 48 * 60) return formatTemplate(t("news.in_hours", "in {h}h {m}m"), { h: iso(Math.floor(m / 60)), m: iso(m % 60) });
            return formatTemplate(t("news.in_days", "in {d} days"), { d: iso(Math.round(m / 1440)) });
          })()}</b>
          <span className="news-next-red-go">${t("news.calendar_link", "Full calendar")} <span className="dir-arrow">→</span></span>
        </a>`}
      ${context && context.pending_events && context.pending_events.length > 0 && html`
        <div className="news-pending">
          <i className="fa-solid fa-calendar-day"></i>
          <div>
            <strong>${context.pending_events.map((e) => e.event).join(" · ")}</strong>
            <span> — ${t("news.pending_note", "scheduled release still ahead. A technical stop cannot be relied on through the print.")}</span>
          </div>
        </div>
      `}
      ${/* The feeds are English RSS; pretending otherwise is worse than
            saying so. One quiet line for the non-English reader. */""}
      ${items.length > 0 && document.documentElement.lang === "ar" && html`
        <p className="news-lang-note">${t("news.english_feeds", "العناوين بالإنجليزية — مصادرها وكالات أجنبية، والمحرّك بيقرأها بأي لغة.")}</p>`}
      ${items.map((item) => html`
        <button className="news-card" onClick=${() => onOpen(item)}>
          <div className="news-card-content">
            <div className="news-title" dir="auto">${item.title}</div>
            <div className="news-meta">
              <span><bdi>${item.source || t("news.source", "Source")}</bdi></span>
              <span>|</span>
              <span>${formatRelativeTime(item.published)}</span>
              ${item.high_impact && html`
                <span className="news-impact">${t("news.high_impact", "High impact")}</span>`}
              ${item.sentiment !== 0 && html`
                <span className=${item.sentiment > 0 ? "news-chip-up" : "news-chip-down"}>
                  ${item.sentiment > 0
                    ? "\u25B2 " + t("news.bullish", "bullish")
                    : "\u25BC " + t("news.bearish", "bearish")}
                </span>`}
            </div>
          </div>

        </button>
      `)}
    </div>
  `;
}

// What a trade looks like while you are in it.
//
// PositionTracker could always open a trade, follow it bar by bar and record a
// realised R -- but nothing ever called it, so the engine was dead code and the
// panel read "Open 0 / Closed 0" forever. This is the surface that makes it
// real: while a position is live it sits above the chart, because when you are
// in a trade that is the most important thing on the screen.
// What the app has to say about one open trade, without instructing.
//
// The coach gives ADVICE and states the measured cost of it. This says what
// IS: the timeline from the append-only log, and a status line that arrives
// whether or not anything happened. That second half is the whole point — a
// check-in that only appears when the trade is in trouble is an alarm, and an
// alarm teaches its own silence to mean nobody is watching.
// Levels decided in advance, and what price has done to them.
//
// Measured reason, not a preference: GATE-SWEEP.md records that a market order
// priced from an entry computed ten minutes earlier "will fill somewhere
// else". Deciding the level first and waiting for price to arrive removes that
// gap rather than bounding it.
//
// Most of these expire untouched, and the copy says so — a level nobody
// reached correctly described a place the market did not go, and a panel that
// only showed the ones that hit would be a highlight reel.
function PreparedLevels({ timeframe, t }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    authenticatedFetch(`/api/prepared-levels?timeframe=${timeframe || ""}`)
      .then((r) => r.json())
      .then((p) => { if (alive && p.success) setData(p); })
      .catch(() => {});
    return () => { alive = false; };
  }, [timeframe]);

  if (!data?.levels?.length) return null;

  return html`
    <section className="panel surface prepared">
      <h3>${t("prep.title", "Levels we are watching")}</h3>
      <p className="settings-hint">${t("prep.note", "")}</p>
      <div className="table-scroll">
        <table className="prepared-table" dir="ltr">
          <thead><tr>
            <th>${t("prep.level", "Level")}</th>
            <th>${t("prep.side", "If reached")}</th>
            <th>${t("prep.stop", "Stop")}</th>
            <th>${t("prep.target", "Target")}</th>
            <th>${t("prep.state", "State")}</th>
          </tr></thead>
          <tbody>
            ${data.levels.map((row) => html`
              <tr key=${row.level_id} className=${"is-" + row.state}>
                <td>${iso(String(row.price))}</td>
                <td>${t(`signals.${row.side}`, row.side)}</td>
                <td>${iso(String(row.stop ?? "—"))}</td>
                <td>${iso(String(row.target ?? "—"))}</td>
                <td>${t(`prep.${row.state}`, row.state)}</td>
              </tr>`)}
          </tbody>
        </table>
      </div>
    </section>`;
}


function TradeCompanion({ tradeId, t }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!tradeId) { setData(null); return undefined; }
    let alive = true;
    const load = () => authenticatedFetch(`/api/positions/${tradeId}/companion`)
      .then((r) => r.json())
      .then((p) => { if (alive && p.success) setData(p); })
      .catch(() => {});
    load();
    const timer = setInterval(load, 60000);
    return () => { alive = false; clearInterval(timer); };
  }, [tradeId]);

  if (!data) return null;
  const line = (row) => formatTemplate(t(`companion.${row.key}`, ""), row.args || {});

  return html`
    <div className="companion">
      ${data.status && html`
        <p className="companion-now">${line(data.status)}</p>`}
      ${data.timeline?.length > 0 && html`
        <details className="companion-timeline">
          <summary>${t("companion.timeline", "What has happened")}</summary>
          <ul>
            ${data.timeline.map((row, i) => html`<li key=${i}>${line(row)}</li>`)}
          </ul>
        </details>`}
      ${data.autopsy?.length > 0 && html`
        <div className="companion-autopsy">
          ${data.autopsy.map((row, i) => html`<p key=${i}>${line(row)}</p>`)}
        </div>`}
    </div>`;
}


function OpenExposure({ exposure, t }) {
  // EVERYTHING AT RISK, ON ONE LINE.
  //
  // The live card shows one trade. A customer holding two read "1R" twice and
  // had nowhere to see they were carrying two — and two positions on the SAME
  // side of one instrument are not two independent bets: one move against them
  // takes both. That is the difference between risking 1R on a decision and
  // risking 2R on it, and it was invisible.
  //
  // Measured from the LIVE price to each stop rather than from the entry, so a
  // stop moved to break-even correctly stops counting. The arithmetic is the
  // server's (see /api/positions), because a second implementation of "what am
  // I risking" is the last place this product can afford two answers.
  if (!exposure || !exposure.open) return null;
  // One position with nothing unusual about it is what the card below already
  // says. This line earns its space when there is more than one, or when the
  // stops have moved past entry and the honest total is negative.
  if (exposure.open < 2 && !exposure.all_locked) return null;

  const locked = !!exposure.all_locked;
  const r = Math.abs(exposure.at_risk_r).toFixed(2);

  return html`
    <section className=${`exposure${exposure.concentrated ? " is-concentrated" : ""}`}>
      <div className="exposure-main">
        <b dir="ltr">${locked ? iso("0R") : iso(r + "R")}</b>
        <span>
          ${locked
            ? t("exposure.locked", "is locked in across your open trades — the stops are past entry")
            : formatTemplate(
                t("exposure.total", "at risk across {n} open trades"),
                { n: iso(String(exposure.open)) })}
          ${exposure.at_risk_amount && !locked && html`
            <b className="exposure-money" dir="ltr">
              ${" · "}${iso("$" + Math.abs(exposure.at_risk_amount)
                .toLocaleString("en-US", { maximumFractionDigits: 0 }))}
            </b>`}
        </span>
      </div>
      ${exposure.concentrated && html`
        <p className="exposure-note">
          ${formatTemplate(
            t("exposure.same_side", "{n} of them are the same direction on one instrument. One move against you takes all of them — that is one bet of {r}R, not {n} separate ones."),
            { n: iso(String(exposure.largest_same_side)), r: iso(r) })}
        </p>`}
    </section>`;
}

function LivePositionCard({ position, livePrice, onClose, closing, onMoveStop, moving, actionError, t }) {
  // Closing a trade is irreversible and books the P&L then and there, so it is
  // the one action here that asks first. A second deliberate tap, not a single
  // stray one on a live card that repaints every few seconds.
  const [confirmClose, setConfirmClose] = useState(false);

  // A different trade (or none) must never inherit the previous one's armed
  // confirmation: the component instance survives position -> null -> new
  // position, so without this the next trade's card could open one tap from
  // an irreversible close the user never asked about.
  const positionId = position && position.id;
  useEffect(() => { setConfirmClose(false); }, [positionId]);

  if (!position) return null;

  const actionErrorText = actionError && (
    actionError === "price_unavailable"
      ? t("positions.price_unavailable", "Price unavailable right now — try again in a moment.")
      : actionError === "not_found"
        ? t("positions.already_closed", "This position is already closed.")
        : t("positions.action_failed", "Could not complete that — try again."));

  const side = position.side;
  const entry = position.entry_price;
  const stop = position.stop_loss;
  const target = position.take_profit;
  const risk = Math.abs(entry - stop) || 1;
  // Computed from the live quote rather than the last poll, so the number moves
  // with the market instead of stepping every thirty seconds.
  const price = livePrice || position.last_price || entry;
  const r = side === "buy" ? (price - entry) / risk : (entry - price) / risk;

  const span = Math.abs(target - stop) || 1;
  const travelled = side === "buy" ? (price - stop) / span : (stop - price) / span;
  const pct = Math.max(0, Math.min(100, travelled * 100));

  const held = (() => {
    const opened = Date.parse(position.opened_at);
    if (!isFinite(opened)) return null;
    const mins = Math.max(0, Math.round((Date.now() - opened) / 60000));
    if (mins < 60) return `${mins}m`;
    return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  })();

  // THE SHELF LIFE, WHICH THE SERVER HAS ALWAYS SENT AND NOTHING EVER DREW.
  //
  // `bars_held` and `max_holding_bars` ride on every position in
  // /api/positions and appeared nowhere in this file — a trade could expire
  // out from under a customer who had no way to see it coming. It is read off
  // the TRADE, never re-derived from the current style: positions.py extends
  // the deadline on a winner mid-flight, so a style-derived countdown would
  // show the wrong number on exactly the trades that are going well.
  const barsHeld = Number(position.bars_held);
  const barsMax = Number(position.max_holding_bars);
  const shelf = isFinite(barsHeld) && isFinite(barsMax) && barsMax > 0
    ? { held: barsHeld, max: barsMax, left: Math.max(0, barsMax - barsHeld) }
    : null;

  // HOW FAR THIS HAS TRAVELLED AGAINST YOU, on the rail that already exists
  // rather than as a second progress element with a second denominator. The
  // rail's span is stop→target; adverse travel is measured on the entry→stop
  // half of it, which is the half a trader is actually watching.
  const adverse = r < 0 ? Math.min(100, Math.round(Math.abs(r) * 100)) : 0;

  const winning = r >= 0;

  return html`
    <section className=${`live-position live-position-${side}`}>
      <div className="live-position-head">
        <div className="live-position-title">
          <span className="live-dot" aria-hidden="true"></span>
          ${t("positions.in_trade", "In a trade")}
          <span className=${`live-side live-side-${side}`}>
            ${side === "buy" ? t("signals.buy", "BUY") : t("signals.sell", "SELL")}
          </span>
          ${held && html`<span className="live-held">${held}</span>`}
          ${shelf && html`
            <span className=${`live-shelf${shelf.left <= 2 ? " is-near" : ""}`}
                  title=${t("positions.shelf_title", "This trade's own shelf life, frozen when it opened")}>
              ${formatTemplate(t("positions.bars_left", "{left} of {max} bars left"),
                { left: iso(String(shelf.left)), max: iso(String(shelf.max)) })}
            </span>`}
        </div>
        <div className=${"live-r " + (winning ? "is-up" : "is-down")}>
          ${iso(`${r > 0 ? "+" : ""}${r.toFixed(2)}R`)}
        </div>
      </div>

      <div className="live-track" role="img"
           aria-label=${`${t("positions.stop", "Stop")} ${formatPrice(stop)}, ${t("positions.target", "Target")} ${formatPrice(target)}`}>
        <div className=${"live-track-fill " + (winning ? "is-up" : "is-down")}
             style=${{ width: `${pct}%` }}></div>
        <div className="live-track-entry"
             style=${{ left: `${Math.max(0, Math.min(100, (side === "buy" ? (entry - stop) : (stop - entry)) / span * 100))}%` }}></div>
      </div>

      ${adverse > 0 && html`
        ${/* State, never advice. It says where the price is; it does not say
              to close, to hold, or to move anything — the product does not
              instruct, and a trade going badly is the moment that rule
              matters most. */""}
        <p className="live-adverse">
          ${formatTemplate(
            t("positions.adverse", "Price has covered {pct}% of the distance from your entry to your stop."),
            { pct: iso(String(adverse)) })}
        </p>`}

      <div className="live-position-levels">
        <span><small>${t("positions.stop", "Stop")}</small><b>${formatPrice(stop)}</b></span>
        <span><small>${t("positions.entry", "Entry")}</small><b>${formatPrice(entry)}</b></span>
        <span><small>${t("positions.now", "Now")}</small><b>${formatPrice(price)}</b></span>
        <span><small>${t("positions.target", "Target")}</small><b>${formatPrice(target)}</b></span>
      </div>

      ${/* What to do about it. Calling the entry is the easy half; the money is
           made and lost in whether the stop was moved once the trade paid for
           itself, and whether anyone noticed the reason for it had gone. */
        (position.advice || []).length > 0 && html`
        <div className="coach">
          ${position.advice.map((item) => html`
            <div className=${`coach-item coach-${item.priority}`} key=${item.key}>
              <div className="coach-text">
                <b>${item.headline}</b>
                <span>${item.detail}</span>
              </div>
              ${item.action === "move_stop" && html`
                <button className="btn btn-secondary coach-action"
                        disabled=${moving}
                        onClick=${() => onMoveStop(position.id, item.key === "trail_stop" ? "trail" : "breakeven")}>
                  ${moving ? t("positions.moving", "Moving…") : item.action_label}
                </button>`}
            </div>`)}
        </div>`}

      ${(position.events || []).length > 0 && html`
        <div className="coach-log">
          ${/* Append-only: what was changed and when, so a record cannot be
               tidied up after the fact. */
            position.events.map((ev) => html`
            <span key=${ev.id}>
              ${t("positions.stop_moved", "Stop moved")} ${formatPrice(ev.old_value)} <span className="dir-arrow">→</span> ${formatPrice(ev.new_value)}
            </span>`)}
        </div>`}

      ${confirmClose
        ? html`
          <div className="live-close-confirm" role="group"
               aria-label=${t("positions.close_confirm_q", "Close this trade at market now?")}>
            <p className="live-close-confirm-q">${t("positions.close_confirm_q", "Close this trade at market now?")}</p>
            <div className="live-close-confirm-actions">
              <button className="btn btn-danger live-close" disabled=${closing}
                      onClick=${() => onClose(position.id)}>
                ${closing
                  ? t("positions.closing", "Closing…")
                  : t("positions.close_confirm_yes", "Yes, close now")}
              </button>
              <button className="btn btn-ghost" disabled=${closing}
                      onClick=${() => setConfirmClose(false)}>
                ${t("positions.close_confirm_no", "Keep it open")}
              </button>
            </div>
          </div>`
        : html`
          <button className="btn btn-secondary live-close" onClick=${() => setConfirmClose(true)}
                  disabled=${closing}>
            ${t("positions.close_now", "Close at market")}
          </button>`}
      ${actionErrorText && html`<p className="live-action-error" role="alert">${actionErrorText}</p>`}
    </section>
  `;
}

// One verdict, stated once, with what it rests on underneath it.
//
// The dashboard used to show four opinions side by side: the signal card, an
// indicator tally, a "Market Sentiment" computed in the browser from rsi > 65,
// and a "Trend Strength" computed from the MA20/MA50 spread. Two of those were
// invented in the view layer using thresholds that exist nowhere else in the
// system -- the engine reads RSI at 30/70 and measures trend with ADX, not an
// MA gap -- and none of the four had been through the news or the yield veto.
// So the page could show BUY, Buy, Neutral and Weak at once, and the reader had
// no way to rank them.
//
// Now the pipeline decides once, and every other number is presented as
// evidence for or against that decision, with the stance named.
function VerdictPanel({ verdict, signals, summary, extended, volatility, t, newsContext,
                        onTake, taking, takeError, inTrade, marketClosed, insufficient, timeframe,
                        riskPrefs, onAnswer, answered, sizing }) {
  // Above the early returns: a hook that renders conditionally crashes React
  // the first time the condition flips while mounted.
  const [overrideConfirm, setOverrideConfirm] = useState(false);
  // The engine's reason sentences arrive in English from the server; this is
  // the same mapping the advisor panel already uses. Unmapped sentences fall
  // back to the original -- never worse than before.
  const { translateTechnicalReason } = useI18n();
  // The analysis stays readable when recording a trade is switched off -- only
  // the button goes. Reading the verdict is not the same service as taking it.
  const canTake = useAuth().can("take_trade");

  if (!verdict) return null;

  // The verdict is computed on one timeframe; the same market reads BUY on the
  // 1h and stand-aside on the 5m. Naming it stops a reading being taken for a
  // frame it was never about.
  const tfLabel = timeframe ? t(`timeframes.${timeframe}`, timeframe) : null;

  // Not enough bars on this timeframe to compute anything. Say so, once,
  // instead of rendering a panel of blank evidence rows.
  if (insufficient && !marketClosed) {
    return html`
      <section className="verdict-panel verdict-none">
        <div className="verdict-panel-label">
          ${t("verdict.title", "The verdict")}
          ${tfLabel && html`<span className="verdict-tf" title=${t("chart.timeframe", "Timeframe")}>${tfLabel}</span>`}
        </div>
        <div className="verdict-panel-status" aria-live="polite" aria-atomic="true">${t("verdict.insufficient", "Not enough history")}</div>
        <p className="verdict-panel-reason">
          ${t("verdict.insufficient_note", "This timeframe does not carry enough candles to compute the indicators. Choose a shorter timeframe.")}
        </p>
      </section>`;
  }

  const dir = String(verdict.ai_action || "hold").toLowerCase();
  const directional = dir === "buy" || dir === "sell";
  const blockers = verdict.blockers || [];
  const cautions = verdict.cautions || [];
  const status = !directional ? "none" : (blockers.length ? "wait" : "take");
  const setup = directional ? (signals || {})[dir] : null;

  const rows = [];

  // The engine's own indicator tally. Comparing it to the direction is a
  // statement of fact about agreement, not a second opinion.
  const overall = String(summary?.overall_signal || "").toLowerCase();
  if (overall) {
    const buys = (summary?.oscillators?.Buy || 0) + (summary?.moving_averages?.Buy || 0);
    const neutrals = (summary?.oscillators?.Neutral || 0) + (summary?.moving_averages?.Neutral || 0);
    const sells = (summary?.oscillators?.Sell || 0) + (summary?.moving_averages?.Sell || 0);
    let stance = "neutral";
    if (overall.includes("buy")) stance = dir === "buy" ? "supports" : (dir === "sell" ? "opposes" : "neutral");
    else if (overall.includes("sell")) stance = dir === "sell" ? "supports" : (dir === "buy" ? "opposes" : "neutral");
    rows.push({
      key: "tally",
      label: t("evidence.tally", "Indicator tally"),
      value: `${iso(buys)} ${t("technical_analysis.buy", "buy")} · ${iso(neutrals)} ${t("technical_analysis.neutral", "neutral")} · ${iso(sells)} ${t("technical_analysis.sell", "sell")}`,
      stance
    });
  }

  // Trend strength, using the engine's own ADX thresholds rather than a new one.
  const adx = extended?.oscillators?.adx;
  if (adx != null && isFinite(adx)) {
    // A stance needs a proposal to take a side on. With no direction on the
    // table, choppiness is information, not opposition -- "Trend: AGAINST"
    // beside "No setup" read as the chart arguing with itself.
    const hasDir = dir === "buy" || dir === "sell";
    const stance = !hasDir
      ? "context"
      : (adx >= 25 ? "supports" : (adx < 20 ? "opposes" : "neutral"));
    const note = adx >= 25
      ? t("evidence.trending", "trending")
      : (adx < 20
        ? t("evidence.too_choppy", "too choppy for a reliable signal")
        : t("evidence.weak_trend", "weak trend"));
    rows.push({
      key: "adx",
      label: `${t("evidence.trend", "Trend")} ${iso(`(ADX ${adx.toFixed(1)})`)}`,
      value: note,
      stance
    });
  }

  if (verdict.news_caution) {
    // Name the release when the feed knows it. "High-impact release pending"
    // raised the only question a trader has — which one? — and refused to
    // answer it while the data sat one prop away.
    const named = (newsContext?.pending_events || [])
      .map((event) => event && event.event)
      .filter(Boolean)
      .slice(0, 2);
    // "Blocks" is a claim about the ENGINE, so it must come from the
    // engine: the news gate stands aside a DIRECTIONAL verdict only. With
    // no setup on the table there is nothing to block, and stamping BLOCKS
    // anyway put a badge on screen the verdict's own blockers list denied
    // -- caught by the owner reading the two half-truths side by side.
    rows.push({
      key: "news",
      label: t("evidence.news", "News"),
      value: named.length
        ? `${iso(named.join(" · "))} — ${t("evidence.news_pending_named", "release pending")}`
        : t("evidence.news_pending", "high-impact release pending"),
      stance: (verdict.blockers || []).includes("high_impact_pending") ? "blocks" : "context"
    });
  } else if (verdict.news_bias && verdict.news_bias !== "unknown") {
    rows.push({
      key: "news",
      label: t("evidence.news", "News"),
      value: `${t("evidence.leans", "leans")} ${t(`statistics.${verdict.news_bias}`, verdict.news_bias)}`,
      stance: cautions.includes("news_opposes") ? "opposes" : "neutral"
    });
  }

  const yieldTrend = verdict.yield_trend;
  if (yieldTrend === 1 || yieldTrend === -1) {
    rows.push({
      key: "yields",
      label: t("evidence.yields", "10y yields"),
      value: yieldTrend === 1
        ? t("evidence.yields_up", "rising — a headwind for gold")
        : t("evidence.yields_down", "falling — a tailwind for gold"),
      // not "neutral": the words say head/tailwind, so a NEUTRAL chip on the
      // same row was the chip calling its own sentence a liar. Until the
      // veto fires this row is context, and now it says so.
      stance: blockers.includes("yield_headwind") ? "blocks" : "context",
      formula: "DFII10 direction over the last 5 published values",
      source: t("sourced.fred", "Federal Reserve (FRED), fetched by our server"),
      note: t("evidence.yields_note", "The only macro filter that improved results in both halves of a nine-year out-of-sample test. It vetoes; it never proposes.")
    });
  }

  const atr = extended?.volatility?.atr;
  if (atr != null && isFinite(atr)) {
    rows.push({
      key: "atr",
      label: t("evidence.volatility", "Volatility"),
      value: `${iso(`ATR ${formatPrice(atr)}`)}${volatility ? ` · ${t(`statistics.${String(volatility).toLowerCase()}`, volatility)}` : ""}`,
      stance: "context",
      formula: "ATR(14) = mean of the last 14 true ranges",
      source: t("sourced.our_candles", "Our own candle history on this timeframe"),
      note: t("evidence.atr_note", "Sets the stop distance and every ATR multiple shown elsewhere.")
    });
  }

  // A closed market has no actionable setup. Showing "PROPOSED: BUY" with an
  // entry, a stop and a target over Friday's closing price invites someone to
  // act on a price that no longer exists -- the same class of error as the
  // badge that said the market was open when it was shut.
  const statusLabel = marketClosed
    ? t("verdict.status_closed", "Market closed")
    : (status === "take"
      ? t("verdict.status_take", "Setup stands")
      : (status === "wait"
        ? t("verdict.status_wait", "Stand aside")
        : t("verdict.status_none", "No setup")));

  const reasonText = marketClosed
    ? t("verdict.closed_note", "Gold is not trading. These are the last readings from the previous session, kept for reference — not a setup you can take.")
    : verdict.reason_key
    ? t(`verdict.reasons.${verdict.reason_key}`, verdict.reason || "")
    : verdict.reason;

  const stanceLabel = {
    supports: t("evidence.supports", "supports"),
    opposes: t("evidence.opposes", "against"),
    blocks: t("evidence.blocks", "blocks"),
    neutral: t("evidence.neutral_stance", "neutral"),
    context: t("evidence.context", "context")
  };

  const readiness = verdict.readiness;
  const showReady = readiness && readiness.forming && status !== "take" && !marketClosed;

  // Feature #35: when there is no clean setup, say so plainly with the reasons
  // and what would change it. Suppressed while the lighter "forming" pulse
  // already speaks, and while a trade is on the table.
  const noTrade = verdict.no_trade;
  // Only when there is genuinely no proposed direction. When the verdict itself
  // is a blocked direction (status "wait"), the verdict panel already says
  // "Stand aside" with the reason, so the No-Trade card would just repeat it.
  const showNoTrade = noTrade && noTrade.is_no_trade && !marketClosed
    && !showReady && status === "none";

  // Feature #75: news and technicals disagree. Shown on a tradeable read so the
  // user sees the conflict before acting, not buried in a cautions list.
  const conflict = verdict.conflict;
  const showConflict = conflict && conflict.present && status === "take" && !marketClosed;

  return html`
    <section className=${`verdict-panel verdict-${status}`}>
      ${showReady && html`
        <div className=${`getready getready-${readiness.side || "none"}`}>
          <span className="getready-pulse" aria-hidden="true"></span>
          <div className="getready-copy">
            <b>${readiness.headline}</b>
            <span>${readiness.detail}</span>
          </div>
        </div>`}
      ${showConflict && html`
        <div className="verdict-conflict" role="note">
          <i className="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
          <div>
            <b>${t("verdict.conflict_title", "News and technicals disagree")}</b>
            <span>${t(`verdict.conflict_${conflict.technical}_${conflict.news}`, conflict.reason)}</span>
          </div>
        </div>`}
      <div className="verdict-panel-head">
        <div>
          <div className="verdict-panel-label">
            ${t("verdict.title", "The verdict")}
            ${tfLabel && html`<span className="verdict-tf" title=${t("chart.timeframe", "Timeframe")}>${tfLabel}</span>`}
          </div>
          ${/* The ONLY live region in the panel. When the whole section was
               aria-live, every price tick re-read the entire trade plan to a
               screen reader; now only the short status phrase announces, and
               only when it changes. The levels and evidence stay readable on
               demand. */""}
          <div className="verdict-panel-status" aria-live="polite" aria-atomic="true">${statusLabel}</div>
        </div>
        ${directional && !marketClosed && html`
          <div className=${`verdict-dir ${status === "take" ? `verdict-dir-${dir}` : "verdict-dir-muted"}`}>
            <i className=${dir === "buy" ? "fa-solid fa-arrow-trend-up" : "fa-solid fa-arrow-trend-down"}></i>
            ${/* A green BUY badge beside "Stand aside" is itself a mixed
                 signal -- the exact fault this panel exists to remove. When
                 the pipeline stood the trade aside, the direction is reported
                 as what was merely proposed, in a neutral colour. */
              status === "take"
                ? (dir === "buy" ? t("signals.buy", "BUY") : t("signals.sell", "SELL"))
                : `${t("verdict.proposed", "proposed")}: ${dir === "buy" ? t("signals.buy", "BUY") : t("signals.sell", "SELL")}`}
          </div>`}
      </div>

      ${reasonText && html`<p className="verdict-panel-reason">${reasonText}</p>`}

      ${/* The answer first, then its explanation. This card used to sit ABOVE
            "The verdict", so the page opened on a paragraph of no-trade prose
            before stating what the verdict even was -- and the duplicate
            headline ("No clean setup right now" over "No setup") read as two
            cards saying the same nothing. The verdict states it once; this
            block explains it. */ ''}
      ${showNoTrade && html`
        <div className="no-trade">
          ${noTrade.reasons && noTrade.reasons.length ? html`
            <div className="no-trade-block">
              <span className="no-trade-label">${t("verdict.no_trade_why", "Why there's no trade")}</span>
              ${/* "(score 2 of 3 needed)" is EXACTLY the first bar of the
                    gauge below, in words, and it is an f-string with numbers
                    in it — so it can never be translated by a lookup map and
                    would sit in English above an Arabic gauge that says the
                    same thing better. The bar states it; the sentence goes. */""}
              <ul>${noTrade.reasons
                .filter((r) => r !== noTrade.headline)
                .filter((r) => !/score \d+ of \d+/i.test(String(r)))
                .map((r) => html`<li key=${r}>${translateTechnicalReason(r)}</li>`)}</ul>
              <${NoTradeMeasuredLine} />
            </div>` : null}
          ${/* "What would change it" USED TO BE A LIST HERE, and a second card
                below the panel drew the same question as bars. Two blocks
                answering one question is the duplication this file has already
                been corrected for once (see the note above about the duplicate
                headline). The gauge replaces the list where the engine
                publishes a NUMBER — points, ADX, the filters, the market — and
                carries the remaining prose items, which are the ones no number
                covers, underneath it. */""}
        </div>`}

      ${/* THE GAUGE RENDERS ON BOTH SIDES OF THE GATE.
            It used to return null the moment the engine said buy or sell — so
            "what the engine checks" was visible only on the days it checked
            and refused. On the ~3% of bars where it acts, the customer could
            not see what it had cleared, which is the half that would actually
            build trust in the call in front of them. One mount, one home, two
            states: what is missing, or what was cleared. */""}
      ${!marketClosed && verdict && html`
        <${ConditionsMeter} verdict=${verdict} extended=${extended}
                            marketOpen=${!marketClosed}
                            help=${showNoTrade ? noTrade.what_would_help : null} inline />`}

      ${setup && !marketClosed && html`
        <div className=${"verdict-setup" + (status === "wait" ? " struck" : "")}>
          ${/* The RESTING price, not the price the engine decided at. This
               product executes nothing -- the customer places the order at
               their own broker -- so the card must show the price the order
               waits at, and say that it waits. A ticket that does not say
               pending gets clicked at market, and the customer pays a spread
               the measurement assumed they would not: on a $20 stop at a
               34-70c spread that is 3 to 7% of R, on every trade. */""}
          <span><small>${t("advisor.entry", "Entry")}</small><b>${formatPrice(setup.entry ?? setup.price)}</b></span>
          ${setup.order_type === "limit" && html`
            <span className="setup-pending"><small>${t("advisor.order_type", "Order")}</small><b>${
              setup.valid_for_bars
                ? t("advisor.pending_bars", "Pending — expires in {n} bars").replace("{n}", String(setup.valid_for_bars))
                : t("advisor.pending", "Pending limit")}</b></span>`}
          ${setup.stop_loss != null && html`
            <span><small>${t("advisor.stop_loss", "Stop")}</small><b>${formatPrice(setup.stop_loss)}</b></span>`}
          ${setup.take_profit != null && html`
            <span><small>${t("advisor.take_profit", "Target")}</small><b>${formatPrice(setup.take_profit)}</b></span>`}
          ${setup.risk_reward != null && html`
            <span><small>${t("advisor.risk_reward", "R:R")}</small><b>${setup.risk_reward}:1</b></span>`}
        </div>`}

      ${/* Position sizing from the user's own balance and risk %. Floor-
           rounded so the real risk never exceeds the chosen risk; greyed with
           the reason while the system's risk halts are up; absent (with a
           pointer to Settings) until a balance exists -- a lot size invented
           from a default balance would be a number pretending to be advice. */
        setup && !marketClosed && setup.stop_loss != null && (() => {
          const balance = Number(riskPrefs?.account_balance) || 0;
          const riskPct = Number(riskPrefs?.risk_percent) || 1;
          const stopDistance = Math.abs((setup.entry ?? setup.price) - setup.stop_loss);
          if (!stopDistance) return null;
          if (!balance) {
            return html`
              <div className="verdict-sizing verdict-sizing-unset">
                <a href="/trading">${t("sizing.set_balance", "Set your balance in Settings to see the lot size for this setup.")}</a>
              </div>`;
          }
          // The SERVER's sizing when it is available. The browser used to
          // recompute balance x risk% / stop distance itself, which was
          // harmless until the server began trimming the budget for
          // volatility -- at which point the screen and the recorded trade
          // would have disagreed, and the screen would have been the wrong
          // one. The local arithmetic stays only as a fallback for a payload
          // that predates this field.
          const served = sizing && sizing.lots != null ? sizing : null;
          // A REFUSAL is an answer, not a gap to fill. When the server returns
          // no lots with a reason -- below the minimum, no room left before
          // the account floor -- the browser was quietly substituting its own
          // untrimmed arithmetic and showing a size that had just been
          // rejected. Say the reason instead.
          if (sizing && sizing.lots == null && sizing.rejected) {
            return html`
              <div className="verdict-sizing verdict-sizing-unset">
                <span className="verdict-sizing-detail">
                  ${t(`sizing.rejected_${sizing.rejected}`,
                      t("sizing.too_small", "This setup risks more than your set % allows."))}
                </span>
              </div>`;
          }
          const riskDollars = balance * riskPct / 100;
          const lots = served ? served.lots
            : Math.floor((riskDollars / stopDistance) / 100 * 100) / 100;
          const actualRisk = served ? served.risk_amount : lots * 100 * stopDistance;
          const trimmed = served && served.volatility_multiplier
            && served.volatility_multiplier < 1;
          const halted = !!verdict.risk_halted;
          return html`
            <div className=${"verdict-sizing" + (halted ? " is-halted" : "")}>
              <span className="verdict-sizing-label">${t("sizing.size", "Size")}</span>
              ${lots >= 0.01 ? html`
                <b dir="ltr">${iso(lots.toFixed(2))} ${t("sizing.lot", "lot")}</b>
                <span className="verdict-sizing-detail">
                  ${/* The amount is what the stop actually costs after
                       flooring to the lot step and any volatility trim, so it
                       is NOT the chosen percentage. Stating it as one was a
                       small false equation on the number that matters most. */""}
                  ${formatTemplate(t("sizing.risks", "risks {amount} — you set {pct}% of {balance}"),
                    { amount: iso("$" + actualRisk.toFixed(0)), pct: iso(riskPct), balance: iso("$" + balance.toFixed(0)) })}
                </span>
                ${/* A user handed a smaller size than their own percentage
                     implies is owed the reason and the number. */""}
                ${trimmed && html`
                  <span className="verdict-sizing-trim">
                    ${formatTemplate(t("sizing.trimmed", "Trimmed to {mult}x — this market is {ratio}x its own normal range right now."),
                      { mult: iso(String(served.volatility_multiplier)),
                        ratio: iso(String(served.volatility_ratio)) })}
                  </span>`}` : html`
                <span className="verdict-sizing-detail">
                  ${t("sizing.too_small", "Below 0.01 lot at this stop — this setup risks more than your set % allows.")}
                </span>`}
              ${halted && html`
                <span className="verdict-sizing-halt">
                  ${t("positions.err_risk_halted", "Trading is halted by the system's risk limits.")}
                </span>`}
            </div>`;
        })()}

      ${/* Feature #22: how much confluence stands behind the proposed
           direction, with what earned the points.
           The caveat is part of the feature, and it used to live in a
           title= tooltip -- on the phone this product is actually used on,
           there is no hover, so the one sentence keeping a letter grade from
           reading as odds was invisible to everyone who mattered. It is a
           visible line now, and it carries the measurement rather than the
           worry: across 6,188 trades the correlation between this score and
           the result is -0.0005, and on two of the three styles the top
           bucket is the WORST performing one. The score is real information
           about agreement. It is not information about winning. */
        verdict.quality && !marketClosed && html`
        <div className="verdict-quality">
          <span className="verdict-quality-label">${t("quality.label", "Setup quality")}</span>
          <span className=${`verdict-quality-score grade-${verdict.quality.grade}`}>
            ${iso(verdict.quality.score)}<small>/100</small> · ${verdict.quality.grade}
          </span>
          <span className="verdict-quality-parts">
            ${(verdict.quality.components || []).map((c) => html`
              <span key=${c.key} className=${c.points < 0 ? "neg" : ""}>
                ${t(`quality.${c.key}`, c.key.replace(/_/g, " "))} ${iso(`${c.points > 0 ? "+" : ""}${c.points}`)}
              </span>`)}
          </span>
          <span className="verdict-quality-caveat">
            ${t("quality.caveat",
                "Agreement between readings, not odds of profit — measured across 6,188 trades, this score does not predict the result.")}
          </span>
        </div>`}

      ${rows.length > 0 && html`
        <div className="verdict-evidence">
          <div className="verdict-evidence-title">${t("verdict.rests_on", "What this rests on")}</div>
          ${rows.map((row) => html`
            <div className="evidence-row" key=${row.key}>
              <span className="evidence-label">${row.label}</span>
              <span className="evidence-value">
                ${/* The rows whose origin the system actually kept become
                     tappable. The rest render plainly -- claiming provenance
                     for a number that has none is the exact dishonesty this
                     mechanism exists to prevent. */""}
                <${Sourced} value=${row.value} label=${row.label}
                            formula=${row.formula} source=${row.source}
                            asOf=${row.asOf} note=${row.note} />
              </span>
              <span className=${`evidence-stance stance-${row.stance}`}>${stanceLabel[row.stance]}</span>
            </div>`)}
        </div>`}

      ${/* Taking the trade. A setup the pipeline stood aside can still be taken
           -- it is the user's money -- but the button says "Take it anyway"
           rather than "I took this trade", and the server records it with
           source='manual_override', so the two populations can be told apart
           when the record is measured. */
        setup && !inTrade && !marketClosed && canTake && html`
        <div className="verdict-actions">
          ${/* The recommended path stays one tap. The OVERRIDE path -- taking
               a trade the pipeline stood aside -- now asks first, restating
               the blocking evidence verbatim: it is the user's money, but the
               product does not let its own warning be skipped unread. */""}
          <button className=${"btn " + (status === "take" ? "btn-primary" : "btn-secondary")}
                  onClick=${() => (status === "take" ? onTake(false) : setOverrideConfirm(true))}
                  disabled=${taking}>
            <i className="fa-solid fa-circle-play"></i>
            ${taking
              ? t("positions.opening", "Opening…")
              : (status === "take"
                ? t("positions.take_trade", "I took this trade")
                : t("positions.take_anyway", "Take it anyway"))}
          </button>
          ${/* The decision nobody else records. A journal can only see the
               trades that happened; most of a trader's results live in the
               setups they passed on, and memory does not keep those honestly
               -- the skipped setup that would have won is never the one
               anybody remembers. One tap, before the outcome exists, which is
               the only moment the answer is not hindsight.

               It is NOT scored. Passing is a legitimate answer to a setup, and
               grading it would be the product claiming an edge it has not
               measured. */""}
          <button className=${"btn btn-quiet verdict-pass" + (answered === "passed" ? " is-answered" : "")}
                  disabled=${taking || answered === "passed"}
                  onClick=${() => onAnswer && onAnswer("passed")}>
            <i className=${answered === "passed" ? "fa-solid fa-check" : "fa-regular fa-circle"}></i>
            ${answered === "passed"
              ? t("positions.passed_done", "Noted — you passed this one")
              : t("positions.pass", "I passed this one")}
          </button>
          <p className="verdict-actions-note">
            ${status === "take"
              ? t("positions.take_note", "Records it and follows it to its stop or target.")
              : t("positions.override_note", "Recorded as an override, against the verdict above.")}
          </p>
          ${takeError && html`<p className="verdict-actions-error">${takeError}</p>`}
        </div>`}

      ${inTrade && html`
        <p className="verdict-actions-note verdict-actions-inline">
          ${t("positions.already_in", "Already following a position on this market.")}
        </p>`}

      <${Modal} open=${overrideConfirm}
        title=${t("positions.override_title", "Take it against the verdict?")}
        onClose=${() => setOverrideConfirm(false)}
        footer=${html`
          <button className="btn btn-secondary" onClick=${() => setOverrideConfirm(false)}>
            ${t("logout.cancel", "Cancel")}
          </button>
          <button className="btn btn-secondary" onClick=${() => { setOverrideConfirm(false); onTake(true); }}>
            ${t("positions.take_anyway", "Take it anyway")}
          </button>`}>
        <p>${reasonText}</p>
        ${rows.filter((row) => row.stance === "blocks" || row.stance === "opposes").map((row) => html`
          <p key=${row.key}><b>${row.label}</b> — ${row.value}</p>`)}
        <p>${t("positions.override_ack", "This is recorded as an override and is measured separately from the verdicts the product stands behind.")}</p>
      <//>
    </section>
  `;
}

// The dashboard's own section bar.
//
// The owner's report, and it is measurable: the Arabic dashboard renders about
// 3,660 pixels tall on a 1280x900 screen -- four screens of scrolling, with
// the market readings, the record and the news all below the fold. A page that
// long has no shape unless it tells you what is on it.
//
// TWO RULES IT KEEPS.
//
// It HIDES NOTHING. Every section stays rendered and in order; this jumps to
// them. A product whose whole position is "we tell you when there is no trade"
// cannot put the reason behind a tap.
//
// And it only lists sections that are ACTUALLY PRESENT. Half this page is
// conditional -- no position, no setup, no news -- and a bar advertising a
// section that is not there sends the reader to an empty space and teaches
// them the bar is unreliable.
function DashboardNav({ t }) {
  const SECTIONS = [
    { id: "dash-price", label: t("dashnav.price", "Price") },
    { id: "dash-verdict", label: t("dashnav.verdict", "The call") },
    { id: "dash-market", label: t("dashnav.market", "The market") },
    { id: "dash-record", label: t("dashnav.record", "The record") },
    { id: "dash-news", label: t("dashnav.news", "News") },
  ];
  const [present, setPresent] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    // Re-read after paint, and again as conditional blocks arrive: the setup
    // card and the position card appear on their own schedule.
    const scan = () => setPresent(
      SECTIONS.filter((s) => document.getElementById(s.id)).map((s) => s.id));
    scan();
    const timer = setInterval(scan, 4000);
    return () => clearInterval(timer);
  }, [t]);

  useEffect(() => {
    if (!present.length) return;
    const observer = new IntersectionObserver((entries) => {
      const seen = entries.filter((e) => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
      if (seen) setActive(seen.target.id);
    }, { rootMargin: "-96px 0px -60% 0px" });
    present.forEach((id) => {
      const node = document.getElementById(id);
      if (node) observer.observe(node);
    });
    return () => observer.disconnect();
  }, [present]);

  if (present.length < 2) return null;

  return html`
    <nav className="dash-nav" aria-label=${t("dashnav.aria", "Sections of this page")}>
      ${SECTIONS.filter((s) => present.includes(s.id)).map((s) => html`
        <a key=${s.id} href=${"#" + s.id}
           className=${"dash-nav-item" + (active === s.id ? " is-active" : "")}
           aria-current=${active === s.id ? "true" : undefined}
           onClick=${(e) => {
             e.preventDefault();
             const node = document.getElementById(s.id);
             if (!node) return;
             const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
             node.scrollIntoView({ behavior: still ? "auto" : "smooth", block: "start" });
           }}>${s.label}</a>`)}
    </nav>`;
}


function DashboardPage() {
  const { t, translateSignal, language } = useI18n();
  const { status } = useAuth();
  const [currentTimeframe, setCurrentTimeframe] = useState(() =>
    normalizeTimeframe(sessionStorage.getItem("preferredTimeframe"))
  );
  const [preferences, setPreferences] = useState(null);
  // The style switcher lives in Settings. A full second copy of its logic --
  // state, fetch and write callback -- sat here unrendered; see
  // attic/2026-08-15-dead-frontend.md.
  const [positions, setPositions] = useState(null);
  const [connection, setConnection] = useState({
    connected: false,
    label: t("connection.connecting", "Connecting...")
  });
  const [showMarketClosed, setShowMarketClosed] = useState(false);

  const socketRef = useRef(null);
  const [socketReady, setSocketReady] = useState(false);
  // Always the CURRENT timeframe, for handlers registered once at mount.
  const timeframeRef = useRef(currentTimeframe);
  timeframeRef.current = currentTimeframe;
  const { data, loadInitial, applyUpdate, loadState, styleRef } = useDashboardData(currentTimeframe);
  const market = useMarketStatus();
  const isMarketOpen = market.open === true;
  const newsSymbol = "XAUUSD.m";
  // The dashboard is a summary. Five headlines and "View All"; ten full
  // headlines here made the summary page longer than the news page.
  const { items: newsItems, loading: newsLoading, error: newsError, context: newsContext } = useNewsFeed(newsSymbol, language, 5);

  useEffect(() => {
    document.title = t("app.title", "Mithqal");
  }, [t]);

  useEffect(() => {
    if (status === "guest") {
      window.location.href = "/login";
    }
  }, [status]);

  useEffect(() => {
    if (status !== "authenticated") return;
    let isMounted = true;
    let healTimer = null;

    const run = async () => {
      const result = await loadInitial();
      if (!isMounted) return;
      if (!result.ok && currentTimeframe !== "1h") {
        const fallback = await loadInitial("1h");
        if (isMounted && fallback.ok) {
          setCurrentTimeframe("1h");
          if (socketRef.current && socketRef.current.connected) {
            socketRef.current.emit("change_timeframe", { timeframe: "1h" });
          }
          return;
        }
      }
      // Self-healing: a boot that hit a dead server retries every 6s until
      // it answers. The owner's console was a wall of CONNECTION_REFUSED
      // that a living page never recovered from without a manual refresh.
      if (!result.ok) {
        healTimer = setTimeout(run, 6000);
      }
    };

    run();
    return () => {
      isMounted = false;
      clearTimeout(healTimer);
    };
  }, [currentTimeframe, status, loadInitial]);

  // Trading preferences: which style this client trades. Replaces a dead
  // loadRiskSettings that called a route deleted from the backend and threw a
  // ReferenceError on every dashboard mount.
  const loadPreferences = useCallback(async () => {
    if (status !== "authenticated") return;
    try {
      const prefs = await (await authenticatedFetch("/api/preferences")).json();
      if (prefs.success) setPreferences(prefs.preferences);
    } catch (error) {
      console.warn("preferences unavailable", error);
    }
  }, [status]);


  const [performance, setPerformance] = useState(null);

  useEffect(() => {
    if (status !== "authenticated") return;
    // The push UI lives in Settings; this mount-time pass only re-syncs a
    // stale browser subscription (key rotation, cleared SW) so alerts keep
    // arriving without the user ever visiting Settings.
    authenticatedFetch("/api/push/config")
      .then((r) => r.json())
      .then((c) => reconcilePushState(c))
      .catch(() => {});
  }, [status]);


  useEffect(() => {
    if (status !== "authenticated") return;
    let alive = true;
    authenticatedFetch("/api/performance")
      .then((r) => r.json())
      .then((p) => { if (alive && p.success) setPerformance(p); })
      .catch(() => {});
    return () => { alive = false; };
  }, [status]);

  const loadPositions = useCallback(async () => {
    if (status !== "authenticated") return;
    try {
      const response = await authenticatedFetch("/api/positions");
      const payload = await response.json();
      if (payload.success) setPositions((prev) => (
        // Same book, same object: an identical poll result keeps the old
        // identity so the marker/price-line effect does not rebuild for it.
        prev && prev.success
          && JSON.stringify(prev.open_positions) === JSON.stringify(payload.open_positions)
          && JSON.stringify(prev.positions) === JSON.stringify(payload.positions)
          ? prev : payload));
    } catch (error) {
      console.warn("positions unavailable", error);
    }
  }, [status]);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  useEffect(() => {
    loadPositions();
    const timer = setInterval(loadPositions, 30000);
    return () => clearInterval(timer);
  }, [loadPositions]);

  // Taking a trade, and closing one. The server reads the levels from its own
  // live signal; nothing here sends prices, because a client that could name
  // its own entry and stop could write any performance record it wanted.
  const [tradeAction, setTradeAction] = useState({ taking: false, closing: false, error: null });
  const verdictAnswer = useVerdictAnswer(data?.verdict, currentTimeframe, data?.current_price);

  const handleTakeTrade = useCallback(async (override) => {
    setTradeAction({ taking: true, closing: false, error: null });
    try {
      const response = await authenticatedFetch("/api/positions/open", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // Name the timeframe being confirmed: the server-global one can be
        // moved by any client between the tap and the request.
        body: JSON.stringify({ override: !!override, timeframe: currentTimeframe })
      });
      const payload = await response.json();
      if (!payload.success) {
        setTradeAction({ taking: false, closing: false,
                         error: takeErrorMessage(t, payload.error) });
        return;
      }
      setTradeAction({ taking: false, closing: false, error: null });
      loadPositions();
    } catch (error) {
      setTradeAction({ taking: false, closing: false, error: error.message || "failed" });
    }
  }, [loadPositions, t, currentTimeframe]);

  const handleClosePosition = useCallback(async (tradeId) => {
    setTradeAction((prev) => ({ ...prev, closing: true, error: null }));
    try {
      const response = await authenticatedFetch(`/api/positions/${tradeId}/close`, { method: "POST" });
      const payload = await response.json();
      // Through the same translator as every other trade refusal: this one
      // was passing payload.error straight to the screen, so a close that
      // lost a race printed the literal "not_found" at the customer.
      setTradeAction({ taking: false, closing: false,
                       error: payload.success ? null
                         : takeErrorMessage(t, payload.error) });
      loadPositions();
    } catch (error) {
      setTradeAction({ taking: false, closing: false,
                       error: t("positions.err_unreachable",
                                "That did not reach the server. Nothing was changed.") });
    }
  }, [loadPositions, t]);

  const handleMoveStop = useCallback(async (tradeId, mode) => {
    setTradeAction((prev) => ({ ...prev, moving: true, error: null }));
    try {
      const response = await authenticatedFetch(`/api/positions/${tradeId}/stop`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode })
      });
      const payload = await response.json();
      setTradeAction({ taking: false, closing: false, moving: false,
                       error: payload.success ? null : takeErrorMessage(t, payload.error) });
      loadPositions();
    } catch (error) {
      setTradeAction({ taking: false, closing: false, moving: false,
                       error: error.message || "failed" });
    }
  }, [loadPositions, t]);

  const openPosition = positions?.open_positions?.[0] || null;

  // Read through refs: keying the socket on loadInitial (per-timeframe) or
  // t (per-language) tore down and rebuilt the whole long-polling session
  // on every timeframe switch and language flip. The socket outlives both.
  const loadInitialRef = useRef(loadInitial);
  useEffect(() => { loadInitialRef.current = loadInitial; }, [loadInitial]);
  const tRef = useRef(t);
  useEffect(() => { tRef.current = t; }, [t]);

  useEffect(() => {
    if (status !== "authenticated") return;

    // The server refuses unauthenticated sockets, so the access token travels
    // with the handshake. Without it the connection is dropped rather than
    // silently downgraded, which is the correct failure: a dashboard that
    // cannot receive updates must not look live.
    // Pinned to long-polling ON PURPOSE: under the threading dev server the
    // polling->websocket upgrade 500s, and the direct-websocket experiment
    // proved flaky enough to abandon. Polling is the transport that always
    // works here; when gunicorn+eventlet lands (infra backlog) this becomes
    // ["websocket", "polling"].
    const socket = io({ transports: ["polling"],
                        auth: { token: getAuthToken() || "" } });
    socketRef.current = socket;

    socket.on("connect", () => {
      setConnection({ connected: true, label: tRef.current("connection.connected", "Connected") });
      setSocketReady(true);
      // Through the ref: this effect is rebuilt on language changes, so the
      // closure's timeframe can be stale by the time a reconnect fires.
      socket.emit("change_timeframe", { timeframe: timeframeRef.current });
    });

    socket.on("disconnect", () => {
      // Socket.IO reconnects on its own, so this is a transient "trying" state,
      // not a dead end -- say so rather than "Disconnected". While it lasts the
      // real-time feed is down even if the last REST poll left a fresh-looking
      // timestamp, which is exactly why this has to be visible.
      setConnection({ connected: false, label: tRef.current("connection.reconnecting", "Reconnecting...") });
      setSocketReady(false);
    });

    socket.on("price_update", (update) => {
      // The broadcast carries the SERVER's current timeframe -- a global any
      // client can move. When it is not ours, take only the timeframe-agnostic
      // spot price and clock; applying its indicators/signals/verdict would
      // silently swap this dashboard onto someone else's chart. (Read through
      // a ref: this handler is registered once and would otherwise compare
      // against the mount-time timeframe forever.)
      if (update.timeframe && update.timeframe !== timeframeRef.current) {
        applyUpdate({
          current_price: update.current_price,
          price_change: update.price_change,
          price_change_pct: update.price_change_pct,
          last_update: update.last_update || null,
        });
        return;
      }
      // OUR style's card out of the fan-out payload. The room serves users on
      // different styles; taking the top-level (default-style) signals wiped
      // a non-default user's card seconds after every refresh.
      const own = update.by_style && styleRef.current
        ? update.by_style[styleRef.current] : null;
      applyUpdate({
        current_price: update.current_price,
        price_change: update.price_change,
        price_change_pct: update.price_change_pct,
        indicators: update.indicators || {},
        signals: (own ? own.signals : update.signals) || {},
        // The clock reads last_update. This handler used to drop it, so the
        // price ticked while the timestamp only moved on a full page load.
        last_update: update.last_update || null,
        // The server sends the verdict on every broadcast on purpose, so a live
        // update cannot revert the card to an un-vetoed direction. (market is
        // NOT taken from here -- the badge has its own useMarketStatus hook.)
        verdict: (own ? own.verdict : update.verdict) || undefined,
        stats: {
          high_24h: update.high,
          low_24h: update.low
        }
      });
    });

    socket.on("timeframe_changed", () => {
      loadInitialRef.current();
    });

    socket.on("error", (payload) => {
      if (payload && payload.message) {
      setConnection({ connected: socket.connected, label: payload.message });
    }
  });

    return () => {
      socket.disconnect();
    };
  }, [applyUpdate, status]);

  useEffect(() => {
    sessionStorage.setItem("preferredTimeframe", currentTimeframe);
  }, [currentTimeframe]);

  // The ONE place that tells the server which frame this tab is on. The server
  // now keeps a room per timeframe instead of one process-global, so a missed
  // announcement means this tab sits in the wrong room for the whole session.
  // Keyed on socketReady so it re-asserts after a reconnect rather than being
  // dropped by a `connected` guard.
  useEffect(() => {
    if (!socketReady || !currentTimeframe) return;
    socketRef.current?.emit("change_timeframe", { timeframe: currentTimeframe });
  }, [currentTimeframe, socketReady]);

  // handleAIAnalysis went with the gold button it existed for. Its one extra
  // behaviour — telling a customer the market is closed before sending them —
  // belonged to a button on the dashboard; the nav rail is navigation, and the
  // analysis page states the market's state itself when it opens.

  // null when the 24h change was not read. `>= 0` on an absent value is the
  // coercion this file has now paid for twice -- it reads as "up".
  const priceUp = data.price_change == null ? null : data.price_change >= 0;
  const indicatorsDisabled = !isMarketOpen;

  // "Market Sentiment" (rsi > 65 && macd > 0) and "Trend Strength" (the
  // MA20/MA50 spread) used to be computed here and rendered as two verdicts of
  // equal weight to the engine's. Both used thresholds that appear nowhere else
  // in the system, and neither had been through the news or yield veto, so the
  // page routinely disagreed with itself. They are gone; VerdictPanel presents
  // the engine's own ADX and tally as evidence under a single verdict instead.
  const volatility = useMemo(() => {
    const high = data.stats?.high_24h;
    const low = data.stats?.low_24h;
    if (!high || !low) return "Low";
    const range = high - low;
    const avg = (high + low) / 2;
    const pct = (range / avg) * 100;
    if (pct > 2) return "High";
    if (pct > 1) return "Medium";
    return "Low";
  }, [data.stats]);

    const handleOpenNews = useCallback((item) => {
      cacheNewsItem(item, language);
      window.location.href = `/news/${item.uuid}`;
    }, [language]);

  if (status !== "authenticated") {
    return null;
  }

  return html`
    <div className="app-shell">
      <nav className="top-nav">
        <a className="brand" href="/dashboard">
          <img className="brand-glyph" src="/static/img/mithqal-mark.svg"
               alt="" aria-hidden="true"
               style=${{ width: "26px", height: "26px" }} />
          <span className="brand-word">Mithqal</span>
        </a>
        ${/* The five destinations, on the laptop, where they were missing. */""}
        <${DesktopNav} />
        <div className="nav-actions">
          <!-- One primary action, gold. AI Analysis is the product's verb. -->
          ${/* THE GOLD BUTTON IS GONE. It was the desktop entry point to the
                AI page back when the desktop had no navigation — the comment
                on `.top-nav .nav-primary` says as much: it is hidden on the
                phone because "the AI tab IS the entry point; the gold top
                button would be the same verb twice on one screen". The
                desktop has that navigation now, so the same sentence applies
                at every width, and the owner asked the same question:
                why is the button repeated at all. */""}
          <${AlertsInbox} />
          <!-- Everything else is quiet, behind one overflow control at every
               width -- no raw OS select, no Refresh (live data is not a user
               action), no separate desktop row competing with the primary. -->
          <${NavMore} />
        </div>
      </nav>

      ${/* THE PRICE LEADS -- the owner's order: the first thing on the
           dashboard is the market itself; the briefing follows it. */""}
      <${DashboardNav} t=${t} />
      <section className="price-hero" id="dash-price">
        <div className="price-title">
          <div style=${{ display: "flex", alignItems: "center", gap: "10px" }}>
            <h1>${t("price.spot_gold", "Spot Gold (XAUUSD)")}</h1>
            ${/* Before the server has answered, say nothing. A badge that
                 guesses is how "MARKET OPEN" got shown on a closed Saturday. */
              market.open != null && html`
              <span className=${`badge ${market.open ? "success" : "warning"}`}>
                ${market.open ? t("market.open", "Market Open") : t("market.closed", "Market Closed")}
              </span>`}
          </div>
          ${data.current_price == null
            ? html`
              <div className="price-value is-placeholder" aria-busy="true">
                ${loadState.state === "error"
                  ? t("price.unavailable", "Price unavailable")
                  : t("price.loading", "Loading…")}
              </div>`
            : html`
              <div className="price-value">
                ${formatPrice(data.current_price)}
              </div>
              ${/* Three states. A change nobody read is not a change of zero,
                   and it must not wear market colour in either direction. */""}
              ${priceUp === null
                ? html`
                  <div className="price-change is-unknown">
                    ${t("price.change_unread", "24h change not available")}
                  </div>`
                : html`
                  <div className="price-change" style=${{ color: priceUp ? "var(--success)" : "var(--danger)" }}>
                    ${priceUp ? "+" : ""}${formatPrice(data.price_change)}
                    ${data.price_change_pct == null ? ""
                      : `(${priceUp ? "+" : ""}${data.price_change_pct.toFixed(2)}%)`}
                  </div>`}`}
          ${/* The brand made literal: the same spot, said in the units the
                region's gold actually trades in. 1 troy oz = 31.1035g;
                مثقال = 4.25g; 21k = 24k × 7/8. Pure arithmetic on the price
                already on screen -- no second source to disagree with. */""}
          ${data.current_price != null && html`
            <div className="gold-units-wrap">
              ${/* THE LABELS WERE "g 24k", "g 21k", "mithqal" and the owner's
                    verdict was that nobody can read them. Three separate
                    failures in one strip: the unit was abbreviated to a single
                    letter, the karat had no word attached to it, and "mithqal"
                    sat next to the product's own name so the third figure read
                    as "the app's price" rather than as a weight. A number
                    whose label needs explaining is a number nobody trusts.

                    The caption is what actually fixes it: these are not three
                    quotes, they are ONE quote said in the units the region's
                    gold is bought in — and saying so is what stops a reader
                    hunting for a source they will never find. */""}
              <small className="gold-units-cap">
                ${t("units.caption", "The same price, in the units gold is sold in here")}
              </small>
              <div className="gold-units" dir="ltr">
                <span>
                  <small>${t("units.gram24", "1 gram · 24k")}</small>
                  <b>${iso("$" + (data.current_price / 31.1034768).toFixed(2))}</b>
                </span>
                <span>
                  <small>${t("units.gram21", "1 gram · 21k")}</small>
                  <b>${iso("$" + (data.current_price / 31.1034768 * 7 / 8).toFixed(2))}</b>
                </span>
                <span>
                  ${/* The QUANTITY is the label, in the same grammar as the
                       two rows beside it. "Mithqal" alone named a unit the
                       reader may not know — and it is the product's own name,
                       so the third figure read as "the app's price". The
                       local name is a gloss now, not the label. */""}
                  <small>${t("units.mithqal", "4.25 grams · 24k (1 mithqal)")}</small>
                  <b>${iso("$" + (data.current_price / 31.1034768 * 4.25).toFixed(2))}</b>
                </span>
              </div>
            </div>`}
          ${loadState.stale && data.current_price != null && html`
            <div className="stale-flag">
              <i className="fa-solid fa-triangle-exclamation"></i>
              ${t("price.stale", "Last update failed — these numbers may be out of date")}
            </div>`}
          <${LivePriceAge} lastUpdate=${data.last_update} t=${t} />
          ${/* The socket state was tracked and never shown, so a dropped
               real-time feed looked identical to a live one. When it is down,
               say it here beside the freshness clock. */
            !connection.connected && html`
            <small className="conn-flag" role="status">
              <span className="conn-flag-dot" aria-hidden="true"></span>
              ${connection.label || t("connection.reconnecting", "Reconnecting...")}
            </small>`}
        </div>
        <dl className="price-facts">
          <div><dt>${t("price.24h_high", "24h High")}</dt><dd>${data.stats?.high_24h != null ? formatPrice(data.stats.high_24h) : "—"}</dd></div>
          <div><dt>${t("price.24h_low", "24h Low")}</dt><dd>${data.stats?.low_24h != null ? formatPrice(data.stats.low_24h) : "—"}</dd></div>
          ${market.open === false && market.next_change
            ? html`<div><dt>${t("market.reopens", "Opens")}</dt><dd className="is-text">${formatOpensIn(market.next_change, language)}</dd></div>`
            : (positions?.session && html`
                <div><dt>${t("styles.session", "Session")}</dt><dd className="is-text">${t("sessions." + positions.session.session, positions.session.label)}</dd></div>`)}
        </dl>
      </section>

      ${/* The session's story and the account floor, right under the price
           they contextualise. */""}
      ${/* The state, in the place the eye lands first, in the same three-state
            language the Telegram ticket uses. */""}
      <${StatusStrip} verdict=${data.verdict} timeframe=${currentTimeframe}
                      marketOpen=${market.open} />
      ${/* Which session is running, and whether this one moves — with the
            measured range on it rather than an adjective. */""}
      <${SessionRibbon} />
      <${SessionBriefing} timeframe=${currentTimeframe} />
      <${FloorStrip} />

      <div className="main-grid">
        <aside className=${`panel surface ${indicatorsDisabled ? "panel-disabled" : ""}`}>
          ${market.open === true && (data.signals?.buy?.stop_loss != null || data.signals?.sell?.stop_loss != null) && html`
            <h3><i className="fa-solid fa-bell"></i> ${t("signals.trading_signals", "Trading Calls")}</h3>`}
          ${(market.open === true ? ["buy", "sell"] : []).filter((side) => {
            // Render only a side that has a setup -- and nothing at all while
            // the market is closed, since a card here would show a tradeable
            // price the verdict has already declared stale.
            const sig = data.signals?.[side];
            return sig && sig.price != null && sig.stop_loss != null;
          }).map((side) => {
            const signal = data.signals?.[side];
            // A card only shows a price when the stop came with it. A level
            // without its risk control is not a recommendation.
            const complete = signal && signal.price != null && signal.stop_loss != null;
            return html`
              <div className=${`signal-card ${side}`} key=${side}>
                <div className="signal-card-head">
                  <div>
                    <div className="stat-label">${t(`signals.${side}`, side.toUpperCase())}</div>
                    <small style=${{ color: "var(--text-3)" }}>
                      ${side === "buy"
                        ? t("signals.long_entry", "Long entry")
                        : t("signals.short_entry", "Short entry")}
                    </small>
                  </div>
                  <div className="signal-value">
                    ${complete ? formatPrice(signal.price) : t("signals.no_signal", "No Call")}
                  </div>
                </div>
                ${complete && html`
                  <div className="signal-levels">
                    <span>${t("advisor.stop_loss", "Stop")} <b>${formatPrice(signal.stop_loss)}</b></span>
                    ${signal.take_profit != null && html`
                      <span>${t("advisor.take_profit", "Target")} <b>${formatPrice(signal.take_profit)}</b></span>`}
                    ${signal.risk_reward != null && html`
                      <span>${t("advisor.risk_reward", "R:R")} <b>${signal.risk_reward}:1</b></span>`}
                  </div>
                  ${signal.reason && html`<div className="signal-reason">${signal.reason}</div>`}
                `}
              </div>`;
          })}

          ${/* ONE QUESTION, ONE PLACE, TWO DEPTHS.
                The scored grid sat as its own panel directly above a fold
                asking "what do the raw indicators read?" — two surfaces about
                the indicators, each with its own tally, and the two tallies
                counted different things (the grid scores five indicators
                across frames; the accordion counts every oscillator on one).
                A reader comparing them found two answers to one question.
                They are one card now: the scored answer first, the raw list
                behind it, and only the grid keeps a headline count. */""}
          <${Fold} id="technicals" icon="fa-solid fa-chart-column"
                   title=${t("ask.technicals", "What do the raw indicators read?")}
                   hint=${(() => {
                     const s = data.summary;
                     if (!s) return null;
                     const buys = (s.oscillators?.Buy || 0) + (s.moving_averages?.Buy || 0);
                     const neutrals = (s.oscillators?.Neutral || 0) + (s.moving_averages?.Neutral || 0);
                     const sells = (s.oscillators?.Sell || 0) + (s.moving_averages?.Sell || 0);
                     if (!(buys || neutrals || sells)) return null;
                     return `${iso(buys)} ${t("technical_analysis.buy", "buy")} · ${iso(neutrals)} ${t("technical_analysis.neutral", "neutral")} · ${iso(sells)} ${t("technical_analysis.sell", "sell")}`;
                   })()}>
            <${IndicatorGrid} timeframe=${currentTimeframe} inline />
            <div className="panel ta-block">
              <p className="ta-raw-label">
                ${t("technical_analysis.raw", "Every reading, unscored")}
              </p>
              <${IndicatorsAccordion}
                extended=${data.extended_indicators}
                summary=${data.summary}
                translateSignal=${translateSignal}
                t=${t}
                disabled=${indicatorsDisabled}
              />
            </div>
          <//>
        </aside>

        <section>
          ${/* Everything at risk, before the card that shows one trade. */""}
          <${OpenExposure} exposure=${positions?.exposure} t=${t} />
          <${TradeCompanion} tradeId=${openPosition?.id} t=${t} />
          <${LivePositionCard}
            position=${openPosition}
            livePrice=${data.current_price}
            onClose=${handleClosePosition}
            closing=${tradeAction.closing}
            onMoveStop=${handleMoveStop}
            moving=${tradeAction.moving}
            actionError=${tradeAction.error}
            t=${t}
          />

          <a className="chart-cta" href="/chart">
            <span className="chart-cta-main">
              <i className="fa-solid fa-chart-line"></i>
              <b>${t("chart.open_chart", "Full chart")}</b>
            </span>
            <span className="chart-cta-meta">${currentTimeframe} · ${t("chart.with_plan", "levels and the plan")}</span>
            <i className="fa-solid fa-chevron-right chart-cta-go"></i>
          </a>

          <div id="dash-verdict"></div>
          <${VerdictPanel}
            verdict=${data.verdict}
            signals=${data.signals}
            summary=${data.summary}
            extended=${data.extended_indicators}
            volatility=${volatility}
            t=${t}
            newsContext=${newsContext}
            onTake=${handleTakeTrade}
            taking=${tradeAction.taking}
            takeError=${tradeAction.error}
            inTrade=${!!openPosition}
            marketClosed=${market.open !== true}
            insufficient=${data.insufficient_data}
            timeframe=${currentTimeframe}
            riskPrefs=${preferences}
            sizing=${data?.sizing}
            onAnswer=${verdictAnswer.answer}
            answered=${verdictAnswer.answered}
          />

          <${PatienceLine} />
          <${CooldownNotice} />
          <div id="dash-market"></div>
          <${PreparedLevels} timeframe=${currentTimeframe} t=${t} />
          <${PreTradeCard} timeframe=${currentTimeframe} />

          ${/* From here down: the trader's SECONDARY questions, in priority
                order, each folded behind the question itself. The page opens
                on the answers (position, verdict, plan) and the rest waits
                to be asked -- which is what "ordered by importance" means
                when the screen is a phone. */""}
          ${/* The panel's one home is the analysis page. A second entry
                point here would be a second place to spend thirteen model
                calls on the same question, which is the duplication the
                owner asked to be rid of — so the dashboard asks the question
                and links to where it is answered. */""}
          <a className="chart-cta" href="/analysis"
             onClick=${(e) => navigate(e, "/analysis")}>
            <span className="chart-cta-main">
              <i className="fa-solid fa-users-viewfinder"></i>
              <b>${t("ask.council", "What does the panel say?")}</b>
            </span>
            <span className="chart-cta-meta">${t("ask.council_meta", "Six experts per frame, and a chair")}</span>
            <i className="fa-solid fa-chevron-right chart-cta-go"></i>
          </a>
          <${Fold} id="macro" icon="fa-solid fa-globe"
                   title=${t("ask.macro", "What is moving gold?")}
                   hint=${data.verdict?.yield_trend === 1
                     ? `${t("evidence.yields", "10y yields")}: ${t("evidence.yields_up", "rising — a headwind for gold")}`
                     : data.verdict?.yield_trend === -1
                       ? `${t("evidence.yields", "10y yields")}: ${t("evidence.yields_down", "falling — a tailwind for gold")}`
                       : null}>
            <${MacroStrip} />
          <//>
          <${Fold} id="record" icon="fa-solid fa-scale-balanced"
                   title=${t("ask.record", "How is the record?")}
                   hint=${(() => {
                     const led = performance?.ledger;
                     if (!led || led.published == null) return null;
                     return `${iso(led.published)} ${t("performance.signals_word", "calls")}` +
                       (led.wins != null && led.losses != null
                         ? ` · ${iso(led.wins)}${t("performance.w_short", "W")}/${iso(led.losses)}${t("performance.l_short", "L")}` : "");
                   })()}>
            <div id="dash-record"></div>
            <${MeasuredStrip} performance=${performance} t=${t} />

        <${ReplayExercise} />
            <${VerdictHistory} timeframe=${currentTimeframe} />
            <${DisciplineLine} analytics=${positions?.analytics}
                               preferences=${preferences} t=${t} />
            ${/* The two columns under that one delta: a difference with no
                  columns beneath it is a claim, not a measurement. */""}
            <${Mirror} analytics=${positions?.analytics}
                       preferences=${preferences} t=${t} />
          <//>
          <${Fold} id="engine-today" icon="fa-solid fa-shield-halved"
                   title=${t("ask.engine_today", "What did the engine do today?")}>
            <${StandAsideReceipt} timeframe=${currentTimeframe} />
          <//>
          <${Fold} id="research" icon="fa-solid fa-magnifying-glass-chart"
                   title=${t("ask.research", "What has happened before like this?")}>
            <${ResearchScanner} />
          <//>
          <${ChatLauncher} timeframe=${currentTimeframe} />

          <${Fold} id="news" icon="fa-solid fa-newspaper"
                   title=${t("ask.news", "What are the headlines saying?")}>
          <div className="surface panel news-section" style=${{ marginTop: "8px" }}>
            <div className="news-header">
              <div>
                <h3><i className="fa-solid fa-newspaper"></i> ${t("news.title", "Market News")}</h3>
                <p style=${{ color: "var(--text-3)" }}>${t("news.subtitle", "Latest headlines affecting gold")}</p>
              </div>
              <button className="btn btn-secondary" onClick=${() => window.location.href = "/news"}>
                ${t("news.view_all", "View All")}
              </button>
            </div>

            ${newsLoading && html`
              <div className="news-state">
                <i className="fa-solid fa-spinner fa-spin"></i>
                <span>${t("news.loading", "Loading news...")}</span>
              </div>
            `}
            ${newsError && html`<div className="alert">${t("news.error", "Unable to load news right now.")}</div>`}
            ${!newsLoading && !newsError && newsItems.length === 0 && html`
              <div className="news-state">${t("news.empty", "No news available.")}</div>
            `}

            <div id="dash-news"></div>
            <${NewsList}
              items=${newsItems}
              onOpen=${handleOpenNews}
              t=${t}
              context=${newsContext}
            />
          </div>
          <//>
        </section>
      </div>


      <${Modal}
        open=${showMarketClosed}
        title=${t("market.ai_unavailable_title", "AI Analysis Unavailable")}
        onClose=${() => setShowMarketClosed(false)}
      >
        <p>${t("market.ai_unavailable_message", "AI Analysis is only available during active trading hours.")}</p>
        <div style=${{ marginTop: "12px" }}>
          <p><strong>${t("market.opens", "Opens: Sunday 5 PM EST")}</strong></p>
          <p><strong>${t("market.closes", "Closes: Friday 5 PM EST")}</strong></p>
        </div>
      </${Modal}>

      <${WelcomeTour} />

    </div>
  `;
}

// Settings. Trading style, alerts and the measured record are configuration,
// not content -- they were sitting at the foot of the dashboard, below the
// news, where a user scrolled past them to reach nothing. They live here now,
// reachable from the bottom bar.
// App-level preferences. Settings held only trading configuration; how the
// app itself looks and behaves belongs to the user too.
function AppSettings() {
  const { t, language, setLanguage } = useI18n();
  const [choice, setChoice] = useState(readThemeChoice);

  const pick = useCallback((next) => {
    setChoice(next);
    try { localStorage.setItem("theme", next); } catch (e) {}
    applyTheme(next);
  }, []);

  // Following the system means following it when it changes, not only at load.
  useEffect(() => {
    if (choice !== "system") return;
    const query = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = () => applyTheme("system");
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, [choice]);

  const options = [
    { key: "light", icon: "fa-sun", label: t("settings.light", "Light") },
    { key: "dark", icon: "fa-moon", label: t("settings.dark", "Dark") },
    { key: "system", icon: "fa-circle-half-stroke", label: t("settings.system", "Auto") },
  ];

  return html`
    <section className="panel">
      <h3><i className="fa-solid fa-palette"></i> ${t("settings.appearance", "Appearance")}</h3>
      ${/* Language lived only in the overflow menu, two taps from anywhere and
           findable only by opening a kebab and reading it. It is a setting;
           it belongs in settings, next to the other one about how the app
           looks. It stays in the menu too -- somebody who cannot read the
           current language needs it reachable from the first screen. */""}
      <div className="setting-row">
        <div className="setting-copy">
          <strong>${t("settings.language", "Language")}</strong>
          <span>${t("settings.language_note", "The whole app, including the direction it reads.")}</span>
        </div>
        <div className="seg-choice" role="group" aria-label=${t("settings.language", "Language")}>
          ${[["en", "English"], ["ar", "العربية"]].map(([code, label]) => html`
            <button key=${code} className=${language === code ? "is-active" : ""}
                    aria-pressed=${language === code}
                    onClick=${() => { setLanguage(code); persistLanguage(code); }}>
              ${label}
            </button>`)}
        </div>
      </div>
      <div className="setting-row">
        <div className="setting-copy">
          <strong>${t("settings.theme", "Theme")}</strong>
          <span>${t("settings.theme_note", "Auto follows your device. Dark is the default: the candles carry the only strong colour on the screen.")}</span>
        </div>
        <div className="seg" role="group" aria-label=${t("settings.theme", "Theme")}>
          ${options.map((option) => html`
            <button key=${option.key} className=${choice === option.key ? "is-active" : ""}
                    aria-pressed=${choice === option.key}
                    onClick=${() => pick(option.key)}>
              <i className=${`fa-solid ${option.icon}`}></i>
              <span>${option.label}</span>
            </button>`)}
        </div>
      </div>
    </section>`;
}

// Changing a password is a once-a-year act that was occupying a third of the
// first screen, above the risk settings a trader actually revisits. It keeps
// its own section at the BOTTOM, closed until asked for.
function AccountSection() {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  return html`
    <section className="panel">
      <h3><i className="fa-solid fa-user"></i> ${t("settings.account", "Account")}</h3>
      <div className="setting-row">
        <div className="setting-copy">
          <strong>${user?.username || "—"}</strong>
          <span>${t("settings.account_note", "Your sign-in. Changing your password ends every other session.")}</span>
        </div>
        <button className="btn btn-secondary" onClick=${() => setOpen((v) => !v)}
                aria-expanded=${open}>
          ${t("password.change", "Change password")}
        </button>
      </div>
      ${open && html`<${ChangePassword} onDone=${() => setOpen(false)} />`}
      <div className="setting-row">
        <div className="setting-copy">
          <strong>${t("settings.tour", "The welcome tour")}</strong>
          <span>${t("settings.tour_note", "Four short cards explaining how the dashboard thinks.")}</span>
        </div>
        <button className="btn btn-secondary"
                onClick=${() => {
                  try { localStorage.removeItem("sv_tour_done"); } catch (e) { /* private mode */ }
                  window.location.href = "/dashboard";
                }}>
          ${t("settings.tour_replay", "Replay")}
        </button>
      </div>
      <div className="setting-row">
        <div className="setting-copy">
          <strong>${t("settings.wizard", "The setup wizard")}</strong>
          <span>${t("settings.wizard_note", "Style, risk and alerts, one step at a time. Your current answers stay until you change them.")}</span>
        </div>
        <button className="btn btn-secondary"
                onClick=${() => {
                  try { localStorage.setItem("sv_wizard_replay", "1"); } catch (e) { /* private mode */ }
                  window.location.href = "/dashboard";
                }}>
          ${t("settings.wizard_rerun", "Run again")}
        </button>
      </div>
      ${/* The moment a phone goes missing: end every OTHER session. Same
            epoch mechanism as login's single-device rule, aimed BY the user.
            This tab gets a fresh token; every other device dies. */""}
      <div className="setting-row">
        <div className="setting-copy">
          <strong>${t("settings.kill_others", "Sign out everywhere else")}</strong>
          <span>${t("settings.kill_others_note", "Ends every session except this one — the button for a lost phone.")}</span>
        </div>
        <button className="btn btn-secondary"
                onClick=${async () => {
                  try {
                    const r = await authenticatedFetch("/api/auth/logout-others", { method: "POST" });
                    const p = await r.json();
                    if (p.success && p.access_token) {
                      try { sessionStorage.setItem("access_token", p.access_token); } catch (e) {}
                      alert(t("settings.kill_others_done", "Done — every other session is signed out."));
                    }
                  } catch (e) { /* the row stays; nothing changed */ }
                }}>
          <i className="fa-solid fa-shield-halved" aria-hidden="true"></i>
          ${t("settings.kill_others_btn", "End other sessions")}
        </button>
      </div>
      ${/* Sign-out lived only in the kebab; the ACCOUNT section is where a
            person looks for the door. */""}
      <div className="setting-row">
        <div className="setting-copy">
          <strong>${t("settings.signout", "Sign out")}</strong>
          <span>${t("settings.signout_note", "Ends this session on this device only.")}</span>
        </div>
        <button className="btn btn-secondary" onClick=${logout}>
          <i className="fa-solid fa-arrow-right-from-bracket" aria-hidden="true"></i>
          ${t("nav.logout", "Sign out")}
        </button>
      </div>
    </section>`;
}

// Changing your own password -- the first thing the owner reported missing.
// The current password is required so a stolen session cannot take the account
// over, and the server drops every OTHER session on success.
function ChangePassword({ forced = false, onDone }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [state, setState] = useState({ busy: false, error: null, ok: false });

  const errorText = (code) => ({
    wrong_password: t("password.err_wrong", "That is not your current password."),
    too_short: t("password.err_short", "Use at least 8 characters."),
    same_as_current: t("password.err_same", "That is the password you already have."),
    same_as_username: t("password.err_username", "Do not use your username as your password."),
    padded: t("password.err_padded", "Remove the spaces at the start or end."),
    mismatch: t("password.err_mismatch", "The two new passwords do not match."),
  }[code] || t("password.err_failed", "Could not change the password."));

  const submit = useCallback(async (event) => {
    event.preventDefault();
    if (form.next !== form.confirm) {
      setState({ busy: false, error: errorText("mismatch"), ok: false });
      return;
    }
    setState({ busy: true, error: null, ok: false });
    try {
      const response = await authenticatedFetch("/api/auth/change-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ current_password: form.current, new_password: form.next })
      });
      const payload = await response.json();
      if (payload.success) {
        setForm({ current: "", next: "", confirm: "" });
        setState({ busy: false, error: null, ok: true });
        if (onDone) onDone();
      } else {
        setState({ busy: false, error: errorText(payload.error), ok: false });
      }
    } catch (error) {
      setState({ busy: false, error: errorText("failed"), ok: false });
    }
  }, [form, onDone, t]);

  return html`
    <section className="panel">
      <h3><i className="fa-solid fa-key"></i> ${t("password.title", "Password")}</h3>
      ${forced && html`
        <p className="password-forced" role="alert">
          ${t("password.forced", "This is a temporary password issued by the operator. Choose your own before continuing.")}
        </p>`}
      <form className="password-form" onSubmit=${submit}>
        <label>
          <small>${t("password.current", "Current password")}</small>
          <input type="password" autoComplete="current-password" required
                 value=${form.current}
                 onChange=${(e) => setForm({ ...form, current: e.target.value })} />
        </label>
        <label>
          <small>${t("password.new", "New password")}</small>
          <input type="password" autoComplete="new-password" required minLength="8"
                 value=${form.next}
                 onChange=${(e) => setForm({ ...form, next: e.target.value })} />
        </label>
        <label>
          <small>${t("password.confirm", "Repeat new password")}</small>
          <input type="password" autoComplete="new-password" required minLength="8"
                 value=${form.confirm}
                 onChange=${(e) => setForm({ ...form, confirm: e.target.value })} />
        </label>
        <button className="btn btn-primary" type="submit" disabled=${state.busy}>
          ${state.busy ? t("password.saving", "Saving…") : t("password.save", "Change password")}
        </button>
      </form>
      ${state.error && html`<p className="password-note is-error" role="alert">${state.error}</p>`}
      ${state.ok && html`
        <p className="password-note is-ok" role="status">
          ${t("password.changed", "Password changed. Your other sessions were signed out.")}
        </p>`}
    </section>
  `;
}

// The customer's own trading rules (spec §59). Without this screen the whole
// rule guardian is INERT: /api/constitution stored rules, the pre-trade card
// checked them, and nothing could ever create one -- a feature that was built,
// tested and unreachable. Found by docs/QA-LAW.md §1.2, run against my own work.
//
// The product never writes a rule and never suggests one. A rule it supplied
// would carry an implicit claim that following it improves results, and the
// record supports no such claim about anything.
// The size calculator (master list 371-384). It sits in Settings beside the
// balance and risk percent it reads, because that is where those numbers are
// already on screen and a calculator on another page is one nobody opens.
//
// Server-side arithmetic on purpose: one implementation answers this form, the
// ticket and anything added later. Two implementations of a lot size is how a
// customer is shown one number and sent another.
/* SizeCalculator lived here: a typed entry/stop/target form that asked
   /api/tools/position-size the same question the risk ruler asks, and drew the
   answer in a different shape directly beneath it. Its two capabilities the
   ruler lacked — a typed entry and an optional target with its R:R — moved
   into the ruler, and the form was deleted rather than left unmounted. A
   component nothing renders is the defect this codebase has paid for more
   than once; keeping a second implementation of a lot size "just in case" is
   how two surfaces come to tell one customer different numbers about one
   order. Recover it from git if the tactile version ever proves wrong. */

const RULE_KIND_LABELS = {
  max_risk_percent: "Maximum risk per trade (%)",
  no_news_within_minutes: "No trading within N minutes of red news",
  sessions_allowed: "Sessions I allow myself",
  styles_allowed: "Styles I allow myself",
  max_trades_per_day: "Maximum trades per day",
  min_reward_risk: "Minimum reward to risk",
};

function TradingRules() {
  const { t } = useI18n();
  const [rules, setRules] = useState(null);
  const [kinds, setKinds] = useState([]);
  const [draft, setDraft] = useState({ kind: "max_risk_percent", value: "1", text: "" });
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await (await authenticatedFetch("/api/constitution")).json();
      if (r.success) { setRules(r.rules || []); setKinds(r.checkable_kinds || []); }
      else setProblem(t("rules.unreadable", "Your rules could not be read."));
    } catch (e) {
      setProblem(t("rules.unreadable", "Your rules could not be read."));
    }
  }, [t]);
  useEffect(() => { load(); }, [load]);

  const add = useCallback(async (event) => {
    event.preventDefault();
    setBusy(true); setProblem(null);
    try {
      const response = await authenticatedFetch("/api/constitution", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          kind: draft.kind, text: draft.text,
          // A list-valued rule is typed as a comma list; everything else is a
          // number. The server refuses anything it cannot use.
          value: draft.kind.endsWith("_allowed")
            ? draft.value.split(",").map((v) => v.trim()).filter(Boolean)
            : Number(draft.value),
        }),
      });
      const payload = await response.json();
      if (!payload.success) {
        setProblem(payload.detail || t("rules.refused", "That rule was refused."));
      } else { setDraft({ ...draft, text: "" }); await load(); }
    } catch (e) {
      setProblem(t("rules.unreachable", "That did not reach the server."));
    }
    setBusy(false);
  }, [draft, load, t]);

  const remove = useCallback(async (id) => {
    try {
      await authenticatedFetch(`/api/constitution/${id}`, { method: "DELETE" });
      await load();
    } catch (e) { /* the list simply does not change */ }
  }, [load]);

  const toggle = useCallback(async (rule) => {
    try {
      await authenticatedFetch(`/api/constitution/${rule.id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: !rule.enabled }),
      });
      await load();
    } catch (e) { /* the list simply does not change */ }
  }, [load]);

  if (rules === null) return null;

  return html`
    <section className="panel" id="rules">
      <h3><i className="fa-solid fa-scroll"></i> ${t("rules.title", "Your own rules")}</h3>
      <p className="settings-hint">
        ${t("rules.intro", "Write the rules you already have. Before a setup, the app tells you which of them it conflicts with — it never blocks you, and it never adds a rule of its own.")}
      </p>
      ${problem && html`<p className="note-bad" role="alert">${problem}</p>`}

      ${rules.length === 0 && html`
        <p className="positions-empty">
          ${t("rules.none", "No rules yet. The ones people break most often are a maximum risk per trade and no trading through high-impact news.")}
        </p>`}

      ${rules.length > 0 && html`
        <ul className="rules-list">
          ${rules.map((rule) => html`
            <li key=${rule.id} className=${rule.enabled ? "" : "is-off"}>
              <span className="rule-text">${rule.text}</span>
              ${!rule.checkable && html`
                <small className="rule-unchecked">
                  ${t("rules.not_checkable", "Kept, but the app cannot check this one automatically.")}
                </small>`}
              <span className="rule-actions">
                <button type="button" onClick=${() => toggle(rule)}>
                  ${rule.enabled ? t("rules.pause", "Pause") : t("rules.resume", "Resume")}
                </button>
                <button type="button" className="btn-ghost" onClick=${() => remove(rule.id)}>
                  ${t("rules.delete", "Delete")}
                </button>
              </span>
            </li>`)}
        </ul>`}

      <form className="rules-form" onSubmit=${add}>
        <label>
          <span>${t("rules.kind", "Rule")}</span>
          <select value=${draft.kind}
                  onChange=${(e) => setDraft({ ...draft, kind: e.target.value })}>
            ${kinds.map((k) => html`
              <option key=${k} value=${k}>${t(`rules.kind_${k}`, RULE_KIND_LABELS[k] || k)}</option>`)}
          </select>
        </label>
        <label>
          <span>${t("rules.value", "Value")}</span>
          <input value=${draft.value}
                 onInput=${(e) => setDraft({ ...draft, value: e.target.value })} />
        </label>
        <label className="rules-sentence">
          <span>${t("rules.text", "In your own words")}</span>
          <input value=${draft.text} maxLength=${300}
                 placeholder=${t("rules.text_ph", "Never more than 1% — I always break this")}
                 onInput=${(e) => setDraft({ ...draft, text: e.target.value })} />
        </label>
        <button type="submit" className="btn btn-primary"
                disabled=${busy || !draft.text.trim()}>
          ${busy ? t("rules.saving", "Saving…") : t("rules.add", "Add rule")}
        </button>
      </form>
    </section>`;
}

function SettingsPage({ section = "app" }) {
  const { t, language } = useI18n();
  const { status, can } = useAuth();
  const [preferences, setPreferences] = useState(null);
  const [tradingStyles, setTradingStyles] = useState([]);
  const [styleSaving, setStyleSaving] = useState(false);
  // Declared here because the save path below calls setStyleError on both
  // branches and the render reads styleError. It was missing: every failed
  // style change threw ReferenceError instead of showing the refusal, so the
  // one error surface this screen has crashed exactly when it was needed.
  const [styleError, setStyleError] = useState(null);
  const [positions, setPositions] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [push, setPush] = useState({ enabled: false, subscribed: false, busy: false, note: null });
  const [sound, setSound] = useState(alertSoundChoice());
  const [loadFailed, setLoadFailed] = useState(false);
  const [riskError, setRiskError] = useState(null);

  useEffect(() => {
    document.title = t(`settings.title_${section}`,
      section === "trading" ? "Trading & risk — Mithqal"
      : section === "alerts" ? "Alerts — Mithqal"
      : section === "account" ? "Account — Mithqal"
      : "Settings — Mithqal");
  }, [t, section]);

  useEffect(() => {
    if (status === "guest") window.location.href = "/login";
  }, [status]);

  const load = useCallback(async () => {
    if (status !== "authenticated") return;
    try {
      const [prefsRes, stylesRes, posRes, perfRes] = await Promise.all([
        authenticatedFetch("/api/preferences"),
        authenticatedFetch("/api/trading-styles"),
        authenticatedFetch("/api/positions"),
        authenticatedFetch("/api/performance"),
      ]);
      const prefs = await prefsRes.json();
      const styles = await stylesRes.json();
      const pos = await posRes.json();
      const perf = await perfRes.json();
      if (prefs.success) setPreferences(prefs.preferences);
      if (styles.success) setTradingStyles(styles.styles || []);
      if (pos.success) setPositions(pos);
      if (perf.success) setPerformance(perf);
      // ANY of the four failing hides a section; all four count.
      setLoadFailed(!(prefs.success && styles.success && pos.success && perf.success));
    } catch (error) {
      // A silent catch here made every section simply VANISH on a failed
      // load -- a settings page with no settings and no explanation. Say so.
      setLoadFailed(true);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const changeStyle = useCallback(async (styleKey) => {
    setStyleSaving(true);
    try {
      const response = await authenticatedFetch("/api/preferences", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ trading_style: styleKey })
      });
      const payload = await response.json();
      // A refusal has to be SAID. This kept the previous selection and told
      // nobody, so a 500 left the customer believing they had switched the
      // setting that decides which signals reach them, which alerts fire, and
      // which record they are measured against. The load paths on this page
      // do this correctly; the write path had no error surface at all.
      if (payload && payload.success) {
        setPreferences(payload.preferences);
        setStyleError(null);
      } else {
        setStyleError(t("styles.save_failed",
          "That did not save — you are still on your previous style. Try again."));
      }
    } catch (error) {
      setStyleError(t("styles.save_offline",
        "The change did not reach the server. You are still on your previous style."));
    }
    finally { setStyleSaving(false); }
  }, []);

  // Balance / risk% for the size calculator. Optimistic local update so typing
  // feels immediate; the server's validated copy replaces it on response.
  //
  // An optimistic update MUST be reverted when the server refuses, and this
  // one was not. preferences.py rejects risk_percent outside (0, 5] and
  // app.py returns 400 — but the branch only acted on success, so the field
  // kept showing the rejected 8 while the server held 1, and the whole
  // position-size calculator below it was sized from a number the customer
  // could see and did not have. A screen that shows unsaved state as saved is
  // worse than one that fails loudly.
  const saveRiskPrefs = useCallback(async (patch) => {
    let before = null;
    setPreferences((prev) => { before = prev; return prev ? { ...prev, ...patch } : prev; });
    setRiskError(null);
    try {
      const response = await authenticatedFetch("/api/preferences", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch)
      });
      const payload = await response.json();
      if (payload.success) { setPreferences(payload.preferences); return; }
      setPreferences(before);
      setRiskError(payload.detail || payload.error
        || t("sizing.refused", "That value was not accepted."));
    } catch (error) {
      setPreferences(before);
      setRiskError(t("sizing.unreachable", "That did not reach the server, so nothing changed."));
    }
  }, [t]);

  const refreshPush = useCallback(async () => {
    try {
      const r = await authenticatedFetch("/api/push/config");
      const p = await r.json();
      const state = await reconcilePushState(p);
      setPush((prev) => ({ ...prev, enabled: !!p.enabled,
                           subscribed: state.subscribed, stale: state.stale }));
    } catch (e) { /* alerts simply show as unavailable */ }
  }, []);

  useEffect(() => { refreshPush(); }, [refreshPush]);

  const togglePush = useCallback(async () => {
    setPush((prev) => ({ ...prev, busy: true, note: null }));
    try {
      if (push.subscribed) {
        await disablePushNotifications();
        setPush((prev) => ({ ...prev, busy: false, subscribed: false }));
      } else {
        // Real helper returns {ok, reason}; the old code called undefined
        // enablePush()/disablePush() and tested `outcome === true`, so the
        // only rendered push toggle in the product never worked at all.
        const result = await enablePushNotifications();
        setPush((prev) => ({ ...prev, busy: false, subscribed: !!result.ok,
                             note: result.ok ? null : (result.reason || "failed") }));
      }
    } catch (e) {
      setPush((prev) => ({ ...prev, busy: false, note: "failed" }));
    }
  }, [push.subscribed]);

  // The self-test: one real notification to THIS device, on demand. Push has
  // several silent failure points (OS-level denial, an iOS tab never added to
  // the home screen, a dropped subscription) and none of them surface until a
  // real signal is missed -- the worst possible moment to learn about them.
  const sendTestAlert = useCallback(async () => {
    setPush((prev) => ({ ...prev, busy: true, note: null }));
    try {
      const response = await authenticatedFetch("/api/push/test", { method: "POST" });
      const data = await response.json().catch(() => ({}));
      setPush((prev) => ({ ...prev, busy: false,
                           note: data.success ? "sent" : "server_refused" }));
    } catch (e) {
      setPush((prev) => ({ ...prev, busy: false, note: "no_server" }));
    }
  }, []);

  return html`
    <div className="app-shell">
      <${PageNav} />

      <div className="settings-body">
      ${loadFailed && html`
        <section className="panel surface" role="alert">
          <p style=${{ margin: '0 0 10px 0' }}>
            <i className="fa-solid fa-triangle-exclamation"></i>
            ${" "}${t("settings.load_failed", "Could not load your settings — the server did not answer.")}
          </p>
          <button className="btn btn-secondary" onClick=${load}>
            ${t("analysis.retry", "Try again")}
          </button>
        </section>`}
      ${preferences && section === "trading" && html`
        <section className="panel style-panel">
          <div className="panel-head">
            <h3><i className="fa-solid fa-sliders"></i> ${t("styles.title", "Trading Style")}</h3>
            ${positions?.session && html`
              <small style=${{ color: "var(--text-3)" }}>
                ${t("sessions." + positions.session.session, positions.session.label)} · ${t("sessions.liq_" + positions.session.liquidity, positions.session.liquidity)}
              </small>`}
          </div>
          <div className="style-options">
            ${tradingStyles.map((style) => html`
              <button
                key=${style.key}
                className=${"style-option" + (preferences.trading_style === style.key ? " active" : "")}
                disabled=${styleSaving}
                onClick=${() => changeStyle(style.key)}
                title=${language === "ar" ? style.description_ar : style.description_en}
              >
                <span className="style-name">${language === "ar" ? style.name_ar : style.name_en}</span>
                <span className="style-meta" dir="ltr">${style.default_timeframe} · ${style.reward_risk_ratio}:1</span>
                ${/* The description lived only in a title= tooltip, which does
                     not exist on a touch screen -- so on the device this
                     product is built for, the three cards a user must choose
                     between explained nothing about themselves. */""}
                <span className="style-desc">
                  ${language === "ar" ? style.description_ar : style.description_en}
                </span>
                ${/* The bar the style must ACTUALLY clear. breakeven_win_rate is the
     geometric 100/(1+RR) -- true only if every trade resolves at a level,
     and 8% of scalping trades do. The realised bar is 4.9 points higher,
     and showing the easy one beside an achieved win rate is exactly how a
     style under water reads as an edge. Three other surfaces were fixed
     for this today; this is the screen where a customer CHOOSES. */""}
                <span className="style-meta">${t("styles.needs", "needs")} <span dir="ltr">${
                  style.realized_breakeven_win_rate ?? style.breakeven_win_rate}%</span></span>
              </button>`)}
          </div>

          ${/* Sizing inputs: the verdict's size row is computed from these.
               Stored per user; the calculator never renders until a balance
               exists, because a lot size invented from a default balance is a
               number pretending to be advice. */""}
          <div className="alerts-row risk-row">
            <div className="alerts-copy">
              <strong>${t("sizing.title", "Position sizing")}</strong>
              <span>${t("sizing.note", "Sets the lot size shown with each setup: balance × risk % ÷ stop distance. Informational — always verify in your own platform.")}</span>
            </div>
            ${riskError && html`
              <p className="note-bad" role="alert">${riskError}</p>`}
            <div className="risk-inputs" dir="ltr">
              <label>
                <small>${t("sizing.balance", "Balance ($)")}</small>
                <input type="number" min="0" step="100" inputMode="decimal"
                       value=${preferences.account_balance ?? ""}
                       onChange=${(e) => saveRiskPrefs({ account_balance: e.target.value === "" ? 0 : Number(e.target.value) })} />
              </label>
              <label>
                <small>${t("sizing.risk", "Risk %")}</small>
                <input type="number" min="0.1" max="5" step="0.1" inputMode="decimal"
                       value=${preferences.risk_percent ?? 1}
                       onChange=${(e) => saveRiskPrefs({ risk_percent: Number(e.target.value) })} />
              </label>
            </div>
          </div>

          ${styleError && html`<p className="style-error" role="alert">${styleError}</p>`}
          ${performance && preferences && html`
            <div className="measured-record">
              ${(() => {
                const m = performance.backtest?.styles?.[preferences.trading_style];
                if (!m) return null;
                // The interval spans zero, so the sign of this number carries
                // no information. Painting it green said "this made money" one
                // line above a sentence saying it cannot be shown to have done
                // so -- the interface contradicting its own disclosure, which
                // is the one thing this product cannot afford to do. A result
                // earns a colour only when its interval excludes zero.
                const conclusive = m.ci_excludes_zero === true;
                const positive = m.expectancy_r > 0;
                const bt = performance.backtest;
                return html`
                  <div className="measured-head">
                    <span>${t("performance.measured", "Measured on 3 years of history")}</span>
                    <span className="measured-sample">${m.trades} ${t("performance.trades", "trades")}</span>
                  </div>
                  <div className="measured-row">
                    <span>${t("performance.achieved", "Win rate achieved")}<b>${m.win_rate}%</b></span>
                    <span>${t("performance.needed", "Needed to break even")}<b>${
                      m.realized_breakeven_win_rate ?? m.breakeven_win_rate}%</b></span>
                    <span>${t("performance.expectancy", "Per trade")}<b className=${conclusive ? (positive ? "pos" : "neg") : "inconclusive"}>${iso(`${positive ? "+" : ""}${m.expectancy_r}R`)}</b></span>
                  </div>
                  <p className="measured-caveat">
                    ${t("performance.caveat", "The 95% confidence interval spans zero: this sample cannot show that the result is better than chance, in either direction.")}
                  </p>
                  ${/* A measurement without its run date and code version is
                        a claim. And when the engine has moved past the run,
                        the record says so instead of wearing a currency it
                        no longer has. */ ''}
                  ${bt?.measured_at && html`
                    <p className="measured-caveat" dir="ltr">
                      ${t("performance.measured_stamp", "Run")} ${bt.measured_at} · ${bt.measured_engine}
                      ${bt.stale ? html` — ${t("performance.measured_stale", "the engine has changed since; re-measurement pending")}` : ""}
                    </p>`}`;
              })()}

              ${/* THE OTHER TWO PROFILES, ON THE SCREEN WHERE THE CHOICE IS MADE.
                    Their measured rows arrive in this same payload and were
                    discarded, so a customer picked between three profiles
                    while seeing the record of exactly one — and the one they
                    were already on.

                    Every row carries its interval, including the ones that
                    span zero, and none of them is ranked or highlighted: all
                    three are `no_demonstrated_edge` in data/measurement.json,
                    so a table that implied one was better would be a claim
                    this product's own tests exist to refuse. */""}
              ${(() => {
                const rows = performance.backtest?.styles || {};
                const keys = Object.keys(rows);
                if (keys.length < 2) return null;
                return html`
                  <table className="style-compare" dir="ltr">
                    <thead>
                      <tr>
                        <th>${t("styles.title", "Trading Style")}</th>
                        <th>${t("performance.trades", "trades")}</th>
                        <th>${t("performance.achieved", "Win rate")}</th>
                        <th>${t("performance.expectancy", "Per trade")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${keys.map((key) => {
                        const row = rows[key];
                        const style = tradingStyles.find((x) => x.key === key);
                        const ci = row.ci95;
                        return html`
                          <tr key=${key} className=${preferences.trading_style === key ? "is-yours" : ""}>
                            <td>${style ? (language === "ar" ? style.name_ar : style.name_en) : key}</td>
                            <td>${iso(String(row.trades))}</td>
                            <td>${iso(String(row.win_rate))}%</td>
                            <td>
                              <b className=${row.decisive ? (row.expectancy_r > 0 ? "pos" : "neg") : "inconclusive"}>
                                ${iso(`${row.expectancy_r > 0 ? "+" : ""}${row.expectancy_r}R`)}
                              </b>
                              ${ci && html`<small>${iso(`[${ci[0]}, ${ci[1]}]`)}</small>`}
                            </td>
                          </tr>`;
                      })}
                    </tbody>
                  </table>
                  <p className="measured-caveat">
                    ${t("performance.compare_caveat", "Every interval here spans zero. None of these profiles has been shown to beat chance, and the table is not a ranking — it is the record each one has.")}
                  </p>`;
              })()}
            </div>`}

          ${positions && (positions.open_positions.length > 0 || positions.performance.trades > 0) && html`
            ${/* Two records sit on this screen: the engine's measured history
                  above, and the user's own journal below. Without a label the
                  second block reads as more engine numbers -- "OPEN 0 CLOSED 5
                  WIN RATE 0%" with no owner is an accusation, not a stat. */ ''}
            <div className="measured-head">
              <span>${t("positions.yours", "Your recorded trades")}</span>
            </div>
            <div className="positions-summary">
              <div className="position-stat">
                <span className="stat-label">${t("positions.open", "Open")}</span>
                <span className="stat-value">${positions.open_positions.length}</span>
              </div>
              <div className="position-stat">
                <span className="stat-label">${t("positions.closed", "Closed")}</span>
                <span className="stat-value">${positions.performance.trades}</span>
              </div>
              ${positions.performance.trades > 0 && html`
                <div className="position-stat">
                  <span className="stat-label">${t("positions.win_rate", "Win rate")}</span>
                  <span className="stat-value">${positions.performance.win_rate}%</span>
                </div>
                <div className="position-stat">
                  <span className="stat-label">${t("positions.expectancy", "Expectancy")}</span>
                  <span className="stat-value" style=${{ color: positions.performance.expectancy_r >= 0 ? "var(--success)" : "var(--danger)" }}>${iso(`${positions.performance.expectancy_r > 0 ? "+" : ""}${positions.performance.expectancy_r}R`)}</span>
                </div>`}
            </div>`}
          ${positions && positions.open_positions.length === 0 && positions.performance.trades === 0 && html`
            <p className="positions-empty">
              ${t("positions.none_yet", "No trades in the record yet. Take a setup from the verdict and it will be followed here to its stop or target.")}
            </p>`}
          ${positions?.open_positions?.length > 0 && html`
            <div className="open-positions">
              ${positions.open_positions.map((position) => html`
                <div key=${position.id} className=${"open-position " + position.side}>
                  <span className="position-side">${position.side.toUpperCase()}</span>
                  <span>${t("positions.entry", "Entry")} ${formatPrice(position.entry_price)}</span>
                  <span>${t("positions.stop", "Stop")} ${formatPrice(position.stop_loss)}</span>
                  <span>${t("positions.target", "Target")} ${formatPrice(position.take_profit)}</span>
                  ${position.unrealised_r !== undefined && html`
                    <span style=${{ color: position.unrealised_r >= 0 ? "var(--success)" : "var(--danger)" }}>${iso(`${position.unrealised_r > 0 ? "+" : ""}${Number(position.unrealised_r).toFixed(2)}R`)}</span>`}
                </div>`)}
            </div>`}
        </section>
        <${CooldownSettings} />
        ${/* ONE POSITION-SIZE CARD. The typed calculator sat directly under
              this one, asking the same question of the same endpoint and
              answering it in a different shape — two panels, two layouts, one
              arithmetic. The ruler took its two inputs (a typed entry, an
              optional target) and its R:R, and it is the only one left. */""}
        <${RiskRuler} preferences=${preferences} />
        <${TradingRules} />`}

      ${section === "alerts" && html`
        <section className="panel style-panel">
          <div className="alerts-row">
            <div className="alerts-copy">
              <strong>${t("alerts.title", "Signal alerts")}</strong>
              <span>${t("alerts.note", "Alerts arrive only when the full pipeline reaches a trade — not on every indicator move.")}</span>
              ${/* The failure the owner actually hit: the server still held a
                   subscription from a browser profile that no longer exists,
                   so the app said "alerts are on" while every push went to a
                   dead endpoint. Silence read as "the feature is broken". */""}
              ${push.stale && html`
                <span className="alerts-stale" role="status">
                  ${t("alerts.stale", "Alerts are enabled on another of your devices, not this one. Turn them on here too and both will ring.")}
                </span>`}
              ${push.note && html`
                <span className="alerts-status">
                  ${push.note === "sent" ? t("alerts.sent", "Test alert sent.")
                    : push.note === "denied" ? t("alerts.denied", "Blocked in browser settings — allow notifications for this site, then try again.")
                    : push.note === "unsupported" ? t("alerts.ios", "On iPhone, add this app to your home screen first — Safari does not deliver alerts to a normal tab.")
                    : push.note === "not_configured" ? t("alerts.not_configured", "Alerts are not set up on the server yet.")
                    : push.note === "insecure" ? formatTemplate(t("alerts.insecure", "Alerts need https, or localhost. This page is on {origin}, and browsers refuse to register there."), { origin: push.detail || "" })
                    : push.note === "browser_refused" ? t("alerts.browser_refused", "Your browser refused to register. A private/incognito window cannot receive alerts.")
                    : push.note === "no_server" ? t("alerts.no_server", "Could not reach the server. Nothing was changed.")
                    : push.note === "server_refused" ? t("alerts.server_refused", "The server rejected the subscription.")
                    : t("alerts.failed", "Could not enable alerts.")}
                  ${push.detail && push.note !== "insecure" && html`
                    <small className="alerts-detail">${push.detail}</small>`}
                </span>`}
            </div>
            <div className="alerts-actions">
              <button
                className=${"btn " + (push.subscribed ? "btn-secondary" : "btn-primary")}
                onClick=${togglePush}
                disabled=${push.busy || !push.enabled}
              >
                <i className=${push.subscribed ? "fa-solid fa-bell-slash" : "fa-solid fa-bell"}></i>
                ${push.subscribed ? t("alerts.disable", "Turn off alerts") : t("alerts.enable", "Turn on alerts")}
              </button>
              ${push.subscribed && html`
                <button className="btn btn-secondary" onClick=${sendTestAlert} disabled=${push.busy}>
                  <i className="fa-solid fa-paper-plane"></i>
                  ${t("alerts.send_test", "Send test")}
                </button>`}
            </div>
          </div>

          <div className="alerts-row sound-row">
            <div className="alerts-copy">
              <strong>${t("alerts.sound", "Alert sound")}</strong>
              <span>${t("alerts.sound_note", "Plays when an alert arrives while the app is open. When it's closed, your device chooses the notification sound.")}</span>
            </div>
            <div className="sound-options">
              ${ALERT_SOUNDS.map((key) => html`
                <button key=${key}
                        className=${"sound-option" + (sound === key ? " active" : "")}
                        aria-pressed=${sound === key}
                        onClick=${() => {
                          setSound(key);
                          setAlertSoundChoice(key);
                          if (key !== "off") playAlertSound(key);
                        }}>
                  <i className=${key === "off" ? "fa-solid fa-volume-xmark" : "fa-solid fa-volume-high"}></i>
                  ${t(`alerts.sound_${key}`, key === "chime" ? "Chime" : key === "bell" ? "Bell" : key === "pulse" ? "Pulse" : "Silent")}
                </button>`)}
            </div>
          </div>

        </section>
        ${can("price_alerts") && html`<${AlertsList} />`}
        ${can("price_alerts") && html`<${ConditionAlerts} />`}
        ${can("signal_alerts") && html`<${AlertPolicy} />`}`}

      ${section === "app" && html`<${AppSettings} />`}
      ${section === "account" && html`<${AccountSection} />`}


      </div>
    </div>
  `;
}

function NewsPage() {
  const { t, language } = useI18n();
  const { status, can } = useAuth();
  const newsSymbol = "XAUUSD.m";
  const { items, loading, error, context } = useNewsFeed(newsSymbol, language, 30, 1);

  useEffect(() => {
    document.title = t("news.page_title", "Market News");
  }, [t]);

  useEffect(() => {
    if (status === "guest") {
      window.location.href = "/login";
    }
  }, [status]);

    const handleOpenNews = useCallback((item) => {
      cacheNewsItem(item, language);
      window.location.href = `/news/${item.uuid}`;
    }, [language]);

  if (status !== "authenticated") {
    return null;
  }

  if (!can("news")) {
    return html`
      <div className="app-shell news-shell">
        <${PageNav} />
        <${ServiceOff} feature="news" />
      </div>`;
  }

  return html`
    <div className="app-shell news-shell">
      <${PageNav} />

      <section className="surface news-hero">
        <div>
          <h1>${t("news.title", "Market News")}</h1>
          <p>${t("news.page_subtitle", "Live headlines and macro updates tied to gold markets.")}</p>
        </div>
      </section>

      <div className="surface panel news-section">
        ${loading && html`
          <div className="news-state">
            <i className="fa-solid fa-spinner fa-spin"></i>
            <span>${t("news.loading", "Loading news...")}</span>
          </div>
        `}
        ${error && html`<div className="alert">${t("news.error", "Unable to load news right now.")}</div>`}
        ${!loading && !error && items.length === 0 && html`
          <div className="news-state">${t("news.empty", "No news available.")}</div>
        `}

        <${NewsList}
          items=${items}
          onOpen=${handleOpenNews}
          t=${t}
          context=${context}
        />
      </div>
    </div>
  `;
}

function NewsDetailPage() {
  const { t, language } = useI18n();
  const { status } = useAuth();
  const uuid = window.location.pathname.split("/").pop();
  const lang = language === "ar" ? "ar" : "en";
  const [item, setItem] = useState(() => getCachedNewsItem(uuid, lang));
  const [loading, setLoading] = useState(!item);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = t("news.detail_title", "News Detail");
  }, [t]);

  useEffect(() => {
    if (status === "guest") {
      window.location.href = "/login";
    }
  }, [status]);

  useEffect(() => {
    if (!uuid) return;
    let active = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await authenticatedFetch(
          `/api/news?symbol=XAUUSD.m&limit=50&page=1&lang=${lang}`,
          { method: "GET" }
        );
        if (!response.ok) {
          throw new Error("Failed to load news");
        }
        const data = await response.json();
        // The API returns the list under `headlines` (same as useNewsFeed); this
        // read `data.results`, which is always undefined, so a direct load /
        // refresh / shared link always 404'd with "News item not found".
        const found = (data.headlines || []).find((entry) => entry.uuid === uuid);
        if (active) {
          if (found) {
            const hydrated = { ...found, _lang: lang };
            cacheNewsItem(hydrated, lang);
            setItem(hydrated);
          } else {
            setError(t("news.not_found", "News item not found."));
          }
        }
      } catch (err) {
        if (active) {
          setError(err.message || t("news.error", "Unable to load news right now."));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    if (!item || item._lang !== lang) {
      load();
    }
    return () => {
      active = false;
    };
  }, [item, uuid, lang, t]);

  if (status !== "authenticated") {
    return null;
  }

  return html`
    <div className="app-shell news-shell">
      <${PageNav} />

      ${loading && html`
        <div className="surface panel news-state">
          <i className="fa-solid fa-spinner fa-spin"></i>
          <span>${t("news.loading", "Loading news...")}</span>
        </div>
      `}

      ${error && html`<div className="surface panel alert">${error}</div>`}

      ${!loading && item && html`
        <section className="surface panel news-detail">
          ${/* No image: the feed emits no image field, and the old code pointed
                at an unrelated project's S3 bucket. */''}
          <div className="news-detail-body">
            <div className="news-detail-meta">
              <span className="news-detail-source"><bdi>${item.source}</bdi></span>
              <span>|</span>
              ${/* Field is `published`, not `published_at`; the old code showed
                    "-" for every article's date. Use the same relative format
                    the list uses so the two surfaces agree. */''}
              <span>${formatRelativeTime(item.published)}</span>
            </div>
            <h1>${item.title}</h1>
            <p>${t("news.detail_note", "Open the original article for full details and context.")}</p>
            <a className="btn btn-primary" href=${item.url} target="_blank" rel="noreferrer">
              <i className="fa-solid fa-up-right-from-square"></i>
              ${t("news.read_more", "Read Full Story")}
            </a>
          </div>
        </section>
      `}
    </div>
  `;
}

// Indicator names that are WORDS rather than symbols. RSI, MACD, ADX, CCI,
// ROC, SMA and EMA stay Latin on every desk on earth and are left alone; these
// are English phrases, and an English phrase on an Arabic screen is copy
// nobody translated. The key is the English name so the map cannot drift from
// the table it labels.
const INDICATOR_NAME_KEYS = {
  "Williams %R": "indicator_names.williams",
  "Ultimate Oscillator": "indicator_names.ultimate",
  "Bull/Bear Power": "indicator_names.bull_bear",
  "Stochastic %K": "indicator_names.stoch_k",
  "Stochastic RSI": "indicator_names.stoch_rsi",
  "Hull MA 9": "indicator_names.hull",
  "Ichimoku Base": "indicator_names.ichimoku",
};

function indicatorName(name, t) {
  const key = INDICATOR_NAME_KEYS[name];
  return key ? t(key, name) : name;
}

function IndicatorsAccordion({ extended, summary, translateSignal, t, disabled }) {
  if (!extended || !summary) {
    return html`<p style=${{ color: "var(--text-3)" }}>${t("indicators.no_oscillator_data", "No indicator data")}</p>`;
  }

  const oscSignals = summary.oscillator_signals || {};
  const maSignals = summary.ma_signals || {};
  const oscillators = [
    { name: "RSI (14)", value: extended.oscillators?.rsi, signal: oscSignals.RSI },
    { name: "Stochastic %K", value: extended.oscillators?.stoch_k, signal: oscSignals.STOCH },
    { name: "Stochastic RSI", value: extended.oscillators?.stoch_rsi, signal: oscSignals["Stochastic RSI"] },
    { name: "MACD", value: extended.oscillators?.macd, signal: oscSignals.MACD },
    { name: "ADX", value: extended.oscillators?.adx, signal: oscSignals.ADX },
    { name: "Williams %R", value: extended.oscillators?.williams_r, signal: oscSignals["Williams %R"] },
    { name: "CCI", value: extended.oscillators?.cci, signal: oscSignals.CCI },
    { name: "Ultimate Oscillator", value: extended.oscillators?.ultimate_oscillator, signal: oscSignals["Ultimate Oscillator"] },
    { name: "ROC", value: extended.oscillators?.roc, signal: oscSignals.ROC },
    { name: "Bull/Bear Power", value: extended.oscillators?.bull_power, signal: oscSignals["Bull/Bear Power"] }
  ];

  const movingAverages = [
    { name: "SMA 5", value: extended.moving_averages?.sma?.ma5, signal: maSignals.MA5 },
    { name: "SMA 10", value: extended.moving_averages?.sma?.ma10, signal: maSignals.MA10 },
    { name: "SMA 20", value: extended.moving_averages?.sma?.ma20, signal: maSignals.MA20 },
    { name: "SMA 50", value: extended.moving_averages?.sma?.ma50, signal: maSignals.MA50 },
    { name: "SMA 100", value: extended.moving_averages?.sma?.ma100, signal: maSignals.MA100 },
    { name: "SMA 200", value: extended.moving_averages?.sma?.ma200, signal: maSignals.MA200 },
    { name: "EMA 10", value: extended.moving_averages?.ema?.ema10, signal: maSignals.EMA10 },
    { name: "EMA 20", value: extended.moving_averages?.ema?.ema20, signal: maSignals.EMA20 },
    { name: "EMA 30", value: extended.moving_averages?.ema?.ema30, signal: maSignals.EMA30 },
    { name: "EMA 50", value: extended.moving_averages?.ema?.ema50, signal: maSignals.EMA50 },
    { name: "EMA 100", value: extended.moving_averages?.ema?.ema100, signal: maSignals.EMA100 },
    { name: "EMA 200", value: extended.moving_averages?.ema?.ema200, signal: maSignals.EMA200 },
    { name: "Hull MA 9", value: extended.moving_averages?.other?.hull_ma9, signal: maSignals["Hull MA"] },
    { name: "Ichimoku Base", value: extended.moving_averages?.other?.ichimoku_base, signal: maSignals.Ichimoku }
  ];

  const pivots = extended.pivots || {};

  // The closed header still answers: how many of this family lean each way.
  // Three bare title bars used to sit at the page foot saying nothing at all
  // until opened -- an accordion that hides its own headline.
  const tally = (items) => {
    const counts = { buy: 0, sell: 0, neutral: 0 };
    items.forEach((item) => {
      const s = String(item.signal || "").toLowerCase();
      if (s.includes("buy")) counts.buy += 1;
      else if (s.includes("sell")) counts.sell += 1;
      else if (item.signal) counts.neutral += 1;
    });
    return (counts.buy + counts.sell + counts.neutral) ? counts : null;
  };

  const sections = [
    {
      title: t("technical_analysis.oscillators", "Oscillators"),
      items: oscillators.filter((item) => item.value !== null && item.value !== undefined)
    },
    {
      title: t("technical_analysis.moving_averages", "Moving Averages"),
      items: movingAverages.filter((item) => item.value !== null && item.value !== undefined)
    },
    {
      title: t("technical_analysis.pivot_points", "Pivot Points"),
      items: buildPivotItems(pivots)
    }
  ].map((section) => ({ ...section, counts: tally(section.items) }));

  return html`
    <div className="accordion">
      ${sections.map((section) => html`
        <${AccordionItem} key=${section.title} title=${section.title} disabled=${disabled}
                          meta=${section.counts && html`
                            <span className="accordion-tally" dir="ltr" aria-hidden="true">
                              ${section.counts.buy > 0 && html`<em className="pos">▲${section.counts.buy}</em>`}
                              ${section.counts.neutral > 0 && html`<em>·${section.counts.neutral}</em>`}
                              ${section.counts.sell > 0 && html`<em className="neg">▼${section.counts.sell}</em>`}
                            </span>`}>
          ${section.items.length === 0
            ? html`<p style=${{ color: "var(--text-3)" }}>${t("general.no_data", "No data")}</p>`
            : section.items.map((item) => html`
                <div className="indicator-row" key=${item.name}>
                  <span>${indicatorName(item.name, t)}</span>
                  ${/* A NUMBER IS LEFT-TO-RIGHT, ALWAYS. Without this the RTL
                       paragraph direction moved the minus sign to the far
                       side and "-26.96" rendered as "26.96-", which is not a
                       negative number in any language — it is a typo the
                       layout invented. */""}
                  <span dir="ltr">${item.value !== undefined ? formatPrice(item.value) : "-"}</span>
                  ${item.signal && html`
                    <span className=${`indicator-signal ${getSignalClass(item.signal)}`}>
                      ${translateSignal(item.signal)}
                    </span>
                  `}
                </div>
              `)}
        </${AccordionItem}>
      `)}
    </div>
  `;
}

function buildPivotItems(pivots) {
  const items = [];
  if (pivots.classic?.pivot) {
    items.push({ name: "Classic Pivot", value: pivots.classic.pivot });
    ["r1", "r2", "r3", "s1", "s2", "s3"].forEach((level) => {
      if (pivots.classic[level]) {
        items.push({ name: `Classic ${level.toUpperCase()}`, value: pivots.classic[level] });
      }
    });
  }
  if (pivots.fibonacci?.pivot) {
    items.push({ name: "Fibonacci Pivot", value: pivots.fibonacci.pivot });
    ["r1", "r2", "r3", "s1", "s2", "s3"].forEach((level) => {
      if (pivots.fibonacci[level]) {
        items.push({ name: `Fibonacci ${level.toUpperCase()}`, value: pivots.fibonacci[level] });
      }
    });
  }
  if (pivots.camarilla?.r1) {
    ["r1", "r2", "r3", "r4", "s1", "s2", "s3", "s4"].forEach((level) => {
      if (pivots.camarilla[level]) {
        items.push({ name: `Camarilla ${level.toUpperCase()}`, value: pivots.camarilla[level] });
      }
    });
  }
  return items;
}

function AccordionItem({ title, children, disabled, meta }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (disabled) {
      setOpen(false);
    }
  }, [disabled]);

  return html`
    <div className=${`accordion-item ${disabled ? "disabled" : ""}`}>
      ${/* A real button: the old div carried role="button" with no tabIndex
           and no key handling -- announced as a button a keyboard could
           neither reach nor press. */""}
      <button
        type="button"
        className="accordion-header"
        aria-expanded=${open ? "true" : "false"}
        disabled=${disabled}
        onClick=${() => {
          if (!disabled) {
            setOpen(!open);
          }
        }}
      >
        <span>${title}</span>
        ${meta || ""}
        <i className=${open ? "fa-solid fa-chevron-up" : "fa-solid fa-chevron-down"}></i>
      </button>
      ${open && html`<div className="accordion-content">${children}</div>`}
    </div>
  `;
}

function getSignalClass(signal) {
  if (!signal) return "neutral";
  const s = signal.toLowerCase();
  if (s.includes("buy")) return "buy";
  if (s.includes("sell")) return "sell";
  return "neutral";
}

// traderProfile and onProfileChange are gone. The page held traderProfile at a
// hardcoded "moderate" and passed setTraderProfile to this component, which
// never called it -- so the advisor computed three profiles per run and two of
// them were unreachable from any screen, in any language. See
// advisor_service._generate_recommendations for why they were removed rather
// than wired to a selector.
/* UnifiedAnalysisResult lived here — the two-column "Technical Analysis vs AI
   Analysis" card with the readings-overlap meter between them. The expert
   panel replaced it on /analysis, and nothing else rendered it. Deleted rather
   than left unmounted: a component no screen mounts is the defect this
   codebase has paid for repeatedly, and the overlap meter in particular was a
   number about two readings that no longer sit on the same page. Recoverable
   from git if the panel is ever rolled back. */

/* AnalysisResult went with it: its only caller was the compact pane inside
   UnifiedAnalysisResult, so deleting one orphaned the other. Two components,
   one screen, both replaced by the panel. */

// The login page's engraved ground. It sat between AnalysisResult and
// LoginPage, so deleting the component took the constant with it — the
// second time in this session that removing a function swallowed the
// declaration underneath it. Restored, and lint is what caught both.
const GUILLOCHE_SVG = "<svg viewBox=\"0 0 400 400\" width=\"560\" height=\"560\"><g fill=\"none\" stroke=\"#C99A3A\" stroke-width=\"0.4\"><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(0 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(3 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(6 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(9 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(12 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(15 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(18 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(21 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(24 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(27 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(30 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(33 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(36 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(39 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(42 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(45 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(48 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(51 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(54 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(57 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(60 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(63 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(66 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(69 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(72 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(75 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(78 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(81 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(84 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(87 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(90 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(93 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(96 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(99 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(102 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(105 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(108 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(111 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(114 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(117 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(120 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(123 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(126 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(129 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(132 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(135 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(138 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(141 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(144 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(147 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(150 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(153 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(156 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(159 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(162 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(165 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(168 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(171 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(174 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(177 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(180 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(183 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(186 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(189 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(192 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(195 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(198 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(201 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(204 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(207 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(210 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(213 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(216 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(219 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(222 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(225 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(228 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(231 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(234 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(237 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(240 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(243 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(246 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(249 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(252 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(255 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(258 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(261 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(264 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(267 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(270 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(273 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(276 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(279 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(282 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(285 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(288 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(291 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(294 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(297 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(300 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(303 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(306 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(309 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(312 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(315 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(318 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(321 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(324 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(327 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(330 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(333 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(336 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(339 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(342 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(345 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(348 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(351 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(354 200 200)\"/><ellipse cx=\"200\" cy=\"200\" rx=\"182\" ry=\"66\" transform=\"rotate(357 200 200)\"/></g></svg>";

function LoginPage() {
  const { t } = useI18n();
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = t("login.title", "Login");
  }, [t]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(username, password);
      // Return them to where they were headed. Only a same-origin PATH is
      // accepted -- anything with a scheme or a host is an open redirect, and
      // a login page that forwards to an attacker's URL is the classic way to
      // hand over a session.
      const wanted = new URLSearchParams(window.location.search).get("next");
      // "/" is the marketing site now; a signed-in person's home is the
      // app. Landing them on the brochure after a successful login read
      // as "login is broken" -- the owner's exact words.
      const home = window.location.port === "5001" ? "/admin" : "/dashboard";
      const safe = wanted && /^\/[^/\\]/.test(wanted) ? wanted : home;
      window.location.href = safe;
    } catch (err) {
      // The one refusal with a specific story: right password, lapsed paid
      // period. Every other failure stays deliberately generic.
      const known = {
        subscription_expired: t("login.expired", "This account's access period has ended. Contact the operator to renew it."),
        license_pending: t("login.license_pending", "This account is not activated yet — enter the license we emailed you on the signup page."),
        rate_limited: t("login.rate_limited", "Too many attempts — wait a minute, then try again."),
        "Invalid credentials": t("login.invalid", "Wrong username or password."),
      };
      setError(known[err.message] || t("login.error_generic", "Login failed — try again."));
    } finally {
      setLoading(false);
    }
  };

  return html`
    <div className="auth-split">
      <!-- Left: what this is and what it honestly claims. A login for a paid
           product should say what the product does; a lone card in a dark void
           says nothing, and repeating the brand name twice under the logo says
           less than nothing. -->
      <section className="auth-brand">
        <div className="guilloche" aria-hidden="true"
             dangerouslySetInnerHTML=${{ __html: GUILLOCHE_SVG }}></div>
        <div className="sv-mark">
          <img className="auth-glyph" src="/static/img/mithqal-mark.svg"
               alt="" aria-hidden="true"
               style=${{ width: "44px", height: "44px" }} />
          <span className="auth-wordmark"><b>MITHQAL — مِثقال</b></span>
        </div>
        <h1 className="auth-hero">
          <span className="au">${t("login.hero_gold", "Gold")}</span> ${t("login.hero_rest", "analysis you can check.")}
        </h1>
        <p className="auth-lede">
          ${t("login.lede", "Indicators, an independent AI reading, the macro backdrop and the news calendar — combined into one verdict, with the reasoning shown.")}
        </p>
        <p className="auth-oneline">${t("login.point1", "Every recommendation carries an entry, a stop and a target, or it is not shown.")}</p>
      </section>

      <!-- Right: the form, and nothing competing with it. -->
      <section className="auth-panel">
        <div className="auth-form-wrap">
          <h2>${t("login.title2", "Sign in")}</h2>

          ${error && html`<div className="alert" role="alert">${error}</div>`}

          <form onSubmit=${handleSubmit} noValidate>
            <div className="form-group">
              <label className="form-label" htmlFor="sv-user">${t("login.username_label", "Username or Phone")}</label>
              <div className="field-icon">
                <i className="fa-solid fa-user"></i>
                <input
                id="sv-user"
                className="input"
                value=${username}
                autoComplete="username"
                inputMode="text"
                autoFocus
                onInput=${(event) => setUsername(event.target.value)}
                required
              />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="sv-pass">${t("login.password_label", "Password")}</label>
              <div className="input-affix field-icon">
                <i className="fa-solid fa-lock"></i>
                <input
                  id="sv-pass"
                  className="input"
                  type=${showPassword ? "text" : "password"}
                  value=${password}
                  autoComplete="current-password"
                  onInput=${(event) => setPassword(event.target.value)}
                  required
                />
                <button
                  type="button"
                  className="input-affix-btn"
                  aria-label=${showPassword ? t("login.hide_password", "Hide password") : t("login.show_password", "Show password")}
                  onClick=${() => setShowPassword(!showPassword)}
                >
                  <i className=${showPassword ? "fa-solid fa-eye-slash" : "fa-solid fa-eye"}></i>
                </button>
              </div>
            </div>
            <button className="btn btn-primary auth-submit" type="submit" disabled=${loading}>
              ${loading ? t("login.signing_in", "Signing in...") : t("login.sign_in", "Sign In")}
            </button>
          </form>

          ${/* The door for people who do NOT have an account yet. Without
               this, the login page was a dead end and "signup doesn't
               exist" was the owner's exact, correct complaint. */""}
          <p className="auth-signup">
            ${t("login.new_here", "New to Mithqal?")}${" "}
            <a className="auth-signup-link" href="/#signup">
              ${t("login.start_free", "Start your 7-day free trial")} <span className="dir-arrow">→</span>
            </a>
          </p>

          <p className="auth-disclaimer">
            ${t("login.risk", "Analysis only. Nothing here is financial advice, and no result is guaranteed — trading gold carries risk of loss.")}
          </p>
          ${/* There is no mail server, so "we sent you a link" would be a lie.
               Say what recovery actually is here. */""}
          <p className="auth-forgot">
            <button type="button" className="linkish"
                    onClick=${() => setShowForgot((v) => !v)}>
              ${t("password.forgot", "Forgot your password?")}
            </button>
          </p>
          ${showForgot && html`
            <p className="auth-forgot-note" role="status">
              ${t("password.forgot_note", "Password recovery is done by the operator: ask them to reset your account. They will give you a one-time temporary password, and the app will ask you to choose your own the moment you sign in with it.")}
            </p>`}

          ${/* Reachable BEFORE signing up, not buried after: the record and
               the risk warning are what someone needs to judge us by. */""}
          <p className="auth-legal">
            <a href="/verify">${t("performance.ours_short", "Our record")}</a>
            <a href="/risk">${t("legal.risk", "Risk warning")}</a>
            <a href="/terms">${t("legal.terms", "Terms")}</a>
            <a href="/privacy">${t("legal.privacy", "Privacy")}</a>
          </p>
        </div>
      </section>
    </div>
  `;
}

// THE FIVE DESTINATIONS, DEFINED ONCE.
//
// They were a phone tab bar and nothing else: `.tab-bar` is display:none above
// 860px and no desktop equivalent was ever built, so a laptop had the section
// chips ("Price · The call · The market") and no way to reach the Chart, the
// AI page, News or the Journal except by opening the overflow kebab. The
// owner's words: "laptop aslan ma f navigation bar".
//
// The list lives here so the bar and the rail cannot drift apart, and the
// feature gate is applied in one place: a service the operator switched off
// loses its entry in both.
function primaryDestinations(t, can) {
  return [
    { href: "/dashboard", icon: "fa-chart-pie", label: t("nav.dashboard", "Dashboard") },
    { href: "/chart", icon: "fa-chart-line", label: t("nav.chart", "Chart"), feature: "chart" },
    { href: "/analysis", icon: "fa-wand-magic-sparkles", label: t("buttons.ai_analysis_short", "AI"), feature: "ai_analysis" },
    { href: "/news", icon: "fa-rss", label: t("nav.news", "News"), feature: "news" },
    { href: "/journal", icon: "fa-book", label: t("nav.journal", "Journal"), feature: "journal" },
  ].filter((item) => !item.feature || can(item.feature));
}

function DesktopNav() {
  // The same five, in the top bar, where a pointer expects them. Hidden below
  // 861px, where the tab bar owns the job — two primary navigations on one
  // screen is the duplication this product is being cleaned of.
  const { t } = useI18n();
  const { can } = useAuth();
  const path = useRoutePath();
  const items = primaryDestinations(t, can);
  if (!items.length) return null;
  return html`
    <nav className="desk-nav" aria-label=${t("nav.primary", "Primary")}>
      ${items.map((item) => html`
        <a key=${item.href} href=${item.href}
           onClick=${(e) => navigate(e, item.href)}
           className=${"desk-nav-item" + (path === item.href ? " is-active" : "")}
           aria-current=${path === item.href ? "page" : undefined}>
          <i className=${`fa-solid ${item.icon}`} aria-hidden="true"></i>
          <span>${item.label}</span>
        </a>`)}
    </nav>`;
}

function TabBar() {
  const { t } = useI18n();
  const { can } = useAuth();
  // The hook, not window.location: read once at render, the active tab would
  // never move after a client-side navigation.
  const path = useRoutePath();
  // The five destinations a trader actually moves between. Settings is NOT
  // one of them -- it is configuration, it lives in the top menu, and having
  // it in both places wasted the most valuable row on the phone. The journal
  // takes the slot: your own record is a daily read, settings are monthly.
  //
  // A service the operator switched off loses its tab entirely: a permanent
  // dead-end in the one row a phone user presses constantly is worse than a
  // shorter row.
  const items = primaryDestinations(t, can);
  return html`
    <nav className="tab-bar" aria-label=${t("nav.primary", "Primary")}>
      ${items.map((item) => html`
        <a key=${item.href} href=${item.href}
           onClick=${(e) => navigate(e, item.href)}
           className=${"tab-item" + (path === item.href ? " is-active" : "")}
           aria-current=${path === item.href ? "page" : undefined}>
          <i className=${`fa-solid ${item.icon}`}></i>
          <span>${item.label}</span>
        </a>`)}
    </nav>
  `;
}

function AlertWhy({ alertId }) {
  const { t } = useI18n();
  const [why, setWhy] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setOpen(true);
    if (why) return;
    try {
      const p = await (await authenticatedFetch(`/api/alerts/${alertId}/why`)).json();
      setWhy(p.success ? p.explanation
        : { rule: t("alerts.why_unavailable", "The reason for this alert could not be read.") });
    } catch (e) {
      setWhy({ rule: t("alerts.why_unavailable", "The reason for this alert could not be read.") });
    }
  }, [alertId, why, t]);

  if (!open) {
    return html`
      <button className="alert-why-open" onClick=${load}>
        ${t("alerts.why", "Why did I get this?")}
      </button>`;
  }
  return html`
    <div className="alert-why">
      ${!why && html`<small className="muted">${t("app.loading", "Loading…")}</small>`}
      ${why && html`
        <p className="alert-why-rule">${why.rule}</p>
        ${why.state && Object.keys(why.state).length > 0 && html`
          <dl className="alert-why-state">
            ${Object.entries(why.state).map(([key, value]) => html`
              <div key=${key}><dt>${key}</dt>
                <dd dir="ltr">${iso(String(value))}</dd></div>`)}
          </dl>`}
        ${why.tally && html`
          <small className="alert-why-tally">
            ${formatTemplate(
              t("alerts.why_tally", "{sent} of these have been sent; {won} won and {lost} lost so far."),
              { sent: iso(String(why.tally.sent)), won: iso(String(why.tally.won)),
                lost: iso(String(why.tally.lost)) })}
          </small>`}
        ${why.note && html`<small className="muted">${why.note}</small>`}`}
    </div>`;
}

function AlertsInbox() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ alerts: [], unread: 0, loaded: false, failed: false });

  const load = useCallback(async () => {
    try {
      const response = await authenticatedFetch("/api/alerts");
      const payload = await response.json();
      if (payload.success) {
        setState((prev) => {
          // A NEW unread alert gets the chosen chime — but never the ones
          // already waiting when the page opened.
          if (prev.loaded && payload.unread > prev.unread) playAlertSound();
          return { alerts: payload.alerts, unread: payload.unread, loaded: true, failed: false };
        });
      } else {
        setState((prev) => ({ ...prev, failed: true }));
      }
    } catch (error) {
      // Not silent: "No alerts yet" over a dead server claims a calm that
      // nobody verified. The panel says it could not look instead.
      setState((prev) => ({ ...prev, failed: true }));
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, [load]);

  const toggle = useCallback(async () => {
    const next = !open;
    setOpen(next);
    if (next && state.unread > 0) {
      // Opening it is reading it -- which is also what clears the OS badge.
      setState((prev) => ({ ...prev, unread: 0 }));
      try { await authenticatedFetch("/api/alerts/read", { method: "POST" }); } catch (e) {}
      if (navigator.clearAppBadge) navigator.clearAppBadge().catch(() => {});
    }
  }, [open, state.unread]);

  const close = useCallback(() => setOpen(false), []);
  const wrapRef = useDismissable(open, close);

  // The inbox had no way to empty itself: opening it marked everything read
  // and the list went on growing, so a customer with two weeks of tickets met
  // a wall instead of a list. Both actions are optimistic -- the row leaves
  // immediately -- and reload on failure rather than leaving a lie on screen.
  const dismiss = useCallback(async (id) => {
    setState((prev) => ({ ...prev, alerts: prev.alerts.filter((a) => a.id !== id) }));
    try {
      const r = await authenticatedFetch(`/api/alerts/${id}`, { method: "DELETE" });
      if (!r.ok) load();
    } catch (e) { load(); }
  }, [load]);

  const clearAll = useCallback(async () => {
    setState((prev) => ({ ...prev, alerts: [], unread: 0 }));
    try {
      const r = await authenticatedFetch("/api/alerts/clear", { method: "POST" });
      if (!r.ok) load();
    } catch (e) { load(); }
    if (navigator.clearAppBadge) navigator.clearAppBadge().catch(() => {});
  }, [load]);

  return html`
    <div className="alerts-inbox" ref=${wrapRef}>
      <button className="btn btn-secondary alerts-bell" onClick=${toggle}
              aria-expanded=${open} aria-label=${t("alerts.title", "Alerts")}>
        <i className="fa-solid fa-bell"></i>
        ${state.unread > 0 && html`<span className="alerts-count">${state.unread}</span>`}
      </button>
      ${open && html`
        <div className="alerts-panel">
          <div className="alerts-panel-head">
            <b>${t("alerts.title", "Alerts")}</b>
            ${state.alerts.length > 0 && html`
              <button className="btn btn-ghost alerts-clear" onClick=${clearAll}>
                ${t("alerts.clear_all", "Clear all")}
              </button>`}
            <button className="btn btn-ghost" onClick=${() => setOpen(false)}
                    aria-label=${t("buttons.close", "Close")}>
              <i className="fa-solid fa-xmark"></i>
            </button>
          </div>
          ${state.failed && state.alerts.length === 0
            ? html`<div className="alerts-empty" role="alert">
                <p>${t("alerts.load_failed", "Could not load alerts — the server did not answer.")}</p>
                <button className="btn btn-ghost" onClick=${load}>${t("analysis.retry", "Try again")}</button>
              </div>`
            : state.alerts.length === 0
            ? html`<div className="alerts-empty">
                <p>${t("alerts.empty", "No alerts yet. You are notified when a setup completes the pipeline — not on every price move.")}</p>
                <a href="/alerts">${t("alerts.empty_hint", "Turn alerts on in Settings to get the ticket on your phone.")}</a>
              </div>`
            : state.alerts.map((a) => {
                // A signal alert carries its own numbers. Rendering them as a
                // laid-out ticket -- side in its market colour, stop and target
                // labelled and side by side -- is the difference between a
                // notification a trader can act on and a sentence they have to
                // parse. Everything else stays plain text, because a coach line
                // or a briefing has no levels to lay out.
                let meta = null;
                try { meta = a.meta ? JSON.parse(a.meta) : null; } catch (e) { meta = null; }
                const side = meta && typeof meta.side === "string"
                  ? meta.side.toLowerCase() : null;
                // A message from a room must not wear the trade ticket's
                // costume. The owner's rule, and the right one: a customer who
                // learns that this shape means "a setup arrived" and then gets
                // it for a chat message has been taught something false about
                // the most important notification the product sends.
                const isRoom = a.kind === "group";
                return html`
                <div className=${"alert-row" + (a.read_at ? "" : " is-unread")
                                + (isRoom ? " is-room" : "")} key=${a.id}>
                  <a className="alert-row-main" href=${a.url || "/"}>
                    <b dir="auto">
                      ${isRoom
                        ? html`<span className="alert-side is-room">
                            <i className="fa-solid fa-comments" aria-hidden="true"></i>
                            ${t("groups.badge", "Room")}
                          </span>`
                        : side && html`<span className=${"alert-side is-" + side}>
                            ${side === "buy" ? t("signal.buy", "BUY") : t("signal.sell", "SELL")}
                          </span>`}
                      ${a.title}
                    </b>
                    <span dir="auto">${a.body}</span>
                    ${!isRoom && meta && meta.stop != null && meta.target != null && html`
                      <div className="alert-levels">
                        ${meta.entry != null && html`<span className="alert-level">
                          <em>${t("signal.entry", "Entry")}</em>
                          <b className="num" dir="ltr">${meta.entry}</b></span>`}
                        <span className="alert-level is-stop">
                          <em>${t("signal.stop", "Stop")}</em>
                          <b className="num" dir="ltr">${meta.stop}</b></span>
                        <span className="alert-level is-target">
                          <em>${t("signal.target", "Target")}</em>
                          <b className="num" dir="ltr">${meta.target}</b></span>
                      </div>`}
                    <div className="alert-row-foot">
                      <small dir="ltr">${formatDate(a.sent_at)}</small>
                      ${a.expires_at && html`
                        <em className=${"alert-status " + (Date.parse(a.expires_at) > Date.now() ? "is-valid" : "is-expired")}>
                          ${Date.parse(a.expires_at) > Date.now()
                            ? t("alerts.valid", "still valid")
                            : t("alerts.expired", "expired")}
                        </em>`}
                    </div>
                  </a>
                  <${AlertWhy} alertId=${a.id} />
                  <button className="alert-dismiss" onClick=${() => dismiss(a.id)}
                          aria-label=${t("alerts.dismiss", "Dismiss this alert")}
                          title=${t("alerts.dismiss", "Dismiss this alert")}>
                    <i className="fa-solid fa-xmark" aria-hidden="true"></i>
                  </button>
                </div>`;
              })}
        </div>`}
    </div>
  `;
}

// One chrome for every page. The news page shipped a language dropdown and a
// "Back to Dashboard" pill, settings a third variant with its own logout —
// three navs in one product, all duplicating the kebab and the tab bar.
// The one overflow menu, everywhere. It used to exist only inside PageNav,
// so the chart page -- which has its own compact header -- simply had no
// menu at all: language, Settings, Help and Sign out vanished on the one
// page a trader lives in. Same component, same contents, every page.
const WHATS_NEW = [
  { key: "2026-08-14", items: [
    ["صفحة السجل /performance", "The record room /performance"],
    ["الأكاديمية /academy", "The academy /academy"],
    ["غرفة الأحداث /events", "The event room /events"],
    ["الكاميرا بالشات — الذكاء بيشوف شارتك", "Chat camera — the AI sees your chart"],
    ["Bar Replay + إعادة تشغيل صفقات السجل", "Bar replay + replay any published call"],
    ["Volume Profile مجاناً", "Volume profile, free"],
    ["انقر أيّ خط: قصّته + اسأل الذكاء الاصطناعي", "Tap any line: its story + ask the AI"],
    ["تنبيهات المؤشرات (RSI/ADX)", "Indicator alerts (RSI/ADX)"],
    ["الملخص الصوتي", "The spoken briefing"],
    ["زر التلفون الضايع بالإعدادات", "The lost-phone button in Settings"],
  ]},
];
const WHATS_NEW_KEY = "sv_whats_new_seen";

// A changelog that must be tapped: features nobody discovers might as well
// not exist, and tonight shipped three pages and a dozen tools.
function WhatsNewSheet({ open, onClose }) {
  const { t, language } = useI18n();
  if (!open) return null;
  const ar = language === "ar";
  return createPortal(html`
    <div className="sourced-scrim" onClick=${onClose}>
      <div className="sourced-sheet" role="dialog" aria-modal="true"
           onClick=${(e) => e.stopPropagation()}>
        <div className="sourced-head">
          <span>${t("whatsnew.title", "What's new")}</span>
          <b dir="ltr">${WHATS_NEW[0].key}</b>
        </div>
        <ul className="whats-new-list">
          ${WHATS_NEW[0].items.map(([arText, enText], i) => html`
            <li key=${i}>${ar ? arText : enText}</li>`)}
        </ul>
      </div>
    </div>`, document.body);
}

function NavMore() {
  const { language, setLanguage, t } = useI18n();
  const { user, logout, can } = useAuth();
  const [open, setOpen] = useState(false);
  const [newsOpen, setNewsOpen] = useState(false);
  const [groupUnread, setGroupUnread] = useState(0);
  const [shared, setShared] = useState(false);

  // The rooms badge. Polled with the menu closed too, because the point of a
  // badge is to be seen before the menu is opened. One cheap call a minute,
  // and only for customers who have the service at all.
  useEffect(() => {
    if (!can("groups")) return undefined;
    let alive = true;
    const read = () => authenticatedFetch("/api/groups")
      .then((r) => r.json())
      .then((p) => { if (alive && p.success) setGroupUnread(p.unread || 0); })
      .catch(() => {});
    read();
    const id = setInterval(read, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [can]);

  // Native share sheet where the platform has one, clipboard otherwise. The
  // URL is built from the live origin rather than hardcoded, so a white-label
  // deployment shares its own address and not ours.
  const shareApp = useCallback(async () => {
    const url = window.location.origin + "/";
    const payload = {
      title: t("share.title", "Mithqal"),
      text: t("share.text",
              "Gold analysis you can verify: every call published with entry, stop and target before the move, and every result sealed in a public record."),
      url,
    };
    try {
      if (navigator.share) { await navigator.share(payload); return; }
      await navigator.clipboard.writeText(`${payload.text}\n${url}`);
      setShared(true);
      setTimeout(() => setShared(false), 2500);
    } catch (e) {
      // A cancelled share sheet throws too. Saying "that failed" when the
      // customer simply changed their mind is worse than saying nothing.
    }
  }, [t]);
  const [unseen, setUnseen] = useState(() => {
    try { return localStorage.getItem(WHATS_NEW_KEY) !== WHATS_NEW[0].key; }
    catch (e) { return false; }
  });
  const close = useCallback(() => setOpen(false), []);
  // The portalled panel counts as INSIDE: it lives on <body>, so without
  // this every button in it was swallowed by the outside-click handler.
  const menuRef = useRef(null);
  const wrapRef = useDismissable(open, close, menuRef);
  return html`
    <div className="nav-more-wrap" ref=${wrapRef}>
      <button className="nav-more" onClick=${() => setOpen((v) => !v)}
              aria-expanded=${open} aria-label=${t("navigation.more", "More")}>
        <i className=${open ? "fa-solid fa-xmark" : "fa-solid fa-ellipsis-vertical"}></i>
        ${unseen && !open && html`<i className="whats-new-dot" aria-hidden="true"></i>`}
      </button>
      <${WhatsNewSheet} open=${newsOpen} onClose=${() => setNewsOpen(false)} />
      ${/* PORTALLED TO THE BODY, and this is not a nicety. The top nav carries
            backdrop-filter (app.css: `.top-nav, .tab-bar { backdrop-filter:
            blur(14px) }`), and a filtered element becomes the containing block
            for every position:fixed descendant. So a sheet pinned to
            inset-block-end inside the nav measured its bottom from the NAV's
            bottom — 56px down the page — and rendered five pixels above the
            top of the screen. Measured, not guessed: bounding bottom = -5 on
            all three phone sizes before this. */""}
      ${open && createPortal(html`
        <div className="nav-scrim" onClick=${close}></div>`, document.body)}
      ${open && createPortal(html`
        <div className="nav-menu" role="menu" ref=${menuRef}
             aria-label=${t("navigation.more", "More")}>
          ${/* ON A PHONE THIS IS A SHEET, NOT A DROPDOWN.
                Fifteen destinations hanging off a kebab under the top nav is
                a list that cannot fit, cannot be reached with a thumb, and —
                because it ended flush against a fixed bottom bar — could not
                be told apart from a list that was simply cut off. The owner
                read it as broken three times, which is the correct reading.

                Bottom-anchored on phones because that is where this app's
                navigation already lives (the tab bar is 60px below it), and
                grouped, because these are four different kinds of thing:
                where you go, what you set, what the product is, and leaving.
                A grabber and a sticky heading say what it is; the desktop
                keeps the dropdown, where a dropdown is correct. */""}
          <p className="nav-menu-group">${t("nav.group_go", "Go to")}</p>
          <button className="nav-menu-item"
                  onClick=${() => { const next = language === "ar" ? "en" : "ar"; setLanguage(next); persistLanguage(next); close(); }}>
            <i className="fa-solid fa-language"></i>
            <span>${language === "ar" ? "English" : "العربية"}</span>
          </button>
          ${/* Four entries where there was one. A split with no way to reach
                the new pages is the "built and reachable by nothing" class
                with a fresh coat, so the nav grows in the same commit as the
                routes -- never the one after. */""}
          <a className="nav-menu-item" href="/execution">
            <i className="fa-solid fa-plug-circle-bolt"></i><span>${t("exec.nav", "Execution")}</span>
          </a>
          <p className="nav-menu-group">${t("nav.group_yours", "Yours")}</p>
          <a className="nav-menu-item" href="/trading">
            <i className="fa-solid fa-gauge-high"></i><span>${t("nav.trading", "Trading & risk")}</span>
          </a>
          <a className="nav-menu-item" href="/alerts">
            <i className="fa-solid fa-bell"></i><span>${t("nav.alerts", "Alerts")}</span>
          </a>
          <a className="nav-menu-item" href="/account">
            <i className="fa-solid fa-user"></i><span>${t("nav.account", "Account")}</span>
          </a>
          <a className="nav-menu-item" href="/settings">
            <i className="fa-solid fa-sliders"></i><span>${t("nav.settings", "Settings")}</span>
          </a>
          ${/* The kebab promised "same contents, every page" and then held
                half the destinations: Calendar, the public record, and the
                limits page existed only in the dashboard's own menu. One
                menu, one truth. */""}

          <p className="nav-menu-group">${t("nav.group_product", "The product")}</p>
          <a className="nav-menu-item" href="/performance">
            <i className="fa-solid fa-scale-balanced"></i><span>${t("nav.performance", "The record")}</span>
          </a>
          ${/* Rooms sit in the menu rather than the tab bar on purpose: the
                phone's bottom row is the five things a trader moves between
                all day, and a room is something you open when the desk has
                said something. The unread count is what makes that work --
                without it a menu entry is a place people forget. */""}
          ${can("groups") && html`
            <a className="nav-menu-item" href="/groups">
              <i className="fa-solid fa-comments"></i>
              <span>${t("nav.groups", "Rooms")}</span>
              ${groupUnread > 0 && html`
                <b className="nav-menu-badge">${iso(String(groupUnread))}</b>`}
            </a>`}
          <a className="nav-menu-item" href="/academy">
            <i className="fa-solid fa-graduation-cap"></i><span>${t("nav.academy", "Academy")}</span>
          </a>
          <a className="nav-menu-item" href="/events">
            <i className="fa-solid fa-bolt"></i><span>${t("nav.events", "Event room")}</span>
          </a>
          <a className="nav-menu-item" href="/limits">
            <i className="fa-solid fa-eye-slash"></i><span>${t("nav.limits", "What we cannot see")}</span>
          </a>
          <a className="nav-menu-item" href="/help">
            <i className="fa-solid fa-circle-question"></i><span>${t("nav.help", "How it works")}</span>
          </a>
          <button className="nav-menu-item"
                  onClick=${() => {
                    setNewsOpen(true); close();
                    try { localStorage.setItem(WHATS_NEW_KEY, WHATS_NEW[0].key); } catch (e) {}
                    setUnseen(false);
                  }}>
            <i className="fa-solid fa-gift"></i>
            <span>${t("whatsnew.menu", "What's new")}</span>
            ${unseen && html`<i className="whats-new-dot" aria-hidden="true"></i>`}
          </button>
          ${/* Share. In the menu, not on the dashboard: a product that asks to
                be shared from the screen where the customer is deciding on a
                trade is a product interrupting the thing it is for. Native
                sheet where the platform has one (every phone), clipboard
                everywhere else, and the label says which happened. */""}
          <button className="nav-menu-item" onClick=${shareApp}>
            <i className="fa-solid fa-share-nodes"></i>
            <span>${shared ? t("share.copied", "Link copied")
                           : t("share.menu", "Share the app")}</span>
          </button>
          ${/* No operator affordance here, by the owner's instruction: the
               two apps are separate products. An admin signed into the
               customer app is a customer and sees exactly what a customer
               sees; the operator app is reached at /admin directly. */""}
          <div className="nav-menu-sep"></div>
          <button className="nav-menu-item nav-menu-user"
                  onClick=${async () => { close(); await logout(); window.location.href = "/login"; }}>
            <i className="fa-solid fa-arrow-right-from-bracket"></i>
            <span>${user?.username || t("general.user", "User")} · ${t("logout.logout", "Sign out")}</span>
          </button>
        </div>`, document.body)}
    </div>`;
}

function PageNav() {
  return html`
    <nav className="top-nav">
      <a className="brand" href="/dashboard">
        <img className="brand-glyph" src="/static/img/mithqal-mark.svg"
             alt="" aria-hidden="true"
             style=${{ width: "26px", height: "26px" }} />
        <span className="brand-word">Mithqal</span>
      </a>
      <${DesktopNav} />
      <div className="nav-actions">
        <${AlertsInbox} />
        <${NavMore} />
      </div>
    </nav>`;
}

// Alert governance. The reason this exists rather than one on/off switch: a
// user who is buzzed at 3am or eleven times on a Wednesday does not
// unsubscribe, they mute -- and a muted subscriber is indistinguishable from
// an engaged one in every metric while receiving nothing at all.
function AlertPolicy() {
  const { t } = useI18n();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await authenticatedFetch("/api/alert-policy");
      const p = await r.json();
      if (p.success) setState(p);
    } catch (error) { /* the section simply does not render */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = useCallback(async (changes) => {
    setBusy(true);
    try {
      const r = await authenticatedFetch("/api/alert-policy", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        // The browser knows the user's offset; the server must not guess it,
        // or a daily cap resets in the middle of somebody's evening.
        body: JSON.stringify({ timezone_offset: -new Date().getTimezoneOffset(),
                               ...changes }),
      });
      const p = await r.json();
      if (p.success) setState((prev) => ({ ...prev, policy: p.policy }));
    } catch (error) { /* the previous state stands */ }
    setBusy(false);
  }, []);

  if (!state) return null;
  const { policy, types } = state;
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const quietOn = policy.quiet_from != null && policy.quiet_to != null;

  return html`
    <div className="settings-card">
      <strong>${t("alerts.which", "Which alerts")}</strong>
      <ul className="alert-types">
        ${(types || []).map((type) => html`
          <li key=${type.key}>
            <label>
              <input type="checkbox" disabled=${busy}
                     checked=${policy.types[type.key] !== false}
                     onChange=${(e) => patch({ types: { [type.key]: e.target.checked } })} />
              <span>
                <b>${t(`alerts.type_${type.key}`, type.label)}</b>
                <small>${type.pending
                  ? t(`alerts.type_${type.key}_pending`, type.note)
                  : t(`alerts.type_${type.key}_note`, type.note)}</small>
              </span>
            </label>
          </li>`)}
      </ul>

      <div className="alert-quiet">
        <label className="alert-quiet-toggle">
          <input type="checkbox" disabled=${busy} checked=${quietOn}
                 onChange=${(e) => patch(e.target.checked
                   ? { quiet_from: 22, quiet_to: 7 }
                   : { quiet_from: null, quiet_to: null })} />
          <span>${t("alerts.quiet", "Quiet hours")}</span>
        </label>
        ${quietOn && html`
          <div className="alert-quiet-range" dir="ltr">
            <select disabled=${busy} value=${policy.quiet_from}
                    onChange=${(e) => patch({ quiet_from: Number(e.target.value) })}>
              ${hours.map((h) => html`<option key=${h} value=${h}>${String(h).padStart(2, "0")}:00</option>`)}
            </select>
            <span>→</span>
            <select disabled=${busy} value=${policy.quiet_to}
                    onChange=${(e) => patch({ quiet_to: Number(e.target.value) })}>
              ${hours.map((h) => html`<option key=${h} value=${h}>${String(h).padStart(2, "0")}:00</option>`)}
            </select>
          </div>`}
        <small>${t("alerts.quiet_note", "A trade you have open is always delivered — a quiet hour is about noise, not about your stop.")}</small>
      </div>

      <div className="alert-cap">
        <label>
          <span>${t("alerts.cap", "Most alerts per day")}</span>
          <input type="number" min="1" max="50" disabled=${busy}
                 value=${policy.daily_cap ?? ""}
                 onChange=${(e) => patch({ daily_cap: e.target.value })} />
        </label>
        <small>
          ${formatTemplate(t("alerts.cap_note", "{n} sent today. Above the cap the product has to choose what matters instead of forwarding everything."),
            { n: iso(String(state.sent_today ?? 0)) })}
        </small>
      </div>
    </div>`;
}


// What the app cannot see. Published deliberately: a system that lists what it
// knows while staying quiet about what it does not is only half checkable, and
// being checkable is this product's entire position. A user who finds a blind
// spot themselves concludes it was hidden; a user who read it here concludes
// the product is honest.
function LimitsPage() {
  const { t } = useI18n();
  const [spots, setSpots] = useState(null);

  useEffect(() => {
    document.title = t("limits.page_title", "What this app cannot see");
  }, [t]);

  useEffect(() => {
    let alive = true;
    fetch("/api/blind-spots")
      .then((r) => r.json())
      .then((p) => { if (alive && p.success) setSpots(p.blind_spots); })
      .catch(() => { if (alive) setSpots([]); });
    return () => { alive = false; };
  }, []);

  return html`
    <div className="app-shell">
      <${PageNav} />
      <main className="limits-body">
        <div className="analysis-head">
          <h1><i className="fa-solid fa-eye-slash"></i> ${t("limits.title", "What this app cannot see")}</h1>
        </div>
        <p className="limits-intro">
          ${t("limits.intro", "Every analysis tool has blind spots. Most do not publish them. This one does, because a system that lists what it knows and stays quiet about what it does not is only half checkable — and being checkable is the whole point.")}
        </p>
        ${/* Where every number comes from -- the honesty page's missing
              half. What we CAN'T see is above; what we CAN see, and from
              whom, is here. Static by design: a sources list that needs a
              server call to render is a sources list that can lie. */""}
        <section className="limit">
          <h2>${t("limits.sources_title", "Where every number comes from")}</h2>
          <ul className="limits-sources">
            <li><b>${t("limits.src_candles", "Candles & history")}</b> — Dukascopy (Swiss bank feed), spot XAUUSD. ${t("limits.src_candles_note", "The same bars feed the chart, the engine, the backtest and the public replay — one source, so they can never disagree.")}</li>
            <li><b>${t("limits.src_spot", "Live spot tick")}</b> — gold-api.com. ${t("limits.src_spot_note", "Display only; decisions read the candle feed.")}</li>
            <li><b>${t("limits.src_macro", "Yields & the dollar")}</b> — FRED (US Federal Reserve): DFII10, DTWEXBGS. ${t("limits.src_macro_note", "Published once a business day; dated accordingly wherever shown.")}</li>
            <li><b>${t("limits.src_news", "News")}</b> — ${t("limits.src_news_note", "Public English RSS from international desks, filtered for gold relevance. Arabic UI states the headlines arrive in English.")}</li>
            <li><b>${t("limits.src_calendar", "Economic calendar")}</b> — ${t("limits.src_calendar_note", "Public weekly feed; a failed fetch is shown as unknown, never as a clear week.")}</li>
            <li><b>${t("limits.src_ai", "AI")}</b> — gpt-4o-mini. ${t("limits.src_ai_note", "It may only confirm or decline the engine's verdict, never originate a trade — and its chat cites only the state it is shown.")}</li>
          </ul>
        </section>
        <${LevelGrades} />
        <${FilterPower} />

        ${(spots || []).map((spot) => html`
          <section key=${spot.key} className="limit">
            <h2>${spot.title}</h2>
            <p>${spot.what}</p>
            <p className="limit-why">${spot.why}</p>
            ${spot.instead && html`
              <p className="limit-instead">
                <b>${t("limits.instead", "What you get instead")}:</b> ${spot.instead}
              </p>`}
            <span className=${"limit-tag " + (spot.fixable ? "is-fixable" : "is-permanent")}>
              ${spot.fixable
                ? t("limits.fixable", "Fixable — on the roadmap")
                : t("limits.permanent", "Permanent — a property of the market or a product decision")}
            </span>
          </section>`)}
      </main>
    </div>`;
}


// The head of the session. First thing on the dashboard because it is the
// reason to open the app BEFORE deciding to trade -- the research on what makes
// a tool indispensable named an outside trigger as the first of three, and
// without one this is somewhere you go when you have already decided.
//
// Every line is a fact the system computed or a fact about the clock. Nothing
// here predicts a price.
// Compose a briefing line in the USER'S language from its params; the
// server's English `text` is only the fallback. The briefing is the first
// thing on the screen -- on the Arabic product it used to render as
// bidi-mangled English prose ("...in the next 48 hours 9.").
function briefLine(t, line) {
  const p = line.params || {};
  switch (line.key) {
    case "session": {
      if (!p.session) return line.text;
      const name = t("sessions." + p.session, line.text);
      const liq = p.liquidity ? " · " + t("sessions.liq_" + p.liquidity, p.liquidity) : "";
      let note = t("brief.note_" + p.session, line.note || "");
      if (p.median_range != null && p.note_hours != null) {
        note += " " + formatTemplate(
          t("brief.note_range", "Median hourly range ${range} over {hours} hours."),
          { range: iso(p.median_range.toFixed ? p.median_range.toFixed(2) : p.median_range),
            hours: iso(p.note_hours) });
      }
      return { text: name + liq, note };
    }
    case "schedule":
      return { text: formatTemplate(
        t("brief.schedule", "{count} scheduled releases in the next 48 hours."),
        { count: iso(p.count) }) };
    case "real_yield":
      return { text: formatTemplate(t("brief.real_yield", "10y real yield {value}%"),
                                    { value: iso(p.value) }),
               note: p.direction > 0 ? t("brief.yield_rising", "rising — a headwind for gold")
                   : p.direction < 0 ? t("brief.yield_falling", "falling — a tailwind for gold")
                   : t("brief.yield_flat", "flat") };
    case "brackets": {
      if (p.below == null && p.above == null) return line.text;
      const parts = [];
      if (p.below != null) parts.push(formatTemplate(t("brief.level_below", "{price} below"), { price: iso(p.below) }));
      if (p.above != null) parts.push(formatTemplate(t("brief.level_above", "{price} above"), { price: iso(p.above) }));
      return { text: t("brief.brackets", "Nearest levels:") + " " + parts.join(" · "),
               note: line.note };
    }
    case "verdict":
      if (p.action === "buy" || p.action === "sell") {
        return { text: formatTemplate(t("brief.setup_stands", "A {side} setup stands."),
          { side: p.action === "buy" ? t("signals.buy", "BUY") : t("signals.sell", "SELL") }) };
      }
      return { text: t("brief.no_setup", "No setup."),
               note: (p.blockers && p.blockers.length)
                 ? p.blockers.map((b) => translateBlocker(t, b && b.key ? b.key : b)).join(" · ")
                 : t("brief.nothing_lined_up", "Nothing has lined up yet.") };
    case "discipline":
      return { text: formatTemplate(
        t("brief.discipline", "Following the verdict has averaged {delta}R more per trade than overriding it."),
        { delta: iso((p.delta_r > 0 ? "+" : "") + p.delta_r) }),
        note: formatTemplate(t("brief.discipline_note", "{followed} followed · {overrode} overridden"),
                             { followed: iso(p.followed), overrode: iso(p.overrode) }) };
    default:
      return line.text;
  }
}

function translateBlocker(t, code) {
  return t("blockers." + code, String(code));
}

function briefHeadline(t, brief) {
  const p = brief.headline_params || {};
  switch (p.kind) {
    case "event":
      return formatTemplate(t("brief.headline_event", "{title} in {minutes} minutes."),
                            { title: p.title, minutes: iso(p.minutes) });
    case "setup":
      return formatTemplate(t("brief.setup_stands", "A {side} setup stands."),
        { side: p.action === "buy" ? t("signals.buy", "BUY") : t("signals.sell", "SELL") });
    case "session_open":
      return formatTemplate(t("brief.headline_session", "{session} is open."),
                            { session: t("sessions." + p.session, p.session) });
    case "session_closed":
      return t("brief.headline_closed", "The market is closed.");
    case "quiet":
      return t("brief.headline_quiet", "No setup, and nothing scheduled.");
    default:
      return brief.headline;
  }
}

// A trader's question, folded. The dashboard is ordered as the questions a
// trader actually asks, most important first; each secondary answer sits
// behind its question until tapped, and the choice is remembered per card --
// so the page opens on the answer (price, verdict) instead of a wall of
// everything, and each reader ends up with their own ranking.
function Fold({ id, icon, title, hint = null, defaultOpen = false, children }) {
  const [open, setOpen] = useState(() => {
    try {
      const saved = localStorage.getItem("sv_fold_" + id);
      return saved == null ? defaultOpen : saved === "1";
    } catch (e) { return defaultOpen; }
  });
  const toggle = useCallback(() => setOpen((v) => {
    try { localStorage.setItem("sv_fold_" + id, v ? "0" : "1"); } catch (e) { /* private mode */ }
    return !v;
  }), [id]);
  return html`
    <div className=${"fold" + (open ? " is-open" : "")}>
      <button className="fold-head" onClick=${toggle} aria-expanded=${open}>
        <span className="fold-title">
          ${icon && html`<i className=${icon} aria-hidden="true"></i>`}
          ${title}
        </span>
        <span className="fold-side">
          ${/* A closed question CARRIES its answer's one-liner: a wall of
               shut accordions told the reader nothing until they worked for
               it, which is the opposite of a dashboard. */""}
          ${!open && hint && html`<small className="fold-hint">${hint}</small>`}
          <i className=${"fold-chev fa-solid " + (open ? "fa-chevron-up" : "fa-chevron-down")} aria-hidden="true"></i>
        </span>
      </button>
      ${open && html`<div className="fold-body">${children}</div>`}
    </div>`;
}



function stateDot(verdict) {
  // THE SAME THREE STATES THE TELEGRAM TICKET USES, and deliberately not a
  // fourth. A scale invites tuning the threshold instead of fixing what it
  // measures, and the reader's question has three answers: does this need me
  // now, does it need care, or neither.
  //
  // RED IS NOT "BAD". It is stand-aside — on a product whose position is that
  // "no trade" is a complete answer, red is the honest state most of the time
  // and must never read as a failure.
  if (!verdict) return "standby";
  const action = String(verdict.action || "").toLowerCase();
  if (action !== "buy" && action !== "sell") return "standby";
  const cautioned = (verdict.cautions || []).length
    || verdict.news_caution
    || verdict.conflict?.present
    || verdict.risk_halted;
  return cautioned ? "caution" : "live";
}

function RiskRuler({ preferences, price }) {
  // THE ONE NUMBER PEOPLE GET WRONG, MADE TACTILE.
  //
  // The size calculator asks for three typed prices and answers once. But the
  // question a trader actually has is a SHAPE: "if I put the stop here instead
  // of there, what happens to my size?" -- and typing three numbers to ask it
  // four times is why most people stop asking and use a round lot.
  //
  // The server stays the only calculator. The slider debounces into
  // /api/tools/position-size rather than doing the arithmetic here, because a
  // second implementation of a lot size is how two surfaces end up telling one
  // customer different numbers about the same order.
  const { t } = useI18n();
  const [distance, setDistance] = useState(20);
  const [side, setSide] = useState("buy");
  const [out, setOut] = useState(null);
  const [rr, setRr] = useState(null);
  const [problem, setProblem] = useState(null);
  const [spot, setSpot] = useState(null);
  // TYPED ENTRY AND TARGET, because the card this one replaced could take
  // them and this one could not. Empty means "use the live price" and "no
  // target" — a trader planning a level that price has not reached yet is the
  // ordinary case, not an advanced one, and sending them to a second
  // calculator for it is what produced two panels answering one question from
  // one endpoint.
  const [typedEntry, setTypedEntry] = useState("");
  const [target, setTarget] = useState("");

  useEffect(() => {
    if (price) return undefined;
    // No price was handed down, so ask for one. The ruler is useless without
    // an entry to measure from, and a made-up round number would teach the
    // wrong size for the real one.
    let alive = true;
    (async () => {
      try {
        const p = await (await authenticatedFetch("/api/price")).json();
        if (alive && p.price) setSpot(p.price);
      } catch (e) { /* the ruler simply does not appear */ }
    })();
    return () => { alive = false; };
  }, [price]);

  const balance = Number(preferences?.account_balance) || 0;
  const riskPercent = Number(preferences?.risk_percent) || 0;
  const entry = Number(typedEntry) || Number(price || spot) || 0;
  const stop = side === "buy" ? entry - distance : entry + distance;
  const targetPrice = Number(target) || null;

  useEffect(() => {
    if (!balance || !riskPercent || !entry) return undefined;
    let alive = true;
    // Debounced: one request per pause, not one per pixel of drag.
    const timer = setTimeout(async () => {
      try {
        const p = await (await authenticatedFetch("/api/tools/position-size", {
          method: "POST",
          body: JSON.stringify({ balance, risk_percent: riskPercent,
                                 entry, stop, target: targetPrice })
        })).json();
        if (!alive) return;
        if (p.success) {
          setOut(p.sizing);
          setRr(p.reward_risk ?? null);
          setProblem(null);
        } else { setOut(null); setRr(null); setProblem(p.detail || null); }
      } catch (e) {
        if (alive) setProblem(t("calc.unreachable", "That did not reach the server."));
      }
    }, 180);
    return () => { alive = false; clearTimeout(timer); };
  }, [balance, riskPercent, entry, stop, targetPrice, t]);

  if (!balance || !riskPercent) return null;
  if (!entry) return null;

  return html`
    <section className="panel surface ruler" id="size">
      <header className="ruler-head">
        <h3>${t("ruler.title", "Move the stop, watch the size")}</h3>
        <div className="ruler-sides">
          ${["buy", "sell"].map((s) => html`
            <button key=${s} type="button"
                    className=${`ruler-side ${s}${side === s ? " is-on" : ""}`}
                    onClick=${() => setSide(s)}>
              ${t(`signals.${s}`, s.toUpperCase())}
            </button>`)}
        </div>
      </header>

      ${/* The ISOLATE goes on each number, never on the row. dir="ltr" on the
            flex row was there to stop "$20" flipping, and it reversed the
            three columns as a side effect: an Arabic reader's eye landed on
            the distance and finished at the entry it is measured from —
            the causal chain backwards, on the half of the card that explains
            the other half. */""}
      <div className="ruler-prices">
        <div><small>${t("ruler.entry", "Entry")}</small><b dir="ltr">${formatPrice(entry)}</b></div>
        <div><small>${t("ruler.stop", "Stop")}</small><b dir="ltr">${formatPrice(stop)}</b></div>
        <div><small>${t("ruler.distance", "Distance")}</small><b dir="ltr">${iso("$" + distance)}</b></div>
      </div>

      <input className="ruler-range" type="range" min="2" max="80" step="1"
             value=${distance} aria-label=${t("ruler.distance", "Distance")}
             onInput=${(e) => setDistance(Number(e.target.value))} />

      ${/* Same rule as the price row above: the isolate belongs on the number,
            not on the row — the row's order is the reading order. */""}
      <div className="ruler-inputs">
        <label>
          <small>${t("ruler.entry_own", "Your entry")}</small>
          <input type="number" step="0.01" inputMode="decimal" dir="ltr"
                 placeholder=${formatPrice(Number(price || spot) || 0)}
                 value=${typedEntry}
                 onInput=${(e) => setTypedEntry(e.target.value)} />
        </label>
        <label>
          <small>${t("ruler.target_opt", "Target (optional)")}</small>
          <input type="number" step="0.01" inputMode="decimal" dir="ltr"
                 value=${target} onInput=${(e) => setTarget(e.target.value)} />
        </label>
      </div>

      ${out && html`
        <div className=${`ruler-out${out.too_small ? " is-refused" : ""}`}>
          <div className="ruler-figure">
            <small>${t("ruler.lots", "Position size")}</small>
            <b dir="ltr">${out.too_small ? "—" : iso(out.lots.toFixed(2))}</b>
          </div>
          <div className="ruler-figure">
            <small>${t("ruler.risking", "You risk")}</small>
            <b dir="ltr">${iso(formatPrice(out.risk_amount) || out.risk_amount)}</b>
          </div>
          ${rr != null && html`
            <div className="ruler-figure">
              <small>${t("advisor.risk_reward", "R:R")}</small>
              <b dir="ltr">${iso(Number(rr).toFixed(2))}:1</b>
            </div>`}
          ${/* THE SENTENCE UNDER THE NUMBER, IN THE READER'S LANGUAGE.
                The server composes `out.note` in English, which is right for
                an API and wrong for this card — the promise it carries
                ("rounded DOWN so it never exceeds the % you set") is the
                reason to trust the figure above it, and a promise nobody can
                read is not a promise. Same numbers, from the same payload;
                only the sentence is assembled here. */""}
          <p className="ruler-note">
            ${out.too_small
              ? formatTemplate(
                  t("ruler.too_small", "The smallest trade available risks more than the {pct}% you set — {requested} on this balance."),
                  { pct: iso(String(riskPercent)),
                    requested: iso(formatPrice(out.requested_amount ?? out.risk_amount) || "") })
              : formatTemplate(
                  t("ruler.rounded", "Rounded DOWN so it never exceeds the {pct}% you set."),
                  { pct: iso(String(riskPercent)) })}
          </p>
        </div>`}
      ${problem && html`<p className="ruler-problem">${problem}</p>`}
    </section>`;
}

function Mirror({ analytics, preferences, t }) {
  // YOUR TRADES, BESIDE THE ONES YOU TOOK FROM THE ENGINE.
  //
  // This is the one comparison in the product that is about the customer
  // rather than about gold, and it is the hardest number to argue with
  // because they produced both columns themselves. DisciplineLine already
  // shows the single delta; this shows the two sides that made it, because a
  // delta with no columns under it is a claim, and the columns are the proof.
  //
  // SAMPLE HONESTY IS THE WHOLE DESIGN. Every column carries its n, and the
  // delta only appears once BOTH sides clear the floor the server sets. Under
  // it the card says "too few to mean anything" instead of printing a number
  // that will be quoted back.
  const d = analytics?.discipline;
  if (!d) return null;
  const followed = d.followed || {};
  const override = d.override || {};
  if (!(followed.trades || override.trades)) return null;

  const floor = d.delta_min_per_side || 8;
  const thin = Math.min(followed.trades || 0, override.trades || 0) < floor;

  const column = (bucket, titleKey, titleFallback, hintKey, hintFallback) => html`
    <div className="mirror-col">
      <h4>${t(titleKey, titleFallback)}</h4>
      <small>${t(hintKey, hintFallback)}</small>
      <dl>
        <div>
          <dt>${t("mirror.trades", "Trades")}</dt>
          <dd dir="ltr">${iso(String(bucket.trades || 0))}</dd>
        </div>
        <div>
          <dt>${t("mirror.won", "Won")}</dt>
          <dd dir="ltr">${bucket.trades ? `${iso(String(bucket.win_rate))}%` : "—"}</dd>
        </div>
        <div>
          <dt>${t("mirror.avg", "Average")}</dt>
          <dd dir="ltr" className=${(bucket.avg_r || 0) >= 0 ? "up" : "down"}>
            ${bucket.trades ? iso(formatR(bucket.avg_r)) : "—"}
          </dd>
        </div>
      </dl>
    </div>`;

  return html`
    <section className="panel surface mirror">
      <h3>${t("mirror.title", "Your trades, beside the engine's")}</h3>
      <div className="mirror-cols">
        ${column(followed, "mirror.followed", "Taken with the verdict",
                 "mirror.followed_hint", "The engine said it; you took it.")}
        ${column(override, "mirror.override", "Your own call",
                 "mirror.override_hint", "You overrode the verdict, or traded without one.")}
      </div>
      ${thin
        ? html`<p className="mirror-thin">
            ${formatTemplate(
              t("mirror.too_few", "Too few to compare yet — this needs {n} on each side before the difference means anything."),
              { n: iso(String(floor)) })}
          </p>`
        : d.delta_r != null && html`
          <p className=${`mirror-delta ${d.delta_r >= 0 ? "up" : "down"}`}>
            <b dir="ltr">${iso((d.delta_r > 0 ? "+" : "") + d.delta_r.toFixed(2) + "R")}</b>
            ${" "}
            ${d.delta_r >= 0
              ? t("mirror.delta_up", "more per trade when you follow the verdict.")
              : t("mirror.delta_down", "more per trade when you decide for yourself. The engine is not helping you here.")}
            ${/* R is the honest unit and stays first. It is also a unit that
                  means nothing to most people, and a subscription is priced in
                  money — so when the customer has told us their balance and
                  risk, the same fact is restated in what they actually risk
                  per trade. Arithmetic on numbers they supplied, never a claim
                  about what they would have made, and it needs a real sample:
                  "$1,372" computed from three overrides is a figure that gets
                  quoted back. */""}
            ${(() => {
              const balance = Number(preferences?.account_balance) || 0;
              const riskPct = Number(preferences?.risk_percent) || 0;
              const perTrade = balance > 0 && riskPct > 0 ? (balance * riskPct) / 100 : 0;
              const n = override.trades || 0;
              if (!perTrade || Math.min(followed.trades || 0, n) < 10) return null;
              const money = d.delta_r * perTrade * n;
              if (!money) return null;
              return html`<b className="mirror-money">
                ${formatTemplate(
                  t("mirror.money", "On your {risk} risk, the {n} overrides come to about {money}."),
                  { risk: iso("$" + perTrade.toFixed(0)), n: iso(String(n)),
                    money: iso("$" + Math.abs(money).toFixed(0)) })}
              </b>`;
            })()}
          </p>`}
      <p className="mirror-note">
        ${t("mirror.note", "Both columns are your own closed trades. Nothing here is a projection, and the count beside each one is the whole sample it rests on.")}
      </p>
    </section>`;
}

function StatusStrip({ verdict, timeframe, marketOpen }) {
  // ONE LINE THAT ANSWERS "IS THERE ANYTHING FOR ME RIGHT NOW".
  //
  // The dashboard answers it eventually — three cards down, after a price, a
  // briefing and a panel. A room scrolls and so does a phone; the state
  // belongs where the eye lands first, in the same language the Telegram
  // ticket uses, so the two surfaces cannot describe one call differently.
  const { t } = useI18n();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, []);

  const state = marketOpen === false ? "standby" : stateDot(verdict);
  const action = String(verdict?.action || "").toLowerCase();

  const words = {
    live: t("strip.live", "A call is live"),
    caution: t("strip.caution", "A call, with something against it"),
    standby: marketOpen === false
      ? t("strip.closed", "The market is closed")
      : t("strip.standby", "Stand aside — no call")
  };

  // The next decision point: this frame's candle close. Not a promise that
  // anything will happen at it, just the clock the engine actually reads.
  const seconds = { "1m": 60, "5m": 300, "15m": 900, "30m": 1800,
                    "1h": 3600, "4h": 14400, "1d": 86400 }[timeframe] || 3600;
  const remaining = seconds - Math.floor((now / 1000) % seconds);

  return html`
    <section className=${`strip strip-${state}`} aria-live="polite">
      <span className=${`strip-dot ${state}`} aria-hidden="true"></span>
      <b className="strip-word">
        ${words[state]}
        ${(state === "live" || state === "caution") && action && html`
          <span className=${`strip-side ${action}`}>
            ${" · "}${t(`signals.${action}`, action.toUpperCase())}
          </span>`}
      </b>
      ${marketOpen !== false && html`
        <span className="strip-clock" dir="ltr" title=${t("strip.next_title", "This frame's candle close")}>
          <i className="fa-regular fa-clock" aria-hidden="true"></i>${" "}
          ${fmtSpan(remaining)}
        </span>`}
    </section>`;
}

// The prose lines the engine emits for "what would help" that a GAUGE ROW
// already states. Keeping both would print "the trend to strengthen (ADX above
// 25)" directly under a bar reading "ADX 21.8 / 25".
const HELP_COVERED_BY_A_ROW = [
  /adx/i, /confirmation/i, /score/i, /filter/i, /release/i, /yield/i,
];

function ConditionsMeter({ verdict, extended, marketOpen, help, inline }) {
  // WHAT IS MISSING, AS A GAUGE RATHER THAN A SENTENCE.
  //
  // "No clean setup right now" is the honest answer and it is also a dead end
  // to look at: the reader has no idea whether the market is one bar away or
  // nowhere near. Every condition here is one the engine already publishes a
  // NUMBER for, so the wait becomes something you can watch instead of a
  // screen that says no.
  //
  // It is NOT a countdown. Nothing here promises the conditions will complete,
  // and the note says so — a progress bar that implies arrival would be the
  // same manufactured-urgency this product exists against.
  const { t, language, translateTechnicalReason } = useI18n();
  if (!verdict) return null;

  const readiness = verdict.readiness || {};
  const score = Number(readiness.score ?? verdict.scores?.decision_score ?? 0);
  const needed = Number(readiness.needed || 3);
  const adx = extended?.oscillators?.adx ?? extended?.adx ?? null;
  const blockers = verdict.blockers || [];
  const withheld = verdict.withheld_by;
  const halted = verdict.risk_halted;

  const live = verdict.action === "buy" || verdict.action === "sell";

  const rows = [
    {
      key: "score",
      label: t("gate.score", "Setup points"),
      value: `${iso(String(score))} / ${iso(String(needed))}`,
      progress: needed ? Math.min(1, score / needed) : 0,
      met: score >= needed
    },
    adx != null && {
      key: "adx",
      label: t("gate.adx", "Trend strength (ADX)"),
      value: `${iso(Number(adx).toFixed(1))} / ${iso("25")}`,
      progress: Math.min(1, Number(adx) / 25),
      met: Number(adx) >= 25
    },
    {
      key: "filters",
      label: t("gate.filters", "News and macro filters"),
      value: blockers.length
        ? t("gate.filters_active", "one is holding")
        : t("gate.filters_clear", "clear"),
      progress: blockers.length ? 0 : 1,
      met: !blockers.length
    },
    {
      key: "market",
      label: t("gate.market", "The market"),
      value: marketOpen ? t("market.open", "Open") : t("market.closed", "Closed"),
      progress: marketOpen ? 1 : 0,
      met: !!marketOpen
    },
    halted && {
      key: "halt",
      label: t("gate.halt", "Operator risk halt"),
      value: t("gate.halt_on", "publishing is halted"),
      progress: 0,
      met: false
    }
  ].filter(Boolean);

  const met = rows.filter((r) => r.met).length;

  return html`
    <section className=${inline ? "gate gate-inline" : "panel surface gate"}>
      <header className="gate-head">
        <h3>${live
          ? t("gate.title_cleared", "What this call cleared")
          : t("gate.title", "What has to happen before there is a trade")}</h3>
        ${/* dir=ltr: a fraction is not a sentence, and RTL flipped "2/4"
              into "4/2" on screen. */""}
        <span className="gate-count" dir="ltr">${iso(String(met))}/${iso(String(rows.length))}</span>
      </header>
      <ul className="gate-rows">
        ${rows.map((row) => html`
          <li key=${row.key} className=${row.met ? "met" : ""}>
            <span className="gate-label">${row.label}</span>
            <span className="gate-bar">
              <span style=${{ transform: `scaleX(${Math.max(0.02, row.progress)})` }}></span>
            </span>
            ${/* A tick as well as a colour: a met condition must be
                  readable without seeing the difference between two greens. */""}
            <span className="gate-value" dir="auto">
              ${row.met && html`<i className="fa-solid fa-check" aria-hidden="true"></i>${" "}`}
              ${row.value}
            </span>
          </li>`)}
      </ul>
      ${withheld && html`
        <p className="gate-withheld">
          ${t("gate.withheld", "A setup exists but this profile's gate is holding it:")}${" "}
          <b>${describeGateReason(withheld, language) || withheld}</b>
        </p>`}
      ${(() => {
        // What the bars cannot say. The engine's own sentences, minus the ones
        // a row above already states as a number.
        const rest = (help || []).filter(
          (line) => !HELP_COVERED_BY_A_ROW.some((rx) => rx.test(String(line))));
        return rest.length ? html`
          <ul className="gate-help">
            ${rest.map((line) => html`<li key=${line}>${translateTechnicalReason(line)}</li>`)}
          </ul>` : null;
      })()}
      <p className="gate-note">
        ${live
          ? t("gate.note_cleared", "Every one of these had to be true before the call was published. None of them says the trade will win.")
          : t("gate.note", "Not a countdown. Nothing here promises these will complete — most days they do not, and that is a complete answer.")}
      </p>
    </section>`;
}

function SessionRibbon() {
  // THE TRADING DAY, DRAWN ONCE, WITH THE MEASURED FACT ON IT.
  //
  // Two things a gold trader needs before anything else: which session is
  // running, and whether this one moves. The second is not folklore here --
  // scripts/measure_sessions.py measured every window, and it contradicts the
  // advice everyone repeats: Asia's median hourly range is WIDER than London's
  // and New York's on this market. So the ribbon carries the number, not an
  // adjective.
  //
  // 21:00-00:00 UTC belongs to no window. It is drawn as its own segment and
  // counts DOWN to the Asian open, because a marker parked past the end of the
  // last session is a clock that has run off its own day.
  const { t, language } = useI18n();
  const [data, setData] = useState(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const payload = await (await authenticatedFetch("/api/sessions")).json();
        if (alive && payload.success) setData(payload);
      } catch (e) { /* the ribbon simply does not appear */ }
    };
    load();
    // The windows are fixed; only the marker moves, and it moves on the
    // client clock so the strip does not poll a server to draw a minute hand.
    const tick = setInterval(() => setNow(new Date()), 60000);
    const refresh = setInterval(load, 15 * 60000);
    return () => { alive = false; clearInterval(tick); clearInterval(refresh); };
  }, []);

  if (!data || !data.windows?.length) return null;

  const minutes = now.getUTCHours() * 60 + now.getUTCMinutes();
  const pct = (m) => (m / 1440) * 100;
  const toMin = (hhmm) => {
    const [h, m] = String(hhmm).split(":").map(Number);
    return h * 60 + m;
  };
  const current = data.now || {};
  const closed = current.market_open === false;
  const preOpen = current.phase === "pre_open";

  const segments = data.windows.map((w) => ({
    ...w,
    from: toMin(w.start),
    to: toMin(w.end) || 1440
  }));
  const lastEnd = Math.max(...segments.map((s) => s.to));
  if (lastEnd < 1440) {
    segments.push({ session: "between", label: t("ribbon.between", "Between sessions"),
                    from: lastEnd, to: 1440, liquidity: "none" });
  }

  const active = segments.find((s) => minutes >= s.from && minutes < s.to);
  const widest = segments.reduce((best, s) =>
    (s.median_range || 0) > (best?.median_range || 0) ? s : best, null);

  return html`
    <section className="ribbon" aria-label=${t("ribbon.title", "The trading day")}>
      ${/* THE FOUR FIGURES NEED THEIR UNIT, ALWAYS. They were explained only
            by the sentence underneath, and that sentence is replaced by a
            countdown for three hours of every day — so for those three hours
            the strip was four dollar amounts with no heading and no way to
            know what they measured. */""}
      <p className="ribbon-cap">
        ${t("ribbon.caption", "The trading day · median hourly range measured in each session")}
      </p>
      <div className="ribbon-track">
        ${segments.map((seg) => html`
          <div key=${seg.session}
               className=${`ribbon-seg ${seg.session}`
                 + (active && active.session === seg.session ? " is-now" : "")
                 + (seg.liquidity === "none" ? " is-quiet" : "")}
               style=${{ insetInlineStart: `${pct(seg.from)}%`, width: `${pct(seg.to - seg.from)}%` }}>
            ${/* Every band carries its name now that the strip runs the full
                  width of the glass. The gap between sessions is 12% of the
                  day — too narrow for a word at 390px, which is why it was
                  left blank and then read as a hole in the rendering. It gets
                  a short form at that width instead of nothing. */""}
            <span className="ribbon-label">
              ${seg.session === "between"
                ? html`<span className="ribbon-label-long">${sessionLabel(seg, language, t)}</span>
                       <span className="ribbon-label-short" aria-hidden="true">—</span>`
                : sessionLabel(seg, language, t)}
            </span>
            ${seg.median_range != null && html`
              <span className="ribbon-range" dir="ltr">$${iso(seg.median_range.toFixed(2))}</span>`}
          </div>`)}
        <span className="ribbon-now" style=${{ insetInlineStart: `${pct(minutes)}%` }}
              aria-hidden="true"></span>
      </div>
      <p className="ribbon-line">
        ${closed
          ? t("ribbon.closed", "The market is closed.")
          : preOpen && current.opens_in_minutes != null
            ? formatTemplate(t("ribbon.opens_in", "{label} opens in {time}"),
                             { /* The session being WAITED FOR, not the gap we
                                  are standing in: "Between sessions opens in
                                  1h32m" names the wrong thing. */
                               label: sessionLabel({ session: current.session }, language, t),
                               time: fmtSpan(current.opens_in_minutes * 60) })
            : formatTemplate(t("ribbon.in_session", "{label} — median hourly range {range}"),
                             { label: sessionLabel(active || {}, language, t),
                               range: current.median_range != null
                                 ? `$${current.median_range.toFixed(2)}` : "—" })}
      </p>
      ${/* The standing fact is a FOOTNOTE, not a second headline. It sat on
            the same line at the same size as the live state, so the sentence
            that changes and the sentence that never does competed as equals —
            and the eye lands on whichever is longer. */""}
      ${widest && html`
        <p className="ribbon-foot">${formatTemplate(
          t("ribbon.widest", "Widest measured: {label}. Not the session the advice names."),
          { label: sessionLabel(widest, language, t) })}</p>`}
    </section>`;
}

function sessionLabel(seg, language, t) {
  const keys = {
    asia: ["ribbon.asia", "Asia"],
    london: ["ribbon.london", "London"],
    london_ny_overlap: ["ribbon.overlap", "London / NY"],
    new_york: ["ribbon.new_york", "New York"],
    between: ["ribbon.between", "Between sessions"]
  };
  const pair = keys[seg.session];
  return pair ? t(pair[0], pair[1]) : (seg.label || "");
}

const SCORE_ROWS = ["rsi", "macd", "stoch", "ema", "adx"];

function scoreSentence(score, t) {
  // The reading's own sentence, carrying its own numbers. `key` and `args`
  // come from the scorer, so the grid cannot describe a reading in words the
  // engine did not produce.
  const fallbacks = {
    rsi_oversold: "RSI {value} — stretched low",
    rsi_overbought: "RSI {value} — stretched high",
    rsi_mid: "RSI {value} — inside the band, saying nothing",
    macd_positive: "MACD histogram {value} — above the line",
    macd_negative: "MACD histogram {value} — below the line",
    macd_flat: "MACD histogram flat",
    stoch_oversold: "Stochastic {value} — low",
    stoch_overbought: "Stochastic {value} — high",
    stoch_mid: "Stochastic {value} — mid range",
    ema_above: "EMA20 {fast} above EMA50 {slow}",
    ema_below: "EMA20 {fast} below EMA50 {slow}",
    ema_tangled: "EMA20 {fast} and EMA50 {slow} are tangled",
    adx_trending: "ADX {value} — there is a trend to trade",
    adx_weak: "ADX {value} — no trend worth trading"
  };
  return formatTemplate(t(`scores.${score.key}`, fallbacks[score.key] || score.key),
                        score.args || {});
}

function IndicatorGrid({ timeframe, inline = false }) {
  // EVERY INDICATOR'S OWN OPINION, ON EVERY FRAME, AS A GRID.
  //
  // "3 of 7" cannot tell anybody that RSI was flat while MACD leaned long on
  // the hour and the 4h disagreed with both. The engine has always kept these
  // rows apart internally -- this is the first screen that shows them that
  // way, and it is deliberately free: /api/indicator-scores is the same
  // arithmetic the expert panel reads, with no model call behind it.
  //
  // The strength bar is a DISTANCE, never a probability. Nothing here is a
  // confidence and nothing here is thresholded into a signal.
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const payload = await (await authenticatedFetch(
          `/api/indicator-scores?timeframe=${encodeURIComponent(timeframe)}`)).json();
        if (alive && payload.success) setData(payload);
      } catch (e) { /* the grid simply does not appear */ }
    })();
    return () => { alive = false; };
  }, [timeframe]);

  if (!data || !data.scores || !data.scores.length) return null;

  const frames = data.frames || [];
  const at = (indicator, frame) =>
    data.scores.find((s) => s.indicator === indicator && s.frame === frame);
  const rows = SCORE_ROWS.filter((name) => frames.some((f) => at(name, f)));
  const split = data.disagreement;

  return html`
    <section className=${inline ? "score-grid score-grid-inline" : "panel surface score-grid"}>
      <header className="score-head">
        ${inline
          ? html`<h4>${t("scores.title_short", "Scored, frame by frame")}</h4>`
          : html`<h3>${t("scores.title", "What each indicator reads, frame by frame")}</h3>`}
        ${split != null && html`
          <span className=${`score-split ${split >= 0.5 ? "wide" : "narrow"}`}>
            ${split >= 0.5
              ? t("scores.split_wide", "They disagree")
              : t("scores.split_narrow", "They mostly agree")}
          </span>`}
      </header>

      ${/* NOT dir="ltr" on the table. Forcing it put 1H in the left column,
            so an Arabic reader met 4H first — while the panel on the same
            page lays its cards out right-to-left and shows 1h first. The
            numerals are isolated individually instead. */""}
      <div className="score-table">
        <div className="score-row score-header">
          <span className="score-name"></span>
          ${frames.map((f) => html`<span key=${f} className="score-cell-head" dir="ltr">${f}</span>`)}
        </div>
        ${rows.map((name) => html`
          <div className="score-row" key=${name}>
            <span className="score-name">${t(`scores.${name}`, name.toUpperCase())}</span>
            ${frames.map((frame) => {
              const cell = at(name, frame);
              if (!cell) {
                return html`<span key=${frame} className="score-cell empty">—</span>`;
              }
              const id = `${name}@${frame}`;
              return html`
                <button key=${frame} type="button"
                        className=${`score-cell ${cell.side}${open === id ? " is-open" : ""}`}
                        onClick=${() => setOpen(open === id ? null : id)}
                        aria-label=${scoreSentence(cell, t)}>
                  <span className="score-bar"
                        style=${{ transform: `scaleX(${Math.max(0.06, cell.strength)})` }}></span>
                  ${/* The word AND the number. A cell that shows only a
                       direction is a colour with a label on it; the reading
                       itself is what lets someone check the call rather than
                       take it. Neutral cells keep their number for exactly
                       that reason — "RSI 53.7, saying nothing" is a fact. */""}
                  ${/* ADX IS NOT "FLAT". It measures whether a trend exists,
                       never which way it points, so the scorer gives it no
                       side — and printing it as "محايد" beside RSI's genuine
                       "no opinion" made the page contradict itself: the same
                       ADX reading was drawn as neutral here and ticked as a
                       MET condition in the gauge one card down. Its two
                       states are trend / no trend, which is what its own
                       reason key already says. */""}
                  <b>${cell.indicator === "adx"
                    ? (cell.key === "adx_trending"
                        ? t("scores.trending", "trend")
                        : t("scores.no_trend", "no trend"))
                    : cell.side === "neutral"
                      ? t("scores.flat", "flat")
                      : t(`signals.${cell.side}`, cell.side)}</b>
                  ${/* EMA is scored from a PAIR — indicator_scores builds
                       {fast, slow} and never a single `value` — so this row
                       was the one cell in the grid with a word and no figure,
                       breaking both the rule stated three lines up and the
                       two-line baseline every other row sits on. The gap
                       between the averages is the number that matters and the
                       one the sentence quotes. */""}
                  ${cell.args?.value != null
                    ? html`<small className="score-figure">${iso(String(cell.args.value))}</small>`
                    : (cell.args?.fast != null && cell.args?.slow != null) && html`
                        <small className="score-figure">
                          ${iso((cell.args.fast - cell.args.slow >= 0 ? "+" : "")
                                + (cell.args.fast - cell.args.slow).toFixed(2))}
                        </small>`}
                </button>`;
            })}
          </div>`)}
      </div>

      ${open && (() => {
        const [name, frame] = open.split("@");
        const cell = at(name, frame);
        return cell && html`
          <p className="score-detail">
            <b>${frame}</b> · ${scoreSentence(cell, t)}
          </p>`;
      })()}

      <p className="score-note">
        ${t("scores.note", "A distance, not a probability. ADX never carries a side — it says whether there is a trend at all.")}
      </p>
    </section>`;
}

function ExpertPanel({ timeframe, auto = false }) {
  // SIX EXPERTS, ONE DEBATE ROUND, AND A CHAIR.
  //
  // The API has existed since the panel landed and nothing rendered it, so the
  // structure the product is built on was invisible to the person paying for
  // it. What makes this a panel rather than one opinion with extra steps is
  // the two things shown here that a conclusion alone would hide: the
  // AGREEMENT RATE (near 1.0 means the experts are not independent and the
  // whole structure is cost without information) and the DISSENTS.
  //
  // It is SHADOW. It rules on the same market the gate rules on, both are
  // logged, and it issues nothing -- said on the card, because a panel verdict
  // that reads like a call is a second signal nobody measured.
  const { t, language } = useI18n();
  const [state, setState] = useState({ loading: false, data: null, error: null });

  const run = useCallback(async (force) => {
    setState({ loading: true, data: null, error: null });
    try {
      // The language rides with the request: the analysts write their reasons
      // in it, rather than being translated afterwards by a second author.
      const url = `/api/panel/shadow?timeframe=${encodeURIComponent(timeframe)}`
        + `&language=${encodeURIComponent(language)}`
        + (force ? "&force=1" : "");
      const payload = await (await authenticatedFetch(url)).json();
      if (!payload.success) {
        setState({ loading: false, data: null,
                   error: t("panel.failed", "The panel could not be reached.") });
        return;
      }
      setState({ loading: false, data: payload, error: null });
    } catch (e) {
      setState({ loading: false, data: null,
                 error: t("panel.failed", "The panel could not be reached.") });
    }
  }, [timeframe, t, language]);

  // On the analysis page the panel IS the answer, so it convenes on arrival
  // and on a frame change — and it FORCES, because the wake gate exists to
  // stop a 60-second loop spending money, not to refuse a reader who came to
  // this page and asked. On the dashboard it stays on a button.
  useEffect(() => {
    if (!auto) return undefined;
    run(true);
    return undefined;
  }, [auto, run]);

  const payload = state.data;
  const panel = payload?.panel;
  const woke = payload?.woke !== false;

  return html`
    <section className="panel surface council">
      <header className="council-head">
        <div>
          <h3>${t("panel.title", "The panel")}</h3>
          ${/* "Six experts" over twelve cards. The roster is expert × FRAME
                — six specialities on each timeframe scored — so the sentence
                and the grid disagreed by a factor of two, on the card whose
                whole claim is that its members are independent. The count is
                read from the ruling rather than written into the copy. */""}
          <small>
            ${panel
              ? formatTemplate(
                  t("panel.subtitle_n", "{experts} readings — {n} specialities across {f} timeframes, each read independently, one debate round, and a chair. It issues nothing."),
                  { experts: iso(String((panel.round_one || []).length)),
                    n: iso(String(new Set((panel.round_one || [])
                      .map((o) => String(o.expert).split("@")[0])).size)),
                    f: iso(String(new Set((panel.round_one || [])
                      .map((o) => String(o.expert).split("@")[1] || "")).size)) })
              : t("panel.subtitle", "Specialists read the market independently, argue once, and a chair rules. It issues nothing.")}
          </small>
        </div>
        <button type="button" className="btn btn-secondary" disabled=${state.loading}
                onClick=${() => run(false)}>
          ${state.loading
            ? html`<i className="fa-solid fa-spinner fa-spin"></i>`
            : html`<i className="fa-solid fa-users-viewfinder"></i>`}${" "}
          ${state.loading ? t("panel.running", "Sitting…") : t("panel.ask", "Convene")}
        </button>
      </header>

      ${state.error && html`<p className="council-error">${state.error}</p>`}

      ${payload && payload.available === false && html`
        <p className="council-quiet">${t("panel.no_key", "The panel is not configured on this server.")}</p>`}

      ${payload && payload.available !== false && !woke && html`
        <div className="council-quiet">
          <p>${t("panel.asleep", "The readings have not reached a level worth spending the panel on.")}</p>
          ${payload.wake?.reason_key && html`
            <small>${t(`panel.wake.${payload.wake.reason_key}`, payload.wake.reason_key)}</small>`}
          <button type="button" className="btn btn-ghost" onClick=${() => run(true)}>
            ${t("panel.force", "Convene it anyway")}
          </button>
        </div>`}

      ${panel && html`
        <div className="council-ruling">
          <div className=${`council-side ${panel.side || "hold"}`}>
            <small>${t("panel.chair", "The chair")}</small>
            <b>${t(`signals.${panel.side}`, String(panel.side || "hold").toUpperCase())}</b>
          </div>
          ${/* THE CHAIR MUST ADD SOMETHING. Its ruling was rendering every
                reason it gave, and the analyst cards below repeated the same
                sentences character for character — five bullets in a bordered
                box, all five already on screen underneath, so the reader
                checks twice whether they missed something. Only what no
                expert already said survives; if nothing does, the verdict
                word stands alone, which is itself the honest answer ("the
                chair agreed with them and had nothing to add"). Same pattern
                as HELP_COVERED_BY_A_ROW one component up. */""}
          ${(() => {
            const said = new Set((panel.round_one || [])
              .flatMap((o) => (o.reasons || []).map((r) => String(r).trim())));
            const fresh = (panel.reasons || [])
              .filter((r) => !said.has(String(r).trim()));
            return fresh.length
              ? html`<ul className="council-reasons">
                  ${fresh.map((r, i) => html`<li key=${i}>${r}</li>`)}
                </ul>`
              : html`<p className="council-agreed">
                  ${t("panel.chair_agreed", "The chair added nothing to what the analysts said.")}
                </p>`;
          })()}
        </div>

        ${payload.agreement_rate != null && html`
          <div className="council-agreement">
            <div className="council-meter">
              <span style=${{ width: `${Math.round(payload.agreement_rate * 100)}%` }}></span>
            </div>
            <small>
              ${formatTemplate(
                t("panel.agreement", "{pct}% of the independent pairs landed on the same side"),
                { pct: Math.round(payload.agreement_rate * 100) })}
              ${" — "}
              ${payload.agreement_rate > 0.9
                ? t("panel.agreement_high", "that high means they are reading the same thing under different names")
                : t("panel.agreement_ok", "they are genuinely seeing different things")}
            </small>
          </div>`}

        <div className="council-experts">
          ${(panel.round_one || []).map((op) => {
            const changed = (panel.changed || []).includes(op.expert);
            const dissent = (panel.dissents || []).some((d) => d.expert === op.expert);
            return html`
              <article key=${op.expert} className=${`council-card ${op.side}${dissent ? " dissent" : ""}`}>
                <header>
                  <b>${t(`panel.expert.${String(op.expert).split("@")[0]}`, String(op.expert).split("@")[0])}</b>
                  <span className="council-frame">${String(op.expert).split("@")[1] || ""}</span>
                </header>
                <span className=${`council-vote ${op.side}`}>
                  ${t(`signals.${op.side}`, String(op.side).toUpperCase())}
                </span>
                <ul>${(op.reasons || []).slice(0, 3).map((r, i) => html`<li key=${i}>${r}</li>`)}</ul>
                ${changed && html`<small className="council-changed">
                  ${t("panel.changed", "revised after the debate")}</small>`}
                ${dissent && html`<small className="council-dissent">
                  ${t("panel.dissent", "still disagrees with the chair")}</small>`}
              </article>`;
          })}
        </div>

        <p className="council-shadow">
          ${t("panel.shadow", "Shadow only: this panel rules on the same market the live gate rules on, and issues nothing. Both are logged so the two can be compared later.")}
        </p>`}
    </section>`;
}

function SessionBriefing({ timeframe }) {
  const { t } = useI18n();
  const [brief, setBrief] = useState(null);
  // Collapsed by default, remembered per device. Expanded, the strip ate the
  // entire first phone screen before the price -- and its late arrival shoved
  // the page down mid-read. The headline and the event warning (the two
  // things that change what you do next) always show; the facts are one tap.
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem("sv_brief_open") === "1"; }
    catch (e) { return false; }
  });
  const toggleOpen = useCallback(() => setOpen((v) => {
    try { localStorage.setItem("sv_brief_open", v ? "0" : "1"); } catch (e) { /* private mode */ }
    return !v;
  }), []);

  useEffect(() => {
    let cancelled = false;
    authenticatedFetch(`/api/briefing?timeframe=${timeframe || ""}`)
      .then((r) => r.json())
      .then((p) => { if (!cancelled && p.success) setBrief(p.briefing); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [timeframe]);

  // Reserve the slot while loading: returning null here made the whole strip
  // pop in a beat after first paint and shove the price card down mid-read.
  if (!brief) {
    // A skeleton that admits it is loading -- the bare reserved box read as
    // a broken empty card on slower fetches.
    return html`
      <section className="briefing is-loading" aria-hidden="true">
        <div className="briefing-head"><span className="briefing-skeleton"></span></div>
      </section>`;
  }
  const warnings = brief.warnings || [];
  const lines = brief.lines || [];

  // The briefing, said out loud: the browser's own voices, the user's own
  // language, zero server cost. A trader getting dressed at 8am does not
  // read -- every platform the owner compares against has a voice somewhere.
  const speak = () => {
    try {
      const synth = window.speechSynthesis;
      if (!synth) return;
      if (synth.speaking) { synth.cancel(); return; }
      const parts = [briefHeadline(t, brief)];
      warnings.forEach((w) => { if (w.text) parts.push(String(w.text)); });
      (brief.lines || []).forEach((line) => {
        const made = briefLine(t, line);
        parts.push(typeof made === "string" ? made : made.text);
      });
      const utterance = new SpeechSynthesisUtterance(parts.filter(Boolean).join(". "));
      const lang = document.documentElement.lang === "ar" ? "ar" : "en-US";
      utterance.lang = lang;
      const voice = synth.getVoices().find((v) => v.lang && v.lang.startsWith(lang.slice(0, 2)));
      if (voice) utterance.voice = voice;
      utterance.rate = 0.95;
      synth.speak(utterance);
    } catch (e) { /* no voices on this device */ }
  };

  return html`
    <section className=${"briefing" + (warnings.length ? " has-warning" : "")}>
      <button className="briefing-head" onClick=${toggleOpen}
              aria-expanded=${open}>
        <span className="briefing-headline">${briefHeadline(t, brief)}</span>
        ${typeof window !== "undefined" && window.speechSynthesis && html`
          <span className="briefing-voice" role="button" tabIndex="0"
                aria-label=${t("brief.listen", "Listen to the briefing")}
                title=${t("brief.listen", "Listen to the briefing")}
                onClick=${(e) => { e.stopPropagation(); speak(); }}
                onKeyDown=${(e) => { if (e.key === "Enter") { e.stopPropagation(); speak(); } }}>
            <i className="fa-solid fa-volume-high"></i>
          </span>`}
        <i className=${open ? "fa-solid fa-chevron-up" : "fa-solid fa-chevron-down"}></i>
      </button>

      ${warnings.map((w) => html`
        <div key=${w.key} className="briefing-warning">
          <i className="fa-solid fa-triangle-exclamation"></i>
          <span>
            ${/* Event titles are release names (Core PPI m/m) and stay as
                  published; the sentences around them translate. */""}
            <b>${w.key === "calendar_unknown"
                  ? t("brief.calendar_unknown", "The calendar could not be read.")
                  : html`<bdi>${w.text}</bdi>`}${w.minutes != null ? ` · ${iso(formatCountdown(w.minutes))}` : ""}</b>
            ${w.key === "event" && html`<small>${t("brief.stands_aside", "The engine stands aside 45 minutes before and 15 after.")}</small>`}
            ${w.key === "calendar_unknown" && html`<small>${t("brief.calendar_unknown_note", "Treat the schedule as unknown, not as clear.")}</small>`}
            ${w.key !== "event" && w.key !== "calendar_unknown" && w.note && html`<small>${w.note}</small>`}
          </span>
        </div>`)}

      ${open && lines.length > 0 && html`
        <ul className="briefing-lines">
          ${lines.map((line) => {
            const made = briefLine(t, line);
            const text = typeof made === "string" ? made : made.text;
            const note = typeof made === "string" ? line.note : made.note;
            return html`
            <li key=${line.key}>
              <span>${text}</span>
              ${note && html`<small>${note}</small>`}
              ${line.as_of && html`<em dir="ltr">${line.as_of}</em>`}
            </li>`;})}
        </ul>`}
    </section>`;
}


// What the engine knows about one user-drawn line. Every row is a fact it
// computed; there is deliberately no verdict on whether the line is any good,
// because the product has not measured that and saying it would be the exact
// invention this whole design refuses.
function DrawingFacts({ m, t }) {
  const rows = [];
  if (m.price != null) {
    rows.push([t("draw.at_price", "At"), iso(formatPrice(m.price))]);
  }
  if (m.coincides_with) {
    // The line the user drew is PROVABLY on a level the engine computed --
    // not near it. That is the sentence no other charting app can produce.
    rows.push([t("draw.sits_on", "Sits on"),
               `${m.coincides_with.sources} · ${iso(formatPrice(m.coincides_with.price))}`]);
  } else if (m.price != null) {
    rows.push([t("draw.sits_on", "Sits on"),
               t("draw.nothing_here", "nothing the engine computed")]);
  }
  if (m.touches != null) {
    rows.push([t("draw.touches", "Bars that reached it"), iso(String(m.touches))]);
  }
  if (m.distance != null) {
    rows.push([t("draw.distance", "From price"),
               `${iso(m.distance > 0 ? "+" + m.distance : String(m.distance))}` +
               (m.distance_atr != null ? ` · ${iso(m.distance_atr + " ATR")}` : "")]);
  }
  if (m.delta != null) {
    rows.push([t("draw.move", "Move"),
               `${iso((m.delta > 0 ? "+" : "") + m.delta)}` +
               (m.pct != null ? ` · ${iso(m.pct + "%")}` : "") +
               (m.delta_atr != null ? ` · ${iso(m.delta_atr + " ATR")}` : "")]);
  }
  return html`
    <dl className="drawing-facts-list">
      ${rows.map(([label, value]) => html`
        <div key=${label}><dt>${label}</dt><dd dir="ltr">${value}</dd></div>`)}
    </dl>`;
}


// The floor that ends a funded account. Shown as room LEFT, not as loss taken:
// "you have $1,850 before the daily limit" is a number a trader can size
// against, and "-$3,150 today" is a number they have to do arithmetic on while
// a position is open.
//
// Floating profit and loss is included, because firm limits count it. A strip
// that showed only realised loss would report room that does not exist.
function FloorStrip() {
  const { t } = useI18n();
  const [state, setState] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => authenticatedFetch("/api/account-profile")
      .then((r) => r.json())
      .then((p) => { if (!cancelled && p.success) setState(p); })
      .catch(() => {});
    load();
    const timer = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);

  const floor = state?.floor;
  if (!floor?.configured) return null;

  const daily = floor.daily_room;
  const max = floor.max_room;
  const pct = (room, limit) =>
    limit ? Math.max(0, Math.min(100, (room / limit) * 100)) : 0;

  return html`
    <section className=${"floor-strip" + (floor.breached ? " is-breached" : "")}>
      <div className="floor-head">
        <span>${t("floor.title", "Room before your floor")}</span>
        ${state.floating !== 0 && html`
          <small dir="ltr">
            ${t("floor.floating", "incl. floating")} ${iso(state.floating.toFixed(2))}
          </small>`}
      </div>

      ${daily != null && html`
        <div className="floor-bar">
          <div className="floor-bar-label">
            <span>${t("floor.daily", "Today")}</span>
            <b dir="ltr" className=${daily <= 0 ? "neg" : ""}>
              ${iso("$" + daily.toFixed(0))}
            </b>
          </div>
          <div className="floor-track">
            <div className="floor-fill" style=${{ width: pct(daily, floor.daily_limit) + "%" }}></div>
          </div>
        </div>`}

      ${max != null && html`
        <div className="floor-bar">
          <div className="floor-bar-label">
            <span>${t("floor.overall", "Overall")}</span>
            <b dir="ltr" className=${max <= 0 ? "neg" : ""}>${iso("$" + max.toFixed(0))}</b>
          </div>
          <div className="floor-track">
            <div className="floor-fill" style=${{ width: pct(max, floor.max_loss) + "%" }}></div>
          </div>
        </div>`}

      ${floor.breached && html`
        <p className="floor-breached">
          ${floor.breached === "daily"
            ? t("floor.breached_daily", "Today's limit is gone. Nothing here stops you trading — but your firm's rule already has.")
            : t("floor.breached_max", "The overall floor is gone.")}
        </p>`}

      ${/* Closed trades with no recorded lot size cannot be valued, so the
           floor is optimistic by exactly their loss. Said out loud: a number
           that is quietly incomplete is worse than one that admits it. */""}
      ${state.unpriced_trades > 0 && html`
        <p className="floor-unpriced">
          ${formatTemplate(t("floor.unpriced", "{n} earlier trades have no recorded size, so they are not counted here. This room is at best correct, and at worst too generous."),
            { n: iso(String(state.unpriced_trades)) })}
        </p>`}

      ${state.profile.source && html`
        <p className="floor-source">
          ${t("floor.from", "Rule read from")} ${state.profile.source}
          ${state.profile.read_on ? ` · ${state.profile.read_on}` : ""} ·
          ${t("floor.verify", "verify against your own dashboard")}
        </p>`}
    </section>`;
}


// The alerts a user armed, and what each one did. A product that lets you set
// alerts but never shows you the list teaches you not to trust that they were
// set -- and a fired alert that vanishes gives you no way to check that the
// level you were watching was actually reached.
// Indicator alerts: "tell me when RSI crosses 30" -- created here, fired by
// the daemon on the engine's own values, delivered as push. The creator is
// deliberately bounded (two metrics, two directions) -- a curated pair that
// always works beats a formula builder that mostly doesn't.
function ConditionAlerts() {
  const { t } = useI18n();
  const [alerts, setAlerts] = useState([]);
  const [form, setForm] = useState({ metric: "rsi", op: "below", value: "30", timeframe: "1h" });
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await authenticatedFetch("/api/condition-alerts");
      const p = await r.json();
      if (p.success) setAlerts(p.alerts || []);
    } catch (e) { /* the card renders empty */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true); setNote(null);
    try {
      const r = await authenticatedFetch("/api/condition-alerts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, value: Number(form.value) }),
      });
      const p = await r.json();
      if (!p.success) throw new Error(p.error || "failed");
      await load();
    } catch (e) {
      setNote(e.message === "value_out_of_range"
        ? t("cond.range", "That value can never fire for this indicator.")
        : t("cond.failed", "Could not create the alert."));
    }
    setBusy(false);
  };

  const cancel = async (id) => {
    try {
      await authenticatedFetch(`/api/condition-alerts/${id}`, { method: "DELETE" });
      await load();
    } catch (e) { /* next load corrects */ }
  };

  const live = alerts.filter((a) => a.active);
  const done = alerts.filter((a) => !a.active && a.fired_at).slice(0, 5);
  const word = (a) => `${a.metric.toUpperCase()} ${a.op === "above"
    ? t("cond.above", "crosses above") : t("cond.below", "crosses below")} ${iso(a.value)} · ${a.timeframe}`;

  return html`
    <div className="settings-card">
      <strong>${t("cond.title", "Indicator alerts")}</strong>
      <p className="cond-note">${t("cond.note", "Fires on the same RSI/ADX the engine reads — an alert and the verdict can never disagree.")}</p>
      <div className="cond-form" dir="ltr">
        <select value=${form.metric} disabled=${busy}
                onChange=${(e) => setForm((f) => ({ ...f, metric: e.target.value }))}>
          <option value="rsi">RSI</option>
          <option value="adx">ADX</option>
        </select>
        <select value=${form.op} disabled=${busy}
                onChange=${(e) => setForm((f) => ({ ...f, op: e.target.value }))}>
          <option value="below">${t("cond.below", "crosses below")}</option>
          <option value="above">${t("cond.above", "crosses above")}</option>
        </select>
        <input type="number" inputMode="decimal" value=${form.value} disabled=${busy}
               onInput=${(e) => setForm((f) => ({ ...f, value: e.target.value }))} />
        <select value=${form.timeframe} disabled=${busy}
                onChange=${(e) => setForm((f) => ({ ...f, timeframe: e.target.value }))}>
          ${Object.keys(TIMEFRAME_SECONDS).map((tf) => html`<option key=${tf} value=${tf}>${tf}</option>`)}
        </select>
        <button className="btn btn-secondary" disabled=${busy || !form.value}
                onClick=${create}>${t("cond.create", "Watch it")}</button>
      </div>
      ${note && html`<p className="cond-error" role="status">${note}</p>`}
      ${live.length > 0 && html`
        <ul className="alert-rows">
          ${live.map((a) => html`
            <li key=${a.id}>
              <span className="alert-row-what"><b>${word(a)}</b>
                <small>${t("alerts.watching_short", "watching")}</small></span>
              <button onClick=${() => cancel(a.id)}
                      aria-label=${t("alerts.cancel", "Cancel this alert")}>
                <i className="fa-solid fa-xmark"></i>
              </button>
            </li>`)}
        </ul>`}
      ${done.length > 0 && html`
        <${React.Fragment}>
          <small className="alert-rows-head">${t("alerts.already_fired", "Already fired")}</small>
          <ul className="alert-rows">
            ${done.map((a) => html`
              <li key=${a.id} className="is-done">
                <span className="alert-row-what"><b>${word(a)}</b>
                  <small>${formatTemplate(t("cond.fired_at", "reached {v}"), { v: iso(Number(a.fired_value).toFixed(1)) })}</small></span>
              </li>`)}
          </ul>
        <//>`}
    </div>`;
}

function AlertsList() {
  const { t } = useI18n();
  const [alerts, setAlerts] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await authenticatedFetch("/api/price-alerts");
      const p = await r.json();
      if (p.success) setAlerts(p.alerts || []);
    } catch (error) { setAlerts([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const cancel = useCallback(async (id) => {
    setBusy(id);
    try {
      await authenticatedFetch(`/api/price-alerts/${id}`, { method: "DELETE" });
      await load();
    } catch (error) { /* the row stays; the next load corrects it */ }
    setBusy(null);
  }, [load]);

  if (!alerts || alerts.length === 0) return null;
  const live = alerts.filter((a) => a.active);
  const done = alerts.filter((a) => !a.active).slice(0, 10);

  const row = (a) => html`
    <li key=${a.id} className=${a.active ? "" : "is-done"}>
      <span className="alert-row-price" dir="ltr">${iso(formatPrice(a.price))}</span>
      <span className="alert-row-what">
        <b>${levelNote(t, a.note) || t(`alerts.kind_${a.kind}`, a.kind)}</b>
        <small>
          ${a.active
            ? t("alerts.watching_short", "watching")
            : formatTemplate(t("alerts.fired_at", "reached {price}"),
                { price: iso(formatPrice(a.fired_price ?? a.price)) })}
          ${a.fired_at ? ` · ${new Date(a.fired_at * 1000).toLocaleString(appDateLocale())}` : ""}
        </small>
      </span>
      ${/* Boolean-coerced: `active` arrives from SQLite as 0 or 1, and
           `0 && jsx` renders the ZERO -- a stray "0" appeared at the end of
           every fired alert. */""}
      ${!!a.active && html`
        <button disabled=${busy === a.id} onClick=${() => cancel(a.id)}
                aria-label=${t("alerts.cancel", "Cancel this alert")}>
          <i className="fa-solid fa-xmark"></i>
        </button>`}
    </li>`;

  return html`
    <div className="settings-card">
      <strong>${t("alerts.your_alerts", "Your price alerts")}</strong>
      ${live.length > 0 && html`<ul className="alert-rows">${live.map(row)}</ul>`}
      ${done.length > 0 && html`
        <${React.Fragment}>
          <small className="alert-rows-head">${t("alerts.already_fired", "Already fired")}</small>
          <ul className="alert-rows">${done.map(row)}</ul>
        <//>`}
    </div>`;
}


// First run. Position sizing is dead until a user enters a balance, and
// nothing tells them that -- so a new subscriber sees "size: —" on every setup
// and concludes the feature is broken rather than unconfigured.
//
// Three questions, one screen, all skippable. Not a tour: a tour teaches
// nothing and is dismissed. These are the answers the app cannot compute on
// its own, and each one visibly turns something on.
// The setup wizard: one question per screen, each step savable or skippable
// on its own, replayable any time from Settings. Every answer it collects is
// also editable later in Settings -- the wizard is a doorway, never a lock.
function FirstRun({ onDone }) {
  const { t, language } = useI18n();
  const [styles, setStyles] = useState([]);
  const [choice, setChoice] = useState(null);
  const [balance, setBalance] = useState("");
  const [risk, setRisk] = useState("1");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(0);
  const [pushNote, setPushNote] = useState(null);

  useEffect(() => {
    let alive = true;
    authenticatedFetch("/api/trading-styles").then((r) => r.json())
      .then((p) => { if (alive) setStyles(p.styles || p || []); })
      .catch(() => {});
    // Prefill from what the server already knows, so a replay edits
    // instead of interrogating from scratch.
    authenticatedFetch("/api/preferences").then((r) => r.json())
      .then((p) => {
        if (!alive) return;
        const prefs = p.preferences || {};
        if (prefs.trading_style) setChoice(prefs.trading_style);
        if (prefs.account_balance) setBalance(String(prefs.account_balance));
        if (prefs.risk_percent) setRisk(String(prefs.risk_percent));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const save = useCallback(async (body) => {
    if (!Object.keys(body).length) return;
    setBusy(true);
    try {
      await authenticatedFetch("/api/preferences", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    } catch (error) { /* editable later in Settings either way */ }
    setBusy(false);
  }, []);

  const finish = useCallback(() => {
    try {
      localStorage.setItem("sv-first-run-done", "1");
      localStorage.removeItem("sv_wizard_replay");
    } catch (e) { /* private mode */ }
    onDone();
  }, [onDone]);

  const enableAlerts = useCallback(async () => {
    setBusy(true);
    try {
      const result = await enablePushNotifications();
      setPushNote(result.ok ? "on" : (result.reason || "failed"));
    } catch (e) { setPushNote("failed"); }
    setBusy(false);
  }, []);

  const steps = [
    { key: "style",
      title: t("firstrun.step_style", "How do you trade?"),
      note: t("firstrun.step_style_note", "This decides the timeframe you watch, the levels you get, and which alerts reach you. Change it any time in Settings."),
      next: () => save(choice ? { trading_style: choice } : {}),
      body: html`
        <div className="firstrun-styles">
          ${styles.map((style) => html`
            <button key=${style.key} type="button"
                    className=${"firstrun-style" + (choice === style.key ? " is-active" : "")}
                    onClick=${() => setChoice(style.key)}>
              <b>${language === "ar" ? style.name_ar : style.name_en}</b>
              <small dir="ltr">${style.default_timeframe} · ${style.reward_risk_ratio}:1</small>
            </button>`)}
        </div>` },
    { key: "risk",
      title: t("firstrun.step_risk", "What does a trade risk?"),
      note: t("firstrun.note", "Used only to work out a lot size on your own device's numbers. Nothing here is sent to a broker, and this app never places an order."),
      next: () => save({
        ...(balance ? { account_balance: Number(balance) } : {}),
        ...(risk ? { risk_percent: Number(risk) } : {}),
      }),
      body: html`
        <div className="firstrun-row">
          <label className="firstrun-field">
            <span>${t("sizing.balance", "Balance ($)")}</span>
            <input type="number" inputMode="decimal" value=${balance}
                   placeholder="10000"
                   onInput=${(e) => setBalance(e.target.value)} />
          </label>
          <label className="firstrun-field">
            <span>${t("sizing.risk", "Risk %")}</span>
            <input type="number" inputMode="decimal" step="0.1" value=${risk}
                   onInput=${(e) => setRisk(e.target.value)} />
          </label>
        </div>` },
    { key: "alerts",
      title: t("firstrun.step_alerts", "Want the desk to come to you?"),
      note: t("firstrun.step_alerts_note", "One tap turns on signal tickets, their outcomes, and the morning briefing — for your style only. You can test it from Settings any time."),
      next: null,
      body: html`
        <div className="firstrun-alerts">
          <button className="btn btn-primary" disabled=${busy || pushNote === "on"}
                  onClick=${enableAlerts}>
            <i className="fa-solid fa-bell"></i>
            ${pushNote === "on"
              ? t("firstrun.alerts_on", "Alerts are on")
              : t("firstrun.alerts_enable", "Turn on alerts")}
          </button>
          ${pushNote && pushNote !== "on" && html`
            <p className="firstrun-note">${t("alerts." + pushNote, t("alerts.failed", "Could not enable alerts — you can retry from Settings."))}</p>`}
        </div>` },
  ];
  const current = steps[step];
  const last = step === steps.length - 1;
  const advance = async () => {
    if (current.next) await current.next();
    if (last) finish(); else setStep(step + 1);
  };

  return html`
    <div className="firstrun-scrim">
      <section className="firstrun" role="dialog" aria-modal="true">
        <div className="tour-dots" aria-hidden="true">
          ${steps.map((_, i) => html`<span key=${i} className=${i === step ? "is-on" : ""}></span>`)}
        </div>
        <h1>${current.title}</h1>
        <p className="firstrun-intro">${current.note}</p>
        ${current.body}
        <div className="firstrun-actions">
          <button className="btn btn-primary" disabled=${busy} onClick=${advance}>
            ${last ? t("firstrun.save", "Save and start") : t("tour.next", "Next")}
          </button>
          <button className="btn btn-quiet" disabled=${busy}
                  onClick=${() => (last ? finish() : setStep(step + 1))}>
            ${t("firstrun.skip_step", "Skip this step")}
          </button>
        </div>
      </section>
    </div>`;
}


// A quiet day is the product working, not the product failing.
//
// The engine reaches a setup roughly 3% of the time. For a subscriber that
// means most days deliver nothing, and "nothing" is indistinguishable from a
// broken app unless the product accounts for its own silence. This is that
// accounting: what was checked, what was refused, and why -- so a day with no
// trade still has an artifact with the user's money-saving in it.
function StandAsideReceipt({ timeframe }) {
  const { t, translateTechnicalReason } = useI18n();
  const [state, setState] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    authenticatedFetch(`/api/stand-aside?timeframe=${timeframe || ""}`)
      .then((r) => r.json())
      .then((p) => {
        if (cancelled) return;
        if (p.success) setState(p.receipt); else setFailed(true);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [timeframe]);

  // This sits behind a fold titled "What did the engine do today?" -- a
  // question that must never open onto silence. Every branch answers it:
  // stood aside (the receipt), reached a setup (the summary), or cannot
  // say (the honest failure). Returning null here left a flipping chevron
  // over an empty panel.
  if (failed) {
    return html`
      <p className="receipt-note">
        ${t("receipt.unavailable", "The day's account is not available right now — try again in a moment.")}
      </p>`;
  }
  if (!state) return null;
  if (state.setups_found > 0) {
    return html`
      <section className="receipt">
        <div className="receipt-head">
          <i className="fa-solid fa-shield-halved"></i>
          <span>${t("receipt.title_active", "Today the engine reached a setup")}</span>
        </div>
        <p className="receipt-note">
          ${formatTemplate(t("receipt.active_note", "{n} setup(s) crossed the line today, each published the moment it did. The journal has the details."), { n: iso(state.setups_found) })}
        </p>
      </section>`;
  }

  return html`
    <section className="receipt">
      <div className="receipt-head">
        <i className="fa-solid fa-shield-halved"></i>
        <span>${t("receipt.title", "Today the engine stood aside")}</span>
      </div>
      <dl className="receipt-rows">
        <div><dt>${t("receipt.watching", "Watching for")}</dt>
             <dd dir="ltr">${iso(String(state.watching_hours))}h</dd></div>
        ${/* top_reason is whichever of withheld_by / blockers[0] the server
             found first, and both are engine shorthand. This row is the whole
             point of the receipt -- it says what the silence was FOR. */""}
        <div><dt>${t("receipt.refused", "Refused by")}</dt>
             <dd>${state.top_reason
                    ? translateTechnicalReason(state.top_reason)
                    : t("receipt.nothing_lined_up", "nothing lined up")}</dd></div>
        ${state.blackouts > 0 && html`
          <div><dt>${t("receipt.blackouts", "Held for releases")}</dt>
               <dd dir="ltr">${iso(String(state.blackouts))}</dd></div>`}
      </dl>
      <p className="receipt-note">
        ${t("receipt.note", "The engine reaches a setup on roughly 3% of bars. A day with none is the filter doing its job — not a day the app was asleep.")}
      </p>
    </section>`;
}


// A number you can interrogate.
//
// This is the mechanism the whole product rests on made visible: every figure
// on screen was computed from something, and the ones that carry their
// derivation say so when you press them. It is deliberately NOT applied to
// every number -- a wrapper that claimed provenance for a figure that has none
// would be the exact dishonesty it exists to prevent. Only numbers whose
// origin the system actually kept are tappable, and the underline is the
// signal that one is.
//
// Rendered as a real <button>: a tappable number has to be reachable by
// keyboard and announced as interactive, not a span with a click handler.
function Sourced({ value, label, formula, source, asOf, note, dir: textDir }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  if (!formula && !source && !asOf && !note) {
    // Nothing to show. Render the number plainly rather than inviting a press
    // that would open an empty sheet.
    return html`<span dir=${textDir}>${value}</span>`;
  }
  return html`
    <${React.Fragment}>
      <button className="sourced" onClick=${() => setOpen(true)}
              aria-label=${formatTemplate(t("sourced.open", "{label}: where this came from"),
                                          { label: label || "" })}>
        <span dir=${textDir}>${value}</span>
      </button>
      ${open && html`
        <div className="sourced-scrim" onClick=${() => setOpen(false)}>
          <div className="sourced-sheet" role="dialog" aria-modal="true"
               onClick=${(e) => e.stopPropagation()}>
            <div className="sourced-head">
              <span>${label}</span>
              <b dir=${textDir}>${value}</b>
            </div>
            ${formula && html`
              <div className="sourced-row">
                <dt>${t("sourced.formula", "Worked out as")}</dt>
                <dd dir="ltr">${formula}</dd>
              </div>`}
            ${source && html`
              <div className="sourced-row">
                <dt>${t("sourced.source", "From")}</dt>
                <dd>${source}</dd>
              </div>`}
            ${asOf && html`
              <div className="sourced-row">
                <dt>${t("sourced.as_of", "As of")}</dt>
                <dd dir="ltr">${asOf}</dd>
              </div>`}
            ${note && html`<p className="sourced-note">${note}</p>`}
            <button className="btn btn-quiet" onClick=${() => setOpen(false)}>
              ${t("chat.close", "Close")}
            </button>
          </div>
        </div>`}
    <//>`;
}


// The pause a trader set for themselves, surfaced when it applies.
//
// It STATES; it does not block. The take-trade button stays live and no setup
// is hidden. The evidence behind cooling off is about deciding in advance
// while calm and being reminded later -- not about a product locking somebody
// out of their own money, which only teaches them to trade somewhere else.
// The patience counter -- the #1 churn neutralizer for an honest
// low-frequency engine: silence must read as the filter working, not the
// app broken. States the time since the last published call BESIDE the
// record's own measured median gap, so "3 days quiet" has a denominator.
function PatienceLine() {
  const { t } = useI18n();
  const [state, setState] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch("/api/public/ledger").then((r) => r.json())
      .then((p) => {
        if (!alive || !p.success) return;
        const times = (p.signals || []).map((sig) => sig.published_at)
          .filter(Boolean).sort((a, b) => a - b);
        if (!times.length) return;
        const last = times[times.length - 1];
        let median = null;
        if (times.length >= 3) {
          const gaps = times.slice(1).map((v, i) => v - times[i]).sort((a, b) => a - b);
          median = gaps[Math.floor(gaps.length / 2)];
        }
        setState({ last, median });
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!state) return null;
  const daysWord = (seconds) => {
    const hours = Math.round(seconds / 3600);
    return hours < 48
      ? formatTemplate(t("patience.hours", "{n} hours"), { n: iso(hours) })
      : formatTemplate(t("patience.days", "{n} days"), { n: iso(Math.round(hours / 24)) });
  };
  const since = Math.max(0, Date.now() / 1000 - state.last);
  return html`
    <a className="patience-line" href="/performance">
      <i className="fa-solid fa-hourglass-half" aria-hidden="true"></i>
      <span>
        ${formatTemplate(t("patience.since", "Last published call: {ago} ago."), { ago: daysWord(since) })}
        ${state.median != null
          ? " " + formatTemplate(t("patience.median", "This engine's measured rhythm is one call every ~{gap} — quiet is the filter working."), { gap: daysWord(state.median) })
          : " " + t("patience.young", "The record is young; the rhythm will state itself here as it accumulates.")}
        ${/* The per-style cadence, stated rather than promised. It comes
              from data/measurement.json, which is where the number now lives
              and what tests/test_honesty_wall.py holds this sentence to.
              It used to claim a nine-year window. None was ever measured,
              and the record carried no cadence row at all, so the sentence
              was checkable by nobody -- which is exactly how it survived a
              purge of the locale files. (Spelled in words, not digits: the
              wall scans this file for figures now, and a note ABOUT a retired
              claim must not read as the claim.) The measured window is three years,
              775 trading days, and the interesting half is not the average
              but how often each style says NOTHING -- 5% of days for
              scalping, 44% for intraday, 84% for swing, which is the number
              a subscriber feels. */""}
        <small className="patience-cadence">
          ${t("patience.cadence", "Measured on 3 years, 775 trading days: scalping 2.4 calls a day and quiet on 5% of days · intraday 0.8 a day and quiet on 44% · swing 0.2 a day, quiet on 84%, with one gap of 23 trading days. Choppy stretches run dry by design.")}
        </small>
      </span>
    </a>`;
}

function CooldownNotice() {
  const { t } = useI18n();
  const [state, setState] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => authenticatedFetch("/api/cooldown")
      .then((r) => r.json())
      .then((p) => { if (!cancelled && p.success) setState(p.state); })
      .catch(() => {});
    load();
    const timer = setInterval(load, 120000);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);

  if (!state?.active) return null;
  return html`
    <section className="cooldown" role="status">
      <i className="fa-solid fa-hourglass-half"></i>
      <div>
        <b>
          ${state.reason === "daily_cap"
            ? formatTemplate(t("cooldown.cap_hit", "You set a limit of {n} trades a day."),
                { n: iso(String(state.daily_trade_cap)) })
            : formatTemplate(t("cooldown.streak_hit", "{n} losses in a row — you asked for a pause after {after}."),
                { n: iso(String(state.streak)), after: iso(String(state.after_losses)) })}
        </b>
        <small>
          ${state.minutes_left != null
            ? formatTemplate(t("cooldown.left", "{n} minutes of it left. Nothing is blocked — this is the rule you wrote."),
                { n: iso(String(state.minutes_left)) })
            : t("cooldown.your_rule", "Nothing is blocked — this is the rule you wrote.")}
        </small>
      </div>
    </section>`;
}


// Where the pause is decided: in settings, while nothing is at stake. That is
// the whole mechanism -- the evidence behind cooling off is about committing
// in advance and being reminded later, not about being stopped in the moment.
function CooldownSettings() {
  const { t } = useI18n();
  const [rule, setRule] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    authenticatedFetch("/api/cooldown").then((r) => r.json())
      .then((p) => { if (alive && p.success) setRule(p.rule); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const patch = useCallback(async (changes) => {
    setBusy(true);
    try {
      const r = await authenticatedFetch("/api/cooldown", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(changes),
      });
      const p = await r.json();
      if (p.success) setRule(p.rule);
    } catch (error) { /* previous value stands */ }
    setBusy(false);
  }, []);

  if (!rule) return null;
  const counts = [0, 2, 3, 4, 5];
  const spans = [15, 30, 60, 120, 240];
  const caps = [0, 1, 2, 3, 5, 10];

  return html`
    <div className="settings-card">
      <strong>${t("cooldown.title", "Your own pause")}</strong>
      <p className="cooldown-note">${t("cooldown.note", "Set while nothing is at stake. The app will remind you; it will never stop you.")}</p>

      <label className="cooldown-field">
        <span>${t("cooldown.after_losses", "Pause after this many losses in a row")}</span>
        <select disabled=${busy} value=${rule.after_losses}
                onChange=${(e) => patch({ after_losses: Number(e.target.value) })}>
          ${counts.map((n) => html`
            <option key=${n} value=${n}>${n === 0 ? t("cooldown.off", "off") : n}</option>`)}
        </select>
      </label>

      ${rule.after_losses > 0 && html`
        <label className="cooldown-field">
          <span>${t("cooldown.minutes", "For how many minutes")}</span>
          <select disabled=${busy} value=${rule.minutes}
                  onChange=${(e) => patch({ minutes: Number(e.target.value) })}>
            ${spans.map((n) => html`<option key=${n} value=${n}>${n}</option>`)}
          </select>
        </label>`}

      <label className="cooldown-field">
        <span>${t("cooldown.cap", "Most trades in a day")}</span>
        <select disabled=${busy} value=${rule.daily_trade_cap}
                onChange=${(e) => patch({ daily_trade_cap: Number(e.target.value) })}>
          ${caps.map((n) => html`
            <option key=${n} value=${n}>${n === 0 ? t("cooldown.off", "off") : n}</option>`)}
        </select>
      </label>
    </div>`;
}


// The level map, graded against its own history.
//
// Every charting app draws pivots; none of them publish whether theirs work,
// because the answer is usually close to chance. This one publishes it, for
// the same reason it publishes the losing tail: the number is not impressive,
// and being the only product that shows it is.
//
// The rate is measured among levels price actually REACHED. Counting untouched
// levels as successes is how a vendor gets to 80%, and it answers a question
// nobody asked -- a level nobody traded did not hold, it was never tested.
function LevelGrades() {
  const { t } = useI18n();
  const [grades, setGrades] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch("/api/level-grades").then((r) => r.json())
      .then((p) => { if (alive && p.success && p.available) setGrades(p); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!grades) return null;
  const o = grades.overall || {};
  // level_stats sends reaction_rate: null when NO level was reached -- the
  // server being deliberately honest that nothing has been tested. `?? 0`
  // turned that into the headline "0% turned price away": the strongest
  // negative claim this product can make about its own levels, invented from
  // a null. The endpoint's comment already says "absent is not zero", and it
  // guards the file being missing; this is the file being present and
  // unmeasured. VerdictHistory, forty lines below, states the rule this
  // follows: below the floor, render NOTHING -- a figure on screen gets
  // believed regardless of the sentence beside it.
  if (o.reaction_rate == null) return null;
  const sources = Object.entries(grades.by_source || {})
    .filter(([name, s]) => !name.startsWith("$") && s.reached >= 40)
    .sort((a, bb) => bb[1].reached - a[1].reached);

  return html`
    <section className="grades">
      <h2>${t("grades.title", "Do our own levels hold?")}</h2>
      <p className="grades-intro">
        ${formatTemplate(t("grades.intro", "Of {reached} levels price actually reached, {reacted} turned it away. Levels price never got to are excluded — a level nobody traded did not hold, it was never tested."),
          { reached: iso(String(o.reached)), reacted: iso(String(o.reacted)) })}
      </p>
      <div className="grades-headline">
        <b>${iso(o.reaction_rate + "%")}</b>
        <span>${t("grades.rate", "turned price away")}</span>
      </div>
      ${sources.length > 0 && html`
        <ul className="grades-rows">
          ${sources.map(([name, s]) => html`
            <li key=${name}>
              <span>${name}</span>
              <b dir="ltr" className=${s.reaction_rate < 55 ? "neg" : ""}>${iso(s.reaction_rate + "%")}</b>
              <small dir="ltr">${iso(String(s.reached))}</small>
            </li>`)}
        </ul>`}
      <p className="grades-note">
        ${t("grades.note", "The central daily pivot is the weakest of them — close enough to a coin flip that it should be read as a reference line, not as support. That is why it is here.")}
      </p>
      <p className="grades-foot">${grades.measured_on}</p>
    </section>`;
}


// What happened the last times the engine said something like this.
//
// Not the pooled win rate -- that averages a trending buy and a chopped-out
// sell into a number describing neither. A class is (side, regime, ADX band)
// and nothing finer, because slicing further produces cells of three signals,
// and a percentage from three signals will be believed and should not be.
//
// Below the sample floor it renders NOTHING, not a greyed-out number with a
// caveat: a figure on screen gets believed regardless of the sentence next to
// it. "Not enough yet" is the honest answer for a young record, and this
// product will be giving it for a while.
function VerdictHistory({ timeframe }) {
  const { t } = useI18n();
  const [h, setH] = useState(null);

  useEffect(() => {
    let cancelled = false;
    authenticatedFetch(`/api/verdict-history?timeframe=${timeframe || ""}`)
      .then((r) => r.json())
      .then((p) => { if (!cancelled && p.success) setH(p.history); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [timeframe]);

  if (!h) return null;

  if (!h.available) {
    if (h.reason !== "too_few") return null;
    return html`
      <p className="vhistory-thin">
        ${formatTemplate(t("vhistory.too_few", "Only {n} of these have resolved so far — too few to put a number on. It needs {min}."),
          { n: iso(String(h.sample)), min: iso(String(h.min_sample)) })}
      </p>`;
  }

  return html`
    <section className="vhistory">
      <h4>${t("vhistory.title", "Last time the engine said this")}</h4>
      <p className="vhistory-class">
        ${formatTemplate(t("vhistory.class", "{side} · {regime} · ADX {band}"),
          // Every one of these three is an engine enum -- 'buy', 'trending_up',
          // 'moderate' -- and all three were interpolated raw, so the Arabic
          // panel headed "آخر مرة قال المحرّك ذلك" described its class in
          // English snake_case. regimeLabel() exists for exactly this leak and
          // this was the one render site that bypassed it.
          { side: t(`signals.${h.side}`, String(h.side || "").toUpperCase()),
            regime: regimeLabel(t, h.regime),
            band: t(`vhistory.band_${h.adx_band}`, String(h.adx_band || "")) })}
      </p>
      <div className="vhistory-rows">
        <div><dt>${t("vhistory.sample", "Resolved")}</dt><dd dir="ltr">${iso(String(h.sample))}</dd></div>
        <div><dt>${t("vhistory.won", "Won")}</dt><dd dir="ltr">${iso(h.win_rate + "%")}</dd></div>
        <div><dt>${t("vhistory.per_trade", "Per trade")}</dt>
             <dd dir="ltr" className=${h.expectancy_r < 0 ? "neg" : ""}>
               ${iso((h.expectancy_r > 0 ? "+" : "") + h.expectancy_r + "R")}</dd></div>
        <div><dt>${t("vhistory.worst", "Worst")}</dt>
             <dd dir="ltr" className="neg">${iso(h.worst_r + "R")}</dd></div>
      </div>
    </section>`;
}


// Whether each of the engine's own filters is still doing anything.
//
// A filter is a claim that can stop being true, and it stops quietly. This is
// the recheck, published -- including the two filters that cannot be measured
// at all, named rather than left silently out of the list.
// One measured sentence under "why there is no trade": the strongest
// filter's delta from /api/filter-power. A refusal that cites its own
// measurement is the deterministic engine explaining itself -- the
// un-copyable feature. (Thin pointer; the full table lives with the journal
// analytics, per the no-repetition doctrine.)
// Measured constants change per measurement round, not per render: one
// authenticated fetch serves every mount (Why sheet + no-trade block).
let filterPowerPromise = null;
function fetchFilterPower() {
  filterPowerPromise = filterPowerPromise
    || authenticatedFetch("/api/filter-power").then((r) => r.json())
         .catch((e) => { filterPowerPromise = null; throw e; });
  return filterPowerPromise;
}

function NoTradeMeasuredLine() {
  const { t } = useI18n();
  const [best, setBest] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchFilterPower()
      .then((p) => {
        if (!alive || !p.success || !p.available) return;
        let top = null;
        Object.values(p.by_style || {}).forEach((filters) => {
          Object.values(filters || {}).forEach((f) => {
            if (f.verdict === "working" && f.delta_r != null
                && (!top || f.delta_r > top.delta_r)) top = f;
          });
        });
        if (top) setBest(top);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!best) return null;
  return html`
    <p className="no-trade-measured">
      ${formatTemplate(
        t("verdict.no_trade_measured", "Not decoration: measured over the record, the trades this class of filter blocked ran {delta}R per trade WORSE than the ones it allowed."),
        { delta: iso("+" + best.delta_r.toFixed(2)) })}
    </p>`;
}

function FilterPower() {
  const { t } = useI18n();
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    fetchFilterPower()
      .then((p) => { if (alive && p.success && p.available) setData(p); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!data) return null;
  const styles = Object.entries(data.by_style || {});
  const rows = [];
  styles.forEach(([style, filters]) => {
    Object.entries(filters || {}).forEach(([key, f]) => {
      rows.push({ style, key, ...f });
    });
  });
  if (!rows.length) return null;

  const label = {
    working: t("power.working", "working"),
    weak: t("power.weak", "weak"),
    dead: t("power.dead", "no longer discriminating"),
    inverted: t("power.inverted", "costing money"),
  };

  return html`
    <section className="grades">
      <h2>${t("power.title", "Are our own filters still working?")}</h2>
      <p className="grades-intro">
        ${t("power.intro", "A filter is a claim: trades taken when it holds do better than trades it blocks. That claim can stop being true, and it stops quietly. Here is the recheck.")}
      </p>
      <ul className="power-rows">
        ${rows.map((r) => html`
          <li key=${r.style + r.key}>
            <span className="power-what">
              <b>${r.label || r.key}</b>
              <small>${r.style}</small>
            </span>
            ${r.available ? html`
              <span className=${"power-band is-" + r.verdict}>${label[r.verdict] || r.verdict}</span>
              <span className="power-delta" dir="ltr">
                ${iso((r.delta_r > 0 ? "+" : "") + r.delta_r + "R")}
              </span>` : html`
              <span className="power-band is-none">
                ${formatTemplate(t("power.too_few", "no reading — {allowed} allowed, {blocked} blocked"),
                  { allowed: iso(String(r.allowed)), blocked: iso(String(r.blocked)) })}
              </span>`}
          </li>`)}
      </ul>
      ${(data.unmeasurable || []).length > 0 && html`
        <div className="power-gaps">
          <h3>${t("power.cannot", "What cannot be measured yet")}</h3>
          ${data.unmeasurable.map((g) => html`
            <p key=${g.label}><b>${g.label}</b> — ${g.why}</p>`)}
        </div>`}
      <p className="grades-foot">${data.measured_on}</p>
    </section>`;
}


// Every check a trader would otherwise do in their head, done and stated.
//
// The research said a tool should END a question, not add one. A verdict that
// shows a setup and leaves someone asking "so should I take it?" has added a
// question. This is not the answer to that question -- the product does not
// give recommendations -- it is everything they would have looked up before
// answering it themselves.
//
// "Ready" means only that nothing in the list is a blocker. The decision stays
// where it belongs.
function PreTradeCard({ timeframe }) {
  const { t } = useI18n();
  const [card, setCard] = useState(null);
  // The endpoint returns six more readings than this component used to keep.
  // Taking only p.card and discarding the rest is how nine working backend
  // features stayed invisible to every customer -- the same shape as the stale
  // bundle and the stale process, one layer up.
  const [extra, setExtra] = useState({});

  useEffect(() => {
    let cancelled = false;
    authenticatedFetch(`/api/pre-trade?timeframe=${timeframe || ""}`)
      .then((r) => r.json())
      .then((p) => {
        if (cancelled || !p.success) return;
        setCard(p.card);
        // Every key this component RENDERS must be set here, and the two that
        // were not are the reason this comment is longer than the code.
        //
        // `extra.wait_quality` and `extra.historical_twin` are read eleven
        // times between them, in two fully built panels -- headline, reasons,
        // "clears when", a matches table with its own honesty note. Neither
        // key was ever set, so `extra.wait_quality?.reasons?.length > 0` was
        // `undefined > 0` on every render, for every customer, since the
        // panels were written. Two finished features, computed on every
        // request, shown to nobody.
        //
        // This is the second time in this exact component: the comment above
        // records the first, when only p.card was kept. A widening fix that
        // stops short leaves the same defect with fewer instances.
        //
        // test_the_pre_trade_panel_sets_every_key_it_reads compares the two
        // sets mechanically now, so neither direction can drift again.
        setExtra({
          cross: p.cross_examination, scenarios: p.scenarios,
          conditions: p.conditions, narration: p.narration,
          guardian: p.rule_guardian, explanation: p.explanation,
          wait_quality: p.wait_quality, historical_twin: p.historical_twin,
          data_health: p.data_health, market_state: p.market_state,
          market_dna: p.market_dna, compatibility: p.compatibility,
          anomalies: p.anomalies,
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [timeframe]);

  // The checklist needs a setup. NOTHING ELSE ON THIS CARD DOES.
  //
  // This gate used to be `if (!card?.available) return null`, which hid the
  // whole card -- and `pre_trade.build()` returns available:false whenever
  // there is no setup. The engine reaches a setup in about 3% of candles, by
  // this product's own published number. So data health, market state, market
  // DNA, the customer's own record, the anomaly reading and the twins were
  // rendered to nobody about 97% of the time, and the moment they are most
  // useful -- "there is no trade, so what IS the market doing?" -- was exactly
  // the moment they were hidden.
  //
  // Every extra.* panel below already guards on its own data, so the only
  // thing that genuinely requires a setup is the checklist and the blocker
  // count beside the heading.
  const hasSetup = Boolean(card?.available && card.checks?.length);
  const hasReadings = Boolean(
    extra.data_health || extra.market_state?.state || extra.market_dna
    || extra.compatibility || extra.anomalies?.available || extra.explanation
    || extra.narration?.length || extra.historical_twin?.matches?.length
    || extra.scenarios?.length || extra.conditions);
  if (!hasSetup && !hasReadings) return null;

  return html`
    <section className=${"pretrade" + (hasSetup && card.blockers ? " has-blockers" : "")
                         + (hasSetup ? "" : " is-no-setup")}>
      <div className="pretrade-head">
        <h3>${hasSetup
          ? t("pretrade.title", "Before you take it")
          : t("pretrade.market_now", "The market right now")}</h3>
        ${hasSetup && html`
          <span className=${card.blockers ? "neg" : ""}>
            ${card.blockers
              ? formatTemplate(t("pretrade.blocked", "{n} blocking"), { n: iso(String(card.blockers)) })
              : card.cautions
                ? formatTemplate(t("pretrade.cautions", "{n} to note"), { n: iso(String(card.cautions)) })
                : t("pretrade.clear", "nothing blocking")}
          </span>`}
      </div>
      ${!hasSetup && html`
        <p className="pretrade-no-setup">
          ${t("pretrade.no_setup_note", "No setup stands right now. These readings are about the market and your own record, and they do not depend on there being a trade.")}
        </p>`}
      ${/* Data health leads, because it is the only reading that QUALIFIES
           every other one on this card. A stale feed does not make the
           analysis below wrong — it makes it unverified, and those are
           different things a customer is entitled to tell apart. Kept to one
           strip rather than a panel: it must be seen first, not read first. */""}
      ${extra.data_health && html`
        <div className=${"pretrade-health" + (extra.data_health.trustworthy ? "" : " is-degraded")}>
          <span className="pretrade-health-count" dir="ltr">
            ${formatTemplate(t("pretrade.health_fresh", "{fresh} of {total} sources fresh"),
              { fresh: iso(String(extra.data_health.fresh)),
                total: iso(String(extra.data_health.total)) })}
          </span>
          <ul className="pretrade-health-sources">
            ${(extra.data_health.sources || []).map((s) => html`
              <li key=${s.key} className=${"src-" + s.status} title=${s.detail || ""}>
                ${/* The server sends an English name ('Live price', '1h
                     candles'). The KEY is language-neutral and this component
                     already knows the timeframe, so the label is built here
                     rather than shipped as prose. */""}
                ${formatTemplate(t(`health.src_${s.key}`, s.name), { tf: timeframe || "" })}
                ${s.status !== "fresh" && html`
                  <small>${t(`pretrade.health_${s.status}`, s.status)}</small>`}
              </li>`)}
          </ul>
          <small className="pretrade-note">${t("pretrade.health_note", "Every reading below is only as good as these. A stale source does not make the analysis wrong; it makes it unverified.")}</small>
        </div>`}

      ${hasSetup && html`
        <ul className="pretrade-rows">
          ${card.checks.map((c) => html`
            <li key=${c.key} className=${"stance-" + c.stance}>
              <span className="pretrade-label">${c.label}</span>
              <span className="pretrade-value" dir="ltr">${iso(String(c.value))}</span>
              ${c.note && html`<span className="pretrade-note">${c.note}</span>`}
            </li>`)}
        </ul>`}
      ${/* "Explain This Market" — the whole card in five sentences, above the
           detail it summarises. It renders its own refusal too: "not enough
           live data to explain anything honestly" is a better answer than a
           confident page assembled from nothing. */""}
      ${extra.explanation && html`
        <div className=${"explain" + (extra.explanation.contested ? " is-contested" : "")}>
          <h4>${t("explain.title", "What is happening")}</h4>
          ${!extra.explanation.available
            ? html`<p className="explain-none">
                ${t(`explain.rsn_${extra.explanation.reason_key}`, "")}
              </p>`
            : html`
              ${/* The headline is a LIST of sentences, not one glued string.
                   The English original grew by concatenation -- ", in a X
                   regime", " — and its own cross-examination disputes that" --
                   and no translation can follow that order in Arabic. Each
                   part is a whole sentence the locale rewrites freely. */""}
              <p className="explain-headline">
                ${(extra.explanation.headline || [])
                  .map((part) => renderPoint(t, part)).join(" ")}
              </p>
              ${extra.explanation.sections.map((sec) => html`
                <div key=${sec.key} className=${"explain-section is-" + sec.key}>
                  <b>${t(`explain.${sec.key}`, sec.title)}</b>
                  <ul>${sec.points.map((point, i) => html`
                    <li key=${i}>${renderPoint(t, point)}</li>`)}</ul>
                </div>`)}`}
        </div>`}

      ${/* Your own rules first: it is the only section that is about a
           decision you already made, so it outranks the market readings. */""}
      ${extra.guardian?.violations?.length > 0 && html`
        <div className="pretrade-guardian">
          <h4>${t("pretrade.your_rules", "Your own rules")}</h4>
          <ul>
            ${extra.guardian.violations.map((v, i) => html`
              <li key=${i}>
                <b>${v.rule}</b>
                <span className="pretrade-note">${v.detail}</span>
              </li>`)}
          </ul>
        </div>`}
      ${extra.guardian?.unevaluated?.length > 0 && html`
        <p className="pretrade-note">
          ${formatTemplate(
            t("pretrade.rules_unchecked",
              "{n} of your rules could not be checked automatically."),
            { n: iso(String(extra.guardian.unevaluated.length)) })}
        </p>`}

      ${/* The product arguing with itself, in public. */""}
      ${extra.cross?.conflicts?.length > 0 && html`
        <div className="pretrade-against">
          <h4>
            ${t("pretrade.against", "What argues against this")}
            ${/* Two naked percentages beside a proposed entry, stop and
                 target read as the chance the trade works. They are neither:
                 decision_confidence is the engine's own agreement score
                 between its readings, and this pair shows what the objections
                 below did to it. It only ever falls. The setup-quality score
                 two panels up carries a permanent caveat for exactly this
                 reason and this pair had none — no label, no unit, nothing on
                 screen saying what was being measured. */""}
            <span className=${extra.cross.contested ? "neg" : ""} dir="ltr">
              <small>${t("pretrade.agreement", "agreement")}</small>
              ${iso(String(extra.cross.stated_confidence))}% →
              ${iso(String(extra.cross.adjusted_confidence))}%
            </span>
          </h4>
          <p className="pretrade-agreement-note">
            ${t("pretrade.agreement_note", "How much the engine's own readings agree with each other, before and after the objections below. It is not a probability of profit, and it only ever falls.")}
          </p>
          <ul>
            ${extra.cross.conflicts.map((c, i) => html`
              <li key=${i}>
                <b>${formatTemplate(t(`cross.opp_${c.opposes_key}`, ""), c.opposes_args || {})}</b>
                <span className="pretrade-note">
                  ${formatTemplate(t(`cross.det_${c.detail_key}`, ""), c.detail_args || {})}
                </span></li>`)}
          </ul>
        </div>`}

      ${/* Conditional paths. Never ranked -- see scenarios.py. */""}
      ${/* Every line here arrives as {key, args}. It used to be f-strings from
           scenarios.py printed verbatim, so this whole panel read in English
           under an Arabic heading -- and it is the longest block on the card.

           `side` and `regime` in the evidence are engine enums, passed as
           VALUES so they run through the same signals.* and regime.*
           dictionaries every other surface uses rather than gaining a third
           spelling of "buy". */""}
      ${extra.scenarios?.length > 0 && html`
        <div className="pretrade-scenarios">
          <h4>${t("scen.title", "What could happen from here")}</h4>
          ${extra.scenarios.map((sc) => html`
            <div key=${sc.key} className="scenario">
              <b>${formatTemplate(t(`scen.ttl_${sc.title_key}`, ""), sc.title_args || {})}</b>
              <ul>${(sc.conditions || []).map((c, i) => html`
                <li key=${i}>${formatTemplate(t(`scen.cnd_${c.key}`, ""), {
                  ...c.args,
                  // A zone with no name of its own arrives as the key
                  // 'generic_zone'; one that has a name carries the chart's
                  // own vocabulary and is shown as-is.
                  zone: c.args?.zone === "generic_zone"
                    ? t("scen.fb_generic_zone", "level") : c.args?.zone,
                })}</li>`)}</ul>
              <span className="scenario-invalidation">
                ${t("scen.invalidated_by", "Invalidated by")}:${" "}
                ${formatTemplate(t(`scen.inv_${sc.invalidation_key}`, ""), {
                  ...sc.invalidation_args,
                  floor: sc.invalidation_args?.floor
                    ?? t(`scen.fb_${sc.invalidation_args?.floor_key}`, ""),
                  ceiling: sc.invalidation_args?.ceiling
                    ?? t(`scen.fb_${sc.invalidation_args?.ceiling_key}`, ""),
                })}
              </span>
              ${sc.evidence?.length > 0 && html`
                <ul className="scenario-evidence">
                  ${sc.evidence.map((e, i) => html`
                    <li key=${i}>${formatTemplate(t(`scen.evd_${e.key}`, ""), {
                      ...e.args,
                      side: t(`signals.${e.args?.side}`, e.args?.side || ""),
                      regime: regimeLabel(t, e.args?.regime),
                      topics: (e.args?.topics || []).join("، "),
                    })}</li>`)}
                </ul>`}
            </div>`)}
        </div>`}

      ${/* What argues for WAITING, given a setup already exists (#8). Never
           "take it": the absence of a reason to wait is not the presence of
           a reason to act, and every reason carries what would clear it, so
           this is a thing that ends rather than a mood. */""}
      ${extra.wait_quality?.reasons?.length > 0 && html`
        <div className="pretrade-wait">
          <h4>${t("pretrade.wait", "What argues for waiting")}</h4>
          <p className="pretrade-wait-head">${extra.wait_quality.headline}</p>
          <ul>
            ${/* THE READER'S LANGUAGE, from the key and its numbers. This
                  printed the server's English `detail` and `clears_when`
                  verbatim — a full English paragraph in the middle of the
                  Arabic dashboard, on the card whose whole job is to argue
                  for patience. The English strings remain the fallback for a
                  key this build does not know, which is the same contract
                  reason_key already has on the verdict. */""}
            ${extra.wait_quality.reasons.map((r) => html`
              <li key=${r.key}>
                <b>${formatTemplate(t(`waitq.${r.key}`, r.detail), r.args || {})}</b>
                <span className="wait-clears">
                  ${t("pretrade.clears", "Clears when")}:${" "}
                  ${formatTemplate(t(`waitq.${r.key}_clears`, r.clears_when), r.args || {})}
                </span>
              </li>`)}
          </ul>
        </div>`}

      ${/* Days that looked like today (#2), as a LIST you can open — never a
           similarity percentage and never a rate. The owner's mock asked for
           "87% similar, 31/47 continuation"; that reads as a probability, a
           reader will use it as one, and 47 matched days is not a sample. */""}
      ${extra.historical_twin?.matches?.length > 0 && html`
        <div className="pretrade-twins">
          <h4>
            ${t("pretrade.twins", "Days that opened like this")}
            <span dir="ltr">${iso(String(extra.historical_twin.matches.length))}
              /${iso(String(extra.historical_twin.searched))}</span>
          </h4>
          <p className="settings-hint">${extra.historical_twin.note}</p>
          <div className="table-scroll">
            <table className="twins-table">
              <thead><tr>
                <th>${t("research.date", "Date")}</th>
                <th>${t("research.state", "Market state")}</th>
                <th dir="ltr">${t("research.vol", "Vol %ile")}</th>
                <th dir="ltr">${t("pretrade.twin_distance", "Distance")}</th>
              </tr></thead>
              <tbody>
                ${extra.historical_twin.matches.map((m) => html`
                  <tr key=${m.date}>
                    <td dir="ltr">${iso(m.date)}</td>
                    <td>${m.state || "—"}</td>
                    <td dir="ltr">${m.volatility_percentile == null ? "—"
                      : iso(String(m.volatility_percentile))}</td>
                    <td dir="ltr">${iso(m.distance.toFixed(2))}</td>
                  </tr>`)}
              </tbody>
            </table>
          </div>
        </div>`}

      ${/* The state machine, with its EVIDENCE rather than on its own. The
           word "expansion" by itself is an assertion; the same word beside the
           two readings that produced it is a measurement the customer can
           disagree with. `previous` is shown only when it differs, because
           "was expansion, is expansion" is noise. */""}
      ${extra.market_state?.state && html`
        <div className="pretrade-mstate">
          <h4>
            ${t("pretrade.state", "Market state")}
            <b>${t(`mstate.${extra.market_state.state}`, extra.market_state.state)}</b>
            ${extra.market_state.previous
              && extra.market_state.previous !== extra.market_state.state
              && html`<small>${formatTemplate(t("pretrade.state_was", "was {previous}"),
                { previous: t(`mstate.${extra.market_state.previous}`, extra.market_state.previous) })}</small>`}
          </h4>
          <div className="pretrade-mstate-nums" dir="ltr">
            ${extra.market_state.efficiency != null && html`
              <span><small>${t("pretrade.state_efficiency", "Efficiency")}</small>
                ${iso(String(extra.market_state.efficiency))}</span>`}
            ${extra.market_state.volatility_percentile != null && html`
              <span><small>${t("pretrade.state_vol", "Volatility %ile")}</small>
                ${iso(String(extra.market_state.volatility_percentile))}</span>`}
          </div>
          ${/* Evidence arrives as {key, args}, never as a finished sentence.
               It used to be English prose built with an f-string in
               market_state.py and printed here verbatim, so this list read in
               English under an Arabic heading. */""}
          ${extra.market_state.evidence?.length > 0 && html`
            <ul>${extra.market_state.evidence.map((e, i) => html`
              <li key=${i}>${formatTemplate(t(`mstate.ev_${e.key}`, ""), e.args || {})}</li>`)}</ul>`}
        </div>`}

      ${/* Market DNA. Renders its own refusal: `available: false` arrives with
           a reason, and hiding the panel instead of printing that reason is
           the same silent-absence defect this card exists to avoid. Every
           trait carries the sample it was measured on, in the same line, so
           no number here is readable without its n. */""}
      ${extra.market_dna && html`
        <div className="pretrade-dna">
          <h4>${t("dna.title", "How this market has been behaving")}</h4>
          ${!extra.market_dna.available
            ? html`<p className="explain-none">
                ${formatTemplate(t(`dna.rsn_${extra.market_dna.reason_key}`, ""),
                  extra.market_dna.reason_args || {})}
              </p>`
            : html`
              ${/* The summary NAMES the traits, so it is assembled from their
                   translated labels here. Joining them in Python would mean
                   translating in Python, which is the thing this batch is
                   removing. */""}
              ${extra.market_dna.summary_key && html`
                <p className="pretrade-dna-summary">
                  ${extra.market_dna.summary_key === "measured"
                    ? formatTemplate(t("dna.measured", "Measured over the window: {named}."),
                        { named: (extra.market_dna.traits || [])
                            .filter((tr) => tr.value != null)
                            .map((tr) => t(`dna.lbl_${tr.label_key}`, tr.label_key))
                            .join("، ") })
                    : t("dna.nothing", "Nothing measurable stood out in this window.")}
                </p>`}
              <ul>
                ${(extra.market_dna.traits || []).map((tr) => html`
                  <li key=${tr.key}>
                    <b>${t(`dna.lbl_${tr.label_key}`, tr.label_key)}</b>
                    ${tr.value != null && html`
                      <span className="pretrade-value" dir="ltr">${iso(String(tr.value))}</span>`}
                    <span className="pretrade-note">
                      ${formatTemplate(t(`dna.det_${tr.detail_key}`, ""), tr.detail_args || {})}
                    </span>
                    <small dir="ltr">${formatTemplate(t("pretrade.dna_sample", "{n} bars"),
                      { n: iso(String(tr.sample)) })}</small>
                  </li>`)}
              </ul>`}
        </div>`}

      ${/* The customer's OWN record in conditions like these. Two rules hold
           this to being analysis rather than advice. It never says whether to
           take the trade -- it reports what their history did and stops. And a
           bucket with `enough: false` prints no rate at all: a percentage off
           four trades gets believed exactly as hard as one off four hundred,
           which is why the server sends the flag rather than the caller
           deciding. */""}
      ${extra.compatibility && html`
        <div className="pretrade-compat">
          <h4>${t("pretrade.compat", "Your own record in conditions like these")}</h4>
          ${!extra.compatibility.available
            ? html`<p className="explain-none">
                ${formatTemplate(t(`compat.rsn_${extra.compatibility.reason_key}`, ""),
                  extra.compatibility.reason_args || {})}
              </p>`
            : html`
              ${extra.compatibility.reading?.length > 0 && html`
                <p className="pretrade-compat-reading">
                  ${extra.compatibility.reading.map((line, i) => html`
                    <span key=${i}>${" "}${formatTemplate(
                      t(`compat.${line.key}`, ""), line.args || {})}</span>`)}
                </p>`}
              <div className="table-scroll">
                <table className="compat-table">
                  <thead><tr>
                    <th></th>
                    <th dir="ltr">${t("pretrade.compat_trades", "Trades")}</th>
                    <th dir="ltr">${t("pretrade.compat_win", "Won")}</th>
                    <th dir="ltr">${t("pretrade.compat_r", "Total R")}</th>
                  </tr></thead>
                  <tbody>
                    ${(extra.compatibility.buckets || []).map((b) => html`
                      <tr key=${b.dimension + ":" + b.value}
                          className=${b.current ? "is-current" : ""}>
                        <td>${b.value}
                          ${b.current && html`<small>${t("pretrade.compat_now", "now")}</small>`}</td>
                        <td dir="ltr">${iso(String(b.trades))}</td>
                        <td dir="ltr">${b.enough && b.win_rate != null
                          ? iso(b.win_rate + "%")
                          : html`<small>${t("pretrade.compat_too_few", "too few")}</small>`}</td>
                        <td dir="ltr" className=${b.enough && b.total_r < 0 ? "neg" : ""}>
                          ${b.enough && b.total_r != null ? iso(formatR(b.total_r)) : "—"}</td>
                      </tr>`)}
                  </tbody>
                </table>
              </div>
              <p className="settings-hint">${t("pretrade.compat_note", "Your history, not a forecast, and not advice about this trade.")}</p>`}
        </div>`}

      ${/* Two numbers, never blended. */""}
      ${(extra.conditions?.volatility_label || extra.conditions?.cleanliness_label) && html`
        <p className="pretrade-conditions">
          ${extra.conditions.volatility_label && html`
            <span>${t("pretrade.volatility", "Volatility")}: ${
              t(`mktcond.vol_${extra.conditions.volatility_label}`, extra.conditions.volatility_label)}</span>`}
          ${extra.conditions.cleanliness_label && html`
            <span>${t("pretrade.movement", "Movement")}: ${
              t(`mktcond.cln_${extra.conditions.cleanliness_label}`, extra.conditions.cleanliness_label)}</span>`}
        </p>`}

      ${extra.narration?.length > 0 && html`
        <div className="pretrade-narration">
          <h4>${t("pretrade.what_happened", "What just happened")}</h4>
          <ul>
            ${extra.narration.slice(-4).map((e, i) => html`
              <li key=${i}>${formatTemplate(t(`narr.${e.key}`, ""), {
                ...e.args,
                label: e.args?.label === "generic_level"
                  ? t("narr.generic_level", "level") : e.args?.label,
              })}</li>`)}
          </ul>
          ${/* The anomaly reading belongs under this heading and nowhere else:
               it is the same question ("what just happened") answered with a
               number instead of a sentence. Zero unusual bars is reported, not
               hidden — "nothing unusual" is an answer, and a panel that only
               appears on excitement teaches the reader that its silence means
               nothing was checked.

               The server's own `rule` and `note` strings are ENGLISH prose and
               are deliberately not echoed. `threshold_atr` carries the same
               fact as a number, so the rule is stated here in the reader's
               language. Echoing the sentence would put English inside the
               Arabic panel, which is the defect this audit keeps finding. */""}
          ${extra.anomalies?.available && html`
            <p className="pretrade-unusual">
              <b>${extra.anomalies.unusual_bars > 0
                ? formatTemplate(t("pretrade.unusual", "{n} of {examined} bars were unusually wide"),
                    { n: iso(String(extra.anomalies.unusual_bars)),
                      examined: iso(String(extra.anomalies.examined)) })
                : formatTemplate(t("pretrade.unusual_none", "No unusually wide bar in the last {examined}."),
                    { examined: iso(String(extra.anomalies.examined)) })}</b>
              ${extra.anomalies.largest_multiple != null && html`
                <span dir="ltr">${formatTemplate(t("pretrade.unusual_largest", "widest was {x}× ATR"),
                  { x: iso(String(extra.anomalies.largest_multiple)) })}</span>`}
              ${extra.anomalies.threshold_atr != null && html`
                <small>${formatTemplate(t("pretrade.unusual_rule", "The rule: a bar wider than {x}× the prevailing ATR."),
                  { x: iso(String(extra.anomalies.threshold_atr)) })}</small>`}
              <small>${t("pretrade.unusual_note", "Price only. Volume is not consolidated for spot gold, so no volume anomaly is reported rather than one being estimated.")}</small>
            </p>`}
        </div>`}

      <p className="pretrade-foot">
        ${t("pretrade.foot", "Facts, not a recommendation. Nothing here decides for you.")}
      </p>
    </section>`;
}

// The macro strip: what gold competes with, what is scheduled, and who is
// positioned. Every tile carries the date its number describes -- these series
// publish with a lag and the COT report is a census of last Tuesday, so a
// figure without its date would be a figure pretending to be live.
//
// Deliberately NOT a signal. Real yields are the one macro filter that survived
// both halves of this project's out-of-sample test, and it is a veto, not a
// forecast; positioning is context about crowding, and a product that turns a
// percentile into a trade is selling a backtest nobody ran.
function MacroStrip() {
  const { t } = useI18n();
  const [macro, setMacro] = useState(null);
  // Correlation belongs here rather than on a screen of its own: this strip
  // already answers "what does gold compete with", and how closely it has been
  // MOVING with those things is the same question one layer down. A separate
  // page would have been a second home for one thought.
  const [corr, setCorr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    authenticatedFetch("/api/macro")
      .then((r) => r.json())
      .then((p) => { if (!cancelled && p.success) setMacro(p); })
      .catch(() => {});
    authenticatedFetch("/api/correlation")
      .then((r) => r.json())
      .then((p) => { if (!cancelled && p.success && p.available) setCorr(p); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!macro) return null;
  const rates = macro.rates || {};
  const cal = macro.calendar || {};
  const pos = macro.positioning || {};
  const next = cal.next;
  const order = ["real_yield", "breakeven", "dollar", "volatility"];
  const tiles = order.filter((k) => rates[k]);
  if (!tiles.length && !next && !pos.available) return null;

  const arrow = (d) => (d > 0 ? "▲" : d < 0 ? "▼" : "–");

  return html`
    <section className="panel surface macro-strip">
      <div className="panel-head">
        <h3><i className="fa-solid fa-globe"></i> ${t("macro.title", "What moves gold")}</h3>
      </div>

      ${next && html`
        <a className=${"macro-next" + (next.minutes <= 60 ? " is-soon" : "")} href="/events">
          <span className="macro-next-when" dir="ltr">${iso(formatCountdown(next.minutes))}</span>
          <span className="macro-next-what">
            <b>${next.title}</b>
            <small>${t("macro.stop_warning", "A stop placed on technical grounds is not a stop through this.")}</small>
          </span>
        </a>`}

      ${tiles.length > 0 && html`
        <div className="macro-tiles">
          ${tiles.map((key) => {
            const r = rates[key];
            return html`
              <div key=${key} className="macro-tile">
                <small>${r.label}</small>
                <b>
                  <${Sourced}
                    value=${iso(r.value + (r.unit === "%" ? "%" : ""))}
                    label=${r.label} dir="ltr"
                    formula=${r.id}
                    source=${t("sourced.fred", "Federal Reserve (FRED), fetched by our server")}
                    asOf=${r.as_of} note=${r.note} />
                </b>
                <span className=${"macro-trend " + (r.direction > 0 ? "up" : r.direction < 0 ? "down" : "flat")}
                      dir="ltr">
                  ${arrow(r.direction)} ${iso(String(Math.abs(r.change ?? 0)))}
                </span>
                <span className="macro-asof" dir="ltr">${r.as_of}</span>
              </div>`;
          })}
        </div>`}

      ${pos.available && html`
        <div className="macro-cot">
          <span>
            ${/* The direction is read from the sign, not asserted. Managed
                 money has been net SHORT gold in real history, and printing
                 "net long -120,000" would be a sentence contradicting its own
                 number. */""}
            ${formatTemplate(
              pos.net >= 0
                ? t("macro.cot_long", "Managed money is net long {net} contracts — the {pct} percentile of three years.")
                : t("macro.cot_short", "Managed money is net short {net} contracts — the {pct} percentile of three years."),
              { net: iso(Math.abs(Number(pos.net)).toLocaleString("en-US")),
                pct: iso(String(pos.percentile ?? "—")) })}
          </span>
          <small>${pos.caveat}</small>
        </div>`}

      ${/* How closely gold has actually been MOVING with those series -- the
           same question one layer down, which is why it lives here and not on
           a page of its own. Only the pairs that could be computed appear: a
           series that could not be fetched is absent, never drawn as zero,
           because "no relationship" and "we could not look" are opposite
           statements and only one of them is ever true here. */""}
      ${corr && Object.keys(corr.pairs || {}).length > 0 && html`
        <div className="macro-corr">
          <span className="macro-corr-head">
            ${formatTemplate(
              t("macro.corr_head", "Moving with gold — {days}-day correlation of daily returns"),
              { days: iso(String((Object.values(corr.pairs)[0] || {}).window_days || 60)) })}
          </span>
          <ul>
            ${Object.entries(corr.pairs).map(([key, row]) => html`
              <li key=${key} className=${row.unusual ? "is-unusual" : ""}>
                <span className="macro-corr-label">${row.label}</span>
                <b dir="ltr">${iso(row.correlation > 0 ? `+${row.correlation}` : String(row.correlation))}</b>
                <small>${row.description}${row.unusual
                  ? ` — ${t("macro.corr_unusual", "outside its usual range")}` : ""}</small>
              </li>`)}
          </ul>
          <small className="macro-corr-note">${corr.note}</small>
        </div>`}
    </section>`;
}

// "in 2h 15m" reads faster than "135 minutes" when it is the thing standing
// between a trader and a stop that will not fill.
function formatCountdown(minutes) {
  if (minutes == null) return "—";
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h < 24) return m ? `${h}h ${m}m` : `${h}h`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}


// When a service is off, the page says so plainly and names it. The failure a
// product must never commit here is silence: a blank page, or a button that
// does nothing, teaches the user that the app is broken rather than that a
// specific service is unavailable.
function ServiceOff({ feature }) {
  const { t } = useI18n();
  const { featureNotes } = useAuth();
  // Every key in src/services/entitlements.FEATURES. It was missing three of
  // them — groups, ai_chat and signal_alerts — and the fallback is the key
  // itself, so a customer whose group rooms were switched off read the
  // heading "groups is switched off". test_every_entitlement_feature_has_a_name
  // fails the build when the two lists drift apart again.
  const names = {
    ai_analysis: t("nav.ai", "AI analysis"),
    ai_chat: t("features.ai_chat", "Ask the AI"),
    news: t("nav.news", "News"),
    journal: t("nav.journal", "Journal"),
    chart: t("nav.chart", "Chart"),
    price_alerts: t("alerts.price_alerts", "Price alerts"),
    signal_alerts: t("features.signal_alerts", "Signal alerts"),
    take_trade: t("buttons.take_trade", "Taking a trade"),
    groups: t("features.groups", "Group rooms"),
  };
  return html`
    <div className="service-off" role="status">
      <i className="fa-solid fa-circle-pause" aria-hidden="true"></i>
      <h2>${t("features.off_title", "{name} is switched off").replace("{name}", names[feature] || feature)}</h2>
      <p>${(featureNotes || {})[feature] || t("features.off_body",
        "Your account does not have this service right now. Nothing you did caused it, and nothing is lost — everything else still works.")}</p>
      <a className="btn btn-secondary" href="/dashboard">${t("nav.dashboard", "Dashboard")}</a>
    </div>`;
}


// The assistant. A bottom sheet rather than a nav tab, because it is about
// what is on the screen behind it -- turn it into a room of its own and it
// becomes a general chatbot, which is exactly the thing this product refuses
// to be.
//
// Every answer arrives as CLAIMS: a translation key, the numbers it may state
// with the bundle address each came from, and the citations behind it. The
// text is assembled here from those parts, so a number that the engine did not
// compute has no path onto the screen -- there is no field it could travel in.
// The pill that opens it. Sits above the tab bar on both the dashboard and the
// chart -- the two screens where "why does it say that?" actually gets asked.
function ChatLauncher({ timeframe }) {
  const { t } = useI18n();
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  // While the replay trainer is up its dock owns the pill's corner -- the
  // wave measured the pill sitting exactly on "back one bar".
  const [replayOn, setReplayOn] = useState(false);
  useEffect(() => {
    const onState = (e) => setReplayOn(!!e.detail?.active);
    window.addEventListener("chart:replay-state", onState);
    return () => window.removeEventListener("chart:replay-state", onState);
  }, []);
  // Tap-any-line's second half: anything on the page can open the chat WITH
  // a question already asked. The sheet consumes it once.
  const [seedQuestion, setSeedQuestion] = useState(null);
  useEffect(() => {
    const onOpen = (e) => {
      setSeedQuestion(e.detail?.question || null);
      setOpen(true);
    };
    window.addEventListener("chat:open", onOpen);
    return () => window.removeEventListener("chat:open", onOpen);
  }, []);
  // The owner's spec, verbatim: the pill rests SMALL so it covers nothing.
  // First press grows it (announcing what it is); a second press while grown
  // opens the chat; three seconds un-pressed and it shrinks back.
  const [grown, setGrown] = useState(false);
  const shrinkTimer = useRef(null);
  useEffect(() => () => clearTimeout(shrinkTimer.current), []);
  const press = useCallback(() => {
    if (grown) {
      clearTimeout(shrinkTimer.current);
      setGrown(false);
      setOpen(true);
      return;
    }
    setGrown(true);
    clearTimeout(shrinkTimer.current);
    shrinkTimer.current = setTimeout(() => setGrown(false), 3000);
  }, [grown]);
  if (!can("ai_chat") || replayOn) return null;
  return html`
    <${React.Fragment}>
      <button className=${"chat-pill" + (grown ? "" : " is-mini")} onClick=${press}
              aria-label=${t("chat.pill", "Ask about this")}
              aria-expanded=${grown}>
        <i className="fa-solid fa-comment-dots"></i>
        ${grown && html`<span>${t("chat.pill", "Ask about this")}</span>`}
      </button>
      <${ChatSheet} open=${open} onClose=${() => { setOpen(false); setSeedQuestion(null); }} timeframe=${timeframe} seedQuestion=${seedQuestion} />
    <//>`;
}

function ChatSheet({ open, onClose, timeframe, seedQuestion = null }) {
  const { t, language } = useI18n();
  const [turns, setTurns] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  // A captured picture of the chart, waiting to ride the next question.
  const [shot, setShot] = useState(null);
  const bodyRef = useRef(null);
  const sheetRef = useDismissable(open, onClose);

  // The tab bar is a fixed element in its own stacking context, so no z-index
  // on the sheet can get the compose row above it -- the send button sat
  // exactly under the Journal tab and could not be pressed. While a modal
  // sheet is up the bar is not a destination anyway, so it goes, and the page
  // behind stops scrolling with it.
  useEffect(() => {
    if (!open) return undefined;
    document.body.classList.add("chat-open");
    return () => document.body.classList.remove("chat-open");
  }, [open]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [turns, busy]);

  const ask = useCallback(async (question) => {
    const text = String(question || "").trim();
    if (!text || busy) return;
    const image = shot;
    setDraft("");
    setShot(null);
    setTurns((prev) => [...prev, { role: "user", text, image }]);
    setBusy(true);
    try {
      const response = await authenticatedFetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, timeframe,
                               image: image || undefined }),
      });
      const payload = await response.json();
      // "Could not reach the engine" is a lie when the engine refused on
      // purpose, and it points the customer at retrying -- the one action
      // that cannot work while a limit is in force.
      if (payload.error_type === "rate_limit") {
        const seconds = payload.retry_after != null ? String(payload.retry_after) : "60";
        setTurns((prev) => [...prev, { role: "error", text: formatTemplate(
          t("chat.rate_limited",
            "You have reached the limit on questions for now. Try again in {seconds}s."),
          { seconds }) }]);
        setBusy(false);
        return;
      }
      if (!payload.success) throw new Error(payload.error || "failed");
      setTurns((prev) => [...prev, { role: "engine", ...payload }]);
    } catch (error) {
      setTurns((prev) => [...prev, { role: "error", text: t("chat.failed",
        "Could not reach the engine. Nothing was answered.") }]);
    }
    setBusy(false);
  }, [busy, t, timeframe, shot]);

  // A seeded question (tap-any-line) asks itself the moment the sheet is
  // ready -- "اكبس عالخط واسأل" must be ONE gesture, not tap-then-retype.
  const seededRef = useRef(null);
  useEffect(() => {
    if (!open || !seedQuestion || seededRef.current === seedQuestion) return;
    seededRef.current = seedQuestion;
    ask(seedQuestion);
  }, [open, seedQuestion, ask]);
  // Closing re-arms the seed, so the SAME line can be asked about twice.
  useEffect(() => { if (!open) seededRef.current = null; }, [open]);

  if (!open) return null;

  const suggestions = [
    ["chat.suggest_why", "Why is there no trade?"],
    ["chat.suggest_what_helps", "What would change it?"],
    ["chat.suggest_levels", "Where are the levels?"],
    ["chat.suggest_record", "How am I doing?"],
  ];

  return html`
    <div className="chat-scrim">
      <section className="chat-sheet" ref=${sheetRef} role="dialog" aria-modal="true"
               aria-label=${t("chat.title", "Ask about this")}>
        <header className="chat-head">
          <span className="chat-grip" aria-hidden="true"></span>
          <h2>${t("chat.title", "Ask about this")}</h2>
          <button className="chat-close" onClick=${onClose}
                  aria-label=${t("chat.close", "Close")}>
            <i className="fa-solid fa-xmark"></i>
          </button>
        </header>

        <div className="chat-body" ref=${bodyRef}>
          ${turns.length === 0 && html`
            <div className="chat-intro">
              <p>${t("chat.intro", "I answer only from the engine's numbers. I don't forecast, and I don't tell you to buy.")}</p>
              <div className="chat-suggestions">
                ${suggestions.map(([key, fallback]) => html`
                  <button key=${key} className="chat-suggestion"
                          onClick=${() => ask(t(key, fallback))}>
                    ${t(key, fallback)}
                  </button>`)}
              </div>
            </div>`}

          ${turns.map((turn, i) => turn.role === "user"
            ? html`<p key=${i} className="chat-you">
                ${turn.image && html`<img className="chat-you-shot" src=${turn.image} alt="" />`}
                ${turn.text}</p>`
            : turn.role === "error"
              ? html`<p key=${i} className="chat-answer is-error">${turn.text}</p>`
              : html`<${ChatAnswer} key=${i} turn=${turn} t=${t} language=${language} />`)}

          ${busy && html`<p className="chat-answer is-thinking">${t("chat.thinking", "Reading the state…")}</p>`}
        </div>

        ${shot && html`
          <div className="chat-shot-strip">
            <img src=${shot} alt="" />
            <span>${t("chat.shot_ready", "The chart rides with your next question")}</span>
            <button type="button" aria-label=${t("buttons.close", "Close")}
                    onClick=${() => setShot(null)}>
              <i className="fa-solid fa-xmark"></i>
            </button>
          </div>`}
        <form className="chat-compose" onSubmit=${(e) => { e.preventDefault(); ask(draft); }}>
          ${typeof window !== "undefined" && window.__mithqalChartShot && html`
            <button type="button" className="chat-shot-btn" disabled=${busy}
                    aria-label=${t("chat.attach_chart", "Attach a picture of the chart")}
                    title=${t("chat.attach_chart", "Attach a picture of the chart")}
                    onClick=${() => {
                      try {
                        const s = window.__mithqalChartShot();
                        if (s) setShot(s);
                      } catch (e) { /* canvas tainted or gone */ }
                    }}>
              <i className="fa-solid fa-camera"></i>
            </button>`}
          ${/* Any platform's screenshot, from the gallery: the vision story's
                second half -- "compare THEIR chart with OUR levels". Down-
                scaled client-side to the same cap as the canvas capture. */""}
          <label className="chat-shot-btn" title=${t("chat.upload_image", "Upload a chart screenshot")}
                 aria-label=${t("chat.upload_image", "Upload a chart screenshot")}>
            <i className="fa-solid fa-image"></i>
            <input type="file" accept="image/*" hidden disabled=${busy}
                   onChange=${(e) => {
                     const file = e.target.files && e.target.files[0];
                     e.target.value = "";
                     if (!file) return;
                     const reader = new FileReader();
                     reader.onload = () => {
                       const img = new Image();
                       img.onload = () => {
                         const scale = Math.min(1, 1000 / img.width);
                         const c = document.createElement("canvas");
                         c.width = Math.round(img.width * scale);
                         c.height = Math.round(img.height * scale);
                         c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
                         setShot(c.toDataURL("image/jpeg", 0.85));
                       };
                       img.src = reader.result;
                     };
                     reader.readAsDataURL(file);
                   }} />
          </label>
          <input value=${draft} disabled=${busy}
                 placeholder=${t("chat.placeholder", "Ask about gold, this setup, your record…")}
                 onInput=${(e) => setDraft(e.target.value)} />
          <button type="submit" disabled=${busy || !draft.trim()}
                  aria-label=${t("chat.send", "Send")}>
            <i className="fa-solid fa-arrow-up"></i>
          </button>
        </form>
      </section>
    </div>`;
}

// One answer: its claims, each with the citations that back it, and a footer
// stating when the state it read was taken. The citations are the honesty
// mechanism -- an answer whose backing cannot be named is an answer this
// product does not make.
function ChatAnswer({ turn, t, language }) {
  const refused = turn.outcome !== "answer";
  return html`
    <div className=${"chat-answer" + (refused ? " is-refusal" : "")}>
      ${/* A model answer arrives as prose, not claims -- rendered as-is and
            labeled below, so it can never wear the engine's citations. */""}
      ${turn.text && html`<p className="chat-claim" dir="auto">${turn.text}</p>`}
      ${(turn.claims || []).map((claim, i) => {
        // Numbers are substituted here, from the values the server validated
        // against the bundle. {n0}, {n1}… are the only way a numeral gets in.
        const params = { ...(claim.params || {}) };
        (claim.numbers || []).forEach((n, k) => {
          params[`n${k}`] = iso(formatChatNumber(n));
        });
        // chat.withheld / chat.blocker carry a gate reason verbatim, so the
        // one Arabic sentence in the answer ended "محجوبة من event:CPI".
        ["by", "blocker"].forEach((key) => {
          if (params[key]) params[key] = describeGateReason(params[key], language) || params[key];
        });
        return html`
          <p key=${i} className="chat-claim">
            ${formatTemplate(t(claim.key, claim.key), params)}
            ${claim.cites && claim.cites.length > 0 && html`
              <span className="chat-cites">
                ${claim.cites.map((cite) => html`
                  <span key=${cite} className="chat-cite" title=${cite}>${shortCite(cite)}</span>`)}
              </span>`}
          </p>`;
      })}
      <p className="chat-foot">
        ${turn.as_of ? formatTemplate(t("chat.as_of", "As of {time}"),
          { time: new Date(turn.as_of * 1000).toLocaleTimeString(language === "ar" ? "en-GB" : undefined,
            { hour: "2-digit", minute: "2-digit" }) }) : ""}
        ${turn.stale ? " · " + t("chat.stale", "This is {mins} min old").replace("{mins}",
          String(Math.round((Date.now() / 1000 - turn.as_of) / 60))) : ""}
        ${turn.source === "model" ? " · " + t("chat.from_model", "assistant — numbers only from the engine's state") : ""}
      </p>
    </div>`;
}

// A dotted bundle address is precise but unreadable. The chip shows the last
// meaningful segment; the full address stays in the title for anyone checking.
function shortCite(cite) {
  const parts = String(cite).split(".");
  const tail = parts.filter((p) => !/^\d+$/.test(p)).slice(-2).join(" ");
  return tail.replace(/_/g, " ");
}

function formatChatNumber(n) {
  const value = Number(n.value);
  if (!Number.isFinite(value)) return String(n.value);
  const role = n.role || "reference";
  if (Number.isInteger(value) && Math.abs(value) < 1000 && role === "reference") {
    return String(value);
  }
  return Math.abs(value) < 10 ? value.toFixed(2) : value.toFixed(2);
}

// The AI analysis as a full page (the owner's ask -- it outgrew the modal).
// A reasoned verdict with sizing, agreement and its technical basis is a
// document to read, not a popup to dismiss: a page can be linked, refreshed,
// landed on from an alert, and returned to without losing the dashboard.
// What the hallucination rail has actually done (master list 185-203).
//
// It sits at the foot of the page where the model's output appears, because
// a customer reading an AI answer is exactly the person entitled to know how
// often this product has caught its own model inventing a price. A quality
// figure on an operator dashboard is a figure the customer has to take on
// trust, and "trust us, we check" is the sentence this product exists to
// avoid.
//
// Everything rendered here is a COUNT of what a mechanical check did. There
// is no accuracy number, because there is nothing honest to measure accuracy
// against.
function AiQualityPanel() {
  const { t } = useI18n();
  const [report, setReport] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const p = await (await authenticatedFetch("/api/ai/quality")).json();
        if (alive && p.success) setReport(p.report);
      } catch (e) { /* the panel simply does not appear */ }
    })();
    return () => { alive = false; };
  }, []);

  if (!report) return null;

  return html`
    <section className="panel ai-quality">
      <h3><i className="fa-solid fa-shield-halved"></i>
        ${t("quality.title", "What this product caught its own model doing")}</h3>

      ${!report.available
        ? html`<p className="settings-hint">${report.reason}</p>`
        : html`
          <div className="ai-quality-figures" dir="ltr">
            <div>
              <b>${iso(String(report.withheld))}</b>
              <small>${t("quality.withheld", "answers withheld")}</small>
            </div>
            <div>
              <b>${iso(String(report.checked))}</b>
              <small>${t("quality.checked", "answers checked")}</small>
            </div>
            <div>
              <b>${iso(String(report.prices_grounded))}<span>/${iso(String(report.prices_checked))}</span></b>
              <small>${t("quality.grounded", "prices found in the data")}</small>
            </div>
          </div>
          <p className="ai-quality-reading">${report.reading}</p>
          ${report.drift && html`<p className="ai-quality-drift">${report.drift}</p>`}
          <p className="ai-quality-caveat">${report.caveat}</p>`}
    </section>`;
}

function AnalysisPage() {
  const { t, language } = useI18n();
  const { status, can } = useAuth();
  const [currentTimeframe, setCurrentTimeframe] = useState(() => {
    const fromUrl = new URLSearchParams(window.location.search).get("tf");
    return normalizeTimeframe(fromUrl || sessionStorage.getItem("preferredTimeframe"));
  });
  const [run, setRun] = useState({ loading: false, data: null, advisor: null, technical: null, error: null });
  // When the shown analysis was READ (ISO) -- stamped so a cached read is
  // honest about its age instead of dressing up as live.
  const [asOf, setAsOf] = useState(null);
  const market = useMarketStatus();

  useEffect(() => {
    if (status === "guest") window.location.href = "/login";
  }, [status]);

  useEffect(() => {
    document.title = t("analysis.page_title", "AI Analysis — Mithqal");
  }, [t]);

  const allowed = can("ai_analysis");

  const runAnalysis = useCallback(async () => {
    setRun({ loading: true, data: null, advisor: null, technical: null, error: null });
    try {
      const response = await authenticatedFetch(`/api/ai-analysis/${currentTimeframe}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ language })
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) {
        // A rate limit is NOT a failure to report as one. It is the only
        // refusal here that "try again" makes worse, so it is set directly
        // rather than thrown -- the catch below deliberately discards
        // messages, and this is the one that must survive.
        if (payload.error_type === "rate_limit") {
          const seconds = payload.retry_after != null ? String(payload.retry_after) : "20";
          setRun({ loading: false, data: null, advisor: null, technical: null,
                   error: formatTemplate(
                     t("ai_modal.rate_limit_error",
                       "Rate limit reached. Try again in {seconds}s."),
                     { seconds }) });
          return;
        }
        throw new Error(payload.error || t("ai_modal.error_title", "Error"));
      }
      setRun({ loading: false, data: payload.analysis, advisor: payload.advisor,
               technical: payload.technical, error: null });
      setAsOf(new Date().toISOString());
    } catch (error) {
      // NEVER error.message. Verified live, this rendered to customers as
      // "Expected double-quoted property name in JSON at position 16" and
      // "Failed to fetch" -- untranslated, in both languages, as the entire
      // customer-facing copy for a failure. A parser's opinion of our own
      // payload is not something a paying customer should ever read.
      console.error("analysis failed:", error);
      setRun({ loading: false, data: null, advisor: null, technical: null,
               error: t("ai_modal.failed",
                        "The analysis did not come back. Nothing was charged — try again.") });
    }
  }, [currentTimeframe, language]);

  // The page opens on the LAST read, instantly and unbilled; the model is
  // called only when there is no cache, the cache has aged past 15 minutes,
  // or the reader presses refresh. Auto-running on every arrival billed a
  // model call per visit and per frame switch.
  useEffect(() => {
    if (status !== "authenticated" || market.open !== true) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await authenticatedFetch(
          `/api/ai-analysis/${currentTimeframe}?language=${language}`);
        const payload = await response.json();
        if (cancelled) return;
        if (payload.success && payload.cached && payload.analysis) {
          setRun({ loading: false, data: payload.analysis, advisor: payload.advisor,
                   technical: payload.technical, error: null });
          setAsOf(payload.as_of || null);
          const age = payload.as_of ? (Date.now() - Date.parse(payload.as_of)) : Infinity;
          if (age > 15 * 60 * 1000) runAnalysis();
          return;
        }
      } catch (error) { /* no cache is fine; run fresh below */ }
      if (!cancelled) runAnalysis();
    })();
    return () => { cancelled = true; };
  }, [status, market.open, runAnalysis, currentTimeframe, language]);

  const changeTimeframe = useCallback((tf) => {
    setCurrentTimeframe(tf);
    try { sessionStorage.setItem("preferredTimeframe", tf); } catch { /* private mode */ }
    const url = new URL(window.location.href);
    url.searchParams.set("tf", tf);
    history.replaceState(null, "", url);
  }, []);

  if (!allowed) {
    return html`
      <div className="app-shell">
        <${PageNav} />
        <main className="analysis-body"><${ServiceOff} feature="ai_analysis" /></main>
      </div>`;
  }

  return html`
    <div className="app-shell">
      <${PageNav} />
      <main className="analysis-body">
        <div className="analysis-head">
          <h1><i className="fa-solid fa-wand-magic-sparkles"></i> ${t("ai_modal.title", "AI Trading Analysis")}</h1>
          <div className="analysis-controls">
            <div className="tf-strip" role="group" aria-label=${t("chart.timeframe", "Timeframe")}>
              ${QUICK_TIMEFRAMES.map((tf) => html`
                <button key=${tf} className=${"tf-chip" + (currentTimeframe === tf ? " is-active" : "")}
                        disabled=${run.loading}
                        onClick=${() => changeTimeframe(tf)}>
                  ${tf}
                </button>`)}
            </div>
            <button className="btn btn-secondary" onClick=${runAnalysis}
                    disabled=${run.loading || market.open !== true}>
              <i className="fa-solid fa-rotate"></i> ${t("analysis.rerun", "Re-run")}
            </button>
            ${/* Share the read: the owner's ask ("wen l share lal tehlil").
                  Web Share where the OS offers it, clipboard elsewhere. The
                  text carries the verdict summary and the app link -- never
                  a screenshot of numbers stripped of their caveats. */""}
            <button className="btn btn-secondary" disabled=${run.loading || !run.data}
                    onClick=${async () => {
                      const d = run.data || {};
                      const lines = [
                        `Mithqal · XAUUSD ${currentTimeframe}`,
                        d.action ? `${t("advisor.signal", "Call")}: ${String(d.action).toUpperCase()}` : "",
                        d.analysis ? String(d.analysis).slice(0, 280) : "",
                        window.location.origin + "/analysis?tf=" + currentTimeframe,
                      ].filter(Boolean);
                      const text = lines.join("\n");
                      try {
                        if (navigator.share) await navigator.share({ text });
                        else {
                          await navigator.clipboard.writeText(text);
                          alert(t("analysis.copied", "Copied — paste it anywhere."));
                        }
                      } catch (e) { /* user cancelled the sheet */ }
                    }}>
              <i className="fa-solid fa-share-nodes"></i> ${t("analysis.share", "Share")}
            </button>
          </div>
          ${asOf && !run.loading && html`
            <p className="analysis-asof">
              <i className="fa-regular fa-clock" aria-hidden="true"></i>
              ${t("analysis.read_at", "Read at")}${" "}
              ${iso(new Date(asOf).toLocaleTimeString(appLocale(), { hour: "2-digit", minute: "2-digit", hour12: false }))}
            </p>`}
        </div>

        ${market.open === false && html`
          <section className="panel surface analysis-state">
            <i className="fa-solid fa-moon"></i>
            <h2>${t("market.ai_unavailable_title", "AI Analysis Unavailable")}</h2>
            <p>${t("market.ai_unavailable_message", "AI Analysis is only available during active trading hours.")}</p>
            <p><strong>${t("market.opens", "Opens: Sunday 5 PM EST")}</strong> · <strong>${t("market.closes", "Closes: Friday 5 PM EST")}</strong></p>
          </section>`}

        ${market.open !== false && run.loading && html`
          <section className="panel surface analysis-state" aria-busy="true">
            <i className="fa-solid fa-spinner fa-spin"></i>
            <p>${t("ai_modal.analyzing", "AI is analyzing market data...")}</p>
          </section>`}

        ${/* Once the market closes, the closed panel stands ALONE: an
             actionable-looking analysis (or a retry button) beneath a
             "market closed" notice is two contradicting instructions. */""}
        ${market.open !== false && run.error && html`
          <section className="panel surface analysis-state" role="alert">
            <i className="fa-solid fa-triangle-exclamation"></i>
            <h2>${t("ai_modal.error_title", "Error")}</h2>
            <p>${run.error}</p>
            <button className="btn btn-primary" onClick=${runAnalysis}>
              ${t("analysis.retry", "Try again")}
            </button>
          </section>`}

        ${/* THE PANEL IS THE ANALYSIS NOW (owner, 2026-08-17: "zabeta").
              The page used to run a single model that could only confirm or
              stand aside on the engine's levels. The new structure — six
              experts per frame, reading independently, one debate round, and a
              chair that is the only layer seeing all of it — was reachable
              only from /api/panel/shadow and rendered on a dashboard fold.
              Two AI opinions on one screen would be the duplication this
              product is being cleaned of, so the panel takes the page and the
              single-model advisor's own read stays underneath as the
              CONFIRM-OR-HOLD line it always was.

              WHAT DID NOT MOVE: the published record. The ledger and the
              gates are still the deterministic engine's, and the panel rules
              on the same market without issuing anything into it. A structure
              with no measured record does not get to publish. */""}
        ${market.open !== false && html`
          <${ExpertPanel} timeframe=${currentTimeframe} auto />`}

        ${/* THE OLD ANALYSIS IS GONE FROM THIS PAGE.
              I left it under the panel as "the confirm-or-hold line it always
              was" and the owner's answer was immediate: the page still shows
              the old analysis. He is right. Two AI readings stacked on one
              screen — a twelve-expert panel and a single model's card, with an
              "overlap 65%" meter between them — is the same duplication being
              cleaned out of every other surface, and it leaves the reader
              deciding which of the two the product means.

              The panel IS the analysis now. The single-model advisor still
              runs where it belongs: as the confirm-or-hold step inside the
              publication path, which is a gate and not a screen. */""}
        <${AiQualityPanel} />
      </main>
    </div>
  `;
}

// The autopsy line (#218): after a trade resolves, one plain sentence about
// what happened -- from the recorded fields, not a narrative invented later.
function tradeAutopsy(trade, t) {
  const r = trade.r_multiple;
  const rTxt = r != null ? `${r > 0 ? "+" : ""}${Number(r).toFixed(2)}R` : "";
  const held = (() => {
    const a = Date.parse(trade.opened_at), b = Date.parse(trade.closed_at);
    if (!isFinite(a) || !isFinite(b)) return null;
    const mins = Math.max(0, Math.round((b - a) / 60000));
    return mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
  })();
  const heldTxt = held ? ` ${formatTemplate(t("journal.after", "after {held}"), { held })}` : "";
  const moves = (trade.events || []).length;
  const movedTxt = moves
    ? ` ${formatTemplate(t("journal.stop_moved_n", "The stop was moved {n} time(s)."), { n: iso(moves) })}`
    : "";
  // The backend's ExitReason enum writes take_profit / stop_loss / manual /
  // expired -- matching anything else renders a raw token in the journal.
  switch (trade.exit_reason) {
    case "take_profit":
      return formatTemplate(t("journal.hit_target", "Reached its target at {price} for {r}{held}."),
        { price: iso(formatPrice(trade.exit_price)), r: iso(rTxt), held: heldTxt }) + movedTxt;
    case "stop_loss":
      return formatTemplate(t("journal.hit_stop", "Stopped out at {price} for {r}{held}."),
        { price: iso(formatPrice(trade.exit_price)), r: iso(rTxt), held: heldTxt }) + movedTxt;
    case "manual":
      return formatTemplate(t("journal.closed_manual", "Closed at market at {price} for {r}{held}."),
        { price: iso(formatPrice(trade.exit_price)), r: iso(rTxt), held: heldTxt }) + movedTxt;
    case "expired":
      return formatTemplate(t("journal.timed_out", "Held past the style's window and closed at {price} for {r}."),
        { price: iso(formatPrice(trade.exit_price)), r: iso(rTxt) });
    default:
      return trade.exit_reason
        ? `${trade.exit_reason} · ${iso(rTxt)}`
        : iso(rTxt);
  }
}

// One mapping from the open-position API's error slugs to human copy. The
// dashboard had this inline while the chart page showed the raw token
// ('risk_halted') -- the same server answer must read the same everywhere.
function takeErrorMessage(t, code) {
  const messages = {
    no_setup: t("positions.err_no_setup", "There is no setup to take right now."),
    setup_incomplete: t("positions.err_incomplete", "That setup has no stop level, so it cannot be tracked."),
    already_open: t("positions.err_already_open", "A position is already open on this market."),
    price_moved: t("positions.err_price_moved", "Price has moved too far from the published entry. Wait for the next setup."),
    vetoed: t("positions.err_vetoed", "The pipeline stood this trade aside."),
    rejected_by_validator: t("positions.err_invalid", "Those levels are the wrong way round and were refused."),
    no_price: t("positions.err_no_price", "No live price available."),
    risk_halted: t("positions.err_risk_halted", "Trading is halted by the system's risk limits."),
    zero_risk: t("positions.err_invalid", "Those levels are the wrong way round and were refused."),
    // The close and stop-move refusals. Without these the customer read the
    // enum itself -- "not_found" under a position they had just closed, which
    // is both untranslated and frightening in a screen about their money.
    not_found: t("positions.err_gone", "That position is no longer open — it has already been closed."),
    price_unavailable: t("positions.err_no_price", "No live price available."),
    bad_price: t("positions.err_bad_price", "That stop level was not a number."),
    // move_stop refuses three ways and reports one code: it would widen risk,
    // it sits through the current price, or the trade closed while you dragged.
    refused: t("positions.err_stop_refused", "That stop was refused — it would widen your risk, sit through the current price, or the position has already closed."),
    unknown_mode: t("positions.err_invalid", "Those levels are the wrong way round and were refused."),
  };
  if (messages[code]) return messages[code];
  // A code with no sentence is a gap in THIS table, not copy. Naming it in the
  // console keeps it findable without printing an enum at a paying customer.
  if (code) console.warn("no customer sentence for refusal code:", code);
  return t("positions.err_unknown", "That did not go through. Nothing was changed.");
}

// The trading journal (#161/#170/#218): what this user actually did, and what
// the product actually called -- two honest lists with outcomes, no charts of
// hypothetical equity, no averaging away of the override trades.
// Replay without the answer, then reveal (signature #9). It lives in the
// journal because it is about learning from the record rather than about the
// live market.
//
// TWO fetches, deliberately. The exercise call carries bars up to the decision
// point and nothing else; the outcome only exists after the answer is posted.
// Sending everything and covering the right-hand side would put the answer one
// network-tab away, and a self-assessment built on that feeds a false reading
// into confidence calibration.
// "Show me every day where..." (master list 241-264). The open version of
// the historical twin: the customer writes the filter.
//
// The search counter is deliberately client-side and deliberately visible. A
// person who can filter history until something looks good will filter history
// until something looks good — it takes about four tries — and the only thing
// that interrupts it is being told, plainly, that this is search five. Keeping
// a server-side log of what everybody searched for would be recording
// something nobody asked us to record to display a number the browser has.
function ResearchScanner() {
  const { t } = useI18n();
  const [query, setQuery] = useState({});
  const [result, setResult] = useState(null);
  const [searches, setSearches] = useState(0);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  const scan = useCallback(async () => {
    setBusy(true); setProblem(null);
    try {
      const response = await authenticatedFetch("/api/research/scan", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query, searches_before: searches }),
      });
      const payload = await response.json();
      if (payload.success) {
        setResult(payload.result);
        setSearches(payload.result.searches_this_session || searches + 1);
      } else {
        setProblem(payload.detail || t("research.refused", "That search could not be run."));
      }
    } catch (e) {
      setProblem(t("research.unreachable", "That did not reach the server."));
    }
    setBusy(false);
  }, [query, searches, t]);

  const set = (key, value) =>
    setQuery({ ...query, [key]: value === "" ? undefined : value });

  return html`
    <section className="research" id="research">
      <p className="settings-hint">
        ${t("research.intro", "Ask the daily record a question. It answers with the days themselves — never with a rate for what happens next.")}
      </p>

      <div className="research-filters">
        <label>
          <span>${t("research.state", "Market state")}</span>
          <select value=${query.state || ""} onChange=${(e) => set("state", e.target.value)}>
            <option value="">${t("research.any", "any")}</option>
            ${["compression", "expansion", "trend", "exhaustion", "quiet drift"].map((v) =>
              html`<option key=${v} value=${v}>${t("state." + v.replace(" ", "_"), v)}</option>`)}
          </select>
        </label>
        <label>
          <span>${t("research.vol_min", "Volatility percentile at least")}</span>
          <input type="number" min="0" max="100" inputMode="numeric"
                 value=${query.volatility_min ?? ""}
                 onInput=${(e) => set("volatility_min", e.target.value)} />
        </label>
        <label>
          <span>${t("research.vol_max", "Volatility percentile at most")}</span>
          <input type="number" min="0" max="100" inputMode="numeric"
                 value=${query.volatility_max ?? ""}
                 onInput=${(e) => set("volatility_max", e.target.value)} />
        </label>
        <label>
          <span>${t("research.prior", "Previous day")}</span>
          <select value=${query.prior_direction || ""}
                  onChange=${(e) => set("prior_direction", e.target.value)}>
            <option value="">${t("research.any", "any")}</option>
            <option value="up">${t("research.prior_up", "closed up")}</option>
            <option value="down">${t("research.prior_down", "closed down")}</option>
            <option value="flat">${t("research.prior_flat", "closed flat")}</option>
          </select>
        </label>
      </div>

      <div className="row-actions">
        <button className="btn btn-primary" disabled=${busy} onClick=${scan}>
          ${busy ? t("research.working", "Searching…") : t("research.go", "Search history")}
        </button>
      </div>

      ${problem && html`<p className="note-bad" role="alert">${problem}</p>`}

      ${result && html`
        <div className="research-out">
          <p className="research-question">${result.describes}</p>

          <div className="research-sample" dir="ltr">
            <b>${iso(String(result.matched))}</b>
            <small>${formatTemplate(
              t("research.of_scanned", "of {n} days"), { n: iso(String(result.scanned)) })}</small>
          </div>
          <p className="settings-hint">${result.reason}</p>

          ${result.counts && html`
            <ul className="research-counts">
              ${Object.entries(result.counts).map(([bucket, n]) => html`
                <li key=${bucket}>
                  <span>${t("research.bucket_" + bucket, bucket)}</span>
                  <b dir="ltr">${iso(String(n))}</b>
                </li>`)}
            </ul>`}

          ${result.caution && html`
            <p className="research-caution" role="note">${result.caution}</p>`}

          ${result.rows.length > 0 && html`
            <div className="table-scroll">
              <table className="research-table">
                <thead><tr>
                  <th>${t("research.date", "Date")}</th>
                  <th>${t("research.state", "Market state")}</th>
                  <th dir="ltr">${t("research.vol", "Vol %ile")}</th>
                  <th dir="ltr">${t("research.move", "Move")}</th>
                </tr></thead>
                <tbody>
                  ${result.rows.map((row) => html`
                    <tr key=${row.date}>
                      <td dir="ltr">${iso(row.date)}</td>
                      <td>${row.state || "—"}</td>
                      <td dir="ltr">${row.volatility == null ? "—" : iso(String(row.volatility))}</td>
                      <td dir="ltr" className=${row.outcome === "up" ? "is-up"
                                              : row.outcome === "down" ? "is-down" : ""}>
                        ${row.move_pct == null ? "—" : iso(row.move_pct.toFixed(2) + "%")}
                      </td>
                    </tr>`)}
                </tbody>
              </table>
            </div>`}
        </div>`}
    </section>`;
}

function ReplayExercise() {
  const { t } = useI18n();
  const [state, setState] = useState({ loading: true });
  const [reveal, setReveal] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setReveal(null);
    setState({ loading: true });
    try {
      const p = await (await authenticatedFetch("/api/replay/exercise")).json();
      setState({ loading: false, ...p });
    } catch (e) {
      setState({ loading: false, available: false,
                 reason: t("replay.unreachable", "The exercise could not be loaded.") });
    }
  }, [t]);
  useEffect(() => { load(); }, [load]);

  const answer = useCallback(async (choice) => {
    if (!state.exercise) return;
    setBusy(true);
    try {
      const p = await (await authenticatedFetch(
        `/api/replay/exercise/${state.exercise.token}/answer`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ answer: choice }),
        })).json();
      if (p.success) setReveal(p.reveal);
    } catch (e) { /* the exercise simply stays unanswered */ }
    setBusy(false);
  }, [state]);

  if (state.loading) return null;
  if (!state.available) {
    return html`
      <section className="card replay-exercise">
        <h3>${t("replay.title", "What would you have seen?")}</h3>
        <p className="journal-empty">${state.reason}</p>
      </section>`;
  }

  const ex = state.exercise;
  const levels = ex.levels || {};
  return html`
    <section className="card replay-exercise">
      <h3>${t("replay.title", "What would you have seen?")}</h3>
      <p className="settings-hint">
        ${t("replay.intro", "A call this product actually published, with the chart stopped at the moment it went out. The rest is not on this page until you answer.")}
      </p>

      <div className="replay-levels" dir="ltr">
        <span><small>${t("replay.side", "Side")}</small><b>${String(levels.side || "").toUpperCase()}</b></span>
        <span><small>${t("positions.entry", "Entry")}</small><b>${iso(formatPrice(levels.entry))}</b></span>
        <span><small>${t("positions.stop", "Stop")}</small><b>${iso(formatPrice(levels.stop))}</b></span>
        <span><small>${t("positions.target", "Target")}</small><b>${iso(formatPrice(levels.target))}</b></span>
      </div>

      ${!reveal && html`
        <div className="replay-ask">
          <p>${ex.asked}</p>
          <div className="row-actions">
            <button className="btn btn-primary" disabled=${busy}
                    onClick=${() => answer("take it")}>
              ${t("replay.take", "I would take it")}
            </button>
            <button className="btn btn-secondary" disabled=${busy}
                    onClick=${() => answer("pass")}>
              ${t("replay.pass", "I would pass")}
            </button>
          </div>
        </div>`}

      ${reveal && html`
        <div className=${"replay-reveal is-" + (reveal.outcome || "unknown")}>
          <p className="replay-lesson">${reveal.lesson}</p>
          ${reveal.verify_url && html`
            <a href=${reveal.verify_url}>
              ${t("replay.verify", "Open this call in the public record")}
            </a>`}
          <div className="row-actions">
            <button className="btn btn-secondary" onClick=${load}>
              ${t("replay.again", "Another one")}
            </button>
          </div>
        </div>`}
    </section>`;
}

function JournalPage() {
  const { t } = useI18n();
  const { status, can } = useAuth();
  const [positions, setPositions] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [performance, setPerformance] = useState(null);

  useEffect(() => {
    if (status === "guest") window.location.href = "/login";
  }, [status]);

  useEffect(() => {
    document.title = t("journal.page_title", "Journal — Mithqal");
  }, [t]);

  // Failures must LOOK like failures. These catches used to swallow every
  // error, and the page then rendered its empty-state copy -- "No trades
  // yet" -- over a server that was down. On the product whose whole pitch is
  // the honest record, a clean-looking record that is actually a fetch error
  // is the worst possible lie.
  const [journalFailed, setJournalFailed] = useState(false);
  // Confidence calibration (spec §62). It rides on the decision log's own
  // endpoint and had no screen -- built, tested, and invisible, which is the
  // defect docs/QA-LAW.md §1.2 exists to catch.
  const [calibration, setCalibration] = useState(null);
  const loadJournal = useCallback(() => {
    if (status !== "authenticated") return;
    setJournalFailed(false);
    Promise.all([
      authenticatedFetch("/api/positions").then((r) => r.json())
        .then((p) => { if (!p.success) throw new Error(); setPositions(p); }),
      authenticatedFetch("/api/signals").then((r) => r.json())
        .then((p) => { if (!p.success) throw new Error(); setLedger(p); }),
      authenticatedFetch("/api/performance").then((r) => r.json())
        .then((p) => { if (!p.success) throw new Error(); setPerformance(p); }),
      // Never throws into the journal's failure path: a calibration that
      // cannot be computed must not blank the record it sits beside.
      authenticatedFetch("/api/verdict-response").then((r) => r.json())
        .then((p) => { if (p.success) setCalibration(p.calibration); })
        .catch(() => {}),
    ]).catch(() => setJournalFailed(true));
  }, [status]);
  useEffect(() => { loadJournal(); }, [loadJournal]);

  const closed = positions?.closed_positions || [];
  const open = positions?.open_positions || [];
  const signals = ledger?.signals || [];

  // This week, from the user's own Monday. The record page should answer
  // "how was the week?" without arithmetic homework.
  const weekStart = (() => {
    const d = new Date(); d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return d.getTime() / 1000;
  })();
  const week = {
    published: signals.filter((s) => (s.published_at || 0) >= weekStart).length,
    resolvedRows: signals.filter((s) => (s.resolved_at || 0) >= weekStart
                                        && (s.status === "won" || s.status === "lost")),
  };
  week.r = week.resolvedRows.reduce((sum, s) => sum + (s.r_multiple || 0), 0);

  // The discipline streak: how many trades IN A ROW went with the verdict.
  // Counted on followed-vs-overridden, never on wins -- a streak that
  // rewards winning teaches overtrading; one that rewards discipline
  // teaches the only behaviour the journal can prove pays.
  const streak = (() => {
    let n = 0;
    for (const trade of closed) {
      if (trade.source === "manual_override") break;
      n += 1;
    }
    return n;
  })();

  // Filter chips for the published record.
  const [sigFilter, setSigFilter] = useState("all");
  const shownSignals = sigFilter === "all"
    ? signals : signals.filter((s) => s.status === sigFilter);

  const statusChip = (s) => ({
    won: { cls: "won", label: t("journal.won", "won") },
    lost: { cls: "lost", label: t("journal.lost", "lost") },
    expired: { cls: "expired", label: t("performance.expired_short", "never filled") },
    open: { cls: "open", label: t("journal.open", "open") },
  }[s] || { cls: "open", label: s });

  if (!can("journal")) {
    return html`
      <div className="app-shell">
        <${PageNav} />
        <main className="journal-body"><${ServiceOff} feature="journal" /></main>
      </div>`;
  }

  return html`
    <div className="app-shell">
      <${PageNav} />
      <main className="journal-body">
        <div className="analysis-head">
          <h1><i className="fa-solid fa-book"></i> ${t("journal.title", "Trading Journal")}</h1>
        </div>

        ${journalFailed && html`
          <section className="panel surface journal-section" role="alert">
            <p className="journal-empty">
              <i className="fa-solid fa-triangle-exclamation"></i>
              ${" "}${t("journal.load_failed", "Part of your record could not be loaded — what is shown below may be incomplete.")}
            </p>
            <button className="btn btn-secondary" onClick=${loadJournal}>
              ${t("analysis.retry", "Try again")}
            </button>
          </section>`}

        <${MeasuredStrip} performance=${performance} t=${t} />

        ${/* Confidence calibration (spec §62): did feeling certain separate
             your outcomes? It refuses to answer under 30 resolved decisions
             and says how far off it is, which is most of the feature -- a rate
             on four decisions is a chart with a lie in it. Rendered either
             way, because "not yet, and here is how far" is the honest state
             and hiding it would make the feature look absent. */""}
        ${calibration && html`
          <section className="card journal-calibration">
            <h3>${t("calib.title", "Your confidence, against your outcomes")}</h3>
            ${calibration.available
              ? html`<p className="calib-reading">${calibration.reading}</p>`
              : html`<p className="journal-empty">${calibration.reason}</p>`}
            ${(calibration.buckets || []).some((b) => b.resolved > 0) && html`
              <ul className="calib-buckets">
                ${calibration.buckets.filter((b) => b.resolved > 0).map((b) => html`
                  <li key=${b.label}>
                    <span className="calib-label">${t(`calib.${b.label.replace(" ", "_")}`, b.label)}</span>
                    <b dir="ltr">${b.enough ? iso(`${b.win_rate}%`) : "—"}</b>
                    <small>${formatTemplate(
                      t("calib.n", "{n} resolved"), { n: iso(String(b.resolved)) })}${
                      b.enough ? "" : ` · ${t("calib.thin", "too few to rate")}`}</small>
                  </li>`)}
              </ul>`}
            <small className="calib-note">
              ${t("calib.note", "This is about you, not about the market. It says whether your own certainty has tracked your own results — nothing about whether a setup is good.")}
            </small>
          </section>`}

        ${/* When do I actually win? Every number carries its n, and every
             small slice says "noise" instead of dressing up as evidence. The
             discipline line leads: what ignoring the verdict has cost. */
          positions?.analytics && positions.analytics.sample > 0 && (() => {
            const a = positions.analytics;
            const noise = (b) => !b.meaningful && html`
              <em className="journal-noise">${t("journal.noise", "too few to mean anything")}</em>`;
            const bucketRow = (label, b) => {
              // A zero-trade bucket is not information; a wall of five
              // "0 trades · 0% · 0.00R" rows read as a broken page. Empty
              // rows vanish; small samples state their count and stop.
              if (!b.trades) return null;
              if (!b.meaningful) return html`
                <div className="journal-stat-row" key=${label}>
                  <span className="journal-stat-label">${label}</span>
                  <span dir="ltr">${iso(b.trades)} ${b.trades === 1 ? t("performance.trade_one", "trade") : t("performance.trades", "trades")}</span>
                  ${noise(b)}
                </div>`;
              return html`
              <div className="journal-stat-row" key=${label}>
                <span className="journal-stat-label">${label}</span>
                <span dir="ltr">${iso(b.trades)} ${t("performance.trades", "trades")}</span>
                <span dir="ltr">${t("performance.achieved_short", "win rate")} ${iso(b.win_rate + "%")}</span>
                <span dir="ltr" className=${b.avg_r > 0 ? "pos" : (b.avg_r < 0 ? "neg" : "")}>
                  ${iso((b.avg_r > 0 ? "+" : "") + b.avg_r.toFixed(2) + "R")} ${t("journal.avg", "avg")}
                </span>
              </div>`;
            };
            return html`
              <section className="panel surface journal-section">
                <h3>${t("journal.analytics", "When do I win?")}</h3>
                ${/* The obedience curve -- the sharpest self-honesty chart a
                      discipline product can draw: cumulative R of trades
                      taken WITH the verdict vs AGAINST it, same axes, same
                      scale. The gap IS the product's value, in the user's
                      own hand. */""}
                ${(() => {
                  const rows = closed
                    .filter((tr) => !tr.excluded && tr.r_multiple != null)
                    .sort((x, y) => String(x.closed_at || "").localeCompare(String(y.closed_at || "")));
                  if (rows.length < 6) return null;
                  let accF = 0, accO = 0;
                  const fol = [], ovr = [];
                  rows.forEach((tr) => {
                    if (tr.source === "manual_override") accO += tr.r_multiple;
                    else accF += tr.r_multiple;
                    fol.push(accF); ovr.push(accO);
                  });
                  if (!ovr.some((v) => v !== 0)) return null;
                  const all = fol.concat(ovr, [0]);
                  const min = Math.min(...all), max = Math.max(...all);
                  const span = max - min || 1;
                  const W = 560, H = 110;
                  const step = W / Math.max(1, rows.length - 1);
                  const y = (v) => H - ((v - min) / span) * H;
                  const line = (arr) => arr.map((v, i) => `${(i * step).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
                  return html`
                    <div className="obedience-curve">
                      <svg viewBox=${`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
                        <line x1="0" y1=${y(0)} x2=${W} y2=${y(0)} className="perf-zero" />
                        <polyline points=${line(fol)} className="obedience-fol" />
                        <polyline points=${line(ovr)} className="obedience-ovr" />
                      </svg>
                      <div className="obedience-legend">
                        <span><i className="obedience-dot fol"></i>${t("journal.followed", "Followed the verdict")}
                          <b className=${accF >= 0 ? "pos" : "neg"} dir="ltr">${iso(formatR(accF))}</b></span>
                        <span><i className="obedience-dot ovr"></i>${t("journal.against", "Against the verdict")}
                          <b className=${accO >= 0 ? "pos" : "neg"} dir="ltr">${iso(formatR(accO))}</b></span>
                      </div>
                    </div>`;
                })()}
                ${a.discipline.override.trades > 0 && html`
                  <div className="journal-discipline">
                    ${bucketRow(t("journal.followed", "Followed the verdict"), a.discipline.followed)}
                    ${bucketRow(t("journal.against", "Against the verdict"), a.discipline.override)}
                    ${a.discipline.delta_r != null && html`
                      <p className="journal-delta">
                        ${formatTemplate(t("journal.delta", "Following the verdict has averaged {delta} more per trade than overriding it."),
                          { delta: iso((a.discipline.delta_r > 0 ? "+" : "") + a.discipline.delta_r.toFixed(2) + "R") })}
                      </p>`}
                    ${streak >= 2 && html`
                      <p className="journal-streak">
                        <i className="fa-solid fa-fire" aria-hidden="true"></i>
                        ${formatTemplate(t("journal.streak", "{n} trades in a row with the verdict."),
                                         { n: iso(streak) })}
                      </p>`}
                  </div>`}
                ${Object.keys(a.by_session).length > 1 && html`
                  <div className="journal-slice">
                    <h4>${t("journal.by_session", "By session")}</h4>
                    ${Object.entries(a.by_session).map(([k, b]) => bucketRow(t("sessions." + k, k), b))}
                  </div>`}
                ${Object.keys(a.by_timeframe).length > 1 && html`
                  <div className="journal-slice">
                    <h4>${t("journal.by_timeframe", "By timeframe")}</h4>
                    ${Object.entries(a.by_timeframe).map(([k, b]) => bucketRow(k, b))}
                  </div>`}
              </section>`;
          })()}

        <section className="panel surface journal-section">
          <h3>${t("journal.my_trades", "My trades")}</h3>
          ${!journalFailed && open.length === 0 && closed.length === 0 && html`
            <p className="journal-empty">${t("journal.no_trades", "No trades yet. When you take a setup, it is recorded here and followed to its stop or target.")}</p>`}
          ${open.map((trade) => html`
            <div className="journal-row" key=${trade.id}>
              <div className="journal-row-head">
                <span className=${`journal-side journal-${trade.side}`}>${trade.side === "buy" ? t("signals.buy", "BUY") : t("signals.sell", "SELL")}</span>
                <span className="journal-when">${formatRelativeTime(trade.opened_at)}</span>
                <span className="journal-chip open">${t("journal.open", "open")}</span>
              </div>
              ${/* No forced dir: the line must follow the page direction (a
                   dir=ltr block left-aligns inside an RTL card and re-orders
                   the labels against their prices); each number is bidi-
                   isolated instead. */""}
              <div className="journal-levels">
                ${iso(formatPrice(trade.entry_price))} · ${t("positions.stop", "Stop")} ${iso(formatPrice(trade.stop_loss))} · ${t("positions.target", "Target")} ${iso(formatPrice(trade.take_profit))}
              </div>
            </div>`)}
          ${closed.map((trade) => html`
            <div className="journal-row" key=${trade.id}>
              <div className="journal-row-head">
                <span className=${`journal-side journal-${trade.side}`}>${trade.side === "buy" ? t("signals.buy", "BUY") : t("signals.sell", "SELL")}</span>
                <span className="journal-when">${formatRelativeTime(trade.closed_at || trade.opened_at)}</span>
                ${trade.source === "manual_override" && html`
                  <span className="journal-chip override">${t("journal.override", "against the verdict")}</span>`}
                ${!!trade.excluded && html`
                  <span className="journal-chip excluded" title=${trade.excluded_reason || ""}>${t("journal.excluded", "excluded from record")}</span>`}
                <span className=${`journal-r ${trade.r_multiple > 0 ? "pos" : (trade.r_multiple < 0 ? "neg" : "")}`}>
                  ${trade.r_multiple != null ? iso(formatR(trade.r_multiple)) : ""}
                </span>
              </div>
              <div className="journal-levels">
                ${iso(formatPrice(trade.entry_price))} <span className="dir-arrow">→</span> ${trade.exit_price != null ? iso(formatPrice(trade.exit_price)) : "—"}
                ${trade.opened_at && html`
                  <a className="journal-on-chart"
                     href=${`/chart?tf=${trade.timeframe || "1h"}&at=${Math.floor(Date.parse(trade.opened_at) / 1000)}`}>
                    <i className="fa-solid fa-chart-line" aria-hidden="true"></i>
                    ${t("journal.view_chart", "See it on the chart")}
                  </a>`}
              </div>
              <p className="journal-autopsy">${tradeAutopsy(trade, t)}</p>
            </div>`)}
        </section>

        <section className="panel surface journal-section">
          <h3>${t("journal.published", "Published calls")}</h3>
          <p className="journal-note">${t("journal.published_note", "Every alert this product sent, resolved against the price that followed — whether or not anyone took it.")}</p>
          ${(week.published > 0 || week.resolvedRows.length > 0) && html`
            <p className="journal-week" dir="auto">
              ${t("journal.this_week", "This week:")} ${iso(week.published)} ${t("performance.signals", "calls")}
              ${week.resolvedRows.length > 0 && html` · ${iso(week.resolvedRows.length)} ${t("journal.resolved", "resolved")} ·
                <b className=${week.r > 0 ? "pos" : week.r < 0 ? "neg" : ""}>
                  ${iso((week.r > 0 ? "+" : "") + week.r.toFixed(2) + "R")}
                </b>`}
            </p>`}
          ${signals.length > 1 && html`
            <div className="journal-filters" role="group">
              ${[["all", t("journal.f_all", "All")], ["won", t("journal.won", "won")],
                 ["lost", t("journal.lost", "lost")], ["open", t("journal.open", "open")],
                 ["expired", t("performance.expired_short", "never filled")]].map(([key, label]) => html`
                <button key=${key} className=${"journal-filter" + (sigFilter === key ? " is-on" : "")}
                        aria-pressed=${sigFilter === key}
                        onClick=${() => setSigFilter(key)}>${label}</button>`)}
            </div>`}
          ${!journalFailed && signals.length === 0 && html`
            <p className="journal-empty">${t("journal.no_signals", "No calls published since the log began.")}</p>`}
          ${!journalFailed && signals.length > 0 && shownSignals.length === 0 && html`
            <p className="journal-empty">${t("journal.filter_empty", "Nothing with this outcome yet.")}</p>`}
          ${shownSignals.map((sig) => {
            const chip = statusChip(sig.status);
            return html`
              <div className="journal-row" key=${sig.id}>
                <div className="journal-row-head">
                  <span className=${`journal-side journal-${sig.side}`}>${sig.side === "buy" ? t("signals.buy", "BUY") : t("signals.sell", "SELL")}</span>
                  <span className="journal-when">${formatRelativeTime(new Date(sig.published_at * 1000).toISOString())}</span>
                  <span className=${`journal-chip ${chip.cls}`}>${chip.label}</span>
                  ${sig.r_multiple != null && html`
                    <span className=${`journal-r ${sig.r_multiple > 0 ? "pos" : "neg"}`}>${iso(formatR(sig.r_multiple))}</span>`}
                </div>
                <div className="journal-levels">
                  ${iso(formatPrice(sig.entry))} · ${t("positions.stop", "Stop")} ${iso(formatPrice(sig.stop))} · ${t("positions.target", "Target")} ${iso(formatPrice(sig.target))}
                  ${(sig.signal_id || sig.id) && html`
                    <a className="journal-on-chart"
                       href=${`/chart?sigreplay=${encodeURIComponent(sig.signal_id || sig.id)}`}>
                      <i className="fa-solid fa-play" aria-hidden="true"></i>
                      ${t("journal.replay_call", "Replay it on the chart")}
                    </a>`}
                </div>
              </div>`;
          })}
          ${/* The anonymous public can audit this record on /verify with a
                hash chain and a CSV; the paying member had no path to either
                from their own journal. */""}
          <p className="journal-audit">
            <a href="/verify" target="_blank" rel="noopener">
              ${t("journal.audit_link", "Audit this on the public ledger")} <span className="dir-arrow">→</span>
            </a>
            <a href="/api/public/ledger.csv">
              ${t("journal.audit_csv", "Download the full record (CSV)")}
            </a>
          </p>
        </section>
      </main>
    </div>
  `;
}

// The trading terminal: the chart at full size with everything a decision
// needs beside it, and nothing that a decision does not need.
//
// On the dashboard the chart is one panel among many and the news feed takes
// more vertical space than the signal does. This page inverts that: the chart
// fills the viewport, and the rail carries the verdict, the size, the levels
// and the open position -- the four things you look at before pressing a
// button. It is also the answer to the phone layout, where the dashboard's
// left rail lands below 2,000px of headlines.
// /performance -- the record as a room. The landing sells it, /verify
// proves it; here a member lives with it: the curve of every published
// call, the weeks, the distribution, and their own record beside the
// engine's. All client-side arithmetic on the public ledger -- the same
// rows a skeptic can pull as CSV, which is the point.
// /academy -- structured Arabic-first education. The signal sellers this
// product exists against keep customers ignorant on purpose: dependency IS
// their product. Ours is the opposite bet. Every lesson is short, honest
// about uncertainty, and ends ON the live surface it taught.
const ACADEMY = [
  { key: "engine", icon: "fa-brain",
    title: ["كيف يفكّر المحرّك", "How the engine thinks"],
    lessons: [
      { key: "e1", title: ["لماذا لا يوجد تنبّؤ", "Why there is no forecasting"],
        body: ["المحرّك لا يتنبّأ بالسعر. يَعُدّ الأدلة مع وضد، وإذا لم يكتمل النصاب قال \"لا صفقة\" — وهذا جواب، لا إخفاق. نحو 97% من الشموع تمرّ بلا صفقة، وهذا المرشِّح هو المنتج.",
               "The engine counts evidence for and against. \"No setup\" is an answer, not a failure — roughly 97% of bars pass with none, and that filter IS the product."],
        link: ["/dashboard", "انظر الحكم الحيّ", "See the live verdict"] },
      { key: "e2", title: ["ما الذي يوقف المحرّك", "What stops the engine"],
        body: ["ثلاث بوابات تُلغي أي صفقة مهما كانت جودتها: خبر أحمر قريب (45 دقيقة قبله و15 بعده)، واتجاه ضعيف (ADX دون العتبة)، وجلسة لا تناسب أسلوبك. البوابة تتقدّم على النتيجة دائماً.",
               "Three gates kill any setup regardless of its score: a red release inside 45/15 minutes, weak trend (ADX below threshold), and a session that does not fit your style. A gate always outranks a score."],
        link: ["/events", "انظر بوابة الأخبار", "See the news gate"] },
      { key: "e3", title: ["اقرأ ورقة الأدلة", "Reading the evidence sheet"],
        body: ["انقر سطر الحكم لتنفتح \"لماذا\": كل مدخل وموقفه — مع، أو ضد، أو مانع، أو سياق. وسطر \"مانع\" يتقدّم على كل شيء.",
               "Tap the verdict line and every input shows its stance — for, against, blocks, or context. A BLOCKS row outranks everything."],
        link: ["/chart", "افتحها على الرسم البياني", "Open it on the chart"] },
    ]},
  { key: "risk", icon: "fa-scale-balanced",
    title: ["المخاطرة والحجم", "Risk and size"],
    lessons: [
      { key: "r1", title: ["ما معنى R", "What R means"],
        body: ["R وحدة مخاطرتك: المسافة من دخولك إلى وقفك. صفقة ربحت +2R تعني أنك ربحت ضعفَي ما كنت مستعدّاً لخسارته. نتحدّث بالـR لأن الدولارات تخدع — تكبر مع الحساب وتصغر معه، أما R فتبقى صادقة.",
               "R is your unit of risk: the distance from entry to stop. +2R means you made twice what you were prepared to lose. We speak in R because dollars flatter and shrink with the account; R stays honest."],
        link: ["/performance", "انظر سجلّنا بالـR", "See our record in R"] },
      { key: "r2", title: ["لماذا 1% ", "Why 1%"],
        body: ["إذا خاطرت بـ1% في كل صفقة، لزمتك نحو 70 خسارة متتالية لتصل إلى نصف حسابك؛ وإذا خاطرت بـ10% كفتك سبع. الحجم الصغير ليس جُبناً — هو ما يُبقيك موجوداً بعد سلسلة الخسائر الآتية لا محالة.",
               "At 1% risk you need ~70 straight losses to halve the account; at 10% just 7. Small size is not cowardice — it is what keeps you alive through the losing run that is certainly coming."],
        link: ["/trading", "اضبط مخاطرتك", "Set your risk"] },
      { key: "r3", title: ["الوقف ليس اقتراحاً", "The stop is not a suggestion"],
        body: ["يُوضع الوقف قبل الدخول ولا يُوسَّع أبداً — التطبيق نفسه يرفض توسيعه. وإبعاد الوقف هو السبب الأول في تفجير الحسابات.",
               "The stop is set before entry and never loosens — this app literally refuses to widen one. Moving stops away is the number-one account killer."],
        link: ["/journal", "انظر صفقاتك", "See your trades"] },
    ]},
  { key: "candles", icon: "fa-chart-column",
    title: ["تشريح الشموع", "Candle anatomy"],
    lessons: [
      { key: "c1", title: ["ماذا تقول الشمعة", "What a candle says"],
        body: ["كل شمعة أربعة أرقام: أين فتحت، وأين أغلقت، وأقصى مدى صعوداً وهبوطاً. الجسم يقول من ربح الجولة، والذيل الطويل يقول إنّ أحدهم وقع في الفخّ.",
               "Four numbers: open, close, and the extremes. The body says who won the round; a long wick says someone got trapped."],
        link: ["/chart", "مرّر فوق الشموع", "Hover the candles"] },
      { key: "c2", title: ["الدعم والمقاومة لدينا", "Our support and resistance"],
        body: ["مستوياتنا ليست مرسومة بالعين — كل خط يأتي من خوارزمية واحدة يقرأها المحرّك والذكاء الاصطناعي والرسم البياني معاً، ومكتوب عليه كم مرة لُمس. انقر أيّ مستوى في الشريط ليظهر لك حسابه.",
               "Our levels are not eyeballed — one algorithm feeds the engine, the AI and the chart, and every line carries its touch count. Tap any level chip for its arithmetic."],
        link: ["/chart", "انظر المستويات", "See the levels"] },
    ]},
  { key: "sessions", icon: "fa-clock",
    title: ["الجلسات المقاسة", "The measured sessions"],
    lessons: [
      { key: "s1", title: ["ليست كل ساعة سواء", "Not every hour is equal"],
        body: ["قِسنا جلسات الذهب على بياناتنا: تقاطع لندن-نيويورك الأوسع مدى بفارق واضح، وجاءت آسيا أوسع من لندن — على عكس الشائع. والوقف المضبوط في ساعة هادئة يصير ضيّقاً جدّاً عند حلول التقاطع.",
               "We measured gold's sessions on our own data: the London-NY overlap is widest by a clear margin, and Asia measured WIDER than London — against the folklore. A stop sized in a quiet hour is too tight when the overlap arrives."],
        link: ["/dashboard", "انظر الجلسة الحالية", "See the current session"] },
    ]},
  { key: "record", icon: "fa-file-shield",
    title: ["اقرأ سجلنا", "Reading our record"],
    lessons: [
      { key: "d1", title: ["لماذا السجلّ علنيّ", "Why the record is public"],
        body: ["كل صفقة ننشرها تُكتب في سلسلة تجزئة قبل أن نعرف نتيجتها، ثم يحكم عليها السوق: ربحت، أو خسرت، أو لم تُنفَّذ. لا نستطيع الحذف ولا التجميل — فأيّ تعديل يكسر السلسلة أمام الجميع.",
               "Every published call is hash-chained BEFORE its outcome is known, then the market grades it: won, lost, or never filled. We cannot delete or dress it up — any edit breaks the chain in public."],
        link: ["/performance", "افتح غرفة السجلّ", "Open the record room"] },
      { key: "d2", title: ["ما معنى \"عيّنة صغيرة\"", "What a small sample means"],
        body: ["نسبة ربح 100% من صفقتين لا تعني شيئاً — الصدفة تلعب دورها. نضع تحذيراً تحت أي رقم عيّنته أصغر من 30، لأنّ رقماً بلا عيّنة كافية تسويقٌ لا قياس.",
               "A 100% win rate over two trades means nothing — chance plays. We caveat every number under n=30 because a figure without a sample is marketing, not measurement."],
        link: ["/verify", "دقّق بنفسك", "Audit it yourself"] },
    ]},
];

// /events -- the room for the thing that freezes the engine. What is ahead
// (with its blackout window drawn), and what past prints MEASURABLY did to
// gold -- accumulated from the day this shipped, never backfilled. No signal
// seller dares publish honest event statistics.
function EventsPage() {
  const { t } = useI18n();
  const { status } = useAuth();
  const [state, setState] = useState(null);
  useEffect(() => { document.title = t("events.page_title", "Events — Mithqal"); }, [t]);
  useEffect(() => {
    if (status === "guest") window.location.href = "/login?next=/events";
  }, [status]);
  useEffect(() => {
    if (status !== "authenticated") return;
    let alive = true;
    authenticatedFetch("/api/events").then((r) => r.json())
      .then((p) => { if (alive && p.success) setState(p); })
      .catch(() => { if (alive) setState({ failed: true }); });
    return () => { alive = false; };
  }, [status]);
  if (status !== "authenticated") return null;

  const upcoming = state?.upcoming || [];
  const history = state?.history || [];
  const clock = (m) => m < 60
    ? formatTemplate(t("news.in_minutes", "in {m}m"), { m: iso(m) })
    : m < 48 * 60
      ? formatTemplate(t("news.in_hours", "in {h}h {m}m"), { h: iso(Math.floor(m / 60)), m: iso(m % 60) })
      : formatTemplate(t("news.in_days", "in {d} days"), { d: iso(Math.round(m / 1440)) });

  return html`
    <div className="app-shell">
      <${PageNav} />
      <main className="events-body">
        <div className="analysis-head">
          <h1><i className="fa-solid fa-bolt"></i> ${t("events.title", "The event room")}</h1>
        </div>
        <p className="perf-intro">
          ${t("events.intro", "The releases that freeze the engine (45 minutes before, 15 after) — and what past prints measurably did to gold, recorded from our own bars since the day this room opened. Never backfilled.")}
        </p>

        ${state?.failed && html`<p className="perf-empty">${t("perf.failed", "The ledger could not be loaded right now.")}</p>`}
        ${state && state.known === false && html`
          <p className="perf-empty">${t("calendar.failed", "The calendar could not be loaded. That is not the same as nothing being scheduled — treat the week as unknown.")}</p>`}

        ${upcoming.length > 0 && html`
          <section className="panel surface">
            <h3>${t("events.upcoming", "Ahead")}</h3>
            ${upcoming.map((e, i) => html`
              <div key=${i} className=${"event-row rel-" + e.relevance}>
                <div className="event-row-head">
                  <b>${e.title}</b>
                  <span className=${"cal-impact " + e.relevance}>
                    ${e.relevance === "high" ? t("calendar.rel_high", "moves gold") : t("calendar.rel_medium", "watch")}
                  </span>
                  ${e.minutes >= 0 && html`<b className="event-clock" dir="ltr">${clock(e.minutes)}</b>`}
                </div>
                ${e.relevance === "high" && html`
                  <div className="event-blackout" dir="ltr" aria-hidden="true">
                    <span className="event-blackout-band"></span>
                    <small>${t("events.blackout", "engine stands aside −45m → +15m")}</small>
                  </div>`}
              </div>`)}
          </section>`}

        <section className="panel surface">
          <h3>${t("events.history", "What past prints did — measured")}</h3>
          ${history.length === 0 && html`
            <p className="perf-caveat">
              ${t("events.no_history", "The archive fills itself from here on: every red release we watch gets its aftermath measured from our own bars about an hour after the print. Nothing is backfilled — an archive that pretends to remember is worse than one that starts today.")}
            </p>`}
          ${history.map((h, i) => html`
            <div key=${i} className="event-row is-measured">
              <div className="event-row-head">
                <b>${h.title}</b>
                <small className="event-when" dir="ltr">${new Date(h.at * 1000).toLocaleDateString(appLocale(), { day: "numeric", month: "short" })}</small>
              </div>
              <div className="event-measures" dir="ltr">
                ${h.measured.move_30m != null && html`
                  <span><small>30m</small><b className=${h.measured.move_30m >= 0 ? "pos" : "neg"}>${iso((h.measured.move_30m > 0 ? "+" : "") + h.measured.move_30m)}</b></span>`}
                ${h.measured.move_60m != null && html`
                  <span><small>60m</small><b className=${h.measured.move_60m >= 0 ? "pos" : "neg"}>${iso((h.measured.move_60m > 0 ? "+" : "") + h.measured.move_60m)}</b></span>`}
                <span><small>${t("events.spike", "max spike")}</small><b>${iso("$" + h.measured.max_spike_45m)}</b></span>
                <a className="journal-on-chart" href=${`/chart?tf=5m&at=${h.at}`}>
                  <i className="fa-solid fa-chart-line" aria-hidden="true"></i>
                  ${t("journal.view_chart", "See it on the chart")}
                </a>
              </div>
            </div>`)}
        </section>
      </main>
    </div>`;
}

function AcademyPage() {
  const { t, language } = useI18n();
  const { status } = useAuth();
  const ar = language === "ar";
  const [done, setDone] = useState(() => {
    try { return JSON.parse(localStorage.getItem("sv_academy_done") || "{}"); }
    catch (e) { return {}; }
  });
  const [openTrack, setOpenTrack] = useState(null);
  useEffect(() => { document.title = t("academy.page_title", "Academy — Mithqal"); }, [t]);
  useEffect(() => {
    if (status === "guest") window.location.href = "/login?next=/academy";
  }, [status]);
  if (status !== "authenticated") return null;

  const mark = (key) => {
    const next = { ...done, [key]: 1 };
    setDone(next);
    try { localStorage.setItem("sv_academy_done", JSON.stringify(next)); } catch (e) {}
  };
  const total = ACADEMY.reduce((n, tr) => n + tr.lessons.length, 0);
  const doneCount = Object.keys(done).length;

  return html`
    <div className="app-shell">
      <${PageNav} />
      <main className="academy-body">
        <div className="analysis-head">
          <h1><i className="fa-solid fa-graduation-cap"></i> ${t("academy.title", "The academy")}</h1>
        </div>
        <p className="perf-intro">
          ${t("academy.intro", "Short, honest lessons — each one ends on the live screen it taught. No prediction courses, no secret sauce: how THIS product actually thinks.")}
        </p>
        <p className="academy-progress">
          ${formatTemplate(t("academy.progress", "{done} of {total} lessons read"),
            { done: iso(doneCount), total: iso(total) })}
        </p>
        ${ACADEMY.map((track) => {
          const trackDone = track.lessons.filter((l) => done[l.key]).length;
          const open = openTrack === track.key;
          return html`
          <section key=${track.key} className="panel surface academy-track">
            <button className="academy-track-head" aria-expanded=${open}
                    onClick=${() => setOpenTrack(open ? null : track.key)}>
              <i className=${"fa-solid " + track.icon}></i>
              <b>${ar ? track.title[0] : track.title[1]}</b>
              <small>${iso(trackDone)}/${iso(track.lessons.length)}</small>
              <i className=${open ? "fa-solid fa-chevron-up" : "fa-solid fa-chevron-down"}></i>
            </button>
            ${open && track.lessons.map((lesson) => html`
              <article key=${lesson.key} className=${"academy-lesson" + (done[lesson.key] ? " is-done" : "")}>
                <h4>${done[lesson.key] ? html`<i className="fa-solid fa-circle-check"></i> ` : ""}${ar ? lesson.title[0] : lesson.title[1]}</h4>
                <p>${ar ? lesson.body[0] : lesson.body[1]}</p>
                <div className="academy-lesson-foot">
                  <a className="btn btn-secondary" href=${lesson.link[0]}
                     onClick=${() => mark(lesson.key)}>
                    ${ar ? lesson.link[1] : lesson.link[2]}
                  </a>
                  ${!done[lesson.key] && html`
                    <button className="academy-mark" onClick=${() => mark(lesson.key)}>
                      ${t("academy.mark", "Read it")}
                    </button>`}
                </div>
              </article>`)}
          </section>`;
        })}
      </main>
    </div>`;
}

function PerformancePage() {
  const { t } = useI18n();
  const { status } = useAuth();
  const [ledger, setLedger] = useState(null);
  const [mine, setMine] = useState(null);
  const [proof, setProof] = useState(null);
  const [health, setHealth] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => { document.title = t("perf.page_title", "The Record — Mithqal"); }, [t]);
  useEffect(() => {
    if (status === "guest") window.location.href = "/login?next=/performance";
  }, [status]);
  useEffect(() => {
    if (status !== "authenticated") return;
    let alive = true;
    fetch("/api/public/ledger").then((r) => r.json())
      .then((p) => { if (alive) { if (p.success) setLedger(p); else setFailed(true); } })
      .catch(() => { if (alive) setFailed(true); });
    authenticatedFetch("/api/positions").then((r) => r.json())
      .then((p) => { if (alive && p.success) setMine(p); })
      .catch(() => {});
    // The user's OWN sealed chain, and the engine's control chart. Both belong
    // on the record page: one is proof about them, the other is proof about us.
    authenticatedFetch("/api/journal/proof").then((r) => r.json())
      .then((p) => { if (alive && p.success) setProof(p.chain); })
      .catch(() => {});
    authenticatedFetch("/api/state/" + (sessionStorage.getItem("preferredTimeframe") || "1h"))
      .then((r) => r.json())
      .then((p) => {
        // The reading lives under state.risk, and the route is authenticated:
        // a plain fetch here worked only while a cookie happened to ride along.
        const h = p?.state?.risk?.engine_health;
        if (alive && h) setHealth(h);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [status]);

  if (status !== "authenticated") return null;

  const signals = (ledger?.signals || []).slice()
    .sort((a, b) => (a.published_at || 0) - (b.published_at || 0));
  // A won/lost row whose r_multiple is NULL is resolved but UNPRICED, and the
  // server already learned what that costs: signal_ledger.stats() carries a
  // comment about a single won-but-unpriced row printing win_rate 150%, and
  // fixes it by ranging wins and the denominator over the same scored rows.
  // This page recomputed every one of those numbers from the raw ledger and
  // kept none of that guard -- so `|| 0` priced an unmeasured outcome at
  // exactly 0R: it fell out of `wins` but stayed in the denominator, added
  // nothing to the total, drew a flat step on the equity curve, and landed in
  // the "0..1R" histogram bin, which renders "we never measured this" as a
  // small win. Same record, two surfaces, two different arithmetics.
  const allResolved = signals.filter((s) => s.status === "won" || s.status === "lost");
  const resolved = allResolved.filter((s) => Number.isFinite(s.r_multiple));
  const unpriced = allResolved.length - resolved.length;
  const wins = resolved.filter((s) => s.r_multiple > 0).length;
  const sumR = resolved.reduce((acc, s) => acc + s.r_multiple, 0);

  // Cumulative R polyline -- the one honest equity curve this product can
  // draw: R units of the PUBLISHED record, not dollars of a hypothetical.
  const curve = (() => {
    if (resolved.length < 2) return null;
    let acc = 0;
    const points = resolved.map((s) => (acc += s.r_multiple));
    const min = Math.min(0, ...points);
    const max = Math.max(0, ...points);
    const span = max - min || 1;
    const W = 600, H = 140;
    const step = W / (points.length - 1);
    const y = (v) => H - ((v - min) / span) * H;
    const path = points.map((v, i) => `${(i * step).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
    return { path, zero: y(0), last: acc, W, H };
  })();

  // Weekly buckets, newest first.
  const weeks = (() => {
    const map = new Map();
    resolved.forEach((s) => {
      const d = new Date((s.resolved_at || s.published_at) * 1000);
      const monday = new Date(d);
      monday.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
      const key = monday.toISOString().slice(0, 10);
      const b = map.get(key) || { n: 0, w: 0, r: 0 };
      b.n += 1; if (s.r_multiple > 0) b.w += 1; b.r += s.r_multiple;
      map.set(key, b);
    });
    return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0])).slice(0, 8);
  })();

  // R histogram in fixed honest bins.
  const bins = [
    { key: "≤-1R", test: (r) => r <= -1 },
    { key: "-1..0", test: (r) => r > -1 && r < 0 },
    { key: "0..1", test: (r) => r >= 0 && r < 1 },
    { key: "1..2", test: (r) => r >= 1 && r < 2 },
    { key: "≥2R", test: (r) => r >= 2 },
  ].map((b) => ({ ...b, n: resolved.filter((s) => b.test(s.r_multiple)).length }));
  const maxBin = Math.max(1, ...bins.map((b) => b.n));

  const byTf = (() => {
    const map = new Map();
    resolved.forEach((s) => {
      const b = map.get(s.timeframe) || { n: 0, w: 0, r: 0 };
      b.n += 1; if (s.r_multiple > 0) b.w += 1; b.r += s.r_multiple;
      map.set(s.timeframe, b);
    });
    return [...map.entries()];
  })();

  const tooFew = resolved.length < 30;

  return html`
    <div className="app-shell">
      <${PageNav} />
      <main className="perf-body">
        <div className="analysis-head">
          <h1><i className="fa-solid fa-scale-balanced"></i> ${t("perf.title", "The record")}</h1>
        </div>
        <p className="perf-intro">
          ${t("perf.intro", "Every call this product published, graphed as published — losses included. The same rows the public can audit on the ledger.")}
          ${" "}<a href="/verify" target="_blank" rel="noopener">${t("journal.audit_link", "Audit this on the public ledger")}</a>
        </p>

        ${health && html`
          <section className=${"panel surface perf-health is-" + health.status}>
            <h3>${t("perf.engine_health", "Engine health")}</h3>
            <p>
              ${health.status === "degraded"
                ? formatTemplate(t("verdict.edge_degraded", "Engine health: the last {n} resolved calls average {mean}R, below the control limit of {limit}R. Publishing is halted."),
                    { n: iso(health.n), mean: iso(formatR(health.mean_r)), limit: iso(formatR(health.lower_limit)) })
                : health.status === "stable"
                  ? formatTemplate(t("verdict.edge_stable", "Engine health: the last {n} resolved calls average {mean}R, inside the control band."),
                      { n: iso(health.n), mean: iso(formatR(health.mean_r)) })
                  : formatTemplate(t("verdict.edge_watching", "Engine health: {n} resolved calls so far — not judged until {min}."),
                      { n: iso(health.resolved_total ?? health.n), min: iso(health.min_sample) })}
            </p>
            <small>${t("perf.engine_health_note", "A control chart over the live record. If the rolling average falls below its own limit, the engine stops publishing rather than waiting to be told.")}</small>
          </section>`}

        ${proof && html`
          <section className="panel surface perf-proof">
            <h3>${t("perf.proof", "Your own sealed record")}</h3>
            <p>
              ${proof.entries > 0
                ? formatTemplate(t("perf.proof_state", "{n} of your own journal events are sealed in a chain only you can export. Chain: {state}."),
                    { n: iso(proof.entries),
                      state: proof.intact ? t("perf.proof_intact", "unbroken") : t("perf.proof_broken", "BROKEN") })
                : t("perf.proof_empty", "Your chain starts with your first trade. From then on, nothing in your record can be quietly edited — including by us.")}
            </p>
            ${proof.entries > 0 && html`
              <a className="btn btn-secondary" href="/api/journal/proof?download=1">
                <i className="fa-solid fa-file-shield"></i> ${t("perf.proof_export", "Export my proof")}
              </a>`}
            <small>${t("perf.proof_note", "The file explains how to recompute it, and scripts/verify_chain.py does it in a page of Python. It proves nothing was edited or reordered — not third-party notarisation.")}</small>
          </section>`}

        ${failed && html`<p className="perf-empty">${t("perf.failed", "The ledger could not be loaded right now.")}</p>`}
        ${ledger && signals.length === 0 && html`
          <p className="perf-empty">${t("journal.no_signals", "No calls published since the log began.")}</p>`}

        ${resolved.length > 0 && html`
          <section className="panel surface perf-cards">
            <div><small>${t("perf.published", "Published")}</small><b>${iso(signals.length)}</b></div>
            <div><small>${t("perf.resolved", "Resolved")}</small><b>${iso(resolved.length)}</b></div>
            <div><small>${t("perf.win_rate", "Win rate")}</small><b>${iso(Math.round((wins / resolved.length) * 100))}%</b></div>
            <div><small>${t("perf.total_r", "Total")}</small>
              <b className=${sumR > 0 ? "pos" : sumR < 0 ? "neg" : ""}>${iso(formatR(sumR))}</b></div>
          </section>`}
        ${tooFew && resolved.length > 0 && html`
          <p className="perf-caveat">
            ${formatTemplate(t("perf.too_few", "{n} resolved of the 30 needed before any of these numbers mean anything. Shown for transparency, not as evidence."), { n: iso(resolved.length) })}
          </p>`}
        ${unpriced > 0 && html`
          <p className="perf-caveat">
            ${formatTemplate(t("perf.unpriced", "{n} resolved calls carry no R and are left out of every number above. Counting them as 0R would price an outcome nobody measured."), { n: iso(unpriced) })}
          </p>`}

        ${curve && html`
          <section className="panel surface">
            <h3>${t("perf.curve", "Cumulative R, call by call")}</h3>
            <svg className="perf-curve" viewBox=${`0 0 ${curve.W} ${curve.H}`} preserveAspectRatio="none" aria-hidden="true">
              <line x1="0" y1=${curve.zero} x2=${curve.W} y2=${curve.zero} className="perf-zero" />
              <polyline points=${curve.path} className=${"perf-line" + (curve.last >= 0 ? " pos" : " neg")} />
            </svg>
          </section>`}

        ${resolved.length > 0 && html`
          <section className="panel surface">
            <h3>${t("perf.dist", "Where the results land")}</h3>
            <div className="perf-bins" dir="ltr">
              ${bins.map((b) => html`
                <div key=${b.key} className="perf-bin">
                  <div className="perf-bin-bar" style=${{ blockSize: `${Math.max(4, (b.n / maxBin) * 72)}px` }}></div>
                  <b>${iso(b.n)}</b><small>${b.key}</small>
                </div>`)}
            </div>
          </section>`}

        ${weeks.length > 0 && html`
          <section className="panel surface">
            <h3>${t("perf.weeks", "By week")}</h3>
            <table className="perf-table" dir="ltr">
              <thead><tr><th>${t("perf.week", "Week of")}</th><th>${t("perf.calls", "Calls")}</th><th>${t("perf.win_rate", "Win rate")}</th><th>R</th></tr></thead>
              <tbody>
                ${weeks.map(([wk, b]) => html`
                  <tr key=${wk}>
                    <td>${wk}</td><td>${iso(b.n)}</td>
                    <td>${iso(Math.round((b.w / b.n) * 100))}%</td>
                    <td className=${b.r > 0 ? "pos" : b.r < 0 ? "neg" : ""}>${iso(formatR(b.r))}</td>
                  </tr>`)}
              </tbody>
            </table>
          </section>`}

        ${byTf.length > 1 && html`
          <section className="panel surface">
            <h3>${t("perf.by_tf", "By timeframe")}</h3>
            <table className="perf-table" dir="ltr">
              <tbody>
                ${byTf.map(([tf, b]) => html`
                  <tr key=${tf}>
                    <td>${tf}</td><td>${iso(b.n)}</td>
                    <td>${iso(Math.round((b.w / b.n) * 100))}%</td>
                    <td className=${b.r > 0 ? "pos" : b.r < 0 ? "neg" : ""}>${iso(formatR(b.r))}</td>
                  </tr>`)}
              </tbody>
            </table>
          </section>`}

        ${mine?.performance?.trades > 0 && html`
          <section className="panel surface">
            <h3>${t("perf.yours", "Your own record, beside it")}</h3>
            <div className="perf-cards">
              <div><small>${t("positions.closed", "Closed")}</small><b>${iso(mine.performance.trades)}</b></div>
              <div><small>${t("perf.win_rate", "Win rate")}</small><b>${iso(mine.performance.win_rate)}%</b></div>
              <div><small>${t("positions.expectancy", "Expectancy")}</small>
                <b className=${mine.performance.expectancy_r >= 0 ? "pos" : "neg"}>${iso(formatR(mine.performance.expectancy_r))}</b></div>
            </div>
            <p className="perf-caveat">${t("perf.yours_note", "From your journal — trades you actually recorded, followed to their stop or target.")}</p>
          </section>`}
      </main>
    </div>`;
}

function ChartPage() {
  const { t, language } = useI18n();
  const { status, can } = useAuth();
  // The tab is filtered out of TabBar, but a bookmark, a tapped alert
  // deep-link or a refresh lands here directly -- and five of this page's
  // endpoints are @requires_feature('chart'), so without this the customer
  // got the full chart furniture and then a cascade of silent 403s.
  const chartAllowed = can("chart");
  const [currentTimeframe, setCurrentTimeframe] = useState(() => {
    // A tapped alert deep-links as /chart?tf=1h; honour it, else the last
    // chosen timeframe.
    const fromUrl = new URLSearchParams(window.location.search).get("tf");
    return normalizeTimeframe(fromUrl || sessionStorage.getItem("preferredTimeframe"));
  });
  // The chosen series style survives the session -- a bars trader should
  // not renegotiate their chart every morning.
  const [chartType, setChartTypeState] = useState(() => {
    try { return localStorage.getItem("sv_chart_style") || "candlestick"; }
    catch (e) { return "candlestick"; }
  });
  const setChartType = useCallback((value) => {
    setChartTypeState(value);
    try { localStorage.setItem("sv_chart_style", value); } catch (e) { /* private */ }
  }, []);
  const [positions, setPositions] = useState(null);
  const [tradeAction, setTradeAction] = useState({ taking: false, closing: false, moving: false, error: null });
  // Replay-a-call: /chart?sigreplay=<id> arrives from the journal's
  // published record. The chart jumps to that call's frame, draws its
  // levels, and plays the bars that decided it -- the public ledger as
  // theater, which no signal seller can afford to build.
  const [replaySignal, setReplaySignal] = useState(null);
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("sigreplay");
    if (!sid) return;
    let alive = true;
    fetch(`/api/replay/${encodeURIComponent(sid)}`)
      .then((r) => r.json())
      .then((p) => {
        if (!alive || !p.success || !p.signal) return;
        setReplaySignal(p.signal);
        if (p.signal.timeframe) setCurrentTimeframe(normalizeTimeframe(p.signal.timeframe));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  // On a phone, arriving from an alert opens the plan rail so the setup is the
  // first thing seen, not the chart the reader has to scroll past.
  const alertSetup = useMemo(() => {
    // A tapped alert arrives as /chart?tf=1h&alert=buy&e=..&sl=..&tp=..
    // Surfaced as a banner so the reader lands ON the announced trade, not on
    // a generic chart they must reconcile with a notification they half-read.
    const q = new URLSearchParams(window.location.search);
    if (!q.get("alert")) return null;
    const num = (k) => { const v = parseFloat(q.get(k)); return isFinite(v) ? v : null; };
    return { side: q.get("alert"), entry: num("e"), stop: num("sl"), target: num("tp") };
  }, []);
  const [alertDismissed, setAlertDismissed] = useState(false);
  const [railOpen, setRailOpen] = useState(() =>
    // Desktop opens WITH the plan (there is room); a phone opens on the
    // chart, unless an alert deep-linked straight to a setup.
    window.innerWidth > 820
    || new URLSearchParams(window.location.search).has("tf"));
  // Esc closes the plan like it closes every other overlay in the app. The
  // scrim already answers a stray tap; the keyboard deserved the same exit.
  useEffect(() => {
    if (!railOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setRailOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [railOpen]);
  const socketRef = useRef(null);
  const [socketReady, setSocketReady] = useState(false);
  // Always the CURRENT timeframe, for handlers registered once at mount.
  const timeframeRef = useRef(currentTimeframe);
  timeframeRef.current = currentTimeframe;
  const { data, loadInitial, applyUpdate, loadState, styleRef } = useDashboardData(currentTimeframe);
  const verdictAnswer = useVerdictAnswer(data?.verdict, currentTimeframe, data?.current_price);
  const market = useMarketStatus();
  const isMarketOpen = market.open === true;
  // The surface a push alert deep-links into. It must carry the same
  // disclosures as the dashboard: our measured record, and the NAMED pending
  // release when the news gate is up -- an alerted reader deciding whether to
  // take the trade is exactly the person who needs both.
  const [performance, setPerformance] = useState(null);
  // Balance/risk% for the size row -- same source the settings page writes.
  const [riskPrefs, setRiskPrefs] = useState(null);
  const { context: newsContext } = useNewsFeed("XAUUSD.m", language, 5);

  useEffect(() => {
    if (status !== "authenticated") return;
    let alive = true;
    authenticatedFetch("/api/performance")
      .then((r) => r.json())
      .then((p) => { if (alive && p.success) setPerformance(p); })
      .catch(() => { /* the strip simply does not render */ });
    authenticatedFetch("/api/preferences")
      .then((r) => r.json())
      .then((p) => { if (alive && p.success) setRiskPrefs(p.preferences); })
      .catch(() => { /* the size row simply does not render */ });
    return () => { alive = false; };
  }, [status]);

  useEffect(() => {
    document.title = t("chart.page_title", "Chart — Mithqal");
  }, [t]);

  useEffect(() => {
    if (status === "guest") window.location.href = "/login";
  }, [status]);

  useEffect(() => {
    if (status !== "authenticated") return;
    loadInitial();
  }, [status, currentTimeframe, loadInitial]);

  const loadPositions = useCallback(async () => {
    if (status !== "authenticated") return;
    try {
      const response = await authenticatedFetch("/api/positions");
      const payload = await response.json();
      if (payload.success) setPositions((prev) => (
        // Same book, same object: an identical poll result keeps the old
        // identity so the marker/price-line effect does not rebuild for it.
        prev && prev.success
          && JSON.stringify(prev.open_positions) === JSON.stringify(payload.open_positions)
          && JSON.stringify(prev.positions) === JSON.stringify(payload.positions)
          ? prev : payload));
    } catch (error) { /* the chart must render without it */ }
  }, [status]);

  useEffect(() => {
    loadPositions();
    const timer = setInterval(loadPositions, 30000);
    return () => clearInterval(timer);
  }, [loadPositions]);

  useEffect(() => {
    if (status !== "authenticated") return;
    // Pinned to long-polling ON PURPOSE: under the threading dev server the
    // polling->websocket upgrade 500s, and the direct-websocket experiment
    // proved flaky enough to abandon. Polling is the transport that always
    // works here; when gunicorn+eventlet lands (infra backlog) this becomes
    // ["websocket", "polling"].
    const socket = io({ transports: ["polling"],
                        auth: { token: getAuthToken() || "" } });
    socketRef.current = socket;
    // Announce the URL-seeded timeframe: without this, a reader arriving via
    // an alert deep link (/chart?tf=4h) received broadcasts computed on
    // whatever timeframe the server-global happened to hold.
    socket.on("connect", () => {
      setSocketReady(true);
      socket.emit("change_timeframe", { timeframe: timeframeRef.current });
    });
    socket.on("disconnect", () => setSocketReady(false));
    socket.on("price_update", (update) => {
      // Cross-timeframe broadcasts contribute the spot price and clock only
      // -- never another chart's indicators, signals or verdict.
      if (update.timeframe && update.timeframe !== timeframeRef.current) {
        applyUpdate({
          current_price: update.current_price,
          price_change: update.price_change,
          price_change_pct: update.price_change_pct,
          last_update: update.last_update || null,
        });
        return;
      }
      // OUR style's signals/verdict out of the fan-out payload -- the
      // top-level fields are the default style's and would wipe this
      // user's own card (same fix as the dashboard handler).
      const own = update.by_style && styleRef.current
        ? update.by_style[styleRef.current] : null;
      applyUpdate(own ? { ...update, signals: own.signals || {},
                          verdict: own.verdict || undefined } : update);
    });
    return () => socket.close();
  }, [status, applyUpdate]);

  const handleTimeframeChange = useCallback((tf) => {
    setCurrentTimeframe(tf);
    sessionStorage.setItem("preferredTimeframe", tf);
    // No emit here: the subscription effect below owns it, so a switch made
    // during a reconnect gap is re-asserted instead of dropped.
  }, []);

  // The ONE place this page announces its room (mirrors the dashboard).
  useEffect(() => {
    if (!socketReady || !currentTimeframe) return;
    socketRef.current?.emit("change_timeframe", { timeframe: currentTimeframe });
  }, [currentTimeframe, socketReady]);

  const openPosition = positions?.open_positions?.[0] || null;

  const post = useCallback(async (url, body, flag) => {
    setTradeAction((prev) => ({ ...prev, [flag]: true, error: null }));
    try {
      const response = await authenticatedFetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body || {})
      });
      const payload = await response.json();
      setTradeAction({ taking: false, closing: false, moving: false,
                       error: payload.success ? null : takeErrorMessage(t, payload.error) });
      loadPositions();
    } catch (error) {
      setTradeAction({ taking: false, closing: false, moving: false, error: error.message });
    }
  }, [loadPositions, t]);

  // `?? 0` here has the same shape as the coercion above it: it only ever
  // reads "up". The render below already guards on price_change != null, so
  // this just has to stop inventing a direction for the guard to work.
  const priceUp = data.price_change == null ? null : data.price_change >= 0;

  // Memoized: built inline, this array had a fresh identity every render,
  // and every render therefore tore down and recreated every price line and
  // marker on the chart -- constant churn on an unchanged chart.
  const chartTrades = useMemo(() => {
    const open = positions?.open_positions || [];
    // History squatted on the canvas: every closed trade's arrows and R
    // bubbles, including the mis-tap era, drew by default (the audit found
    // "-4.42R" plus three "0.00R" circles owning the left third). Open
    // capital always draws; of the closed, only the LAST one -- the journal
    // holds the rest, one tap away.
    const closed = (positions?.closed_positions || [])
      .filter((tr) => !tr.excluded)
      .sort((a, b) => String(b.closed_at || "").localeCompare(String(a.closed_at || "")))
      .slice(0, 1);
    return [...open, ...closed]
      .filter((tr) => (tr.timeframe || currentTimeframe) === currentTimeframe);
  }, [positions, currentTimeframe]);

  if (!chartAllowed) {
    return html`
      <div className="app-shell">
        <${PageNav} />
        <main className="journal-body"><${ServiceOff} feature="chart" /></main>
      </div>`;
  }

  return html`
    <div className=${"terminal" + (railOpen ? " rail-open" : "")}>
      <header className="terminal-bar">
        <a className="terminal-back" href="/dashboard" aria-label=${t("chart.back", "Dashboard")}>
          <i className="fa-solid fa-arrow-left"></i>
        </a>
        <div className="terminal-symbol">
          <b>XAUUSD</b>
          <span className=${"terminal-dot " + (isMarketOpen ? "is-open" : "is-closed")}></span>
        </div>
        <div className="terminal-price">
          ${/* The price is a reading, in ink — only the change wears market
               colour, same contract as the dashboard hero. */""}
          <b dir="ltr">
            ${data.current_price != null ? formatPrice(data.current_price) : "—"}
          </b>
          ${data.current_price != null && data.price_change != null && html`
            <span className=${priceUp ? "pos" : "neg"} dir="ltr">
              ${priceUp ? "▲" : "▼"}${" "}
              ${/* Two representations of one fact do not both fit in a 390px
                   header, and the one that got clipped was the percentage --
                   the one a trader reads first. The absolute change keeps its
                   own element so a wide screen can still show both. */""}
              <span className="terminal-change-abs">
                ${Math.abs(data.price_change).toFixed(2)}${" "}
              </span>
              ${data.price_change_pct != null ? `${data.price_change_pct.toFixed(2)}%` : "—"}
            </span>`}
        </div>
        <div className="terminal-stats">
          <span><small>${t("statistics.high_24h", "24H HIGH")}</small><b dir="ltr">${data.stats?.high_24h ? formatPrice(data.stats.high_24h) : "—"}</b></span>
          <span><small>${t("statistics.low_24h", "24H LOW")}</small><b dir="ltr">${data.stats?.low_24h ? formatPrice(data.stats.low_24h) : "—"}</b></span>
        </div>
        <${AlertsInbox} />
        <${NavMore} />
        <button className="terminal-rail-toggle" onClick=${() => setRailOpen((v) => !v)}
                aria-expanded=${railOpen}
                aria-label=${t("chart.the_plan", "The plan")}>
          <i className="fa-solid fa-list-check"></i>
          <span>${t("chart.the_plan", "The plan")}</span>
        </button>
      </header>

      <main className="terminal-body">
        <section className="terminal-chart">
          <${ChartPanel}
            timeframe=${currentTimeframe}
            chartType=${chartType}
            onChangeTimeframe=${handleTimeframeChange}
            onChangeChartType=${setChartType}
            livePrice=${data.current_price}
            verdict=${data.verdict}
            onWhy=${() => setRailOpen(true)}
            trades=${chartTrades}
            replaySignal=${replaySignal}
            onDragStop=${(id, price) => post(`/api/positions/${id}/stop`, { mode: "price", price }, "moving")}
            marketOpen=${isMarketOpen}
          />
        </section>

        ${railOpen && html`
          <div className="rail-scrim" onClick=${() => setRailOpen(false)}></div>`}
        <aside className="terminal-rail">
          <div className="rail-head">
            <span>${t("chart.the_plan", "The plan")}</span>
            <button className="rail-close" onClick=${() => setRailOpen(false)}
                    aria-label=${t("buttons.close", "Close")}>
              <i className="fa-solid fa-xmark"></i>
            </button>
          </div>
          ${alertSetup && !alertDismissed && html`
            <div className=${`alert-setup alert-setup-${alertSetup.side}`}>
              <div className="alert-setup-head">
                <span className="alert-setup-tag">
                  <i className="fa-solid fa-bell"></i>
                  ${t("alert_setup.from_alert", "From your alert")}
                </span>
                <button className="alert-setup-x" onClick=${() => setAlertDismissed(true)}
                        aria-label=${t("buttons.close", "Close")}>
                  <i className="fa-solid fa-xmark"></i>
                </button>
              </div>
              <div className="alert-setup-dir">
                ${alertSetup.side === "buy" ? t("signals.buy", "BUY") : t("signals.sell", "SELL")} XAUUSD
              </div>
              <div className="alert-setup-levels">
                <span><small>${t("advisor.entry", "Entry")}</small><b>${formatPrice(alertSetup.entry)}</b></span>
                ${alertSetup.stop != null && html`<span><small>${t("positions.stop", "Stop")}</small><b>${formatPrice(alertSetup.stop)}</b></span>`}
                ${alertSetup.target != null && html`<span><small>${t("positions.target", "Target")}</small><b>${formatPrice(alertSetup.target)}</b></span>`}
              </div>
              <p className="alert-setup-note">
                ${t("alert_setup.note", "The plan below is live. If it still stands, take it; if the verdict now says wait, the moment has passed.")}
              </p>
            </div>`}
          <${LivePositionCard}
            position=${openPosition}
            livePrice=${data.current_price}
            onClose=${(id) => post(`/api/positions/${id}/close`, {}, "closing")}
            closing=${tradeAction.closing}
            onMoveStop=${(id, mode) => post(`/api/positions/${id}/stop`, { mode }, "moving")}
            moving=${tradeAction.moving}
            actionError=${tradeAction.error}
            t=${t}
          />

          <${VerdictPanel}
            verdict=${data.verdict}
            signals=${data.signals}
            summary=${data.summary}
            extended=${data.extended_indicators}
            volatility=${null}
            t=${t}
            newsContext=${newsContext}
            onTake=${(override) => post("/api/positions/open", { override: !!override, timeframe: currentTimeframe }, "taking")}
            taking=${tradeAction.taking}
            takeError=${tradeAction.error}
            inTrade=${!!openPosition}
            marketClosed=${market.open !== true}
            insufficient=${data.insufficient_data}
            timeframe=${currentTimeframe}
            riskPrefs=${riskPrefs}
            sizing=${data?.sizing}
            onAnswer=${verdictAnswer.answer}
            answered=${verdictAnswer.answered}
          />

          <${MeasuredStrip} performance=${performance} t=${t} />

          ${loadState.stale && html`
            <p className="terminal-stale">
              <i className="fa-solid fa-triangle-exclamation"></i>
              ${t("chart.stale", "These numbers are the last good ones, not live.")}
            </p>`}
        </aside>
      </main>
      <${ChatLauncher} timeframe=${currentTimeframe} />
    </div>
  `;
}

// A render throw anywhere below used to blank the entire product (a white
// #root and nothing else). This catches it, shows a recoverable fallback, and
// reports it best-effort. Error boundaries must be class components in React 18.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    try {
      fetch("/api/client-error", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        body: JSON.stringify({
          message: String((error && error.message) || error),
          stack: String((error && error.stack) || ""),
          component: String((info && info.componentStack) || ""),
          path: window.location.pathname,
          ua: navigator.userAgent,
        }),
      }).catch(() => {});
    } catch (e) { /* reporting must never break the fallback */ }
  }
  render() {
    if (this.state.error) {
      return html`
        <div role="alert" style=${{
          maxWidth: "32rem", margin: "12vh auto", padding: "2rem",
          textAlign: "center", fontFamily: "system-ui, sans-serif",
        }}>
          <h1 style=${{ fontSize: "1.25rem", marginBottom: ".5rem" }}>Something went wrong</h1>
          <p style=${{ opacity: 0.7, marginBottom: "1.5rem" }}>
            The page hit an unexpected error. Reloading usually fixes it.
          </p>
          <button className="btn btn-primary"
                  onClick=${() => window.location.reload()}>Reload</button>
        </div>`;
    }
    return this.props.children;
  }
}

// The whole screen until a temporary password is replaced.
function ForcedPasswordChange() {
  const { t } = useI18n();
  return html`
    <div className="app-shell">
      <main className="analysis-body">
        <div className="analysis-head">
          <h1><i className="fa-solid fa-key"></i> ${t("password.choose_title", "Choose your password")}</h1>
        </div>
        <${ChangePassword} forced=${true}
          onDone=${() => { window.location.href = "/dashboard"; }} />
      </main>
    </div>
  `;
}

// Client-side navigation. Every tab tap was a full page reload: a new bundle
// parse, a fresh auth check, every panel re-fetched, and a visible white flash
// -- on a phone, roughly a second of nothing between two screens the user
// thinks of as one app. Every review corpus in the research praises speed and
// punishes confusion; none of them praise features.
//
// Deliberately tiny: pathname in, component out, plus popstate. A router
// library would be a dependency and a bundle for a decision this app makes in
// eight lines.
function useRoutePath() {
  const [path, setPath] = useState(window.location.pathname);
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    // navigate() dispatches this so a programmatic push updates the view too.
    window.addEventListener("popstate", onPop);
    window.addEventListener("sv-navigate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      window.removeEventListener("sv-navigate", onPop);
    };
  }, []);
  return path;
}

// Same-origin, plain left click, no modifier: everything else is a real
// navigation the browser should handle (new tab, download, external link).
function navigate(event, href) {
  if (event && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
                || event.button !== 0)) {
    return;
  }
  if (event) event.preventDefault();
  if (window.location.pathname === href) {
    window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }
  window.history.pushState({}, "", href);
  window.dispatchEvent(new Event("sv-navigate"));
  window.scrollTo({ top: 0 });
}

// How the product thinks, once, properly. A tool that refuses to trade 97%
// of the time NEEDS this page: without it every honest silence reads as a
// malfunction. Content lives here (not in the locale files) because it is a
// document with a voice, not UI chrome.
const HELP_CONTENT = {
  en: [
    ["What this is", "A gold desk that tells the truth. A deterministic engine reads XAUUSD, applies session, trend and news-event gates, and publishes a verdict. Most hours the honest verdict is: no setup. That silence is the filter doing its job — the engine reaches a setup on roughly 3% of bars."],
    ["How a signal is born", "Data → indicators → the decision engine scores both sides → session/ADX/event gates → your style's levels → the AI reviews and can only confirm or stand aside (it can never invent a trade) → news and yields can only downgrade → risk halts. Only what survives all of that is published — and recorded."],
    ["The three styles", "Scalping lives on 5-minute bars, Intraday on 1-hour, Swing on 4-hour. Your chosen style decides the levels you see, the alerts you get, and the record you are measured against. Change it in Settings."],
    ["What a notification means", "A ticket (side, entry, stop, target) means the full pipeline agreed on YOUR style's timeframe. When it resolves, a second notification replaces it: target hit, stop hit, or expired unresolved — with the R multiple. A signal that ends is never left silent."],
    ["The public record", "Every published signal is recorded before it resolves, in a hash-chained ledger that breaks visibly if anyone edits history — verify it yourself at /verify, no login needed. The measured expectancy currently spans zero: this product does not claim a proven edge, and will not until the record shows one."],
    ["Your journal", "Every trade you take is followed to its stop or target and written down, including the ones where you overrode the verdict. The journal measures whether following the engine beats overriding it — with your own money's numbers."],
    ["Why alerts may not arrive", "Web push needs the app installed (Add to Home Screen on iPhone) and HTTPS when away from this network. Test the channel any time with 'Send test' in Settings. Quiet hours and your daily cap are yours to set."],
    ["Ask about this", "The chat answers only from the engine's current numbers, with the address of every claim it makes. It does not forecast, and it will refuse to tell you to buy — that refusal is a feature."],
  ],
  ar: [
    ["ما هذا التطبيق", "مكتب ذهب يقول الحقيقة. محرّك حتميّ يقرأ XAUUSD، ويطبّق بوابات الجلسة والاتجاه والأحداث، ثم ينشر حكماً. وفي معظم الساعات يكون الحكم الصادق: لا صفقة. هذا الصمت هو المرشِّح وهو يعمل — إذ لا يبلغ المحرّك إعداداً إلا في نحو ٣٪ من الشموع."],
    ["كيف تُولَّد الإشارة", "بيانات ← مؤشّرات ← محرّك القرار يسجّل للطرفين ← بوابات الجلسة والـADX والأحداث ← مستويات أسلوبك ← الذكاء الاصطناعي يراجع فلا يملك إلا التأكيد أو الإيقاف (ولا يخترع صفقة أبداً) ← الأخبار والعوائد تخفض الحكم ولا تنشئه ← حدود المخاطر. وما ينجو من هذا كلّه هو وحده ما يُنشر، ويُسجَّل."],
    ["الأساليب الثلاثة", "السكالبينغ على شموع خمس دقائق، واليوميّ على ساعة، والسوينغ على أربع ساعات. أسلوبك يحدّد المستويات التي تراها، والإشعارات التي تصلك، والسجلّ الذي تُقاس عليه. غيّره من الإعدادات."],
    ["ماذا يعني الإشعار", "التذكرة (اتجاه، ودخول، ووقف، وهدف) تعني أن السلسلة كاملةً اتّفقت على الإطار الزمني لأسلوبك أنت. وحين تُحسم، يحلّ محلّها إشعار ثانٍ: تحقّق الهدف، أو ضُرب الوقف، أو انتهت بلا حسم — مع مضاعف الـR. ولا تنتهي إشارة في صمت."],
    ["السجلّ العلني", "كل إشارة منشورة تُسجَّل قبل أن تُحسم، في سجلّ متسلسل بالتجزئة ينكسر بصورة مرئية إن عدّل أحد التاريخ — تحقّق بنفسك على ‎/verify بلا تسجيل دخول. والتوقّع المقاس حالياً يمرّ بالصفر: هذا المنتج لا يدّعي أفضلية مثبتة، ولن يدّعيها قبل أن يُظهرها السجلّ."],
    ["دفترك", "كل صفقة تأخذها تُتابَع حتى وقفها أو هدفها وتُكتب، بما فيها التي تجاوزت فيها الحكم. والدفتر يقيس هل اتّباع المحرّك أفضل من تجاوزه — بأرقام مالك الحقيقي."],
    ["لماذا قد لا تصل الإشعارات", "يحتاج الإشعار الفوري إلى تثبيت التطبيق (أضِفه إلى الشاشة الرئيسية على iPhone) وإلى HTTPS خارج هذه الشبكة. جرّب القناة في أيّ وقت بزرّ «أرسل تجربة» في الإعدادات. وساعات الهدوء والسقف اليومي بيدك."],
    ["اسأل عن هذا الوضع", "تجيب المحادثة من أرقام المحرّك الحالية وحدها، مع عنوان كل معلومة تذكرها. لا تتنبّأ، وترفض أن تقول لك اشترِ — وهذا الرفض ميزة لا عيب."],
  ],
};

// The small education hub: what a serious gold trader should know, said in
// this product's measured voice -- every claim here is either arithmetic or
// something this repository actually measured. No folklore.
const HELP_GUIDES = {
  en: [
    ["What actually moves gold", "Real yields, first: gold pays nothing, so when 10-year real yields rise, holding it costs more — the one macro filter that measured positive out-of-sample in BOTH halves of our nine-year sample. The dollar, equities and VIX all failed that test, which is worth knowing because the popular story says otherwise. Scheduled releases (CPI, NFP, FOMC) can reprice gold in seconds — which is why the engine refuses to trade around them."],
    ["Risk and the R multiple", "One R is the distance from your entry to your stop — the amount you agreed to lose before the trade started. A +2R win pays two of those; a −1R loss costs one. Sizing is arithmetic, not courage: balance × risk% ÷ stop distance. A strategy's worth lives in its average R per trade, not its win rate — 40% winners at 2:1 beats 60% winners at 1:2."],
    ["The sessions, as measured", "On this market's own data: the London/New York overlap is the widest stretch of the day by a clear margin; Asia measured WIDER than London and New York — not the narrow chop the folklore claims; and New York measured narrowest despite carrying most releases. A stop sized in a quiet hour is tighter than it looks when the overlap arrives."],
    ["Standing aside is a position", "The engine reaches a setup on roughly 3% of bars. The other 97% is not absence — it is the filter refusing conditions with no measured edge. Every no-trade day the engine explains itself, and the journal can show you what overriding those refusals costs. The hardest discipline in trading is the one this product does for you."],
    ["How to read the verdict", "The headline is the decision. 'What this rests on' lists each input with its stance — FOR, AGAINST, BLOCKS, or context. A BLOCKS row (a pending release, a risk halt) outranks everything: no setup survives it. The score out of 7 counts the rules that aligned; it is not a probability, and nothing in this product claims to know one."],
  ],
  ar: [
    ["ما الذي يحرّك الذهب فعلاً", "العوائد الحقيقية أولاً: الذهب لا يدفع فائدة، فإذا ارتفعت عوائد العشر سنوات الحقيقية صار الاحتفاظ به أغلى — وهو المرشِّح الكلّي الوحيد الذي قاس إيجابياً خارج العيّنة في نصفَي تسع سنوات من بياناتنا. أمّا الدولار والأسهم ومؤشّر VIX فسقطت كلّها في هذا الاختبار، ومن المهم معرفة ذلك لأن الرواية الشائعة تقول العكس. والإصدارات المجدولة (CPI وNFP وFOMC) تعيد تسعير الذهب في ثوانٍ — ولهذا يرفض المحرّك التداول حولها."],
    ["المخاطرة ومضاعف الـR", "الـR الواحد هو المسافة من دخولك إلى وقفك — المبلغ الذي قبلت خسارته قبل أن تبدأ الصفقة. ربح ‎+2R يدفع لك اثنين منها، وخسارة ‎−1R تكلّفك واحداً. والحجم حساب لا شجاعة: الرصيد × نسبة المخاطرة ÷ مسافة الوقف. وقيمة الاستراتيجية في متوسّط الـR للصفقة لا في نسبة الربح — ‏40% رابحة بنسبة 2:1 تتقدّم على 60% رابحة بنسبة 1:2."],
    ["الجلسات بالقياس", "على بيانات هذا السوق نفسه: تداخل لندن/نيويورك أوسع فترة في اليوم بفارق واضح؛ وقاست آسيا أوسع من لندن ونيويورك — لا النطاق الضيّق الذي تقوله النصائح الشائعة؛ وقاست نيويورك الأضيق رغم أنها تحمل معظم الإصدارات. والوقف المحسوب في ساعة هادئة أضيق ممّا يبدو حين يحلّ التداخل."],
    ["الوقوف جانباً مركز", "يبلغ المحرّك إعداداً في نحو 3% من الشموع. والـ97% الباقية ليست غياباً — هي المرشِّح يرفض ظروفاً لا أفضلية مقاسة فيها. وفي كل يوم بلا صفقة يشرح المحرّك موقفه، ويريك الدفتر كم يكلّف تجاوز هذا الرفض. وأصعب انضباط في التداول هو ما يقوم به هذا المنتج عنك."],
    ["كيف تقرأ الحكم", "العنوان هو القرار. و«على ماذا يستند» يعدّد كل مدخل وموقفه — مع، أو ضد، أو مانع، أو سياق. وسطر «مانع» (إصدار وشيك، أو وقف مخاطر) يتقدّم على كل شيء: لا ينجو منه إعداد. والنتيجة من 7 تعدّ القواعد المتوافقة؛ وهي ليست احتمالاً، ولا شيء في هذا المنتج يدّعي معرفة احتمال."],
  ],
};

// The rooms the desk opens. Read-and-reply, and nothing more.
//
// Deliberately not a messenger: no direct messages, no creating a room, no
// inviting anyone. A customer can answer an invitation, read a room they
// accepted, and speak in it unless the desk has silenced them. Everything that
// creates structure lives in the operator app, which is the owner's rule and
// the reason this cannot turn into strangers messaging strangers.
function GroupsPage() {
  const { t } = useI18n();
  const { can } = useAuth();
  const allowed = can("groups");
  const [rooms, setRooms] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [thread, setThread] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const endRef = useRef(null);
  // Every write below is behind this. The page polls every ten seconds and
  // its fetches outlive a tab change: without the guard, a reply that lands
  // after the customer has navigated away writes into a component that is no
  // longer on screen -- and, worse, a slow reply can overwrite a newer one.
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  useEffect(() => {
    document.title = t("groups.page_title", "Rooms — Mithqal");
  }, [t]);

  const loadRooms = useCallback(async () => {
    if (!allowed) return;
    try {
      const r = await authenticatedFetch("/api/groups");
      const p = await r.json();
      if (!mounted.current) return;
      setRooms(p.success ? (p.groups || []) : []);
    } catch (e) { if (mounted.current) setRooms([]); }
  }, [allowed]);

  useEffect(() => { loadRooms(); }, [loadRooms]);

  // One poll for the whole page. A room is a conversation, not a price feed:
  // ten seconds is fast enough to feel live and slow enough that a phone left
  // open on this screen does not spend its battery here.
  useEffect(() => {
    if (!allowed) return undefined;
    const id = setInterval(() => {
      loadRooms();
      if (openId) loadThread(openId, { quiet: true });
    }, 10000);
    return () => clearInterval(id);
  }, [allowed, openId, loadRooms]);

  const loadThread = useCallback(async (groupId, opts) => {
    try {
      const r = await authenticatedFetch(`/api/groups/${groupId}/messages`);
      const p = await r.json();
      if (!mounted.current) return;
      if (!p.success) { setThread([]); return; }
      setThread(p.messages || []);
      const last = (p.messages || [])[(p.messages || []).length - 1];
      if (last) {
        // Marking read is fire-and-forget: a failed mark costs a stale badge,
        // and blocking the render on it would cost the whole conversation.
        authenticatedFetch(`/api/groups/${groupId}/read`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message_id: last.id })
        }).then(() => loadRooms()).catch(() => {});
      }
      if (!opts || !opts.quiet) {
        setTimeout(() => endRef.current && endRef.current.scrollIntoView(), 0);
      }
    } catch (e) { if (mounted.current) setThread([]); }
  }, [loadRooms]);

  const openRoom = useCallback((room) => {
    setOpenId(room.id); setThread(null); setError(null);
    loadThread(room.id);
  }, [loadThread]);

  const respond = useCallback(async (groupId, accept) => {
    setBusy(true); setError(null);
    try {
      const r = await authenticatedFetch(`/api/groups/${groupId}/respond`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accept })
      });
      const p = await r.json();
      if (!mounted.current) return;
      if (!p.success) setError(groupError(t, p.error));
      await loadRooms();
      if (accept && p.success && mounted.current) openRoom({ id: groupId });
    } catch (e) {
      if (mounted.current) {
        setError(t("groups.offline", "That did not reach the server. Nothing changed."));
      }
    } finally { if (mounted.current) setBusy(false); }
  }, [t, loadRooms, openRoom]);

  const send = useCallback(async () => {
    const body = draft.trim();
    if (!body || !openId) return;
    setBusy(true); setError(null);
    try {
      const r = await authenticatedFetch(`/api/groups/${openId}/messages`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body })
      });
      const p = await r.json();
      if (!mounted.current) return;
      if (p.success) { setDraft(""); await loadThread(openId); }
      // A refusal names its own rule. "muted" and "too_fast" are different
      // facts about the customer's standing in the room, and telling them
      // only that it failed is how a rule becomes a bug report.
      else setError(groupError(t, p.error));
    } catch (e) {
      if (mounted.current) {
        setError(t("groups.offline", "That did not reach the server. Nothing changed."));
      }
    } finally { if (mounted.current) setBusy(false); }
  }, [draft, openId, t, loadThread]);

  if (!allowed) {
    return html`
      <div className="app-shell">
        <${PageNav} />
        <main className="journal-body"><${ServiceOff} feature="groups" /></main>
      </div>`;
  }

  const room = (rooms || []).find((r) => r.id === openId) || null;
  const invitations = (rooms || []).filter((r) => r.state === "invited");
  const joined = (rooms || []).filter((r) => r.state === "joined");

  return html`
    <div className="app-shell">
      <${PageNav} />
      <main className="journal-body">
        <div className="analysis-head">
          <h1><i className="fa-solid fa-comments"></i> ${t("groups.title", "Rooms")}</h1>
          <p className="lede">${t("groups.lede", "Rooms the desk opened, and the people it invited. Only the desk opens a room.")}</p>
        </div>

        ${rooms === null && html`<p className="muted">${t("app.loading", "Loading…")}</p>`}

        ${rooms !== null && !rooms.length && html`
          <div className="empty-state" role="status">
            <i className="fa-regular fa-comments" aria-hidden="true"></i>
            <p>${t("groups.none", "You are not in any room yet. The desk invites you to one; nothing here is something you can open yourself.")}</p>
          </div>`}

        ${invitations.map((r) => html`
          <div className="group-invite" key=${"inv" + r.id}>
            <div>
              <strong>${r.name}</strong>
              ${r.description && html`<small>${r.description}</small>`}
              <em>${t("groups.invited_you", "The desk invited you to this room.")}</em>
            </div>
            <div className="group-invite-actions">
              <button className="btn btn-primary" disabled=${busy}
                      onClick=${() => respond(r.id, true)}>
                ${t("groups.accept", "Join")}
              </button>
              <button className="btn btn-secondary" disabled=${busy}
                      onClick=${() => respond(r.id, false)}>
                ${t("groups.decline", "No thanks")}
              </button>
            </div>
          </div>`)}

        ${joined.length > 0 && html`
          <div className="group-list">
            ${joined.map((r) => html`
              <button key=${r.id}
                      className=${"group-row" + (r.id === openId ? " is-open" : "")}
                      onClick=${() => openRoom(r)}>
                <span className="group-row-name">
                  ${r.name}
                  ${r.unread > 0 && html`<b className="group-unread">${iso(String(r.unread))}</b>`}
                </span>
                ${r.description && html`<small>${r.description}</small>`}
                ${!r.can_post && html`
                  <em className="group-muted-note">
                    <i className="fa-solid fa-microphone-slash"></i>
                    ${t("groups.you_are_muted", "You can read this room but not write in it.")}
                  </em>`}
              </button>`)}
          </div>`}

        ${room && html`
          <section className="group-thread" aria-live="polite">
            <h2>${room.name}</h2>
            ${thread === null && html`<p className="muted">${t("app.loading", "Loading…")}</p>`}
            ${thread !== null && !thread.length && html`
              <p className="muted">${t("groups.empty_room", "Nothing has been said here yet.")}</p>`}
            ${(thread || []).map((m) => html`
              <div className=${"group-msg" + (m.is_owner ? " is-desk" : "")} key=${m.id}>
                <div className="group-msg-head">
                  <b>${m.username}</b>
                  ${m.is_owner === 1 && html`
                    <span className="group-desk-badge">${t("groups.desk", "Desk")}</span>`}
                  <small dir="ltr">${formatDate(new Date(m.created_at * 1000).toISOString())}</small>
                </div>
                <p dir="auto">${m.body}</p>
              </div>`)}
            <div ref=${endRef}></div>

            ${room.archived_at
              ? html`<p className="muted">${t("groups.archived", "This room is closed. What was said in it stays.")}</p>`
              : room.can_post
                ? html`
                  <div className="group-composer">
                    <textarea value=${draft} rows="2" dir="auto"
                              maxLength=${2000}
                              placeholder=${t("groups.placeholder", "Write to the room…")}
                              onInput=${(e) => setDraft(e.target.value)}
                              onKeyDown=${(e) => {
                                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); }
                              }}></textarea>
                    <button className="btn btn-primary" disabled=${busy || !draft.trim()}
                            onClick=${send}>
                      <i className="fa-solid fa-paper-plane"></i> ${t("groups.send", "Send")}
                    </button>
                  </div>`
                : html`
                  <p className="group-muted-banner" role="status">
                    <i className="fa-solid fa-microphone-slash"></i>
                    ${t("groups.muted_banner", "The desk has turned off writing for you in this room. You still receive everything said here.")}
                  </p>`}
            ${error && html`<p className="form-error" role="alert">${error}</p>`}
          </section>`}
      </main>
      <${TabBar} />
    </div>`;
}

// Every refusal the rooms API can return, in the customer's language. A
// server that says `muted` and an interface that says "something went wrong"
// are together worse than either alone: the rule was applied and the person
// it was applied to was not told which one.
function groupError(t, reason) {
  const map = {
    muted: t("groups.err_muted", "The desk has turned off writing for you in this room."),
    too_fast: t("groups.err_too_fast", "Too many messages in a minute. Wait a moment."),
    too_long: t("groups.err_too_long", "That message is too long for a room."),
    empty: t("groups.err_empty", "Write something first."),
    not_a_member: t("groups.err_not_member", "You are not in this room."),
    not_invited: t("groups.err_not_invited", "That invitation is no longer open."),
    group_archived: t("groups.err_archived", "This room is closed."),
    no_such_group: t("groups.err_gone", "That room no longer exists."),
  };
  return map[reason] || t("groups.err_generic", "That did not go through.");
}

// The execution page: one button, and the number that must stay beside it.
//
// Batch 21. MT5 runs on the customer's OWN machine, so this page talks to a
// local connector rather than to a broker through us. Three things it refuses
// to do, each for a stated reason:
//
//   * it never asks for MT5 credentials — the connector holds them, on the
//     customer's computer, and this app never sees them;
//   * it renders "no connector" as an ANSWER, not an error, because that is
//     the state most customers are in most of the time;
//   * it shows the measured expectancy beside the automatic-execution row,
//     always. A switch that turns on automated trading sits next to the number
//     saying the edge is indistinguishable from zero, or it does not ship.
function preflightValue(req, t, language) {
  // A satisfied row shows the SETTING, not the raw column. "1" is not a risk
  // percentage and "intraday" is not a word this app shows an Arabic reader
  // anywhere else — a checklist that prints the database is a checklist the
  // customer has to translate before they can check it.
  const v = req.value;
  if (v == null || v === "") return "—";
  // THE PERCENT SIGN, and the rule behind it: Arabic PROSE uses ٪ (the locale
  // files were unified to it); numeric readouts inside dir="ltr" spans keep
  // "%" beside their Latin numerals, because iso() forces Western digits
  // everywhere in this app and "2٪" in an LTR numeric field mixes two scripts
  // in four characters. Do not "fix" one to match the other without moving
  // both.
  if (req.key === "risk_percent") return `${iso(String(v))}%`;
  if (req.key === "account_balance") {
    // A BALANCE IS MONEY. formatPrice is a PRICE formatter — two decimals, no
    // grouping, no symbol — which is right for 4,382.70 on a chart axis and
    // wrong for an account: "10000.00" is a figure the reader has to count the
    // digits of, with no clue what currency it is in. Grouped, with the sign,
    // and no cents: nobody's balance is meaningful to the cent on a checklist.
    return iso("$" + Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 }));
  }
  if (req.key === "trading_style") {
    return (language === "ar" ? req.value_ar : req.value_en) || String(v);
  }
  if (req.key === "rules") {
    return `${iso(String(v))} ${t("preflight.rules_count", "rules")}`;
  }
  return iso(String(v));
}

function AutoTradePreflight({ inline = false }) {
  // WHAT MUST BE TRUE BEFORE A MACHINE TRADES THIS ACCOUNT.
  //
  // The owner's rule (2026-08-17): a customer who has not filled in their risk
  // management does not get to switch automatic execution on — the app names
  // what is missing, links to the page that fixes it, and says plainly that it
  // has to be filled to continue.
  //
  // The list is not computed here. /api/execution/preflight decides, the same
  // function the arming route refuses against, so this screen cannot show
  // green on a state the server would reject.
  const { t, language } = useI18n();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const payload = await (await authenticatedFetch("/api/execution/preflight")).json();
      if (payload.success) setState(payload);
    } catch (e) { /* the card simply does not appear */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = useCallback(async () => {
    if (!state) return;
    setBusy(true);
    setError(null);
    try {
      const res = await authenticatedFetch("/api/execution/auto-trade", {
        method: "POST",
        body: JSON.stringify({ enabled: !state.armed })
      });
      const payload = await res.json();
      if (payload.requirements) setState({ ...payload, success: true });
      if (!payload.success) {
        setError(payload.error_key === "preflight_incomplete"
          ? t("preflight.refused", "Fill in what is missing above first.")
          : t("preflight.not_saved", "That did not save. Try again."));
      }
    } catch (e) {
      setError(t("preflight.not_saved", "That did not save. Try again."));
    } finally { setBusy(false); }
  }, [state, t]);

  if (!state) return null;

  const LABELS = {
    account_balance: ["preflight.balance", "Account balance",
                      "preflight.balance_why", "The lot size is balance × risk ÷ stop distance. Without it there is no lot to place."],
    risk_percent: ["preflight.risk", "Risk per trade",
                   "preflight.risk_why", "The other half of the same arithmetic."],
    trading_style: ["preflight.style", "Trading profile",
                    "preflight.style_why", "Scalping and swing place different orders with different stops."],
    rules: ["preflight.rules", "Your own rules",
            "preflight.rules_why", "A machine trading with no stated limit is the thing this product refuses to be."],
    connector: ["preflight.connector", "The connector",
                "preflight.connector_why", "The terminal on your computer has to be answering."]
  };

  return html`
    <section className=${inline ? "preflight preflight-inline" : "panel surface preflight"}>
      <header className="preflight-head">
        <h4>${t("preflight.requirements", "What it cannot start without")}</h4>
        <span className=${`preflight-chip ${state.ready ? "ok" : "todo"}`}>
          ${state.ready
            ? t("preflight.complete", "Complete")
            : `${iso(String(state.missing.length))} ${t("preflight.missing_word", "missing")}`}
        </span>
      </header>

      <ol className="preflight-list">
        ${state.requirements.map((req) => {
          const [key, fallback, whyKey, whyFallback] = LABELS[req.key] || [req.key, req.key, "", ""];
          return html`
            <li key=${req.key} className=${req.satisfied ? "done" : "todo"}>
              <i className=${req.satisfied
                ? "fa-solid fa-circle-check" : "fa-regular fa-circle"}></i>
              <div className="preflight-body">
                <b>${t(key, fallback)}</b>
                <small>${t(whyKey, whyFallback)}</small>
              </div>
              ${req.satisfied
                ? html`<span className="preflight-value" dir="ltr">
                    ${preflightValue(req, t, language)}
                  </span>`
                : html`<a className="btn btn-ghost preflight-fix" href=${req.fix}
                          onClick=${(e) => navigate(e, req.fix)}>
                    ${t("preflight.fix", "Fill it in")}
                    <i className="fa-solid fa-arrow-right"></i>
                  </a>`}
            </li>`;
        })}
      </ol>

      <div className="preflight-switch">
        <button type="button" disabled=${busy || (!state.ready && !state.armed)}
                className=${`btn ${state.armed ? "btn-secondary" : "btn-primary"}`}
                onClick=${toggle}>
          ${state.armed
            ? t("preflight.disarm", "Turn automatic execution off")
            : t("preflight.arm", "Turn automatic execution on")}
        </button>
        ${!state.ready && !state.armed && html`
          <small className="preflight-blocked">
            ${t("preflight.blocked", "This stays off until everything above is filled in.")}
          </small>`}
      </div>

      ${/* ARMED IS NOT TRADING, and the customer is told so in the same
            breath. Nothing in this product places an order yet; a green
            switch that implied otherwise would be the one lie the whole
            record exists to make impossible. */""}
      ${state.armed && !state.active && html`
        <p className="preflight-note">
          <i className="fa-solid fa-circle-info"></i>${" "}
          ${t("preflight.armed_not_active",
              "You have asked for it and nothing is missing. No order is being placed yet: execution starts only when the connector is live and the operator's switch is on.")}
        </p>`}
      ${error && html`<p className="preflight-error" role="alert">${error}</p>`}
    </section>`;
}

function ExecutionPage() {
  const { t } = useI18n();
  const { status } = useAuth();
  const [mt5, setMt5] = useState(null);
  const [positions, setPositions] = useState(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [connectorState, setConnectorState] = useState({ busy: false, error: null });

  // THE DOWNLOAD HAS TO AUTHENTICATE LIKE EVERYTHING ELSE.
  //
  // This was `<a href="/api/mt5/connector" download>`, and a plain navigation
  // carries no Authorization header -- it can only present the HttpOnly
  // cookie. The moment that cookie lapsed (it lives an hour; the SPA itself
  // rides a refreshed bearer token and never notices) the route answered 401
  // with a JSON body, and the browser filed it as a failed download of
  // "connector.json" -- the file the customer needs, replaced by an error
  // they cannot read, on the one page that asks them to install something.
  //
  // Fetched through authenticatedFetch it takes the same token, refresh and
  // cookie-retry path as every other call, and a failure becomes a sentence
  // on the page instead of a browser-level dead end.
  const downloadConnector = useCallback(async () => {
    setConnectorState({ busy: true, error: null });
    let url = null;
    try {
      const res = await authenticatedFetch("/api/mt5/connector");
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "mithqal_connector.py";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setConnectorState({ busy: false, error: null });
    } catch (e) {
      setConnectorState({
        busy: false,
        error: t("exec.download_failed",
                 "The download did not complete. Check you are still signed in, then try again.")
      });
    } finally {
      // Revoked on the next frame, not immediately: Chrome reads the blob
      // asynchronously after the click, and revoking in the same tick
      // cancels the save it was about to do.
      if (url) setTimeout(() => URL.revokeObjectURL(url), 30000);
    }
  }, [t]);

  useEffect(() => { document.title = t("exec.page_title", "Execution — Mithqal"); }, [t]);
  useEffect(() => {
    if (status === "guest") window.location.href = "/login?next=/execution";
  }, [status]);

  const load = useCallback(async () => {
    if (status !== "authenticated") return;
    try {
      const res = await authenticatedFetch("/api/mt5/status");
      const payload = await res.json();
      if (payload.success) { setMt5(payload.mt5); setFailed(false); }
      else setFailed(true);
    } catch (e) { setFailed(true); }
  }, [status]);

  useEffect(() => {
    load();
    // A terminal can close at any moment, and a page showing a green light
    // over a dead connection is worse than one showing nothing.
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    if (!mt5?.connected) { setPositions(null); return; }
    let alive = true;
    authenticatedFetch("/api/mt5/positions").then((r) => r.json())
      .then((p) => { if (alive && p.success) setPositions(p.positions || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [mt5?.connected]);

  const connect = useCallback(async () => {
    setBusy(true);
    try {
      await authenticatedFetch("/api/mt5/connect", { method: "POST" });
      await load();
    } catch (e) { setFailed(true); } finally { setBusy(false); }
  }, [load]);

  if (status !== "authenticated") return null;

  const account = mt5?.account || {};
  const live = Boolean(mt5?.available && mt5?.connected);
  const measured = mt5?.measured;

  return html`
    <div className="app-shell">
      <${PageNav} />
      <main className="exec-body">
        <div className="analysis-head">
          <h1><i className="fa-solid fa-plug-circle-bolt"></i> ${t("exec.title", "Execution")}</h1>
        </div>

        <section className=${"panel surface exec-status is-" + (live ? "live" : "down")}>
          <div className="exec-head">
            <b>${live ? t("exec.connected", "MT5 connected")
                      : t("exec.not_connected", "MT5 not connected")}</b>
            ${!live && html`
              <button className="btn btn-primary" onClick=${connect} disabled=${busy}>
                ${busy ? t("exec.connecting", "Connecting…") : t("exec.connect", "Connect MT5")}
              </button>`}
          </div>

          ${live ? html`
            <dl className="exec-rows">
              <div><dt>${t("exec.account", "Account")}</dt>
                   <dd dir="ltr">${account.login ? "****" + String(account.login).slice(-4) : "—"}</dd></div>
              <div><dt>${t("exec.balance", "Balance")}</dt>
                   <dd dir="ltr">${iso(String(account.balance ?? "—"))} ${account.currency || ""}</dd></div>
              <div><dt>${t("exec.equity", "Equity")}</dt>
                   <dd dir="ltr">${iso(String(account.equity ?? "—"))} ${account.currency || ""}</dd></div>
              <div><dt>${t("exec.trading", "Trading")}</dt>
                   <dd>${account.trade_allowed ? t("exec.ready", "Ready")
                                               : t("exec.not_ready", "Not available")}</dd></div>
              <div><dt>${t("exec.mode", "Mode")}</dt>
                   <dd>${mt5.demo || mt5.mode === "demo"
                        ? t("exec.mode_demo", "Demo") : t("exec.mode_live", "Live")}</dd></div>
            </dl>`
            : html`
            <p className="exec-reason">
              ${t(`exec.err_${mt5?.reason_key || "connector_not_running"}`, "")}
            </p>`}
        </section>

        ${/* Always rendered, connected or not. The switch and the number that
             argues against using it belong on the same screen; putting the
             number one page away is how a caveat becomes small print. */""}
        <section className="panel surface exec-auto">
          <div className="exec-auto-head">
            <b>${t("exec.auto", "Automatic execution")}</b>
            <span className=${mt5?.auto_execute ? "pos" : ""}>
              ${mt5?.auto_execute ? t("exec.auto_on", "On") : t("exec.auto_off", "Off")}
            </span>
          </div>
          <p className="pretrade-note">${t("exec.auto_note", "")}</p>
          <h4>${t("exec.measured", "What the record says about trading this automatically")}</h4>
          <p className="exec-measured">
            ${measured && measured.expectancy_r != null
              ? formatTemplate(t("exec.measured_line", ""), {
                  r: iso(formatR(measured.expectancy_r).replace("R", "")),
                  low: iso(String(measured.ci_low)),
                  high: iso(String(measured.ci_high)),
                })
              : t("exec.measured_none", "")}
          </p>
          ${/* ONE CARD ABOUT AUTOMATIC EXECUTION, NOT TWO. The checklist was a
                second panel directly beneath this one, with its own heading
                that also said "automatic execution" — two cards, adjacent,
                reading opposite states, on the screen where somebody decides
                whether a machine may trade their account. The switch, the
                number that argues against using it, and the requirements it
                cannot start without are one subject and now one card. */""}
          <${AutoTradePreflight} inline />
        </section>

        ${live && html`
          <section className="panel surface">
            <h3>${t("exec.positions", "Open in the terminal")}</h3>
            ${positions?.length > 0 ? html`
              <div className="table-scroll">
                <table className="exec-table" dir="ltr">
                  <thead><tr>
                    <th>${t("exec.ticket", "Ticket")}</th>
                    <th>${t("exec.side", "Side")}</th>
                    <th>${t("exec.volume", "Volume")}</th>
                    <th>${t("exec.entry", "Entry")}</th>
                  </tr></thead>
                  <tbody>
                    ${positions.map((row) => html`
                      <tr key=${row.ticket}>
                        <td>${iso(String(row.ticket))}</td>
                        <td>${t(`signals.${row.side}`, row.side)}</td>
                        <td>${iso(String(row.volume))}</td>
                        <td>${iso(String(row.price_open))}</td>
                      </tr>`)}
                  </tbody>
                </table>
              </div>`
              : html`<p className="perf-empty">${t("exec.no_positions", "")}</p>`}
          </section>`}

        <section className="panel surface exec-setup">
          <h3>${t("exec.setup", "How to connect")}</h3>
          <ol>
            <li>${t("exec.step1", "")}</li>
            <li>
              ${t("exec.step2", "")}
              ${/* The file itself, one click away. "Run a connector on your
                   machine" is only a simple instruction if the customer does
                   not have to go find the file first — that hunt IS the
                   technical configuration this was told not to require. */""}
              <div className="exec-download">
                <button className="btn btn-secondary" type="button"
                        onClick=${downloadConnector} disabled=${connectorState.busy}>
                  <i className=${connectorState.busy
                    ? "fa-solid fa-spinner fa-spin" : "fa-solid fa-download"}></i>${" "}
                  ${connectorState.busy
                    ? t("exec.downloading", "Preparing…")
                    : t("exec.download", "Download the connector")}
                </button>
                <small>${t("exec.download_note", "")}</small>
                ${connectorState.error && html`
                  <p className="exec-download-error" role="alert">
                    <i className="fa-solid fa-triangle-exclamation"></i>${" "}
                    ${connectorState.error}
                  </p>`}
              </div>
              <p className="exec-run">${t("exec.run_cmd", "")}</p>
              <code className="exec-cmd" dir="ltr">${t("exec.run_line", "python mithqal_connector.py")}</code>
              <small className="settings-hint">${t("exec.run_demo", "")}</small>
            </li>
            <li>${t("exec.step3", "")}</li>
          </ol>
          <p className="settings-hint">${t("exec.needs_python", "")}</p>
          <p className="settings-hint">${t("exec.why_local", "")}</p>
        </section>
        ${failed && html`<p className="perf-empty">${t("exec.retry", "Try again")}</p>`}
      </main>
    </div>`;
}


// The seventeen terms in locales/*.json `glossary`, in reading order rather
// than alphabetical: a beginner meets ATR and R before they meet a confidence
// interval. GLOSSARY_LATIN are the ones whose LABEL stays Latin in Arabic —
// every desk says RSI and ADX, and an Arabic transliteration of an acronym is
// a word nobody searches for.
const GLOSSARY_TERMS = [
  "r", "stop_loss", "take_profit", "risk_reward", "atr", "adx", "rsi", "macd",
  "session", "spread", "slippage", "pivot", "confluence", "regime",
  "expectancy", "drawdown", "confidence_interval",
];
// Pure-Latin labels only. "r" is NOT here: its Arabic label is "الـR (وحدة
// المخاطرة)" — mixed script, and forcing dir=ltr on it reversed the Arabic and
// printed "JIR". A bidi override belongs only on a string with nothing
// bidirectional in it.
const GLOSSARY_LATIN = ["atr", "adx", "rsi", "macd"];

function Glossary() {
  // THE GLOSSARY THE CHAT ALREADY PROMISES.
  //
  // src/services/chat_answer.py answers every beginner "what is X" with
  // `chat.glossary` — "Open the glossary for that term — it is defined there
  // with what it does and does not tell you." Seventeen terms are written in
  // both languages in the locale files. Nothing rendered them, anywhere. The
  // product was sending its least confident customers to a screen that did
  // not exist, from inside the one feature built to answer them.
  //
  // The definitions live in locales/*.json rather than here for the same
  // reason the chat can cite them: one text, one place, and the assistant and
  // the page cannot define a term differently.
  const { t, language } = useI18n();
  const [open, setOpen] = useState(null);

  const terms = GLOSSARY_TERMS.filter((key) => t(`glossary.${key}`, "") !== "");
  if (!terms.length) return null;

  return html`
    <section className="panel surface glossary" id="glossary">
      <h3><i className="fa-solid fa-book"></i>${" "}
        ${t("help.glossary_title", "The words, defined")}</h3>
      <p className="journal-note">
        ${t("help.glossary_note", "What each term means, and what it does not tell you — which is the half most definitions leave out.")}
      </p>
      <dl className="glossary-list">
        ${terms.map((key) => html`
          <div key=${key} className=${open === key ? "is-open" : ""}>
            <dt>
              <button type="button" onClick=${() => setOpen(open === key ? null : key)}
                      aria-expanded=${open === key}>
                <span dir=${GLOSSARY_LATIN.includes(key) ? "ltr" : "auto"}>
                  ${t(`glossary_label.${key}`, key.toUpperCase())}
                </span>
                <i className=${`fa-solid ${open === key ? "fa-minus" : "fa-plus"}`}
                   aria-hidden="true"></i>
              </button>
            </dt>
            ${open === key && html`<dd lang=${language}>${t(`glossary.${key}`, "")}</dd>`}
          </div>`)}
      </dl>
    </section>`;
}

function HelpPage() {
  const { t, language } = useI18n();
  useEffect(() => {
    document.title = t("help.page_title", "How it works — Mithqal");
  }, [t]);
  const sections = HELP_CONTENT[language] || HELP_CONTENT.en;
  const guides = HELP_GUIDES[language] || HELP_GUIDES.en;
  return html`
    <div className="app-shell">
      <${PageNav} />
      <main className="journal-body">
        <div className="analysis-head">
          <h1><i className="fa-solid fa-circle-question"></i> ${t("help.title", "How it works")}</h1>
        </div>
        ${sections.map(([head, body]) => html`
          <section className="panel surface journal-section" key=${head}>
            <h3>${head}</h3>
            <p style=${{ lineHeight: 1.7, color: "var(--text-2)", margin: 0 }}>${body}</p>
          </section>`)}
        <div className="analysis-head" style=${{ marginTop: "28px" }}>
          <h1><i className="fa-solid fa-graduation-cap"></i> ${t("help.guides_title", "Gold, without the folklore")}</h1>
        </div>
        <p className="journal-note">${t("help.guides_note", "Short guides in this product's measured voice — every claim below is arithmetic, or something this system actually measured on its own data.")}</p>
        ${guides.map(([head, body]) => html`
          <section className="panel surface journal-section" key=${head}>
            <h3>${head}</h3>
            <p style=${{ lineHeight: 1.7, color: "var(--text-2)", margin: 0 }}>${body}</p>
          </section>`)}
        <${Glossary} />
      </main>
    </div>`;
}

// Four coach cards, first visit only. Not a modal maze: one small sheet,
// next/skip, done forever. The product explains its shape once, then gets
// out of the way.
function WelcomeTour() {
  const { t } = useI18n();
  const [step, setStep] = useState(() => {
    try { return localStorage.getItem("sv_tour_done") === "1" ? -1 : 0; }
    catch (e) { return -1; }
  });
  const finish = useCallback(() => {
    try { localStorage.setItem("sv_tour_done", "1"); } catch (e) { /* private mode */ }
    setStep(-1);
  }, []);
  if (step < 0) return null;
  const steps = [
    [t("tour.brief_title", "The top strip is your morning"),
     t("tour.brief_body", "One line: is anything about to happen? Tap it for the session, the schedule, and the levels that matter.")],
    [t("tour.verdict_title", "The verdict is the answer"),
     t("tour.verdict_body", "One card says trade or no-trade, and why. 'No setup' most hours is the filter working — not the app sleeping.")],
    [t("tour.questions_title", "Everything else is a question"),
     t("tour.questions_body", "Macro, the record, headlines, raw indicators — each waits behind its question, in your order of importance.")],
    [t("tour.alerts_title", "Turn on alerts once"),
     t("tour.alerts_body", "In Settings: choose your style, set your risk, enable alerts, send yourself a test. After that, the desk comes to you.")],
  ];
  const [title, body] = steps[step];
  const last = step === steps.length - 1;
  return html`
    <div className="tour-scrim" role="dialog" aria-modal="true" aria-label=${title}>
      <div className="tour-card">
        <div className="tour-dots" aria-hidden="true">
          ${steps.map((_, i) => html`<span key=${i} className=${i === step ? "is-on" : ""}></span>`)}
        </div>
        <h3>${title}</h3>
        <p>${body}</p>
        <div className="tour-actions">
          <button className="btn btn-ghost" onClick=${finish}>${t("tour.skip", "Skip")}</button>
          <button className="btn btn-primary"
                  onClick=${() => (last ? finish() : setStep(step + 1))}>
            ${last ? t("tour.done", "Start trading honestly") : t("tour.next", "Next")}
          </button>
        </div>
      </div>
    </div>`;
}

// ─────────────────────────────────────────────────── the command palette
//
// Master list §16 (professional UX). Ctrl/Cmd-K, or "/" from anywhere that
// is not already a text field.
//
// A palette earns its place only if it does things. A list of links behind a
// keyboard shortcut is a slower navigation menu, so this one carries actions
// too — set the timeframe, change the language, open a search of history —
// and every entry states what it will do rather than naming a screen.
//
// Deliberately NOT fuzzy-matching. A palette that reorders as you type is a
// palette where the item under your finger changes between the keystroke and
// the Enter, and the actions here change settings. Plain substring matching,
// stable order, and the selection follows the filtered list by index.
const PALETTE_COMMANDS = (t) => [
  { id: "dashboard", icon: "fa-gauge-high", go: "/dashboard",
    label: t("cmd.dashboard", "Go to the dashboard"),
    keywords: "home verdict price" },
  { id: "chart", icon: "fa-chart-line", go: "/chart",
    label: t("cmd.chart", "Open the chart"), keywords: "candles draw levels" },
  { id: "analysis", icon: "fa-wand-magic-sparkles", go: "/analysis",
    label: t("cmd.analysis", "Run an AI analysis"), keywords: "ai model read" },
  { id: "journal", icon: "fa-book", go: "/journal",
    label: t("cmd.journal", "Open my journal"),
    keywords: "trades positions record alerts" },
  { id: "performance", icon: "fa-chart-pie", go: "/performance",
    label: t("cmd.performance", "See the measured record"),
    keywords: "results honest ledger" },
  { id: "news", icon: "fa-newspaper", go: "/news",
    label: t("cmd.news", "Read the headlines"), keywords: "market news" },
  { id: "events", icon: "fa-calendar-day", go: "/events",
    label: t("cmd.events", "See what is scheduled"),
    keywords: "calendar economic red folder" },
  { id: "settings", icon: "fa-sliders", go: "/settings",
    label: t("cmd.settings", "Open settings"),
    keywords: "theme language appearance" },
  { id: "execution", icon: "fa-plug-circle-bolt", go: "/execution",
    label: t("cmd.execution", "Connect MT5"),
    keywords: "mt5 metatrader connect execute broker terminal" },
  { id: "trading", icon: "fa-gauge-high", go: "/trading",
    label: t("cmd.trading", "Trading and risk"),
    keywords: "risk balance style rules cooldown size" },
  { id: "alerts", icon: "fa-bell", go: "/alerts",
    label: t("cmd.alerts", "Alerts"), keywords: "notifications push sound" },
  { id: "account", icon: "fa-user", go: "/account",
    label: t("cmd.account", "Your account"),
    keywords: "password subscription expiry" },
  { id: "help", icon: "fa-circle-question", go: "/help",
    label: t("cmd.help", "How this product works"), keywords: "help about" },
  { id: "size", icon: "fa-calculator", go: "/trading#size",
    label: t("cmd.size", "Work out a position size"),
    keywords: "lot risk calculator" },
  { id: "rules", icon: "fa-scroll", go: "/trading#rules",
    label: t("cmd.rules", "Edit my own trading rules"),
    keywords: "constitution discipline" },
  { id: "research", icon: "fa-magnifying-glass-chart", go: "/dashboard#research",
    label: t("cmd.research", "Search history for days like this"),
    keywords: "scan similar past" },
];

function CommandPalette() {
  const { t, language, setLanguage } = useI18n();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    const onKey = (event) => {
      const el = document.activeElement;
      const typing = el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA"
                            || el.tagName === "SELECT" || el.isContentEditable);
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((v) => !v);
      } else if (event.key === "/" && !typing && !event.ctrlKey && !event.metaKey) {
        event.preventDefault();
        setOpen(true);
      } else if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery(""); setCursor(0);
      // The focus has to wait for the input to exist.
      const id = setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [open]);

  const commands = useMemo(() => {
    const base = PALETTE_COMMANDS(t);
    base.push({
      id: "language", icon: "fa-language",
      label: language === "ar" ? t("cmd.to_en", "Switch to English")
                               : t("cmd.to_ar", "التبديل إلى العربية"),
      keywords: "language arabic english lang",
      run: () => { const next = language === "ar" ? "en" : "ar";
                   setLanguage(next); persistLanguage(next); },
    });
    return base;
  }, [t, language, setLanguage]);

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return commands;
    return commands.filter((c) =>
      c.label.toLowerCase().includes(needle)
      || (c.keywords || "").includes(needle));
  }, [commands, query]);

  const run = useCallback((command) => {
    setOpen(false);
    if (!command) return;
    if (command.run) command.run();
    else if (command.go) window.location.href = command.go;
  }, []);

  const onKeyDown = useCallback((event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setCursor((c) => Math.min(c + 1, Math.max(0, shown.length - 1)));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setCursor((c) => Math.max(0, c - 1));
    } else if (event.key === "Enter") {
      event.preventDefault();
      run(shown[cursor]);
    }
  }, [shown, cursor, run]);

  if (!open) return null;

  return html`
    <div className="palette-scrim" onClick=${() => setOpen(false)}>
      <div className="palette" role="dialog" aria-modal="true"
           aria-label=${t("cmd.title", "Commands")}
           onClick=${(e) => e.stopPropagation()}>
        <div className="palette-field">
          <i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
          <input ref=${inputRef} type="text" value=${query}
                 placeholder=${t("cmd.placeholder", "What do you want to do?")}
                 aria-label=${t("cmd.placeholder", "What do you want to do?")}
                 onInput=${(e) => { setQuery(e.target.value); setCursor(0); }}
                 onKeyDown=${onKeyDown} />
          <kbd>esc</kbd>
        </div>
        ${shown.length === 0
          ? html`<p className="palette-empty">
              ${t("cmd.nothing", "Nothing here matches that.")}</p>`
          : html`
            <ul className="palette-list" role="listbox">
              ${shown.map((command, i) => html`
                <li key=${command.id} role="option" aria-selected=${i === cursor}
                    className=${i === cursor ? "is-cursor" : ""}
                    onMouseEnter=${() => setCursor(i)}
                    onClick=${() => run(command)}>
                  <i className=${"fa-solid " + command.icon} aria-hidden="true"></i>
                  <span>${command.label}</span>
                </li>`)}
            </ul>`}
        <div className="palette-foot">
          <span><kbd>↑</kbd><kbd>↓</kbd> ${t("cmd.move", "move")}</span>
          <span><kbd>↵</kbd> ${t("cmd.choose", "choose")}</span>
          <span><kbd>ctrl</kbd><kbd>k</kbd> ${t("cmd.reopen", "reopen")}</span>
        </div>
      </div>
    </div>`;
}

function App() {
  const path = useRoutePath();
  const { user, status } = useAuth();
  const { t } = useI18n();
  const [showFirstRun, setShowFirstRun] = useState(false);

  useEffect(() => {
    if (!user) return undefined;
    // A deliberate replay from Settings outranks every "already done" flag.
    let replay = false;
    try { replay = localStorage.getItem("sv_wizard_replay") === "1"; } catch (e) {}
    if (replay) { setShowFirstRun(true); return undefined; }
    let seen = false;
    try { seen = localStorage.getItem("sv-first-run-done") === "1"; } catch (e) {}
    if (seen) return undefined;
    let cancelled = false;
    authenticatedFetch("/api/preferences").then((r) => r.json())
      .then((p) => {
        const prefs = p.preferences || {};
        // Only when sizing is genuinely unconfigured. Asking a returning user
        // to re-enter what they already told us is how an onboarding screen
        // becomes the thing people complain about.
        if (!cancelled && !prefs.account_balance) setShowFirstRun(true);
        else { try { localStorage.setItem("sv-first-run-done", "1"); } catch (e) {} }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user]);
  // The server is not answering and the session is still valid. Say so, keep
  // the credentials, and let the retry in AuthProvider do its work -- instead
  // of a blank page, a login bounce, or a spinner that never ends.
  if (status === "unreachable" && path !== "/login") {
    return html`
      <div className="app-shell">
        <main className="chart-overlay" role="status">
          <div>
            <i className="fa-solid fa-plug-circle-exclamation" aria-hidden="true"></i>
            <p>${t("app.unreachable", "Cannot reach the server right now. You are still signed in — this retries on its own.")}</p>
            <button className="btn btn-secondary chart-retry"
                    onClick=${() => window.location.reload()}>
              ${t("chart.retry", "Try again")}
            </button>
          </div>
        </main>
      </div>`;
  }

  if (path === "/login") return html`<${LoginPage} />`;

  // A temporary password issued by the operator gets the user in and no
  // further: leaving it usable would mean the operator keeps knowing a working
  // credential. One screen, one job, until they choose their own.
  if (user && user.must_change_password) {
    return html`<${ForcedPasswordChange} />`;
  }

  // First run, on the dashboard only, and only when the app genuinely cannot
  // size a position yet. Gated on the SERVER's answer, not just a local flag,
  // so a user who set this up on their phone is not asked again on a laptop.
  if ((path === "/" || path === "/dashboard") && showFirstRun) {
    return html`<${FirstRun} onDone=${() => setShowFirstRun(false)} />`;
  }

  // The bottom tab bar rides every authenticated page, mounted once here so a
  // page cannot forget it.
  let page;
  if (path === "/chart") page = html`<${ChartPage} />`;
  else if (path === "/calendar") page = html`<${EventsPage} />`;
  else if (path === "/limits") page = html`<${LimitsPage} />`;
  else if (path === "/analysis") page = html`<${AnalysisPage} />`;
  else if (path === "/journal") page = html`<${JournalPage} />`;
  else if (path === "/performance") page = html`<${PerformancePage} />`;
  else if (path === "/academy") page = html`<${AcademyPage} />`;
  else if (path === "/events") page = html`<${EventsPage} />`;
  else if (path === "/help") page = html`<${HelpPage} />`;
  else if (path === "/groups") page = html`<${GroupsPage} />`;
  // Settings held four unrelated families in 415 lines: the account, app
  // preferences, alerts, and trading controls that change what the ENGINE
  // does. The owner's objection was exact -- "there are things that don't
  // belong in settings" -- and a max-risk-per-trade field is not the same kind
  // of thing as a dark theme.
  //
  // Four routes, one component. Separate URLs and separate rendering, which is
  // what the split is for; a second copy of the loader would be a second thing
  // to keep in step. /settings keeps its name and holds only what the word
  // actually means.
  else if (path === "/execution") page = html`<${ExecutionPage} />`;
  else if (path === "/settings") page = html`<${SettingsPage} section="app" />`;
  else if (path === "/account") page = html`<${SettingsPage} section="account" />`;
  else if (path === "/trading") page = html`<${SettingsPage} section="trading" />`;
  else if (path === "/alerts") page = html`<${SettingsPage} section="alerts" />`;
  else if (path === "/news") page = html`<${NewsPage} />`;
  else if (path.startsWith("/news/")) page = html`<${NewsDetailPage} />`;
  else page = html`<${DashboardPage} />`;
  return html`${page}<${TabBar} /><${CommandPalette} />`;
}

const root = createRoot(document.getElementById("root"));
root.render(html`<${ErrorBoundary}><${I18nProvider}><${AuthProvider}><${App} /><//><//><//>`);

// The sign-in page is shared with the operator app, which is served on its own
// port and deliberately has NO service worker -- a cached operator screen
// during an incident is the opposite of what that app is for. Registering
// there only produced a 404 in the console, so ask the server which app this
// page belongs to before registering.
const IS_CUSTOMER_APP = (document.querySelector('meta[name="sv-app"]') || {}).content !== "operator";

if ("serviceWorker" in navigator && IS_CUSTOMER_APP) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/service-worker.js").catch(() => {});
  });

  // The service worker pings open tabs when a push lands, so the in-app
  // chime plays even if the OS notification is what the user notices first.
  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event.data && event.data.type === "sv-alert") playAlertSound();
  });

  // Opening the app is reading the alert. The service worker only closed a
  // notification inside its notificationclick handler, so arriving any other
  // way -- tapping the dock icon, switching to an open tab -- left the
  // notification undismissed in the OS centre and the installed app kept an
  // unread badge that nothing could clear. Dismiss on every return to the
  // foreground, and clear the badge where the Badging API exists.
  const dismissDeliveredNotifications = () => {
    if (document.visibilityState !== "visible") return;
    navigator.serviceWorker.ready
      .then((registration) => registration.getNotifications())
      .then((notifications) => notifications.forEach((n) => n.close()))
      .catch(() => {});
    if (navigator.clearAppBadge) {
      navigator.clearAppBadge().catch(() => {});
    }
  };

  document.addEventListener("visibilitychange", dismissDeliveredNotifications);
  window.addEventListener("focus", dismissDeliveredNotifications);
  dismissDeliveredNotifications();
}
